import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { getErrorMessage } from "./utils/errorHandler";
import ErrorAlert from "./components/ErrorAlert";
import LLMStatusIndicator from "./components/LLMStatusIndicator";
import SearchModeToggle from "./components/SearchModeToggle";
import AISearchProgress from "./components/AISearchProgress";
import InfrastructureDashboard from "./components/InfrastructureDashboard";
import HierarchyManagement from "./components/HierarchyManagement";
import ROIWidget from "./components/ROIWidget";
import { TmpLabsLogo, AriseBrand } from "./components/brand";
import ThemeToggle from "./components/ThemeToggle";
import { Icon } from "./components/Icon";
import { useColorScheme } from "./hooks/useColorScheme";
import { trackEvent } from "./lib/posthog";

// Liquid Design System Components
import {
  NextBestAction,
  EmptyState,
  ActiveJDContext,
  JDNextStep,
  SetupStepper,
  SetupProgress,
  DensityToggle,
  DensityProvider,
  useDensity,
} from "./components/liquid";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// TmpLabsLogo imported from ./components/brand

// AI Assistant Avatar Component - Professional Young Lady (for UI elements, not branding)
const AIAvatar = ({ className = "", animate = true, size = "md" }) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12", 
    lg: "w-16 h-16",
    xl: "w-20 h-20"
  };
  
  return (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      <div className="w-full h-full rounded-full overflow-hidden border border-line shadow-sm">
        <img 
          src="https://cdn.gamma.app/x7b9vzc41j0qqk1/generated-images/zEfsDV_5275YWXRxwPiYZ.png"
          alt="AI Assistant"
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to a simple emoji if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        {/* Fallback display */}
        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-bold" style={{display: 'none'}}>
          👩‍💼
        </div>
      </div>
      
      {/* AI Indicator - smaller for UI use */}
      {animate && size !== 'sm' && (
        <div className="absolute -inset-0.5 rounded-full border border-cyan-400 opacity-50 animate-pulse">
        </div>
      )}
    </div>
  );
};

// User Dropdown Menu Component
const UserDropdownMenu = ({ user, systemStats, onLogout, onHelp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [llmStatus, setLlmStatus] = useState(null);

  // Get LLM status
  useEffect(() => {
    const fetchLLMStatus = async () => {
      try {
        const response = await axios.get(`${API}/llm/status`);
        setLlmStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch LLM status:', error);
      }
    };
    fetchLLMStatus();
  }, []);

  const getRoleDisplay = (role) => {
    const roleMap = {
      'admin': { icon: '👑', label: 'Administrator' },
      'auditor': { icon: '🔍', label: 'Auditor' },
      'recruiter': { icon: '👤', label: 'Recruiter' },
      'viewer': { icon: '👁️', label: 'Viewer' }
    };
    return roleMap[role] || { icon: '👤', label: 'User' };
  };

  const roleInfo = getRoleDisplay(user?.role);

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 bg-muted hover:bg-subtle rounded-lg transition-colors border border-line"
      >
        <div className="text-right">
          <div className="text-sm font-medium text-ink">{user?.full_name || 'User'}</div>
          <div className="text-xs text-ink-subtle">{roleInfo.label}</div>
        </div>
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white text-sm font-bold">
          {(user?.full_name || 'U')[0].toUpperCase()}
        </div>
        <svg className={`w-4 h-4 text-ink-subtle transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-elevated rounded-lg shadow-lg border border-line z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-line-subtle bg-muted">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold">
                {(user?.full_name || 'U')[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-ink">{user?.full_name || 'User'}</div>
                <div className="text-sm text-ink-subtle">{user?.email}</div>
                <div className="flex items-center space-x-1 mt-1">
                  <span className="text-sm">{roleInfo.icon}</span>
                  <span className="text-xs font-medium text-blue-600">{roleInfo.label}</span>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="px-4 py-3 border-b border-line-subtle">
            <div className="text-xs font-medium text-ink-subtle uppercase tracking-wide mb-2">System Status</div>
            
            {/* Profile Count */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-ink-muted">Database</span>
              </div>
              <span className="text-sm font-medium text-green-600">
                {systemStats ? `${systemStats.total_profiles} profiles` : 'Loading...'}
              </span>
            </div>

            {/* LLM Status */}
            {llmStatus && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${llmStatus.health === 'healthy' ? 'bg-green-400 animate-pulse' : llmStatus.health === 'offline' ? 'bg-gray-400' : 'bg-red-400'}`}></div>
                  <span className="text-sm text-ink-muted">AI Engine</span>
                </div>
                <span className={`text-sm font-medium ${
                  llmStatus.health === 'healthy' ? 'text-green-600' : 
                  llmStatus.health === 'offline' ? 'text-ink-subtle' : 'text-red-600'
                }`}>
                  {llmStatus.health === 'healthy' ? llmStatus.model : llmStatus.status}
                </span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Liquid Design: Density Toggle */}
            <div className="px-4 py-2 border-b border-line-subtle">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-muted">View Density</span>
                <DensityToggle showLabel={true} />
              </div>
            </div>

            <button
              onClick={() => {
                onHelp();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-ink-muted hover:bg-muted flex items-center space-x-3"
            >
              <span className="text-blue-500">❓</span>
              <span>Help & FAQ</span>
            </button>

            <button
              onClick={() => {
                onLogout();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-ink-muted hover:bg-muted flex items-center space-x-3"
            >
              <span className="text-red-500">🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// WORKFLOW STEPPER - Visual hiring pipeline indicator
// ============================================================================
const WorkflowStepper = ({ activeTab, onStepClick, userRole, hasPermission }) => {
  // Define hiring workflow stages with role-based visibility
  const workflowStages = [
    {
      id: 'define',
      label: 'Define',
      icon: '✍️',
      tabs: ['jd-builder'],
      description: 'Create job descriptions',
      roles: ['admin', 'manager'] // BO and TAM can define JDs
    },
    {
      id: 'source',
      label: 'Source',
      icon: '📥',
      tabs: ['upload', 'email-settings'],
      description: 'Upload CVs & Email intake',
      roles: ['admin', 'manager', 'recruiter'] // All can source
    },
    {
      id: 'search',
      label: 'Search',
      icon: '🔍',
      tabs: ['search', 'profiles'],
      description: 'Find candidates',
      roles: ['admin', 'manager', 'recruiter']
    },
    {
      id: 'evaluate',
      label: 'Evaluate',
      icon: '⭐',
      tabs: ['shortlist'],
      description: 'Shortlist candidates',
      roles: ['admin', 'manager', 'recruiter']
    },
    {
      id: 'interview',
      label: 'Interview',
      icon: '🗓️',
      tabs: ['interviews'],
      description: 'Schedule interviews',
      roles: ['admin', 'manager', 'recruiter']
    },
    {
      id: 'ai-intelligence',
      label: 'AI Engine',
      icon: '🧠',
      tabs: ['ai-intelligence'],
      description: 'AutoMatch & Identity Resolution',
      roles: ['admin', 'manager'] // Only BO and TAM see AI Intelligence
    },
    {
      id: 'analyze',
      label: 'Analyze',
      icon: '📊',
      tabs: ['analytics'],
      description: 'View insights',
      roles: ['admin', 'manager'] // Only BO and TAM see analytics
    }
  ];

  // Filter stages based on user role
  const visibleStages = workflowStages.filter(stage => {
    if (userRole === 'admin') return true; // BO sees all
    if (userRole === 'manager') return stage.roles.includes('manager');
    if (userRole === 'recruiter') return stage.roles.includes('recruiter');
    return false;
  });

  // Determine current stage based on active tab
  const getCurrentStageIndex = () => {
    for (let i = 0; i < visibleStages.length; i++) {
      if (visibleStages[i].tabs.includes(activeTab)) return i;
    }
    return -1; // Dashboard or other tab
  };

  const currentStageIndex = getCurrentStageIndex();
  const stageIcon = { define: 'requirements', source: 'import', search: 'search', evaluate: 'evaluation', interview: 'interviews', 'ai-intelligence': 'ai', analyze: 'analytics' };

  return (
    <div className="bg-surface border-b border-line px-6 py-2.5">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          {/* Workflow Steps */}
          <div className="flex items-center space-x-1 overflow-x-auto no-scrollbar">
            {visibleStages.map((stage, index) => {
              const isActive = stage.tabs.includes(activeTab);
              const isCompleted = index < currentStageIndex;
              const isClickable = stage.tabs.some(tab => {
                if (tab === 'jd-builder') return hasPermission('canViewJDs');
                if (tab === 'upload') return hasPermission('canUploadProfiles');
                if (tab === 'search' || tab === 'profiles') return hasPermission('canViewProfiles');
                if (tab === 'shortlist') return hasPermission('canManageShortlist');
                if (tab === 'interviews') return hasPermission('canViewInterviews');
                if (tab === 'analytics') return hasPermission('canViewAnalytics');
                if (tab === 'email-settings') return hasPermission('canManageTenant');
                return true;
              });

              return (
                <React.Fragment key={stage.id}>
                  {/* Step */}
                  <button
                    onClick={() => isClickable && onStepClick(stage.tabs[0])}
                    disabled={!isClickable}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : isCompleted
                        ? 'bg-accent-soft text-accent-ink hover:opacity-80'
                        : isClickable
                        ? 'text-ink-muted hover:text-ink hover:bg-muted'
                        : 'text-ink-subtle/60 cursor-not-allowed'
                    }`}
                    title={stage.description}
                  >
                    <span className="shrink-0">
                      {isCompleted && !isActive ? <span className="text-sm font-bold">✓</span> : <Icon name={stageIcon[stage.id]} className="w-4 h-4" />}
                    </span>
                    <span className="hidden sm:block text-xs font-semibold">{stage.label}</span>
                  </button>

                  {/* Connector Line */}
                  {index < visibleStages.length - 1 && (
                    <div className={`w-8 h-0.5 ${
                      index < currentStageIndex ? 'bg-blue-400' : 'bg-line'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Workflow Guide */}
          <div className="hidden lg:flex items-center space-x-2 ml-4 text-xs text-ink-subtle">
            <span className="w-2 h-2 rounded-full bg-blue-400"></span>
            <span>Completed</span>
            <span className="w-2 h-2 rounded-full bg-blue-600 ml-2"></span>
            <span>Current</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HIRING DASHBOARD - Role-based landing page with guided actions
// ============================================================================
const HiringDashboard = ({
  user,
  systemStats,
  shortlistCount,
  onNavigate,
  hasPermission,
  isFeatureEnabled
}) => {
  const [interviewStats, setInterviewStats] = useState({ today: 0, thisWeek: 0, pending: 0 });
  const [jdStats, setJdStats] = useState({ active: 0, draft: 0 });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const interviewRes = await axios.get(`${API}/interviews/overview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (interviewRes.data) {
        setInterviewStats({
          today: interviewRes.data.today_interviews || 0,
          thisWeek: interviewRes.data.this_week_interviews || 0,
          pending: interviewRes.data.pending_confirmations || 0
        });
      }

      const jdRes = await axios.get(`${API}/jd?status=active`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (jdRes.data) {
        setJdStats({
          active: jdRes.data.filter(j => j.status === 'active').length,
          draft: jdRes.data.filter(j => j.status === 'draft').length
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.full_name?.split(' ')[0] || 'there';

  // Derived KPIs from real counts (honest estimates, clearly labelled)
  const profilesSourced = systemStats?.total_profiles || 0;
  const shortlisted = shortlistCount || 0;
  const interviewsCount = interviewStats.thisWeek || 0;
  const hoursSaved = Math.round((profilesSourced * 2) / 60); // ~2 min manual screening saved per CV
  const costSaved = hoursSaved * 45; // ~$45/hr recruiter time
  const roleLabel = user?.role === 'admin' ? 'Business Owner' : user?.role === 'manager' ? 'Team Manager' : 'Recruiter';

  const kpis = [
    { label: 'Active Requirements', value: jdStats.active, tab: 'jd-builder', icon: 'requirements', perm: 'canViewJDs', feature: 'jd_builder' },
    { label: 'Profiles Sourced', value: profilesSourced.toLocaleString(), tab: 'profiles', icon: 'talent', perm: 'canViewProfiles' },
    { label: 'Shortlisted', value: shortlisted, tab: 'shortlist', icon: 'evaluation', perm: 'canManageShortlist' },
    { label: 'Interviews', value: interviewsCount, tab: 'interviews', icon: 'interviews', perm: 'canViewInterviews' },
    { label: 'Hours Saved', value: `${hoursSaved}h`, icon: 'clock' },
    { label: 'Cost Saved', value: `$${costSaved.toLocaleString()}`, icon: 'bolt' },
  ].filter(k => !k.perm || hasPermission(k.perm)).filter(k => !k.feature || isFeatureEnabled(k.feature));

  const funnel = [
    { label: 'Profiles Sourced', value: profilesSourced, tab: 'profiles' },
    { label: 'Shortlisted', value: shortlisted, tab: 'shortlist' },
    { label: 'Interviewing', value: interviewsCount, tab: 'interviews' },
    { label: 'Selected', value: 0, tab: 'shortlist' },
  ];
  const funnelMax = Math.max(1, ...funnel.map(f => f.value));

  const nextActions = [
    { title: 'Create Hiring Requirement', desc: 'Define a role with AI', tab: 'jd-builder', icon: 'requirements', perm: 'canViewJDs', feature: 'jd_builder' },
    { title: 'Import Candidate Profiles', desc: 'Bulk or single upload', tab: 'upload', icon: 'import', perm: 'canUploadProfiles' },
    { title: 'Search Talent Pool', desc: 'AI semantic matching', tab: 'search', icon: 'search', perm: 'canViewProfiles' },
    { title: 'Review Evaluation Queue', desc: 'Shortlist & schedule', tab: 'shortlist', icon: 'evaluation', perm: 'canManageShortlist' },
  ].filter(a => !a.perm || hasPermission(a.perm)).filter(a => !a.feature || isFeatureEnabled(a.feature));

  return (
    <div className="space-y-5">
      {/* Command panel — greeting, setup state, primary/secondary CTAs */}
      <div className="arise-panel p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-ink tracking-tight">{timeGreeting}, {firstName}</h1>
              {jdStats.active === 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Setup incomplete · Requirement needed
                </span>
              )}
            </div>
            <p className="text-sm text-ink-subtle mt-1">{roleLabel} · ARISE Recruitment Intelligence</p>
            <p className="text-ink-muted mt-3 max-w-2xl">
              {jdStats.active > 0
                ? `${jdStats.active} active requirement${jdStats.active > 1 ? 's' : ''} in progress — keep the pipeline moving.`
                : `${profilesSourced.toLocaleString()} profiles are sourced, but no hiring requirement is active yet — so AI matching hasn't started. Create a requirement to begin.`}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
            {hasPermission('canViewJDs') && isFeatureEnabled('jd_builder') && (
              <button onClick={() => onNavigate('jd-builder')} className="arise-btn-primary h-10 px-4 rounded-lg text-sm font-semibold whitespace-nowrap">
                Create Hiring Requirement
              </button>
            )}
            {hasPermission('canViewProfiles') && (
              <button onClick={() => onNavigate('search')} className="arise-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold whitespace-nowrap">
                Search Talent Pool
              </button>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => {
          const isZero = k.value === 0 || k.value === '0' || k.value === '$0' || k.value === '0h';
          const inner = (
            <>
              <span className={isZero ? 'text-ink-subtle/60' : 'text-blue-500'}><Icon name={k.icon} className="w-4 h-4" /></span>
              <div className={`text-2xl font-bold tracking-tight mt-2 ${isZero ? 'text-ink-subtle' : 'text-ink'}`}>{k.value}</div>
              <div className="text-xs text-ink-subtle mt-0.5">{k.label}</div>
            </>
          );
          return k.tab ? (
            <button key={k.label} onClick={() => onNavigate(k.tab)} className="arise-card p-4 text-left">{inner}</button>
          ) : (
            <div key={k.label} className="arise-panel p-4">{inner}</div>
          );
        })}
      </div>

      {/* Main grid: Recommended Next Actions (left) + Pipeline Funnel (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 arise-panel p-5">
          <h2 className="text-xs font-bold text-ink-subtle uppercase tracking-wider mb-3">Recommended Next Actions</h2>
          <div className="space-y-2.5">
            {nextActions.slice(0, 1).map((a) => (
              <button
                key={a.title}
                onClick={() => onNavigate(a.tab)}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-600/15 to-blue-600/[0.04] border border-blue-500/40 hover:border-blue-500/70 transition-all text-left"
              >
                <span className="w-11 h-11 rounded-lg arise-btn-primary flex items-center justify-center shrink-0">
                  <Icon name={a.icon} className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-ink text-sm">{a.title}</div>
                  <div className="text-xs text-ink-muted">{a.desc} · recommended first step</div>
                </div>
                <span className="text-blue-500 shrink-0"><Icon name="arrowRight" className="w-5 h-5" /></span>
              </button>
            ))}
            {nextActions.length > 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {nextActions.slice(1).map((a) => (
                  <button
                    key={a.title}
                    onClick={() => onNavigate(a.tab)}
                    className="flex items-center gap-2.5 p-3 rounded-lg border border-line hover:border-blue-500/40 hover:bg-muted transition-all text-left"
                  >
                    <span className="w-8 h-8 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center shrink-0">
                      <Icon name={a.icon} className="w-4 h-4" />
                    </span>
                    <div className="font-semibold text-ink text-xs leading-tight">{a.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pipeline Funnel (honest — proportional bars, no fake segments) */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <div className="arise-panel p-5">
            <h2 className="text-xs font-bold text-ink-subtle uppercase tracking-wider mb-4">Pipeline Funnel</h2>
            <div className="space-y-3">
              {funnel.map((f) => (
                <button key={f.label} onClick={() => onNavigate(f.tab)} className="w-full text-left group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-ink-muted">{f.label}</span>
                    <span className="text-sm font-semibold text-ink">{f.value}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-500 group-hover:bg-blue-400 transition-colors"
                      style={{ width: `${Math.round((f.value / funnelMax) * 100)}%` }}
                    ></div>
                  </div>
                </button>
              ))}
            </div>
            {jdStats.active === 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs bg-amber-500/[0.08] rounded-lg p-3 border border-amber-500/20">
                <span className="text-amber-500 mt-0.5 shrink-0"><Icon name="requirements" className="w-4 h-4" /></span>
                <span className="text-ink-muted"><strong className="text-ink">No active requirement linked.</strong> Matching begins once you create a hiring requirement.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Activity + AI Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="arise-panel p-5">
          <h2 className="text-xs font-bold text-ink-subtle uppercase tracking-wider mb-4">Recent Activity</h2>
          {profilesSourced > 0 ? (
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center shrink-0"><Icon name="talent" className="w-4 h-4" /></span>
                <span className="text-ink-muted"><strong className="text-ink">{profilesSourced.toLocaleString()}</strong> profiles sourced into the talent pool.</span>
              </li>
              {interviewStats.today > 0 && (
                <li className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center shrink-0"><Icon name="interviews" className="w-4 h-4" /></span>
                  <span className="text-ink-muted"><strong className="text-ink">{interviewStats.today}</strong> interview{interviewStats.today > 1 ? 's' : ''} scheduled today.</span>
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-ink-subtle">No activity yet. Import profiles or create a requirement to get started.</p>
          )}
        </div>
        <div className="arise-panel p-5">
          <h2 className="text-xs font-bold text-ink-subtle uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="text-blue-500"><Icon name="sparkles" className="w-4 h-4" /></span> AI Suggestions
          </h2>
          <ul className="space-y-2.5 text-sm">
            {[
              jdStats.active === 0 && { text: `Create a requirement to unlock matching on ${profilesSourced.toLocaleString()} sourced profiles.`, cta: 'Create', tab: 'jd-builder' },
              (shortlisted > 0 && hasPermission('canScheduleInterviews')) && { text: `${shortlisted} candidate${shortlisted > 1 ? 's' : ''} in the evaluation queue are ready to schedule.`, cta: 'Schedule', tab: 'interviews' },
              { text: 'Import more profiles to widen your talent pool and improve match quality.', cta: 'Import', tab: 'upload' },
            ].filter(Boolean).map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted border border-line-subtle">
                <span className="text-ink-muted">{s.text}</span>
                <button onClick={() => onNavigate(s.tab)} className="shrink-0 text-xs font-semibold text-blue-500 hover:text-blue-400 whitespace-nowrap">{s.cta} →</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');

  // Hide the global floating /tmp Labs badge inside the authenticated app
  // (it belongs on the marketing/landing page, not over the console). The
  // app footer carries the single brand mark instead.
  useEffect(() => {
    const badge = document.querySelector('.tmplabs-badge');
    if (badge) badge.style.display = 'none';
    return () => { if (badge) badge.style.display = ''; };
  }, []);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shortlist, setShortlist] = useState([]);
  const [emailConfig, setEmailConfig] = useState({ recipientEmail: user?.email || 'admin@company.com' });
  const [activeJD, setActiveJD] = useState(null); // Liquid Design: Active JD context
  const [enabledFeatures, setEnabledFeatures] = useState([]); // Feature flags for tenant

  // RBAC Integration - Use actual user permissions from backend
  const hasPermission = (permission) => {
    console.log(`Checking permission ${permission}:`, user?.permissions?.[permission]);
    return user?.permissions?.[permission] || false;
  };

  // Feature flag check - returns true if feature is enabled for tenant
  // Superadmins see all features
  const isFeatureEnabled = (featureKey) => {
    if (user?.role === 'superadmin') return true;
    return enabledFeatures.includes(featureKey);
  };

  // Fetch enabled features for the current tenant
  const fetchEnabledFeatures = async () => {
    // Superadmins don't have a tenant, so skip feature fetching
    if (user?.role === 'superadmin' || !user?.tenant_id) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/tenants/${user.tenant_id}/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract enabled features list from response
      setEnabledFeatures(response.data?.enabled_features || []);
    } catch (error) {
      console.error('Failed to fetch enabled features:', error);
      // Don't block access if feature check fails - allow features by default
      setEnabledFeatures([]);
    }
  };

  useEffect(() => {
    fetchSystemStats();
    loadShortlist();
    fetchEnabledFeatures();
  }, []);

  const fetchSystemStats = async () => {
    try {
      // Use tenant-specific analytics instead of global stats for consistency
      const response = await axios.get(`${API}/analytics/profiles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      // Map profile analytics to stats format for backward compatibility
      const profileAnalytics = response.data;
      setSystemStats({
        total_profiles: profileAnalytics.total_profiles || 0,
        recent_uploads: profileAnalytics.profiles_today || 0,
        vector_store_count: profileAnalytics.total_profiles || 0,
        system_status: "operational",
        powered_by: "/tmp labs",
        technology_stack: ["FastAPI", "React", "MongoDB", "ChromaDB", "Python", "JavaScript"]
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Fallback to empty stats
      setSystemStats({
        total_profiles: 0,
        recent_uploads: 0,
        vector_store_count: 0,
        system_status: "error",
        powered_by: "/tmp labs"
      });
    }
  };

  const loadShortlist = () => {
    const savedShortlist = localStorage.getItem('recruitmentShortlist');
    if (savedShortlist) {
      setShortlist(JSON.parse(savedShortlist));
    }
  };

  const addToShortlist = (candidate) => {
    if (!hasPermission('canManageShortlist')) {
      alert('❌ Access denied: You do not have permission to manage shortlist');
      return;
    }
    const updatedShortlist = [...shortlist, { ...candidate, addedAt: new Date().toISOString() }];
    setShortlist(updatedShortlist);
    localStorage.setItem('recruitmentShortlist', JSON.stringify(updatedShortlist));
  };

  const removeFromShortlist = (candidateId) => {
    if (!hasPermission('canManageShortlist')) {
      alert('❌ Access denied: You do not have permission to manage shortlist');
      return;
    }
    const updatedShortlist = shortlist.filter(item => item.profile.id !== candidateId);
    setShortlist(updatedShortlist);
    localStorage.setItem('recruitmentShortlist', JSON.stringify(updatedShortlist));
  };

  const clearShortlist = () => {
    if (!hasPermission('canManageShortlist')) {
      alert('❌ Access denied: You do not have permission to manage shortlist');
      return;
    }
    setShortlist([]);
    localStorage.removeItem('recruitmentShortlist');
  };

  const isInShortlist = (candidateId) => {
    return shortlist.some(item => item.profile.id === candidateId);
  };

  // Liquid Design: Run AutoMatch for a JD
  const runAutoMatch = async (jdId) => {
    if (!jdId) {
      alert('Please select a JD first');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/automatch/run/${jdId}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data) {
        alert(`✅ AutoMatch completed! Found ${response.data.matches_found || 0} matches.`);
        // Navigate to JD Builder to see results
        setActiveTab('jd-builder');
      }
    } catch (error) {
      console.error('AutoMatch failed:', error);
      alert(`❌ AutoMatch failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyticsDrillThrough = (type, value) => {
    // Navigate to search tab with pre-filled query based on drill-through type
    setActiveTab('search');
    
    // Create search query based on type
    let searchQuery = '';
    switch(type) {
      case 'skill':
        searchQuery = value; // e.g., "Python"
        break;
      case 'role':
        searchQuery = value; // e.g., "Software Engineer"
        break;
      case 'location':
        searchQuery = `candidates in ${value}`; // e.g., "candidates in Bengaluru, India"
        break;
      case 'category':
        searchQuery = `${value} developer`; // e.g., "Cloud & DevOps developer"
        break;
      default:
        searchQuery = value;
    }
    
    // Store drill-through query for SearchInterface to pick up
    sessionStorage.setItem('drillThroughQuery', searchQuery);
    sessionStorage.setItem('drillThroughType', type);
  };

  // Filter tabs based on RBAC permissions AND feature flags
  const getAvailableTabs = () => {
    const allTabs = [
      { id: 'dashboard', name: 'Dashboard', icon: 'dashboard', desc: 'Your hiring overview', permission: null, alwaysVisible: true },
      { id: 'jd-builder', name: 'Requirements', icon: 'requirements', desc: 'Create hiring requirements', permission: 'canViewJDs', feature: 'jd_builder', workflowStage: 'define' },
      { id: 'upload', name: 'Import', icon: 'import', desc: 'Import candidate profiles', permission: 'canUploadProfiles', workflowStage: 'source' },
      { id: 'search', name: 'Matching', icon: 'search', desc: 'Search the talent pool', permission: 'canViewProfiles', workflowStage: 'search' },
      { id: 'profiles', name: 'Talent Pool', icon: 'talent', desc: 'Browse candidate profiles', permission: 'canViewProfiles', workflowStage: 'search' },
      { id: 'shortlist', name: 'Evaluation', icon: 'evaluation', desc: `${shortlist.length} in queue`, badge: shortlist.length, permission: 'canManageShortlist', workflowStage: 'evaluate' },
      { id: 'interviews', name: 'Interviews', icon: 'interviews', desc: 'Schedule & manage', permission: 'canViewInterviews', feature: 'interview_scheduling', hasSubmenu: true, workflowStage: 'interview' },
      { id: 'analytics', name: 'Analytics', icon: 'analytics', desc: 'Insights & reports', permission: 'canViewAnalytics', workflowStage: 'analyze' },
      { id: 'settings', name: 'Settings', icon: 'settings', desc: 'Team & configuration', permission: 'canManageUsers', hasSubmenu: true }
    ];

    // Add superadmin-only tabs
    if (user?.role === 'superadmin') {
      allTabs.push(
        { id: 'tenants', name: 'Tenant Management', icon: '🏢', desc: 'Manage multi-tenant organizations', permission: null, superAdminOnly: true },
        { id: 'tenant-analytics', name: 'Tenant Analytics', icon: '📈', desc: 'Per-tenant profile counts and support access', permission: null, superAdminOnly: true },
        { id: 'jd-builder-admin', name: 'JD Builder (All Tenants)', icon: '✍️', desc: 'Cross-tenant job descriptions management', permission: null, superAdminOnly: true, badge: 'NEW' },
        { id: 'billing', name: 'Billing & Usage', icon: '💰', desc: 'Tenant usage metrics and billing dashboard', permission: null, superAdminOnly: true },
        { id: 'users', name: 'User Directory', icon: '👥', desc: 'Cross-tenant user management', permission: null, superAdminOnly: true },
        { id: 'features', name: 'Feature Flags', icon: '🚀', desc: 'Manage feature rollouts and targeting', permission: null, superAdminOnly: true },
        { id: 'ai-intelligence', name: 'AI Engine', icon: '🧠', desc: 'AutoMatch & Identity Resolution', permission: null, superAdminOnly: true },
        { id: 'email-intake', name: 'Intelligent Intake Inbox™', icon: '📧', desc: 'STP++ email processing with AI routing and deduplication', permission: null, superAdminOnly: true },
        { id: 'observability', name: 'Platform Observability', icon: '📊', desc: 'Real-time platform metrics and performance monitoring', permission: null, superAdminOnly: true },
        { id: 'security', name: 'Security & Compliance', icon: '🔒', desc: 'Security monitoring and compliance dashboard', permission: null, superAdminOnly: true },
        { id: 'audit', name: 'System-wide Audit Logs', icon: '📋', desc: 'Comprehensive audit trail across all tenants and operations', permission: null, superAdminOnly: true },
        { id: 'rbac', name: 'RBAC Matrix Management', icon: '🛡️', desc: 'Configure role-based access control permissions across platform', permission: null, superAdminOnly: true },
        { id: 'health', name: 'System Health', icon: '❤️', desc: 'Infrastructure monitoring and alerts', permission: null, superAdminOnly: true },
        { id: 'infrastructure', name: 'K8s Infrastructure', icon: '☸️', desc: 'Kubernetes cluster health, pods, and deployments', permission: null, superAdminOnly: true }
      );
    }

    // Filter tabs based on RBAC permissions AND feature flags
    return allTabs.filter(tab => {
      if (tab.superAdminOnly) {
        return user?.role === 'superadmin';
      }

      // Always visible tabs (like Dashboard)
      if (tab.alwaysVisible) {
        return true;
      }

      // Check RBAC permission
      if (tab.permission && !hasPermission(tab.permission)) {
        return false;
      }

      // Check feature flag if specified
      if (tab.feature && !isFeatureEnabled(tab.feature)) {
        return false;
      }

      return true;
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-muted to-surface">
      {/* Header - Simplified */}
      <div className="bg-elevated border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">
            {/* Left - ARISE product brand (with /tmp Labs secondary) */}
            <div className="flex-shrink-0">
              <AriseBrand variant="light" size="md" />
            </div>

            {/* Center - Console name (hidden on small screens) */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 text-center">
              <h1 className="text-base font-bold text-ink tracking-tight">Recruitment Intelligence Console</h1>
              <p className="text-xs text-ink-subtle">AI-assisted sourcing, matching, evaluation &amp; interview workflow</p>
            </div>
            
            {/* Right - User Dropdown Menu */}
            <div className="relative">
              <UserDropdownMenu 
                user={user}
                systemStats={systemStats}
                onLogout={onLogout}
                onHelp={() => window.open('/faq.html', '_blank')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout Container */}
      <div className="flex flex-1 min-h-0 bg-muted">
        {/* Superadmin Sidebar */}
        {user?.role === 'superadmin' && (
          <div className="w-64 bg-gradient-to-b from-slate-900 to-slate-800 text-white shadow-xl flex-shrink-0">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="text-yellow-400">👑</span>
                Superadmin Panel
              </h2>
              <p className="text-slate-300 text-sm mt-1">Platform Administration</p>
            </div>
            
            <div className="p-4 space-y-1">
              <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-3">
                Core Management
              </div>
              {getAvailableTabs().filter(tab => tab.superAdminOnly).slice(0, 4).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center space-x-3 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <span className="shrink-0"><Icon name={tab.icon} className="w-4 h-4" /></span>
                  <div>
                    <div className="font-medium">{tab.name}</div>
                    <div className="text-xs opacity-70">{tab.desc}</div>
                  </div>
                </button>
              ))}
              
              <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-3 mt-6">
                System Operations  
              </div>
              {getAvailableTabs().filter(tab => tab.superAdminOnly).slice(4).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center space-x-3 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <span className="shrink-0"><Icon name={tab.icon} className="w-4 h-4" /></span>
                  <div>
                    <div className="font-medium">{tab.name}</div>
                    <div className="text-xs opacity-70">{tab.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Workflow Stepper - Visual hiring pipeline */}
          {user?.role !== 'superadmin' && activeTab !== 'dashboard' && activeTab !== 'admin' && activeTab !== 'hierarchy' && activeTab !== 'email-settings' && (
            <WorkflowStepper
              activeTab={activeTab}
              onStepClick={setActiveTab}
              userRole={user?.role}
              hasPermission={hasPermission}
            />
          )}

          {/* Main Tab Navigation */}
          <div className="bg-elevated border-b border-line px-6 shadow-sm">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-1 overflow-x-auto flex-1 no-scrollbar">
              {getAvailableTabs().filter(tab => !tab.superAdminOnly).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                      : 'text-ink-muted hover:text-ink hover:bg-subtle'
                  }`}
                >
                  <span className="shrink-0"><Icon name={tab.icon} className="w-4 h-4" /></span>
                  <div className="text-left">
                    <div className="font-semibold flex items-center space-x-2">
                      <span>{tab.name}</span>
                      {tab.badge > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                          {tab.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              </div>
              {/* ROI Widget - Compact version in header */}
              <div className="hidden lg:block ml-4 flex-shrink-0">
                <ROIWidget compact={true} />
              </div>
              {/* Global Light/Dark theme toggle — lives in the shared app shell so it
                  appears on every authenticated page (near the savings pill). */}
              <div className="ml-3 flex-shrink-0">
                <ThemeToggle />
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto bg-muted">
            <div className="max-w-full px-6 py-6">
        {/* Dashboard - Role-based landing page */}
        {activeTab === 'dashboard' && (
          <HiringDashboard
            user={user}
            systemStats={systemStats}
            shortlistCount={shortlist.length}
            onNavigate={setActiveTab}
            hasPermission={hasPermission}
            isFeatureEnabled={isFeatureEnabled}
          />
        )}
        {activeTab === 'search' && (
          <SearchInterface
            shortlist={shortlist}
            addToShortlist={addToShortlist}
            isInShortlist={isInShortlist}
            emailConfig={emailConfig}
            hasPermission={hasPermission}
          />
        )}
        {activeTab === 'upload' && (
          <UploadInterface
            onUploadSuccess={fetchSystemStats}
            user={user}
            activeJD={activeJD}
            setActiveView={setActiveTab}
            stats={systemStats}
            handleAutoMatch={runAutoMatch}
          />
        )}
        {activeTab === 'profiles' && (
          <ProfileDatabase
            activeJD={activeJD}
            setActiveView={setActiveTab}
            stats={systemStats}
            handleAutoMatch={runAutoMatch}
            addToShortlist={addToShortlist}
            isInShortlist={isInShortlist}
          />
        )}
        {activeTab === 'shortlist' && (
          <ShortlistManager 
            shortlist={shortlist} 
            removeFromShortlist={removeFromShortlist} 
            clearShortlist={clearShortlist} 
            emailConfig={emailConfig} 
            setEmailConfig={setEmailConfig}
            hasPermission={hasPermission}
            onNavigate={setActiveTab}
          />
        )}
        {activeTab === 'analytics' && (
          <Analytics 
            stats={systemStats} 
            onDrillThrough={handleAnalyticsDrillThrough}
          />
        )}
        {activeTab === 'interviews' && (
          <InterviewManagement currentUser={user} hasPermission={hasPermission} />
        )}
        {activeTab === 'ai-intelligence' && (
          <AIIntelligenceDashboard currentUser={user} hasPermission={hasPermission} isFeatureEnabled={isFeatureEnabled} />
        )}
        {activeTab === 'settings' && (
          <SettingsPage currentUser={user} hasPermission={hasPermission} isFeatureEnabled={isFeatureEnabled} />
        )}

        {/* Superadmin-only tabs */}
        {activeTab === 'tenants' && user?.role === 'superadmin' && (
          <TenantManagement currentUser={user} />
        )}
        {activeTab === 'tenant-analytics' && user?.role === 'superadmin' && (
          <TenantAnalyticsDashboard currentUser={user} />
        )}
        {activeTab === 'billing' && user?.role === 'superadmin' && (
          <BillingUsageDashboard currentUser={user} />
        )}
        {activeTab === 'users' && user?.role === 'superadmin' && (
          <CrossTenantUserDirectory currentUser={user} />
        )}
        {activeTab === 'features' && user?.role === 'superadmin' && (
          <FeatureFlagManagement currentUser={user} />
        )}
        {activeTab === 'observability' && user?.role === 'superadmin' && (
          <PlatformObservabilityDashboard currentUser={user} />
        )}
        {activeTab === 'email-intake' && user?.role === 'superadmin' && (
          <EmailIntakeManagement currentUser={user} />
        )}
        {activeTab === 'security' && user?.role === 'superadmin' && (
          <SecurityComplianceDashboard currentUser={user} />
        )}
        {activeTab === 'audit' && user?.role === 'superadmin' && (
          <SystemWideAuditLogs currentUser={user} />
        )}
        {activeTab === 'rbac' && user?.role === 'superadmin' && (
          <RBACMatrixManagement currentUser={user} />
        )}
        {activeTab === 'health' && user?.role === 'superadmin' && (
          <SystemHealthDashboard currentUser={user} />
        )}
        {activeTab === 'infrastructure' && user?.role === 'superadmin' && (
          <InfrastructureDashboard currentUser={user} />
        )}

        {/* Smart JD Builder - Tenant Users */}
        {activeTab === 'jd-builder' && hasPermission('canViewJDs') && (
          <SmartJDBuilder currentUser={user} />
        )}

        {/* Smart JD Builder - Superadmin Cross-Tenant View */}
        {activeTab === 'jd-builder-admin' && user?.role === 'superadmin' && (
          <SmartJDBuilderAdmin currentUser={user} />
        )}

        {/* Access Denied Message */}
        {(() => {
          const currentTab = getAvailableTabs().find(tab => tab.id === activeTab);
          if (!currentTab) return false;
          
          // Check permission-based access only
          return currentTab.permission !== null && !hasPermission(currentTab.permission);
        })() && (
          <div className="bg-elevated rounded-xl shadow-lg p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-red-500/15 border border-red-500/25 rounded-full flex items-center justify-center">
              <span className="text-4xl">🚫</span>
            </div>
            <h3 className="text-xl font-bold text-ink mb-2">Access Denied</h3>
            <p className="text-ink-subtle mb-6">You don't have permission to access this section.</p>
            <p className="text-sm text-ink-subtle">Current role: <span className="font-medium">{user?.role || 'unknown'}</span></p>
          </div>
        )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer — compact utility bar */}
      <footer className="mt-auto border-t border-line bg-surface">
        <div className="max-w-7xl mx-auto px-6 py-3 md:py-0 md:h-14 flex flex-col md:flex-row items-center justify-between gap-2 text-xs">
          <div className="font-medium text-ink-muted whitespace-nowrap">
            ARISE <span className="text-ink-subtle font-normal">by /tmp Labs</span>
          </div>
          <div className="hidden min-[480px]:block text-ink-subtle whitespace-nowrap">
            © 2025 /tmp labs · v1.0.0
          </div>
          <div className="flex items-center gap-2 text-ink-subtle whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-ink-muted">System Operational</span>
            <span>· Role: {user?.role || 'admin'}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

// RBAC Hook for Role-Based Access Control
const useRBAC = (initialRole = 'viewer') => {
  const [userRole, setUserRole] = useState(initialRole);
  const [permissions, setPermissions] = useState({}); 

  const hasPermission = (permission) => {
    return permissions[permission] || false;
  };

  // Initialize permissions when role changes
  useEffect(() => {
    updateUserRole(initialRole);
  }, [initialRole]);

  const updateUserRole = (newRole) => {
    setUserRole(newRole);
    // Role-based permissions
    const rolePermissions = {
      superadmin: {
        canViewProfiles: true,
        canUploadProfiles: true,
        canViewAnalytics: true,
        canManageShortlist: true,
        canEmailCandidates: true,
        canDownloadData: true,
        canAccessDeveloperMode: true,
        canViewAuditTrace: true,
        canManageUsers: true,
        canTransferProfiles: true,
        // Interview Management Permissions
        canScheduleInterviews: true,
        canViewInterviews: true,
        canManageInterviews: true,
        canManageIntegrations: true,
        canManageTemplates: true,
        canSubmitFeedback: true,
        canViewFeedback: true
      },
      admin: {
        canViewProfiles: true,
        canUploadProfiles: true,
        canViewAnalytics: true,
        canManageShortlist: true,
        canEmailCandidates: true,
        canDownloadData: true,
        canAccessDeveloperMode: true,
        canViewAuditTrace: true,
        canManageUsers: true,
        canTransferProfiles: true,
        // Interview Management Permissions
        canScheduleInterviews: true,
        canViewInterviews: true,
        canManageInterviews: true,
        canManageIntegrations: true,
        canManageTemplates: true,
        canSubmitFeedback: true,
        canViewFeedback: true
      },
      auditor: {
        canViewProfiles: true,
        canUploadProfiles: false,
        canViewAnalytics: true,
        canManageShortlist: false,
        canEmailCandidates: false,
        canDownloadData: true,
        canAccessDeveloperMode: false,
        canViewAuditTrace: true,
        // Interview Management Permissions
        canScheduleInterviews: false,
        canViewInterviews: true,
        canManageInterviews: false,
        canManageIntegrations: false,
        canManageTemplates: false,
        canSubmitFeedback: false,
        canViewFeedback: true
      },
      recruiter: {
        canViewProfiles: true,
        canUploadProfiles: true,
        canViewAnalytics: false,
        canManageShortlist: true,
        canEmailCandidates: true,
        canDownloadData: true,
        canAccessDeveloperMode: false,
        canViewAuditTrace: false,
        // Interview Management Permissions
        canScheduleInterviews: true,
        canViewInterviews: true,
        canManageInterviews: true,
        canManageIntegrations: false,
        canManageTemplates: true,
        canSubmitFeedback: true,
        canViewFeedback: true
      },
      viewer: {
        canViewProfiles: true,
        canUploadProfiles: false,
        canViewAnalytics: false,
        canManageShortlist: false,
        canEmailCandidates: false,
        canDownloadData: false,
        canAccessDeveloperMode: false,
        canViewAuditTrace: false,
        // Interview Management Permissions
        canScheduleInterviews: false,
        canViewInterviews: false,
        canManageInterviews: false,
        canManageIntegrations: false,
        canManageTemplates: false,
        canSubmitFeedback: false,
        canViewFeedback: false
      }
    };
    setPermissions(rolePermissions[newRole] || rolePermissions.viewer);
  };

  return { hasPermission, updateUserRole };
};

// Smart Retry Logic Hook
const useSmartRetry = () => {
  const [retryOptions, setRetryOptions] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const suggestRetry = (originalQuery, resultsCount) => {
    if (resultsCount === 0) {
      const suggestions = [
        {
          type: 'synonym_expansion',
          title: 'Try with Synonym Expansion',
          description: 'Expand your search with related terms and synonyms',
          action: () => expandWithSynonyms(originalQuery)
        },
        {
          type: 'skill_variation',
          title: 'Try Alternative Skill Terms',
          description: 'Search with alternative skill names and variations',
          action: () => expandWithSkillVariations(originalQuery)
        },
        {
          type: 'broader_search',
          title: 'Broaden Search Criteria',
          description: 'Remove some filters to cast a wider net',
          action: () => broadenSearch(originalQuery)
        }
      ];
      setRetryOptions(suggestions);
    } else {
      setRetryOptions(null);
    }
  };

  const expandWithSynonyms = (query) => {
    // Skill synonyms mapping
    const synonyms = {
      'React': ['ReactJS', 'React.js', 'React Native'],
      'JavaScript': ['JS', 'ECMAScript', 'Node.js', 'NodeJS'],
      'Python': ['Python3', 'Python 3', 'Django', 'Flask'],
      'Java': ['Java 8', 'Spring', 'Spring Boot'],
      'AWS': ['Amazon Web Services', 'EC2', 'S3', 'Lambda'],
      'Docker': ['Containerization', 'Kubernetes', 'K8s'],
      'SQL': ['MySQL', 'PostgreSQL', 'Database', 'RDBMS'],
      'Machine Learning': ['ML', 'AI', 'Deep Learning', 'Data Science']
    };

    let expandedQuery = query;
    Object.entries(synonyms).forEach(([key, values]) => {
      if (query.toLowerCase().includes(key.toLowerCase())) {
        expandedQuery += ` ${values.join(' ')}`;
      }
    });

    return expandedQuery;
  };

  const expandWithSkillVariations = (query) => {
    const variations = {
      'frontend': ['front-end', 'UI', 'user interface', 'web development'],
      'backend': ['back-end', 'server-side', 'API', 'microservices'],
      'fullstack': ['full-stack', 'full stack', 'MEAN', 'MERN'],
      'mobile': ['iOS', 'Android', 'React Native', 'Flutter'],
      'cloud': ['AWS', 'Azure', 'GCP', 'Google Cloud'],
      'devops': ['DevOps', 'CI/CD', 'deployment', 'automation']
    };

    let expandedQuery = query;
    Object.entries(variations).forEach(([key, values]) => {
      if (query.toLowerCase().includes(key.toLowerCase())) {
        expandedQuery += ` ${values.join(' ')}`;
      }
    });

    return expandedQuery;
  };

  const broadenSearch = (query) => {
    // Remove specific version numbers and make terms more generic
    return query
      .replace(/\d+\+?\s*years?/gi, 'experience')
      .replace(/senior|junior|lead/gi, '')
      .replace(/,/g, ' ')
      .trim();
  };

  const executeRetry = async (retryOption, originalSearchFn) => {
    setIsRetrying(true);
    try {
      const expandedQuery = retryOption.action();
      await originalSearchFn(expandedQuery);
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
      setRetryOptions(null);
    }
  };

  const clearRetryOptions = () => {
    setRetryOptions(null);
  };

  return { retryOptions, isRetrying, suggestRetry, executeRetry, clearRetryOptions };
};

// Smart Retry Component
const SmartRetryComponent = ({ retryOptions, isRetrying, onRetry, onClear }) => {
  if (!retryOptions) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <span className="text-orange-600 text-xl">🔄</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Smart Retry Suggestions</h3>
          <p className="text-sm text-gray-600">Try these alternatives to find more matches</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {retryOptions.map((option, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex-1">
              <div className="font-medium text-gray-900">{option.title}</div>
              <div className="text-sm text-gray-600">{option.description}</div>
            </div>
            <button
              onClick={() => onRetry(option)}
              disabled={isRetrying}
              className="flex items-center space-x-2 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isRetrying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                  <span>Retrying...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Try This</span>
                </>
              )}
            </button>
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t flex justify-between items-center">
        <p className="text-xs text-ink-subtle">
          💡 Smart retry powered by /tmp labs AI reasoning
        </p>
        <button
          onClick={onClear}
          className="text-sm text-ink-subtle hover:text-ink-muted"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};
const SearchInterface = ({ shortlist, addToShortlist, isInShortlist, emailConfig, hasPermission }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [requiredSkills, setRequiredSkills] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState('hybrid');
  const [searchMode, setSearchMode] = useState('fast');  // 'fast' or 'ai-native'
  const [enableAgentTrace, setEnableAgentTrace] = useState(false);

  // JD Selector state
  const [availableJDs, setAvailableJDs] = useState([]);
  const [selectedJDId, setSelectedJDId] = useState('');
  const [loadingJDs, setLoadingJDs] = useState(false);

  // Fetch available JDs for selector
  useEffect(() => {
    const fetchJDs = async () => {
      try {
        setLoadingJDs(true);
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${BACKEND_URL}/api/jd?status=active`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableJDs(data.items || data.jds || []);
        }
      } catch (err) {
        console.error('Failed to fetch JDs:', err);
      } finally {
        setLoadingJDs(false);
      }
    };
    fetchJDs();
  }, []);

  // Handle JD selection - populate job description from selected JD
  const handleJDSelect = (jdId) => {
    setSelectedJDId(jdId);
    if (jdId) {
      const selectedJD = availableJDs.find(jd => jd.id === jdId);
      if (selectedJD) {
        // Build a comprehensive job description from JD data
        let jdText = '';
        if (selectedJD.summary) jdText += selectedJD.summary + '\n\n';
        if (selectedJD.description) jdText += selectedJD.description + '\n\n';
        if (selectedJD.responsibilities?.length) {
          jdText += 'Responsibilities:\n' + selectedJD.responsibilities.map(r => `- ${r}`).join('\n') + '\n\n';
        }
        if (selectedJD.requirements?.length) {
          jdText += 'Requirements:\n' + selectedJD.requirements.map(r => `- ${r}`).join('\n') + '\n\n';
        }
        setJobDescription(jdText.trim());

        // Also set required skills from extracted skills
        if (selectedJD.extracted_skills?.length) {
          setRequiredSkills(selectedJD.extracted_skills.join(', '));
        }
      }
    } else {
      // Clear if no JD selected
      setJobDescription('');
      setRequiredSkills('');
    }
  };

  // Smart Retry Logic Integration
  const { retryOptions, isRetrying, suggestRetry, executeRetry, clearRetryOptions } = useSmartRetry();

  // Check for drill-through query on component mount
  useEffect(() => {
    const drillQuery = sessionStorage.getItem('drillThroughQuery');
    const drillType = sessionStorage.getItem('drillThroughType');
    
    if (drillQuery) {
      setSearchQuery(drillQuery);
      
      // For skills, add to required skills field as well
      if (drillType === 'skill') {
        setRequiredSkills(drillQuery);
      }
      
      // Clear the drill-through data
      sessionStorage.removeItem('drillThroughQuery');
      sessionStorage.removeItem('drillThroughType');
      
      // Don't auto-execute search here to avoid dependency issues
    }
  }, []);

  const handleSearch = async (queryOverride = null) => {
    const currentQuery = queryOverride || searchQuery;
    if (!currentQuery.trim()) return;

    setLoading(true);
    clearRetryOptions(); // Clear any existing retry options
    
    try {
      const searchRequest = {
        query: currentQuery,
        job_description: jobDescription || null,
        required_skills: requiredSkills ? requiredSkills.split(',').map(s => s.trim()) : [],
        search_type: searchType,
        search_mode: searchMode,  // Add search mode
        max_results: 20,
        enable_agent_trace: (enableAgentTrace && hasPermission('canAccessDeveloperMode')) || searchMode === 'ai-native',
        explain_scores: true  // Enable AI score explainability for transparency
      };

      const response = await axios.post(`${API}/search`, searchRequest);
      setSearchResults(response.data);
      
      // Trigger smart retry suggestions if no results
      if (response.data.results.length === 0) {
        suggestRetry(currentQuery, response.data.results.length);
      }
      
    } catch (error) {
      console.error('Search failed:', error);
      alert('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (retryOption) => {
    await executeRetry(retryOption, handleSearch);
  };

  // Get selected JD details for context strip
  const selectedJD = selectedJDId ? availableJDs.find(jd => jd.id === selectedJDId) : null;

  return (
    <div className="space-y-6">
      {/* Liquid Design: Active JD Context Strip */}
      {selectedJD && (
        <ActiveJDContext
          jd={{
            title: selectedJD.title,
            id: selectedJD.id,
            status: selectedJD.status,
            department: selectedJD.department,
          }}
          stats={{
            profiles: selectedJD.profile_count || 0,
            matches: selectedJD.match_count || 0,
            suggested: selectedJD.suggested_count || 0,
          }}
          onChangeJD={() => handleJDSelect('')}
          compact
        />
      )}

      {/* Enhanced Search Form */}
      <div className="bg-elevated rounded-2xl shadow-xl border border-line-subtle overflow-hidden">
        {/* Header Section */}
        <div className="bg-muted border-b border-line px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-accent-soft rounded-xl flex items-center justify-center backdrop-blur-sm p-1">
              <AIAvatar size="md" animate={true} />
            </div>
            <div className="text-ink">
              <h2 className="text-2xl font-bold">AI-Powered Candidate Search</h2>
              <p className="text-ink-subtle text-sm font-medium">Powered by /tmp labs • AI Recruitment Engine</p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-8 space-y-6">
          {/* Search Query */}
          <div>
            <label className="block text-sm font-semibold text-ink-muted mb-3">
              Search Query *
            </label>
            <textarea
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="E.g., Senior React developer with 5+ years experience in fintech..."
              className="w-full px-4 py-4 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-ink"
              rows={3}
            />
          </div>

          {/* Search Mode Toggle */}
          <SearchModeToggle
            searchMode={searchMode}
            onModeChange={setSearchMode}
            disabled={loading}
          />

          {/* JD Selector - Select from existing JDs */}
          <div className="bg-surface p-4 rounded-xl border border-line">
            <label className="block text-sm font-semibold text-ink-muted mb-3">
              Select Job Description (Optional)
            </label>
            <div className="flex gap-3">
              <select
                value={selectedJDId}
                onChange={(e) => handleJDSelect(e.target.value)}
                disabled={loadingJDs}
                className="flex-1 px-4 py-3 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-elevated"
              >
                <option value="">-- Select from existing JDs --</option>
                {availableJDs.map(jd => (
                  <option key={jd.id} value={jd.id}>
                    {jd.title} ({jd.jd_code}) {jd.client_name ? `- ${jd.client_confidential ? jd.client_display_name || 'Confidential' : jd.client_name}` : ''}
                  </option>
                ))}
              </select>
              {selectedJDId && (
                <button
                  type="button"
                  onClick={() => handleJDSelect('')}
                  className="px-4 py-2 text-blue-400 hover:bg-accent-soft rounded-lg transition-colors"
                  title="Clear selection"
                >
                  Clear
                </button>
              )}
            </div>
            {selectedJDId && (
              <p className="mt-2 text-xs text-blue-400">
                JD content and skills have been auto-filled below. You can still edit them.
              </p>
            )}
            {availableJDs.length === 0 && !loadingJDs && (
              <p className="mt-2 text-xs text-ink-subtle">
                No active JDs found. Create JDs in Smart JD Builder first, or paste job description manually below.
              </p>
            )}
          </div>

          {/* Job Description */}
          <div>
            <label className="block text-sm font-semibold text-ink-muted mb-3">
              Job Description {selectedJDId ? '(from selected JD)' : '(Optional)'}
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here for more accurate matching..."
              className="w-full px-4 py-4 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-ink"
              rows={4}
            />
          </div>

          {/* Required Skills */}
          <div>
            <label className="block text-sm font-semibold text-ink-muted mb-3">
              Required Skills (comma-separated)
            </label>
            <input
              type="text"
              value={requiredSkills}
              onChange={(e) => setRequiredSkills(e.target.value)}
              placeholder="E.g., React, Node.js, AWS, MongoDB"
              className="w-full px-4 py-4 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-ink"
            />
          </div>

          {/* Search Type */}
          <div>
            <label className="block text-sm font-semibold text-ink-muted mb-3">
              Search Type
            </label>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="w-full px-4 py-4 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-elevated"
            >
              <option value="hybrid">🔄 Hybrid (Semantic + Keywords)</option>
              <option value="semantic">🧠 Semantic (AI Understanding)</option>
              <option value="keyword">🔤 Keywords Only</option>
            </select>
          </div>

          {/* Developer Mode Toggle - Only show if user has permission */}
          {hasPermission('canAccessDeveloperMode') && (
            <div className="bg-surface p-6 rounded-xl border border-line">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableAgentTrace}
                      onChange={(e) => setEnableAgentTrace(e.target.checked)}
                      className="w-5 h-5 text-blue-400 bg-elevated border-line rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">🧠</span>
                        <span className="text-sm font-bold text-ink">Developer Mode</span>
                        <span className="px-2 py-1 bg-accent-soft text-blue-300 rounded-full text-xs font-medium">
                          BETA
                        </span>
                      </div>
                      <p className="text-xs text-ink-muted mt-1">
                        Enable detailed agent reasoning trace for debugging and transparency
                      </p>
                    </div>
                  </label>
                </div>
                <div className="text-xs text-blue-400 bg-accent-soft px-3 py-1 rounded-full">
                  🔍 Match Reasoning
                </div>
              </div>
            </div>
          )}

          {/* Search Readiness panel */}
          <div className="bg-surface border border-line rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-ink-subtle">Search Readiness</h4>
              <span className="text-xs text-ink-subtle">Est. runtime · {searchMode === 'ai-native' ? '~2–3 min' : '< 1 min'}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'Mode', value: searchMode === 'ai-native' ? 'Deep AI' : 'Fast', tone: 'set' },
                { label: 'Query', value: searchQuery.trim() ? 'Entered' : 'Required', tone: searchQuery.trim() ? 'set' : 'required' },
                { label: 'Job Desc', value: (selectedJDId || jobDescription.trim()) ? 'Provided' : 'Optional', tone: (selectedJDId || jobDescription.trim()) ? 'set' : 'optional' },
                { label: 'Skills', value: requiredSkills.trim() ? 'Specified' : 'Optional', tone: requiredSkills.trim() ? 'set' : 'optional' },
              ].map((r) => (
                <div key={r.label} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted border border-line-subtle">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.tone === 'set' ? 'bg-green-500' : r.tone === 'required' ? 'bg-amber-500' : 'bg-ink-subtle'}`}></span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-ink-subtle leading-none">{r.label}</div>
                    <div className="text-xs font-semibold text-ink truncate mt-0.5">{r.value}</div>
                  </div>
                </div>
              ))}
            </div>
            {hasPermission('canAccessDeveloperMode') && enableAgentTrace && (
              <div className="mt-2 flex items-center gap-2 text-xs text-ink-muted">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Match reasoning trace enabled
              </div>
            )}
            {!searchQuery.trim() && (
              <p className="mt-3 text-xs text-amber-400">A search query is required before you can run the search.</p>
            )}
          </div>

          {/* Search Button */}
          <button
            onClick={() => handleSearch()}
            disabled={loading || !searchQuery.trim()}
            className="arise-btn-primary w-full py-5 px-6 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                <span>{searchMode === 'ai-native' ? 'AI Agents Processing...' : 'Fast Search...'}</span>
              </>
            ) : (
              <>
                <span className="text-xl">{searchMode === 'ai-native' ? '👩‍🔬' : '⚡'}</span>
                <span>{searchMode === 'ai-native' ? 'Run Deep AI Candidate Search' : 'Run Fast Candidate Search'}</span>
              </>
            )}
          </button>

          {/* /tmp labs Attribution */}
          <div className="pt-4 border-t border-line">
            <div className="flex items-center justify-center space-x-3 text-sm text-ink-muted">
              <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"></div>
              <span className="font-medium">AI agents orchestrated by /tmp labs technology</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Search Progress Modal */}
      <AISearchProgress
        isSearching={loading}
        searchMode={searchMode}
        onCancel={() => setLoading(false)}
        searchQuery={searchQuery}
        hasJobDescription={Boolean(jobDescription && jobDescription.trim())}
        hasRequiredSkills={Boolean(requiredSkills && requiredSkills.trim())}
      />

      {/* Smart Retry Component */}
      <SmartRetryComponent 
        retryOptions={retryOptions}
        isRetrying={isRetrying}
        onRetry={handleRetry}
        onClear={clearRetryOptions}
      />

      {/* Search Results */}
      {searchResults && (
        <SearchResults
          results={searchResults}
          addToShortlist={addToShortlist}
          isInShortlist={isInShortlist}
          emailConfig={emailConfig}
          hasPermission={hasPermission}
          selectedJDId={selectedJDId}
          availableJDs={availableJDs}
        />
      )}
    </div>
  );
};

// Match Reasoning Viewer Component
const AgentTraceViewer = ({ agentTrace, isVisible, onToggle }) => {
  const [expandedStep, setExpandedStep] = useState(null);
  
  if (!agentTrace || !isVisible) return null;

  const formatDuration = (durationMs) => {
    return `${Math.round(durationMs)}ms`;
  };

  const getStepIcon = (agentName) => {
    const icons = {
      // ARISE Agent names (backend)
      'Search Strategy Agent': '🧠',
      'Profile Analysis Agent': '🎯',
      'ARISE Agent Orchestrator': '🤖',
      'Database Search Agent': '🔍',
      'AI Search Orchestrator': '📊',
      'Error Handler': '⚠️',
      // Legacy names (keep for compatibility)
      'Search Strategist': '🧠',
      'Local Search Agent': '🔍',
      'Profile Matcher': '🎯',
      'Ranking Specialist': '📊',
      'External Search Agent': '🌐',
      'Audit Generator': '📝'
    };
    return icons[agentName] || '🤖';
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 border-green-500/25';
      case 'failed': return 'bg-red-500/10 border-red-500/25';
      case 'running': return 'bg-amber-500/10 border-yellow-500/25';
      default: return 'bg-muted border-line';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'running': return 'text-amber-400';
      default: return 'text-ink-muted';
    }
  };

  return (
    <div className="bg-elevated rounded-xl shadow-lg border border-line overflow-hidden">
      {/* Header */}
      <div className="bg-muted border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent-soft rounded-lg flex items-center justify-center">
              <span className="text-accent-ink text-xl">🧠</span>
            </div>
            <div className="text-ink">
              <h3 className="text-lg font-bold">Search Audit Trail</h3>
              <p className="text-ink-subtle text-sm">Compliance & Audit • AI Decision Process</p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="bg-subtle hover:bg-muted text-ink-muted px-3 py-1 rounded-lg text-sm font-medium transition-colors"
          >
            {isVisible ? 'Hide' : 'Show'} Trace
          </button>
        </div>
      </div>

      {/* Trace Summary */}
      <div className="px-6 py-4 bg-muted border-b">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-400">{agentTrace.steps.length}</div>
            <div className="text-xs text-ink-muted">Total Steps</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400">{formatDuration(agentTrace.total_duration_ms)}</div>
            <div className="text-xs text-ink-muted">Total Time</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-400">
              {agentTrace.steps.filter(s => s.status === 'completed').length}
            </div>
            <div className="text-xs text-ink-muted">Completed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-400">
              {agentTrace.steps.filter(s => s.status === 'failed').length}
            </div>
            <div className="text-xs text-ink-muted">Failed</div>
          </div>
        </div>
      </div>

      {/* Agent Steps */}
      <div className="p-6 max-h-96 overflow-y-auto">
        <div className="space-y-4">
          {agentTrace.steps.map((step, index) => (
            <div
              key={step.step_id}
              className={`border rounded-lg p-4 transition-all duration-200 ${getStepColor(step.status)}`}
            >
              {/* Step Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold text-ink-subtle">#{index + 1}</span>
                    <span className="text-lg">{getStepIcon(step.agent_name)}</span>
                    <div>
                      <div className="font-medium text-ink">{step.agent_name}</div>
                      <div className="text-sm text-ink-muted">{step.step_name}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`text-xs font-medium ${getStatusColor(step.status)}`}>
                    {step.status.toUpperCase()}
                  </span>
                  <span className="text-xs text-ink-subtle">{formatDuration(step.duration_ms)}</span>
                  <button
                    onClick={() => setExpandedStep(expandedStep === step.step_id ? null : step.step_id)}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                  >
                    {expandedStep === step.step_id ? 'Collapse' : 'Expand'}
                  </button>
                </div>
              </div>

              {/* Expanded Step Details */}
              {expandedStep === step.step_id && (
                <div className="mt-4 pt-4 border-t border-line">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Input Data */}
                    <div>
                      <h4 className="text-sm font-medium text-ink-muted mb-2">Input Data</h4>
                      <div className="bg-subtle rounded-lg p-3 text-xs">
                        <pre className="whitespace-pre-wrap text-ink">
                          {JSON.stringify(step.input_data, null, 2)}
                        </pre>
                      </div>
                    </div>

                    {/* Output Data */}
                    <div>
                      <h4 className="text-sm font-medium text-ink-muted mb-2">Output Data</h4>
                      <div className="bg-subtle rounded-lg p-3 text-xs">
                        <pre className="whitespace-pre-wrap text-ink">
                          {JSON.stringify(step.output_data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="mt-3 text-xs text-ink-subtle">
                    Executed at: {new Date(step.timestamp).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-muted border-t text-center">
        <p className="text-xs text-ink-muted">
          🚀 Search intelligence powered by <span className="font-medium text-blue-400">/tmp labs</span> • 
          AI Recruitment Engine
        </p>
      </div>
    </div>
  );
};

// Search Progress Indicator Component
const SearchProgressIndicator = ({ searchProgress = [], currentStep = 0, isVisible = false }) => {
  if (!isVisible) return null;

  return (
    <div className="bg-elevated rounded-xl shadow-lg border border-line p-6 mb-6">
      <h3 className="text-lg font-bold text-ink mb-4 flex items-center space-x-2">
        <span className="text-xl">⚡</span>
        <span>AI Search Progress</span>
      </h3>
      
      <div className="space-y-3">
        {searchProgress.map((step, index) => (
          <div
            key={index}
            className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
              index < currentStep 
                ? 'bg-green-500/10 border border-green-500/25' 
                : index === currentStep 
                  ? 'bg-accent-soft border border-blue-500/25' 
                  : 'bg-muted border border-line'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              index < currentStep 
                ? 'bg-green-500 text-white' 
                : index === currentStep 
                  ? 'bg-blue-500 text-white animate-pulse' 
                  : 'bg-subtle text-ink-muted'
            }`}>
              {index < currentStep ? '✓' : index + 1}
            </div>
            <div className={`flex-1 ${
              index <= currentStep ? 'text-ink' : 'text-ink-subtle'
            }`}>
              {step}
            </div>
            {index === currentStep && (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-blue-400 font-medium">Processing...</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// CV Viewer Modal Component
const CVViewerModal = ({ profile, isOpen, onClose, matchedSkills = [], searchQuery = '' }) => {
  if (!isOpen || !profile) return null;

  // Color palette for unique skill colors (each searched skill gets its own color)
  const skillColorPalette = [
    { name: 'Green', bgClass: 'bg-green-300', textClass: 'text-green-300', badgeBg: 'bg-green-500', legendBg: 'bg-green-500/15', legendBorder: 'border-green-500/25', legendText: 'text-green-300' },
    { name: 'Orange', bgClass: 'bg-orange-300', textClass: 'text-orange-300', badgeBg: 'bg-orange-500/100', legendBg: 'bg-orange-500/15', legendBorder: 'border-orange-500/25', legendText: 'text-orange-300' },
    { name: 'Blue', bgClass: 'bg-blue-300', textClass: 'text-blue-300', badgeBg: 'bg-blue-500', legendBg: 'bg-accent-soft', legendBorder: 'border-blue-500/25', legendText: 'text-blue-300' },
    { name: 'Slate', bgClass: 'bg-slate-300', textClass: 'text-ink', badgeBg: 'bg-muted0', legendBg: 'bg-subtle', legendBorder: 'border-line', legendText: 'text-ink' },
    { name: 'Cyan', bgClass: 'bg-cyan-300', textClass: 'text-cyan-300', badgeBg: 'bg-cyan-500/100', legendBg: 'bg-cyan-500/15', legendBorder: 'border-cyan-500/25', legendText: 'text-cyan-300' },
    { name: 'Teal', bgClass: 'bg-teal-300', textClass: 'text-teal-300', badgeBg: 'bg-teal-500/100', legendBg: 'bg-teal-500/15', legendBorder: 'border-teal-500/25', legendText: 'text-teal-300' },
    { name: 'Amber', bgClass: 'bg-amber-300', textClass: 'text-amber-300', badgeBg: 'bg-amber-500', legendBg: 'bg-amber-500/15', legendBorder: 'border-amber-500/25', legendText: 'text-amber-300' },
    { name: 'Emerald', bgClass: 'bg-emerald-300', textClass: 'text-emerald-300', badgeBg: 'bg-emerald-500/100', legendBg: 'bg-emerald-500/15', legendBorder: 'border-emerald-500/25', legendText: 'text-emerald-300' },
    { name: 'Rose', bgClass: 'bg-rose-300', textClass: 'text-blue-300', badgeBg: 'bg-blue-500/100', legendBg: 'bg-blue-500/15', legendBorder: 'border-blue-500/25', legendText: 'text-blue-300' },
    { name: 'Sky', bgClass: 'bg-sky-300', textClass: 'text-sky-300', badgeBg: 'bg-sky-500/100', legendBg: 'bg-sky-500/15', legendBorder: 'border-sky-500/25', legendText: 'text-sky-300' },
    { name: 'Teal', bgClass: 'bg-teal-300', textClass: 'text-teal-300', badgeBg: 'bg-teal-500/100', legendBg: 'bg-teal-500/15', legendBorder: 'border-teal-500/25', legendText: 'text-teal-300' },
    { name: 'Lime', bgClass: 'bg-lime-300', textClass: 'text-lime-300', badgeBg: 'bg-lime-500/100', legendBg: 'bg-lime-500/15', legendBorder: 'border-lime-500/25', legendText: 'text-lime-300' },
  ];

  // Create a mapping of each unique skill to its color index
  const skillColorMap = {};
  matchedSkills.forEach((skill, index) => {
    const skillLower = skill.toLowerCase();
    if (!skillColorMap[skillLower]) {
      skillColorMap[skillLower] = index % skillColorPalette.length;
    }
  });

  // Get color for a specific skill
  const getSkillColor = (skill) => {
    const skillLower = skill.toLowerCase();
    const colorIndex = skillColorMap[skillLower] ?? 0;
    return skillColorPalette[colorIndex];
  };

  // Function to highlight matched skills in content with unique colors per skill
  const highlightSkillsInContent = (content, skills) => {
    if (!skills || skills.length === 0) return content;

    let highlightedContent = content;
    const skillPatterns = skills.map((skill, index) => ({
      skill,
      colorIndex: skillColorMap[skill.toLowerCase()] ?? index % skillColorPalette.length,
      pattern: new RegExp(`\\b(${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi')
    }));

    // Replace skills with highlighted spans (using markers first to avoid nested replacements)
    let parts = [{ type: 'text', content: highlightedContent }];

    skillPatterns.forEach(({ skill, colorIndex, pattern }) => {
      const newParts = [];
      parts.forEach(part => {
        if (part.type === 'highlight') {
          newParts.push(part);
        } else {
          const text = part.content;
          let lastIndex = 0;
          let match;

          while ((match = pattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
              newParts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
            }
            newParts.push({ type: 'highlight', content: match[0], skill, colorIndex });
            lastIndex = pattern.lastIndex;
          }

          if (lastIndex < text.length) {
            newParts.push({ type: 'text', content: text.slice(lastIndex) });
          }
        }
      });
      parts = newParts.length > 0 ? newParts : parts;
    });

    return parts;
  };

  const isSkillMatched = (skill) => {
    return matchedSkills.some(ms => ms.toLowerCase() === skill.toLowerCase());
  };

  // Get highlight class based on color index
  const getHighlightClass = (colorIndex) => {
    const color = skillColorPalette[colorIndex] || skillColorPalette[0];
    return `${color.bgClass} ${color.textClass}`;
  };

  const contentParts = highlightSkillsInContent(profile.content, matchedSkills);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-elevated rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden flex flex-col w-full">
        {/* Modal Header */}
        <div className="bg-muted border-b border-line px-6 py-4 flex justify-between items-center">
          <div className="text-ink">
            <h2 className="text-xl font-bold">{profile.filename}</h2>
            <p className="text-ink-subtle text-sm">CV Content Viewer {matchedSkills.length > 0 && `• ${matchedSkills.length} matched skills highlighted`}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-subtle hover:bg-muted text-ink-muted px-4 py-2 rounded-lg font-medium transition-colors"
          >
            ✕ Close
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="space-y-6">
            {/* Matched Skills Legend - Each Skill with Unique Color */}
            {matchedSkills.length > 0 && (
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-ink font-semibold">🎯 Required Skills Color Legend</span>
                  <span className="text-sm text-ink-muted">({matchedSkills.length} skills highlighted)</span>
                </div>

                {/* Each skill with its unique color */}
                <div className="flex flex-wrap gap-3">
                  {matchedSkills.map((skill, index) => {
                    const color = getSkillColor(skill);
                    return (
                      <div key={index} className={`${color.legendBg} border ${color.legendBorder} rounded-lg px-3 py-2 flex items-center space-x-2`}>
                        <span className={`w-4 h-4 rounded ${color.badgeBg}`}></span>
                        <span className={`font-semibold ${color.legendText}`}>{skill}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Helper text */}
                <div className="mt-3 pt-3 border-t border-line">
                  <p className="text-xs text-ink-subtle">
                    💡 Each required skill is highlighted in its own unique color in the CV content below.
                    Hover over highlighted text to see which skill it matches.
                  </p>
                </div>
              </div>
            )}

            {/* Profile Metadata */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <span className="text-sm font-medium text-ink-subtle">Uploaded:</span>
                <div className="font-semibold">{new Date(profile.created_at).toLocaleDateString()}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-subtle">Skills Extracted:</span>
                <div className="font-semibold">{(profile.extracted_skills || []).length} skills</div>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-subtle">Profile ID:</span>
                <div className="font-mono text-sm">{profile.id}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-subtle">File Size:</span>
                <div className="font-semibold">{profile.content?.length || 0} characters</div>
              </div>
            </div>

            {/* CV Content with Highlighted Skills */}
            <div>
              <h3 className="text-lg font-bold text-ink mb-4 flex items-center space-x-2">
                <span>📄</span>
                <span>CV Content</span>
                {matchedSkills.length > 0 && (
                  <span className="text-sm font-normal text-ink-subtle ml-2">
                    (Each skill highlighted in unique color)
                  </span>
                )}
              </h3>
              <div className="bg-muted rounded-lg p-6 border border-line max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-ink font-mono leading-relaxed">
                  {Array.isArray(contentParts) ? contentParts.map((part, i) =>
                    part.type === 'highlight' ? (
                      <mark
                        key={i}
                        className={`${getHighlightClass(part.colorIndex)} px-1 rounded font-semibold`}
                        title={`Required Skill: ${part.skill}`}
                      >
                        {part.content}
                      </mark>
                    ) : (
                      <span key={i}>{part.content}</span>
                    )
                  ) : profile.content}
                </pre>
              </div>
            </div>

            {/* Extracted Skills */}
            {(profile.extracted_skills || []).length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-ink mb-4 flex items-center space-x-2">
                  <span>🎯</span>
                  <span>Extracted Skills</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(profile.extracted_skills || []).map((skill, index) => {
                    const isMatched = isSkillMatched(skill);
                    const color = isMatched ? getSkillColor(skill) : null;
                    return (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-sm border font-medium ${
                          isMatched && color
                            ? `${color.legendBg} ${color.legendText} ${color.legendBorder}`
                            : 'bg-subtle text-ink-muted border-line'
                        }`}
                        title={isMatched ? `✓ Required Skill: ${skill}` : `Extracted skill: ${skill}`}
                      >
                        {isMatched && '✓ '}{skill}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Experience */}
            {(profile.extracted_experience || []).length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-ink mb-4 flex items-center space-x-2">
                  <span>💼</span>
                  <span>Experience</span>
                </h3>
                <ul className="space-y-2">
                  {(profile.extracted_experience || []).map((exp, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span className="text-ink-muted">{exp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-muted border-t flex justify-between items-center">
          <div className="text-sm text-ink-subtle">
            Powered by /tmp labs • AI Document Processing
          </div>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Close Viewer
          </button>
        </div>
      </div>
    </div>
  );
};

// Shortlist Manager Component
const ShortlistManager = ({ shortlist, removeFromShortlist, clearShortlist, emailConfig, setEmailConfig, hasPermission, onNavigate }) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [cvItem, setCvItem] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);

  const go = (tab) => (typeof onNavigate === 'function' ? onNavigate(tab) : (window.location.hash = tab));
  const copyText = (text) => {
    try { if (navigator.clipboard) navigator.clipboard.writeText(text); } catch (e) { /* clipboard unavailable */ }
    setMenuOpenId(null);
  };

  // Score -> semantic tint / label / recommendation (4-tier, mirrors ProfileCard).
  // -400 text is remapped to a darker shade under the light-mode accent bridge.
  const scoreText = (s) => (s >= 80 ? 'text-green-400' : s >= 60 ? 'text-blue-400' : s >= 40 ? 'text-amber-400' : 'text-red-400');
  const scorePill = (s) => (s >= 80
    ? 'text-green-400 bg-green-500/15 border-green-500/25'
    : s >= 60 ? 'text-blue-400 bg-blue-500/15 border-blue-500/25'
    : s >= 40 ? 'text-amber-400 bg-amber-500/15 border-amber-500/25'
    : 'text-red-400 bg-red-500/15 border-red-500/25');
  const scoreLabel = (s) => (s >= 80 ? 'Strong Match' : s >= 60 ? 'Good Match' : s >= 40 ? 'Needs Review' : 'Weak Match');
  const recoText = (s) => (s >= 80 ? 'Advance to interview' : s >= 60 ? 'Worth a closer look' : s >= 40 ? 'Review before deciding' : 'Likely not a fit');

  const count = shortlist.length;
  const avgScore = count ? Math.round(shortlist.reduce((sum, item) => sum + (item.matching_score?.overall_score || 0), 0) / count) : 0;
  const highMatch = shortlist.filter((item) => (item.matching_score?.overall_score || 0) >= 80).length;
  const interviewsReady = shortlist.filter((item) => (item.matching_score?.overall_score || 0) >= 70).length;
  const canSchedule = hasPermission('canScheduleInterviews');
  const metrics = [
    { label: 'Shortlisted', value: count, sub: 'in the evaluation queue', color: 'text-ink' },
    { label: 'Average Score', value: `${avgScore}%`, sub: 'across shortlist', color: scoreText(avgScore) },
    { label: 'High Match', value: highMatch, sub: 'strong picks (80%+)', color: 'text-green-400' },
    ...(canSchedule ? [{ label: 'Interviews Ready', value: interviewsReady, sub: 'ready to advance (70%+)', color: 'text-blue-400' }] : []),
  ];

  const handleDownloadShortlist = () => {
    if (!hasPermission('canDownloadData')) {
      alert('❌ Access denied: You do not have permission to download data');
      return;
    }
    const csvContent = [
      ['Rank', 'Filename', 'Overall Score', 'Skills Match', 'Matched Skills', 'Missing Skills', 'Added Date'].join(','),
      ...shortlist.map((item, index) => [
        index + 1,
        `"${item.profile.filename}"`,
        `${item.matching_score.overall_score}%`,
        `${item.matching_score.skill_match}%`,
        `"${item.matched_skills.join(', ')}"`,
        `"${item.missing_skills.join(', ')}"`,
        new Date(item.addedAt).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recruitment-shortlist-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleEmailShortlist = () => {
    if (!hasPermission('canEmailCandidates')) {
      alert('❌ Access denied: You do not have permission to email candidates');
      return;
    }
    alert(`Shortlist would be emailed to: ${emailConfig.recipientEmail}\n\nThis would include:\n• ${shortlist.length} candidates\n• Detailed scores and skills\n• Contact information\n• /tmp labs branding`);
    setEmailModalOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header — recruiter decision board */}
      <div className="bg-elevated rounded-xl border border-line shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400">
              <Icon name="evaluation" className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-ink">Evaluation</h2>
              <p className="text-sm text-ink-subtle">Review, compare and advance your shortlisted candidates</p>
            </div>
          </div>

          {count > 0 && (
            <div className="flex items-center gap-2">
              {hasPermission('canDownloadData') && (
                <button
                  onClick={handleDownloadShortlist}
                  className="inline-flex items-center gap-2 rounded-lg border border-green-500/25 bg-green-500/10 px-3 py-2 text-sm font-medium text-green-400 hover:bg-green-500/20 transition-colors"
                >
                  <Icon name="arrowRight" className="w-4 h-4 rotate-90" />
                  <span>Export CSV</span>
                </button>
              )}
              {hasPermission('canEmailCandidates') && (
                <button
                  onClick={() => setEmailModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-blue-500/25 bg-blue-500/10 px-3 py-2 text-sm font-medium text-blue-400 hover:bg-blue-500/20 transition-colors"
                >
                  <Icon name="email" className="w-4 h-4" />
                  <span>Email list</span>
                </button>
              )}
              <button
                onClick={clearShortlist}
                className="inline-flex items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/15 hover:border-red-500/25 transition-colors"
              >
                <span>Clear all</span>
              </button>
            </div>
          )}
        </div>

        {/* Decision metrics */}
        {count > 0 && (
          <div className={`mt-6 grid grid-cols-2 gap-3 ${metrics.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
            {metrics.map((m) => (
              <div key={m.label} className="rounded-xl border border-line bg-surface p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">{m.label}</div>
                <div className={`mt-1.5 text-2xl font-bold ${m.color}`}>{m.value}</div>
                <div className="mt-0.5 text-xs text-ink-subtle">{m.sub}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shortlist Items */}
      {count === 0 ? (
        <EmptyState
          type="no-data"
          title="No candidates shortlisted yet"
          reason="Search for candidates, review matches, and add top picks to your shortlist."
          action="Search Candidates"
          onAction={() => go('search')}
          secondaryAction="View Talent Pool"
          onSecondaryAction={() => go('profiles')}
          icon="shortlist"
          showNBA={false}
        />
      ) : (
        <div className="space-y-4">
          {shortlist.map((item, index) => {
            const score = item.matching_score?.overall_score || 0;
            const matched = item.matched_skills || [];
            const gaps = item.missing_skills || [];
            const jdTitle = item.matched_jd_title || item.jd_title || (item.profile && item.profile.matched_jd_title) || null;
            const menuOpen = menuOpenId === item.profile.id;
            return (
              <div key={item.profile.id} className="rounded-xl border border-line bg-surface p-5 transition-colors hover:border-blue-500/40">
                {/* Top: identity + score / recommendation */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line bg-subtle text-sm font-semibold text-ink-muted">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <h4 className="truncate text-base font-semibold text-ink">{item.profile.filename}</h4>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-subtle">
                        <span className="inline-flex items-center gap-1">
                          <Icon name="evaluation" className="w-3.5 h-3.5" />
                          {jdTitle ? (<>Matched to <span className="font-medium text-ink-muted">{jdTitle}</span></>) : 'General talent-pool match'}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Icon name="clock" className="w-3.5 h-3.5" />
                          Added {new Date(item.addedAt).toLocaleDateString()}
                        </span>
                        <span>{matched.length} skills matched</span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-bold ${scorePill(score)}`}>
                      {score}% · {scoreLabel(score)}
                    </div>
                    <div className={`mt-1 text-xs font-medium ${scoreText(score)}`}>{recoText(score)}</div>
                  </div>
                </div>

                {/* Skills comparison */}
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">Matched skills</div>
                    {matched.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {matched.slice(0, 6).map((skill, idx) => (
                          <span key={idx} className="inline-flex items-center rounded-md border border-green-500/20 bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">
                            {skill}
                          </span>
                        ))}
                        {matched.length > 6 && (
                          <span className="inline-flex items-center rounded-md border border-line bg-subtle px-2 py-0.5 text-xs text-ink-muted">
                            +{matched.length - 6} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-ink-subtle">None recorded</p>
                    )}
                  </div>
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">Skill gaps</div>
                    {gaps.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {gaps.slice(0, 6).map((skill, idx) => (
                          <span key={idx} className="inline-flex items-center rounded-md border border-line bg-subtle px-2 py-0.5 text-xs text-ink-muted">
                            {skill}
                          </span>
                        ))}
                        {gaps.length > 6 && (
                          <span className="inline-flex items-center rounded-md border border-line bg-subtle px-2 py-0.5 text-xs text-ink-muted">
                            +{gaps.length - 6} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs font-medium text-green-400">No gaps — full skill match</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
                  <button
                    onClick={() => setCvItem(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-1.5 text-sm font-medium text-blue-400 hover:bg-blue-500/20 transition-colors"
                  >
                    <Icon name="requirements" className="w-4 h-4" />
                    <span>View CV</span>
                  </button>
                  {canSchedule && (
                    <button
                      onClick={() => { setSelectedProfile(item.profile); setShowScheduleModal(true); }}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                    >
                      <Icon name="interviews" className="w-4 h-4" />
                      <span>Schedule Interview</span>
                    </button>
                  )}
                  <button
                    onClick={() => removeFromShortlist(item.profile.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium text-red-400 hover:bg-red-500/15 hover:border-red-500/25 transition-colors"
                  >
                    <span>Remove</span>
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setMenuOpenId(menuOpen ? null : item.profile.id)}
                      aria-label="More actions"
                      className="inline-flex items-center justify-center rounded-lg border border-line bg-transparent p-1.5 text-ink-subtle hover:bg-muted hover:text-ink transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
                    </button>
                    {menuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
                        <div className="absolute right-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-lg border border-line bg-elevated py-1 shadow-xl">
                          <button onClick={() => copyText(item.profile.filename)} className="flex w-full items-center px-3 py-2 text-left text-sm text-ink-muted hover:bg-muted hover:text-ink transition-colors">Copy candidate name</button>
                          <button onClick={() => copyText(matched.join(', '))} className="flex w-full items-center px-3 py-2 text-left text-sm text-ink-muted hover:bg-muted hover:text-ink transition-colors">Copy matched skills</button>
                          <div className="my-1 border-t border-line" />
                          <button onClick={() => { removeFromShortlist(item.profile.id); setMenuOpenId(null); }} className="flex w-full items-center px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/15 transition-colors">Remove from shortlist</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Email Configuration Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-elevated rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-ink mb-4">Email Shortlist</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-2">
                    Recipient Email Address
                  </label>
                  <input
                    type="email"
                    value={emailConfig.recipientEmail}
                    onChange={(e) => setEmailConfig({ ...emailConfig, recipientEmail: e.target.value })}
                    className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email address"
                  />
                </div>

                <div className="text-sm text-ink-muted">
                  This will send a detailed report with {shortlist.length} candidates including:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Candidate profiles and scores</li>
                    <li>Matched and missing skills</li>
                    <li>Contact information (when available)</li>
                    <li>Professional /tmp labs formatting</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEmailModalOpen(false)}
                  className="px-4 py-2 text-ink-muted hover:text-ink font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailShortlist}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && selectedProfile && (
        <ScheduleInterviewModal
          profile={selectedProfile}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedProfile(null);
          }}
          onSuccess={() => {
            setShowScheduleModal(false);
            setSelectedProfile(null);
          }}
        />
      )}

      {/* CV Viewer Modal */}
      {cvItem && (
        <CVViewerModal
          profile={cvItem.profile}
          isOpen={true}
          onClose={() => setCvItem(null)}
          matchedSkills={cvItem.matched_skills || []}
        />
      )}
    </div>
  );
};
// Search Results Component
const SearchResults = ({ results, addToShortlist, isInShortlist, emailConfig, hasPermission, selectedJDId, availableJDs }) => {
  const [showAgentTrace, setShowAgentTrace] = useState(false);
  
  const hasAgentTrace = results.agent_trace && results.agent_trace.steps && results.agent_trace.steps.length > 0;

  return (
    <div className="space-y-6">
      {/* Search Progress (if available) */}
      {results.search_progress && (
        <SearchProgressIndicator 
          searchProgress={results.search_progress} 
          currentStep={results.search_progress.length}
          isVisible={true}
        />
      )}

      {/* Audit Trail Viewer - Only show if user has audit permission */}
      {hasAgentTrace && hasPermission('canViewAuditTrace') && (
        <AgentTraceViewer 
          agentTrace={results.agent_trace}
          isVisible={showAgentTrace}
          onToggle={() => setShowAgentTrace(!showAgentTrace)}
        />
      )}

      {/* Main Results */}
      <div className="bg-elevated rounded-xl shadow-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-ink">
              🎯 Search Results ({results.total_matches} matches)
            </h3>
            <p className="text-sm text-ink-subtle mt-1">
              Powered by /tmp labs AI • Execution time: {results.execution_time?.toFixed(2)}s
              {results.local_results_count > 0 && (
                <span className="ml-2">• Local: {results.local_results_count}</span>
              )}
              {results.external_results_count > 0 && (
                <span className="ml-2">• External: {results.external_results_count}</span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {hasAgentTrace && hasPermission('canViewAuditTrace') && (
              <button
                onClick={() => setShowAgentTrace(!showAgentTrace)}
                className="flex items-center space-x-2 bg-accent-soft hover:bg-blue-500/25 text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <span className="text-lg">🧠</span>
                <span>{showAgentTrace ? 'Hide' : 'Show'} Audit Trail</span>
              </button>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-400">AI Analysis Complete</span>
            </div>
          </div>
        </div>

        {results.results.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 bg-accent-soft rounded-full flex items-center justify-center">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-ink mb-2">No matches found</h3>
            <p className="text-ink-subtle">Try adjusting your search criteria or uploading more profiles.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {results.results.map((result, index) => (
              <ProfileCard
                key={result.profile.id}
                result={result}
                rank={index + 1}
                addToShortlist={addToShortlist}
                isInShortlist={isInShortlist}
                emailConfig={emailConfig}
                hasPermission={hasPermission}
                selectedJDId={selectedJDId}
                availableJDs={availableJDs}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Card Component
const ProfileCard = ({ result, rank, addToShortlist, isInShortlist, emailConfig, hasPermission, selectedJDId, availableJDs }) => {
  const { profile, matching_score, matched_skills, missing_skills, highlights } = result;
  // Score breakdown is nested inside matching_score for Explainable AI
  const score_breakdown = matching_score?.score_breakdown;
  const [showExplainability, setShowExplainability] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [linkingJD, setLinkingJD] = useState(false);
  const [showJDSelector, setShowJDSelector] = useState(false);
  const [selectedLinkJDId, setSelectedLinkJDId] = useState(selectedJDId || '');

  // AutoMatch state for candidate
  const [matchingJDs, setMatchingJDs] = useState([]);
  const [loadingMatchingJDs, setLoadingMatchingJDs] = useState(false);
  const [showMatchingJDs, setShowMatchingJDs] = useState(false);
  const [runningCandidateAutoMatch, setRunningCandidateAutoMatch] = useState(false);

  const fetchMatchingJDs = async () => {
    if (!profile.id) return;
    setLoadingMatchingJDs(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/automatch/run/candidate/${profile.id}?max_matches=10&min_score=0.70`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMatchingJDs(data.matches || []);
      }
    } catch (err) {
      console.error('Failed to fetch matching JDs:', err);
    } finally {
      setLoadingMatchingJDs(false);
      setRunningCandidateAutoMatch(false);
    }
  };

  // Dark-mode colour budget — colour communicates meaning, not decoration:
  // 80+ Strong=green · 60-79 Good=blue · 40-59 Needs Review=amber · <40 Weak=red.
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400 bg-green-500/15 border-green-500/25';
    if (score >= 60) return 'text-blue-400 bg-blue-500/15 border-blue-500/25';
    if (score >= 40) return 'text-amber-400 bg-amber-500/15 border-amber-500/25';
    return 'text-red-400 bg-red-500/15 border-red-500/25';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Strong Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Needs Review';
    return 'Weak Match';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-500 to-green-600';
    if (score >= 60) return 'from-blue-500 to-blue-600';
    if (score >= 40) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  const handleViewCV = () => {
    setCvModalOpen(true);
  };

  const handleAddToShortlist = () => {
    if (!isInShortlist(profile.id)) {
      addToShortlist(result);
      alert(`✅ ${profile.filename} has been added to your shortlist!`);
    } else {
      alert(`ℹ️ ${profile.filename} is already in your shortlist.`);
    }
  };

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    from: '',
    to: '',
    subject: `Candidate Profile: ${profile.filename}`,
    body: '',
    attachCV: true
  });

  // Initialize email form with user data
  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setEmailForm(prev => ({
      ...prev,
      from: user.email || '',
      body: `Hi,\n\nPlease find attached the profile for ${profile.filename}.\n\nMatch Score: ${matching_score.overall_score}%\nMatched Skills: ${matched_skills.join(', ')}\n\nBest regards,\n${user.full_name || 'Recruiter'}`
    }));
  }, [profile, matching_score, matched_skills]);

  const handleEmailResume = () => {
    if (!hasPermission('canEmailCandidates')) {
      alert('❌ Access denied: You do not have permission to email candidates');
      return;
    }
    setEmailModalOpen(true);
  };

  const sendEmailWithCV = async () => {
    try {
      const response = await axios.post(`${API}/profiles/${profile.id}/email`, {
        from_email: emailForm.from,
        to_email: emailForm.to,
        subject: emailForm.subject,
        body: emailForm.body,
        attach_cv: emailForm.attachCV
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });

      alert('✅ Email sent successfully!');
      setEmailModalOpen(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(`❌ Failed to send email: ${getErrorMessage(error)}`);
    }
  };

  const handleDownloadCV = async () => {
    if (!hasPermission('canDownloadData')) {
      alert('❌ Access denied: You do not have permission to download data');
      return;
    }

    try {
      // First try to get the download info (may return signed URL for cloud storage)
      const response = await axios.get(`${API}/profiles/${profile.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });

      // Check if response contains a signed URL (for GCS/S3 storage)
      if (response.data && response.data.download_url) {
        // Cloud storage - open signed URL in new tab to trigger download
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.download = response.data.filename || profile.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Local storage - response is already streamed as blob
        // Re-fetch with blob response type
        const blobResponse = await axios.get(`${API}/profiles/${profile.id}/download`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          responseType: 'blob'
        });

        // Get filename from response headers or use profile filename
        const contentDisposition = blobResponse.headers['content-disposition'];
        let filename = profile.filename;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        // Create download link
        const url = window.URL.createObjectURL(new Blob([blobResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download file:', error);
      // Check if it's a 404 (original document not available)
      const errorMsg = error.response?.data?.detail || '';
      if (errorMsg.includes('Original document not available')) {
        alert('📄 Original document not available. Downloading profile analysis instead.');
      }
      // Fallback to text download if original file not available
      const content = `CANDIDATE PROFILE\n==================\n\nFilename: ${profile.filename}\nMatch Score: ${matching_score.overall_score}%\nSkills Match: ${matching_score.skill_match}%\n\nMATCHED SKILLS:\n${matched_skills.join(', ')}\n\nMISSING SKILLS:\n${missing_skills.join(', ')}\n\nCV CONTENT:\n${profile.content}\n\n---\nGenerated by /tmp labs Recruitment Engine`;

      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${profile.filename.replace(/\.[^/.]+$/, '')}_analysis.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  // Link profile to JD function
  const handleLinkToJD = async (jdId, pipelineStage = 'sourced') => {
    if (!jdId) {
      alert('Please select a Job Description to link this profile to.');
      return;
    }

    setLinkingJD(true);
    try {
      const response = await axios.post(`${API}/profiles/${profile.id}/link-jd`, {
        jd_id: jdId,
        pipeline_stage: pipelineStage
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });

      const linkedJD = availableJDs?.find(jd => jd.id === jdId);
      alert(`Profile linked to "${linkedJD?.title || 'JD'}" (Stage: ${pipelineStage})`);
      setShowJDSelector(false);
    } catch (error) {
      console.error('Failed to link profile to JD:', error);
      alert(`Failed to link profile: ${getErrorMessage(error)}`);
    } finally {
      setLinkingJD(false);
    }
  };

  const shortlisted = isInShortlist(profile.id);

  return (
    <>
      <div className="border border-line rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 bg-elevated">
        {/* Card Header */}
        <div className="p-6 bg-muted border-b">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-4">
              {/* Rank Badge */}
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg bg-subtle text-ink border border-line-subtle">
                {rank}
              </div>
              
              {/* Profile Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h4 className="text-xl font-bold text-ink">{profile.filename}</h4>
                  <div className={`px-4 py-1.5 rounded-full text-base font-extrabold border ${getScoreColor(matching_score.overall_score)}`}>
                    {matching_score.overall_score}% · {getScoreLabel(matching_score.overall_score)}
                  </div>
                  {shortlisted && (
                    <div className="px-3 py-1 rounded-full text-sm font-bold bg-amber-500/15 text-amber-300 border border-yellow-500/25">
                      ⭐ Shortlisted
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-ink-muted mt-2">
                  <div className="flex items-center space-x-1">
                    <span>📅</span>
                    <span>Uploaded: {new Date(profile.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>🧠</span>
                    <span className="text-ink-subtle font-medium">Analyzed by /tmp labs AI</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>📊</span>
                    <span>{(profile.extracted_skills || []).length} skills extracted</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleViewCV}
                className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-colors"
                title="View CV Content"
              >
                <span>📎</span>
                <span>View CV</span>
              </button>
              
              <button
                onClick={handleAddToShortlist}
                className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  shortlisted
                    ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-sm'
                }`}
                title={shortlisted ? "Already in shortlist" : "Add to Shortlist"}
              >
                <span>{shortlisted ? '⭐' : '✅'}</span>
                <span>{shortlisted ? 'Shortlisted' : 'Shortlist'}</span>
              </button>

              {/* Secondary actions */}
              <div className="w-px h-6 bg-line mx-1 self-center" />
              
              {hasPermission('canEmailCandidates') && (
                <button
                  onClick={handleEmailResume}
                  className="flex items-center space-x-1 bg-transparent border border-line text-ink-subtle hover:bg-muted hover:text-ink px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  title="Email Resume"
                >
                  <span>📤</span>
                  <span>Email</span>
                </button>
              )}

              {hasPermission('canDownloadData') && (
                <button
                  onClick={handleDownloadCV}
                  className="flex items-center space-x-1 bg-transparent border border-line text-ink-subtle hover:bg-muted hover:text-ink px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  title="Download Analysis"
                >
                  <span>💾</span>
                  <span>Download</span>
                </button>
              )}

              {/* Link to JD Button */}
              {hasPermission('canManageShortlist') && availableJDs?.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowJDSelector(!showJDSelector)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      profile.linked_jd_id
                        ? 'bg-blue-500/10 text-blue-300 border-blue-500/25 hover:bg-blue-500/20'
                        : 'bg-transparent text-ink-subtle border-line hover:bg-muted hover:text-ink'
                    }`}
                    title={profile.linked_jd_id ? "Linked to JD" : "Link to JD"}
                    disabled={linkingJD}
                  >
                    <span>{profile.linked_jd_id ? '🔗' : '📋'}</span>
                    <span>{linkingJD ? 'Linking...' : (profile.linked_jd_id ? 'Linked' : 'Link JD')}</span>
                  </button>

                  {/* JD Selector Dropdown */}
                  {showJDSelector && (
                    <div className="absolute right-0 top-full mt-1 w-72 bg-elevated rounded-lg shadow-lg border z-50 p-3">
                      <div className="text-sm font-medium text-ink-muted mb-2">Link to Job Description</div>
                      <select
                        value={selectedLinkJDId}
                        onChange={(e) => setSelectedLinkJDId(e.target.value)}
                        className="w-full px-2 py-1.5 border border-line rounded text-sm mb-2"
                      >
                        <option value="">Select JD...</option>
                        {availableJDs.map(jd => (
                          <option key={jd.id} value={jd.id}>
                            {jd.title} {jd.department ? `(${jd.department})` : ''}
                          </option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleLinkToJD(selectedLinkJDId, 'sourced')}
                          disabled={!selectedLinkJDId || linkingJD}
                          className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Link as Sourced
                        </button>
                        <button
                          onClick={() => handleLinkToJD(selectedLinkJDId, 'shortlisted')}
                          disabled={!selectedLinkJDId || linkingJD}
                          className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Link as Shortlisted
                        </button>
                      </div>
                      <button
                        onClick={() => setShowJDSelector(false)}
                        className="w-full mt-2 px-2 py-1 text-ink-subtle text-xs hover:text-ink-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-1 bg-transparent border border-line text-ink-subtle hover:bg-muted hover:text-ink px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <span>{isExpanded ? '🔼' : '🔽'}</span>
                <span>{isExpanded ? 'Less' : 'More'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="p-6 bg-elevated">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400 mb-1">{matching_score.skill_match}%</div>
              <div className="text-xs text-ink-subtle uppercase tracking-wide">Skills Match</div>
              <div className="w-full bg-subtle rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${matching_score.skill_match}%` }}
                />
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400 mb-1">{matching_score.experience_match}%</div>
              <div className="text-xs text-ink-subtle uppercase tracking-wide">Experience</div>
              <div className="w-full bg-subtle rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${matching_score.experience_match}%` }}
                />
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400 mb-1">{matching_score.education_match}%</div>
              <div className="text-xs text-ink-subtle uppercase tracking-wide">Education</div>
              <div className="w-full bg-subtle rounded-full h-2 mt-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${matching_score.education_match}%` }}
                />
              </div>
            </div>
            
            <div className="text-center">
              <div className={`text-xl font-bold mb-1 ${matching_score.semantic_similarity === 0 ? 'text-ink-subtle' : 'text-blue-400'}`}>{matching_score.semantic_similarity === 0 ? 'N/A' : `${matching_score.semantic_similarity}%`}</div>
              <div className="text-xs text-ink-subtle uppercase tracking-wide flex items-center justify-center gap-1">
                Semantic
                {matching_score.semantic_similarity === 0 && (
                  <span className="cursor-help text-ink-subtle normal-case" title="No semantic score for this result. Fast mode skips deep semantic analysis, and a short query or sparse resume can leave too little context to compute one — it does not indicate a poor match.">ⓘ</span>
                )}
              </div>
              <div className="w-full bg-subtle rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${matching_score.semantic_similarity}%` }}
                />
              </div>
            </div>
            
            <div className="text-center rounded-xl bg-muted border border-line-subtle py-2 -my-1">
              <div className={`text-3xl font-extrabold mb-1 ${matching_score.overall_score >= 80 ? 'text-green-400' : matching_score.overall_score >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                {matching_score.overall_score}%
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wide font-semibold">Overall Score</div>
              <div className="w-full bg-subtle rounded-full h-2 mt-2">
                <div 
                  className={`bg-gradient-to-r ${getScoreGradient(matching_score.overall_score)} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${matching_score.overall_score}%` }}
                />
              </div>
            </div>
          </div>

          {/* Skills Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Matched Skills */}
            {matched_skills.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-lg">✅</span>
                  <span className="text-sm font-bold text-green-400">Matched Skills ({matched_skills.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {matched_skills.map((skill, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-green-500/15 text-green-300 rounded-full text-sm border border-green-500/25 font-medium hover:bg-green-500/25 transition-colors"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Skills */}
            {missing_skills.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-lg">❌</span>
                  <span className="text-sm font-bold text-red-400">Missing Skills ({missing_skills.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {missing_skills.map((skill, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-red-500/15 text-red-300 rounded-full text-sm border border-red-500/25 font-medium hover:bg-red-500/25 transition-colors"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* AI Insights */}
          {highlights.length > 0 && (
            <div className="mt-6 p-4 bg-accent-soft rounded-lg border border-blue-500/25">
              <div className="flex items-center space-x-2 mb-3">
                <span className="text-lg">💡</span>
                <span className="text-sm font-bold text-blue-400">AI Insights</span>
              </div>
              <ul className="space-y-2">
                {highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-blue-500 text-sm mt-1">•</span>
                    <span className="text-sm text-ink-muted">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Why This Match - Business-Friendly Score Explanation */}
          {score_breakdown && (
            <div className="mt-6 p-4 bg-accent-soft rounded-lg border border-blue-500/25">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowExplainability(!showExplainability)}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🎯</span>
                  <span className="text-sm font-bold text-blue-400">Why This Match?</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    score_breakdown.confidence_level === 'high' ? 'bg-green-500/15 text-green-400' :
                    score_breakdown.confidence_level === 'medium' ? 'bg-amber-500/15 text-amber-400' :
                    'bg-red-500/15 text-red-400'
                  }`}>
                    {score_breakdown.confidence_level === 'high' ? 'Strong Match' :
                     score_breakdown.confidence_level === 'medium' ? 'Good Match' : 'Needs Review'}
                  </span>
                </div>
                <span className="text-blue-500">{showExplainability ? '▼' : '▶'}</span>
              </div>

              {showExplainability && (
                <div className="mt-4 space-y-4">
                  {/* AI Summary */}
                  {score_breakdown.summary && (
                    <div className="p-3 bg-elevated rounded-lg border border-blue-500/25">
                      <div className="text-xs text-blue-400 mb-1 font-medium">AI Assessment</div>
                      <p className="text-sm text-ink-muted">{score_breakdown.summary}</p>
                    </div>
                  )}

                  {/* Match Breakdown - Simple Visual */}
                  {score_breakdown.components && score_breakdown.components.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-blue-400 mb-3">How We Scored This Candidate</div>
                      <div className="space-y-3">
                        {score_breakdown.components.map((component, idx) => (
                          <div key={idx} className="p-3 bg-elevated rounded-lg border border-line-subtle">
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">
                                  {component.name === 'Resume Keywords' ? '📝' :
                                   component.name === 'Role Fit' ? '🎯' :
                                   component.name === 'Skills Match' ? '⚡' :
                                   component.name === 'Experience Level' ? '📈' : '✓'}
                                </span>
                                <span className="text-sm font-medium text-ink-muted">{component.name}</span>
                              </div>
                              <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                                component.percentage >= 25 ? 'bg-green-500/15 text-green-400' :
                                component.percentage >= 10 ? 'bg-amber-500/15 text-amber-400' :
                                'bg-subtle text-ink-muted'
                              }`}>
                                +{component.percentage?.toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-subtle rounded-full h-2 mb-2">
                              <div
                                className={`h-2 rounded-full ${
                                  component.percentage >= 25 ? 'bg-green-500' :
                                  component.percentage >= 10 ? 'bg-yellow-500' :
                                  'bg-ink-subtle'
                                }`}
                                style={{ width: `${Math.min(component.percentage || 0, 100)}%` }}
                              />
                            </div>
                            {component.description && (
                              <p className="text-xs text-ink-subtle">{component.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills Found */}
                  {score_breakdown.skill_matches && score_breakdown.skill_matches.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-blue-400 mb-2">
                        ✅ Matching Skills Found ({score_breakdown.skill_matches.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {score_breakdown.skill_matches.slice(0, 12).map((match, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 rounded-full text-sm ${
                              match.match_type === 'exact' ? 'bg-green-500/15 text-green-400 border border-green-500/25' :
                              match.match_type === 'synonym' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/25' :
                              'bg-subtle text-ink-muted border border-line'
                            }`}
                            title={match.match_type === 'exact' ? 'Exact match' :
                                   match.match_type === 'synonym' ? 'Similar skill' : 'Related skill'}
                          >
                            {match.matched_skill}
                          </span>
                        ))}
                        {score_breakdown.skill_matches.length > 12 && (
                          <span className="px-3 py-1 bg-subtle text-ink-muted rounded-full text-sm">
                            +{score_breakdown.skill_matches.length - 12} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Experience Bonus */}
                  {score_breakdown.boosters && score_breakdown.boosters.length > 0 && (
                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/25">
                      <div className="text-xs font-semibold text-green-400 mb-2">🌟 Bonus Points</div>
                      {score_breakdown.boosters.map((booster, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-ink-muted">{booster.reason}</span>
                          <span className="text-sm font-bold text-green-400">
                            +{(booster.boost_value * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Notes for Recruiter */}
                  {score_breakdown.data_quality_notes && score_breakdown.data_quality_notes.length > 0 && (
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/25">
                      <div className="text-xs font-semibold text-amber-300 mb-2 flex items-center gap-1.5">
                        <span>⚠️</span> Recommendations
                      </div>
                      <ul className="space-y-1.5">
                        {score_breakdown.data_quality_notes.map((note, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-amber-200">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Overall Score */}
                  <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg text-center text-white">
                    <div className="text-sm opacity-90 mb-1">Overall Match Score</div>
                    <div className="text-4xl font-bold">
                      {score_breakdown.final_score_percentage?.toFixed(0) || Math.round(score_breakdown.final_score * 100)}%
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {score_breakdown.final_score_percentage >= 70 ? 'Highly Recommended' :
                       score_breakdown.final_score_percentage >= 50 ? 'Worth Reviewing' :
                       'Consider Other Candidates'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-6 pt-6 border-t border-line">
              <div className="space-y-4">
                {/* All Skills */}
                <div>
                  <h5 className="text-sm font-bold text-ink-muted mb-2">All Extracted Skills</h5>
                  <div className="flex flex-wrap gap-2">
                    {(profile.extracted_skills || []).map((skill, index) => (
                      <span 
                        key={index} 
                        className="px-2 py-1 bg-subtle text-ink-muted rounded-full text-xs border border-line"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                {(profile.extracted_experience || []).length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-ink-muted mb-2">Experience</h5>
                    <ul className="text-sm text-ink-muted space-y-1">
                      {(profile.extracted_experience || []).map((exp, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          <span>{exp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Education */}
                {profile.education && profile.education.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-ink-muted mb-2">Education</h5>
                    <ul className="text-sm text-ink-muted space-y-1">
                      {profile.education.map((edu, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-500 mr-2">•</span>
                          <span>{edu}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Score Explanation */}
                <div className="p-4 bg-muted rounded-lg">
                  <h5 className="text-sm font-bold text-ink-muted mb-2">Score Explanation</h5>
                  <p className="text-sm text-ink-muted">{matching_score.explanation}</p>
                </div>

                {/* AutoMatch - Matching JDs Section */}
                <div className="pt-4 border-t border-line">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-700 rounded flex items-center justify-center">
                        <span className="text-white text-xs">AI</span>
                      </div>
                      <h5 className="text-sm font-bold text-ink-muted">Matching Job Openings</h5>
                    </div>
                    <button
                      onClick={() => {
                        setShowMatchingJDs(!showMatchingJDs);
                        if (!showMatchingJDs && matchingJDs.length === 0) {
                          setRunningCandidateAutoMatch(true);
                          fetchMatchingJDs();
                        }
                      }}
                      className="text-xs px-3 py-1 bg-accent-soft text-blue-400 rounded-lg hover:bg-blue-500/25"
                    >
                      {showMatchingJDs ? 'Hide' : 'Find Matching JDs'}
                    </button>
                  </div>

                  {showMatchingJDs && (
                    <div className="space-y-2">
                      {loadingMatchingJDs && (
                        <div className="flex items-center justify-center py-4">
                          <svg className="animate-spin h-5 w-5 text-blue-400" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span className="ml-2 text-sm text-ink-muted">Finding matching job openings...</span>
                        </div>
                      )}

                      {!loadingMatchingJDs && matchingJDs.length === 0 && (
                        <div className="text-center py-4 bg-muted rounded-lg">
                          <p className="text-sm text-ink-subtle">No matching job openings found</p>
                        </div>
                      )}

                      {!loadingMatchingJDs && matchingJDs.length > 0 && (
                        <div className="space-y-2">
                          {matchingJDs.slice(0, 5).map((match) => (
                            <div
                              key={match.match_id}
                              className="p-3 bg-elevated border rounded-lg hover:border-blue-500/25 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-ink text-sm">{match.jd_title}</span>
                                    <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${
                                      match.grade === 'A+' || match.grade === 'A' ? 'bg-green-500/15 text-green-300' :
                                      match.grade === 'B+' || match.grade === 'B' ? 'bg-accent-soft text-blue-300' :
                                      'bg-amber-500/15 text-amber-300'
                                    }`}>
                                      {match.grade}
                                    </span>
                                  </div>
                                  <p className="text-xs text-ink-subtle mt-0.5">{match.jd_code}</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-blue-400">
                                    {Math.round(match.overall_score * 100)}%
                                  </div>
                                </div>
                              </div>
                              {/* Mini score breakdown */}
                              <div className="mt-2 flex gap-2 text-xs">
                                <span className="px-2 py-0.5 bg-accent-soft text-blue-400 rounded">
                                  Skills: {Math.round(match.skills_score * 100)}%
                                </span>
                                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded">
                                  Exp: {Math.round(match.experience_score * 100)}%
                                </span>
                                <span className="px-2 py-0.5 bg-muted text-ink-muted rounded">
                                  Domain: {Math.round(match.domain_score * 100)}%
                                </span>
                              </div>
                            </div>
                          ))}
                          {matchingJDs.length > 5 && (
                            <p className="text-xs text-ink-subtle text-center">
                              +{matchingJDs.length - 5} more matching positions
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card Footer */}
        <div className="px-6 py-4 bg-muted border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-ink-subtle">
              <span>Profile ID: {profile.id.slice(0, 8)}...</span>
              <span>•</span>
              <span>Last updated: {new Date(profile.updated_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-blue-400 font-medium">Powered by /tmp labs AI</span>
            </div>
          </div>
        </div>
      </div>

      {/* CV Viewer Modal */}
      <CVViewerModal
        profile={profile}
        isOpen={cvModalOpen}
        onClose={() => setCvModalOpen(false)}
        matchedSkills={matched_skills}
      />

      {/* Email CV Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-elevated rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-muted border-b border-line px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="text-ink">
                  <h3 className="text-lg font-bold">📧 Email CV</h3>
                  <p className="text-ink-subtle text-sm">{profile.filename}</p>
                </div>
                <button
                  onClick={() => setEmailModalOpen(false)}
                  className="text-ink-muted hover:bg-muted rounded-lg p-2"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">From</label>
                <input
                  type="email"
                  value={emailForm.from}
                  onChange={(e) => setEmailForm({...emailForm, from: e.target.value})}
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 bg-muted"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">To *</label>
                <input
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({...emailForm, to: e.target.value})}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">Subject</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">Message</label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                  rows={5}
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                <input
                  type="checkbox"
                  id="attachCV"
                  checked={emailForm.attachCV}
                  onChange={(e) => setEmailForm({...emailForm, attachCV: e.target.checked})}
                  className="h-4 w-4 text-blue-400 focus:ring-blue-500 rounded"
                />
                <label htmlFor="attachCV" className="text-sm text-ink-muted flex items-center space-x-2">
                  <span>📎</span>
                  <span>Attach CV ({profile.filename})</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setEmailModalOpen(false)}
                  className="px-4 py-2 text-ink-muted hover:bg-subtle rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={sendEmailWithCV}
                  disabled={!emailForm.to}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium flex items-center space-x-2"
                >
                  <span>📤</span>
                  <span>Send Email</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Upload Interface Component  
const UploadInterface = ({
  onUploadSuccess,
  user,
  activeJD = null,
  setActiveView = () => {},
  stats = {},
  handleAutoMatch = () => {},
}) => {
  const [uploadMode, setUploadMode] = useState('single'); // 'single', 'bulk', 'zip', 'queue'
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [singleFile, setSingleFile] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [profileMetadata, setProfileMetadata] = useState({
    country: '',
    city: '',
    willing_to_relocate: false,
    remote_work_preference: 'flexible'
  });

  // Bulk / ZIP related state
  const [bulkJobs, setBulkJobs] = useState([]);
  const [zipFile, setZipFile] = useState(null);
  const [zipAnalysis, setZipAnalysis] = useState(null);
  const [bulkJobName, setBulkJobName] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);

  // Warning modal state for large file uploads in single mode
  const [showBulkWarning, setShowBulkWarning] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const BULK_WARNING_THRESHOLD = 10; // Show warning when selecting more than 10 files

  // Initial location load
  useEffect(() => {
    fetchLocationData();
  }, []);

  const fetchLocationData = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocationData(response.data);
    } catch (error) {
      console.error('Failed to fetch location data:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(files);

    // Auto-generate batch name if not already set (for non-tech users)
    if (!bulkJobName.trim() && files.length > 0) {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).replace(/ /g, '-');
      const timeStr = now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      }).replace(':', '');
      const userName = currentUser?.full_name?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'Upload';
      const autoName = `${userName}_${dateStr}_${timeStr}`;
      setBulkJobName(autoName);
    }
  };

  const handleFileSelect = (e, isFromFolder = false) => {
    const files = Array.from(e.target.files);

    // In Single Mode, warn if too many files selected
    if (uploadMode === 'single' && files.length > BULK_WARNING_THRESHOLD) {
      setPendingFiles(files);
      setShowBulkWarning(true);
      return; // Don't set files yet, wait for user decision
    }

    setSelectedFiles(files);

    // Auto-generate batch name if not already set (for non-tech users)
    if (!bulkJobName.trim() && files.length > 0) {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).replace(/ /g, '-');
      const timeStr = now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      }).replace(':', '');
      const userName = currentUser?.full_name?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'Upload';
      const autoName = `${userName}_${dateStr}_${timeStr}`;
      setBulkJobName(autoName);
    }
  };

  // Handle switching to bulk mode from warning modal
  const handleSwitchToBulkMode = () => {
    setShowBulkWarning(false);
    setUploadMode('bulk');
    setSelectedFiles(pendingFiles);

    // Auto-generate batch name
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-');
    const timeStr = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    }).replace(':', '');
    const userName = user?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'Upload';
    const autoName = `${userName}_${dateStr}_${timeStr}`;
    setBulkJobName(autoName);

    setPendingFiles([]);
  };

  // Continue with single mode upload despite warning
  const handleContinueSingleMode = () => {
    setShowBulkWarning(false);
    setSelectedFiles(pendingFiles);
    setPendingFiles([]);
  };

  // Cancel the file selection
  const handleCancelSelection = () => {
    setShowBulkWarning(false);
    setPendingFiles([]);
  };

  const handleUploadWithLocation = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const results = [];

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        // Add location metadata to the upload
        Object.entries(profileMetadata).forEach(([key, value]) => {
          if (value !== '' && value !== null) {
            formData.append(key, value);
          }
        });

        const response = await axios.post(`${API}/profiles/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        results.push({ ...response.data, filename: file.name, status: 'success' });
      } catch (error) {
        results.push({
          filename: file.name,
          status: 'error',
          error: error.response?.data?.detail || 'Upload failed'
        });
      }
    }

    setUploadResults(results);
    setUploading(false);
    setSelectedFiles([]);
    onUploadSuccess();
  };

  const getCitiesForCountry = (country) => {
    if (!locationData || !country) return [];
    return locationData.cities_by_country[country] || [];
  };

  // Bulk + queue helpers
  const fetchBulkJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/upload/bulk/jobs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const jobs = await response.json();
        setBulkJobs(jobs);
      }
    } catch (error) {
      console.error('Failed to fetch bulk jobs:', error);
    }
  };

  const fetchQueueStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/upload/queue/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const status = await response.json();
        setQueueStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFiles.length) {
      alert('Please select files for bulk upload');
      return;
    }

    if (!bulkJobName.trim()) {
      alert('Please enter a job name');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      
      // Append all selected files
      selectedFiles.forEach((file, index) => {
        formData.append('files', file);
      });
      formData.append('job_name', bulkJobName);

      const response = await fetch(`${BACKEND_URL}/api/upload/bulk/upload-files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        // Show smart batch processing success message
        const batchInfo = result.batch_strategy;
        const processingInfo = result.processing_details;
        
        const successMsg = `🚀 Smart Batch Processing Initiated!\n\n` +
          `📋 Job Name: ${bulkJobName}\n` +
          `📄 Total Files: ${result.total_files} (${result.valid_files} valid, ${result.invalid_files} invalid)\n` +
          `🧠 Intelligence: ${processingInfo.subscription_tier.toUpperCase()} tier processing\n` +
          `⚡ Mini-Batches: ${processingInfo.total_mini_batches} batches of ${processingInfo.batch_size} files each\n` +
          `🔄 Concurrent Processing: Up to ${processingInfo.concurrent_batches} batches simultaneously\n` +
          `⏱️ Estimated Time: ${Math.round(result.estimated_processing_time_minutes)} minutes\n` +
          `🆔 Parent Job ID: ${result.parent_job_id}\n\n` +
          `🎯 Platform Intelligence: No upload limits - we automatically split large uploads into optimal mini-batches for efficient processing!\n\n` +
          `Switch to "Queue Status" mode to monitor real-time progress.`;
        
        alert(successMsg);
        
        // Reset form
        setSelectedFiles([]);
        setBulkJobName('');
        setUploadResults([]);
        
        // Refresh stats
        if (onUploadSuccess) {
          await onUploadSuccess();
        }
        
        // Refresh queue data
        await fetchBulkJobs();
        await fetchQueueStatus();
      } else {
        alert(`❌ Upload failed: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Bulk upload failed:', error);
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleZipUpload = async (file, analyzeOnly = false) => {
    if (!bulkJobName.trim()) {
      alert('Please enter a job name');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_name', bulkJobName);
      formData.append('analyze_only', analyzeOnly);

      const response = await fetch(`${BACKEND_URL}/api/upload/bulk/upload-zip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        if (analyzeOnly) {
          setZipAnalysis(result.analysis || result);
        } else {
          setUploadResults((prev) => [
            ...prev,
            {
              ...result,
              filename: file.name,
              status: 'success',
              message: 'Bulk job created'
            }
          ]);
          setZipFile(null);
          setZipAnalysis(null);
          fetchBulkJobs();
          fetchQueueStatus();
        }
      } else {
        setUploadResults((prev) => [
          ...prev,
          {
            filename: file.name,
            status: 'error',
            error: result.detail || 'ZIP upload failed'
          }
        ]);
      }
    } catch (error) {
      console.error('ZIP upload error:', error);
      setUploadResults((prev) => [
        ...prev,
        {
          filename: file.name,
          status: 'error',
          error: 'ZIP upload failed'
        }
      ]);
    } finally {
      setUploading(false);
    }
  };

  // When user switches to bulk/zip/queue, refresh jobs + queue
  useEffect(() => {
    if (uploadMode === 'bulk' || uploadMode === 'zip' || uploadMode === 'queue') {
      fetchBulkJobs();
      fetchQueueStatus();
    }
  }, [uploadMode]);

  return (
    <div className="space-y-8">
      {/* Active JD Context - Liquid Design */}
      {activeJD && (
        <ActiveJDContext
          jd={activeJD}
          stats={{
            profiles: stats?.total_profiles || 0,
            matched: activeJD.matchCount || 0,
            shortlisted: activeJD.shortlistCount || 0,
          }}
          onViewMatches={() => setActiveView('search')}
          onChangeJD={() => setActiveView('jd-builder')}
          onRunAutoMatch={() => handleAutoMatch(activeJD.id)}
          compact={false}
        />
      )}

      {/* No Active JD Banner - Liquid Design */}
      {!activeJD && (
        <div className="bg-amber-500/[0.08] border border-amber-500/25 rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-[200px]">
              <h4 className="font-semibold text-ink">No active requirement selected</h4>
              <p className="text-sm text-ink-muted">You can still import profiles — but <strong className="text-amber-400">AutoMatch is disabled</strong> until an active requirement is selected.</p>
            </div>
            <button
              onClick={() => setActiveView('jd-builder')}
              className="px-4 h-9 bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Select Requirement
            </button>
          </div>
        </div>
      )}

      {/* Import Area + Tabs */}
      <div className="arise-panel p-6">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center">
              <Icon name="import" className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-ink tracking-tight">
                Import Candidate Profiles
              </h2>
              <p className="text-sm text-ink-subtle">
                AI document processing · PDF, DOCX, images
              </p>
            </div>
          </div>
          {/* Current Stats - Liquid Design */}
          <div className="flex items-center gap-4 text-sm">
            <div className="px-3 py-1.5 bg-subtle rounded-lg">
              <span className="text-ink-muted">Total Profiles: </span>
              <span className="font-semibold text-ink">{stats?.total_profiles || 0}</span>
            </div>
          </div>
        </div>

        {/* Upload Mode Toggle with Recommendations */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-center gap-1 bg-subtle p-1.5 rounded-xl max-w-xl mx-auto">
            {[
              { id: 'single', label: 'Single File', icon: '📄' },
              { id: 'bulk', label: 'Bulk Files', icon: '📁' },
              { id: 'zip', label: 'ZIP Upload', icon: '📦' },
              { id: 'queue', label: 'Queue Status', icon: '🔄' }
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => {
                  setUploadMode(mode.id);
                  // Reset state when switching modes
                  setSelectedFiles([]);
                  setSingleFile(null);
                  setZipFile(null);
                  setZipAnalysis(null);
                  setBulkJobName('');
                  setUploadResults([]);
                }}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  uploadMode === mode.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-ink-muted hover:text-ink hover:bg-muted'
                }`}
              >
                <span>{mode.icon}</span>
                <span>{mode.label}</span>
              </button>
            ))}
          </div>
          {/* Mode Recommendations - Liquid Design */}
          <div className="mt-3 text-center text-xs text-ink-subtle">
            {uploadMode === 'single' && "Best for: Quick testing or adding individual candidates"}
            {uploadMode === 'bulk' && "Best for: 2-50 files at once with smart batch processing"}
            {uploadMode === 'zip' && "Best for: Large batches (50+ files) from email exports or archives"}
            {uploadMode === 'queue' && "Monitor processing progress and view job history"}
          </div>
        </div>

        {/* Upload Content */}
        <div className="space-y-6">
          {/* SINGLE MODE */}
          {uploadMode === 'single' && (
            <div>
              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${
                  dragActive
                    ? 'border-blue-500 bg-blue-500/[0.06]'
                    : 'border-line hover:border-blue-500/50 hover:bg-muted'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-accent-soft text-accent-ink rounded-2xl flex items-center justify-center">
                    <Icon name="import" className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-ink">
                      Drop CV / resume files here
                    </h3>
                    <p className="mt-1 text-sm text-ink-subtle">
                      PDF, DOCX, TXT and other standard document formats
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <label className="arise-btn-primary inline-flex items-center gap-2 px-5 h-11 rounded-lg font-semibold cursor-pointer">
                      Browse Files
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </label>
                    <label className="arise-btn-secondary inline-flex items-center gap-2 px-5 h-11 rounded-lg font-semibold cursor-pointer">
                      Select Folder
                      <input
                        type="file"
                        webkitdirectory=""
                        directory=""
                        multiple
                        className="hidden"
                        onChange={(e) => handleFileSelect(e, true)}
                      />
                    </label>
                  </div>
                  {selectedFiles.length > 0 && (
                    <p className="mt-3 text-sm text-ink-muted">
                      Selected {selectedFiles.length} file
                      {selectedFiles.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>

              {/* Warning Modal for Large File Selection */}
              {showBulkWarning && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-elevated rounded-2xl p-6 max-w-lg mx-4 shadow-2xl">
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 mx-auto bg-amber-500/15 rounded-full flex items-center justify-center mb-3">
                        <span className="text-3xl">⚠️</span>
                      </div>
                      <h3 className="text-xl font-bold text-ink">
                        Large Upload Detected
                      </h3>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg p-4 mb-4">
                      <p className="text-amber-300 font-medium text-center">
                        You've selected <span className="text-amber-300 font-bold">{pendingFiles.length} files</span>
                      </p>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg">
                        <span className="text-red-500 text-xl">🐢</span>
                        <div>
                          <p className="font-medium text-red-300">Single Mode = Slow</p>
                          <p className="text-sm text-red-400">
                            Files upload one-by-one. {pendingFiles.length} files could take {Math.round(pendingFiles.length * 3 / 60)} - {Math.round(pendingFiles.length * 5 / 60)} minutes.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
                        <span className="text-green-500 text-xl">🚀</span>
                        <div>
                          <p className="font-medium text-green-300">Bulk Mode = Fast</p>
                          <p className="text-sm text-green-400">
                            Smart batch processing with parallel uploads. Much faster and tracks progress!
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleSwitchToBulkMode}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                      >
                        <span>🚀</span>
                        <span>Switch to Bulk Mode (Recommended)</span>
                      </button>
                      <button
                        onClick={handleContinueSingleMode}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        <span>🐢</span>
                        <span>Continue with Single Mode Anyway</span>
                      </button>
                      <button
                        onClick={handleCancelSelection}
                        className="w-full border border-line text-ink-muted px-6 py-3 rounded-lg hover:bg-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Location metadata */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    Country
                  </label>
                  <select
                    value={profileMetadata.country}
                    onChange={(e) =>
                      setProfileMetadata({
                        ...profileMetadata,
                        country: e.target.value,
                        city: '' // reset city when country changes
                      })
                    }
                    className="block w-full rounded-lg bg-surface text-ink border border-line px-3 py-2 focus:border-blue-500 focus:ring-blue-500 focus:outline-none text-sm disabled:opacity-50"
                  >
                    <option value="">Select country</option>
                    {locationData?.countries?.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    City
                  </label>
                  <select
                    value={profileMetadata.city}
                    onChange={(e) =>
                      setProfileMetadata({
                        ...profileMetadata,
                        city: e.target.value
                      })
                    }
                    className="block w-full rounded-lg bg-surface text-ink border border-line px-3 py-2 focus:border-blue-500 focus:ring-blue-500 focus:outline-none text-sm disabled:opacity-50"
                    disabled={!profileMetadata.country}
                  >
                    <option value="">Select city</option>
                    {getCitiesForCountry(profileMetadata.country).map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    Remote work preference
                  </label>
                  <select
                    value={profileMetadata.remote_work_preference}
                    onChange={(e) =>
                      setProfileMetadata({
                        ...profileMetadata,
                        remote_work_preference: e.target.value
                      })
                    }
                    className="block w-full rounded-lg bg-surface text-ink border border-line px-3 py-2 focus:border-blue-500 focus:ring-blue-500 focus:outline-none text-sm disabled:opacity-50"
                  >
                    <option value="flexible">Flexible</option>
                    <option value="remote_only">Remote only</option>
                    <option value="on_site_only">On-site only</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex items-center">
                <input
                  type="checkbox"
                  id="willing_to_relocate"
                  checked={profileMetadata.willing_to_relocate}
                  onChange={(e) =>
                    setProfileMetadata({
                      ...profileMetadata,
                      willing_to_relocate: e.target.checked
                    })
                  }
                  className="h-4 w-4 accent-blue-600 text-blue-600 focus:ring-blue-500 border-line rounded"
                />
                <label
                  htmlFor="willing_to_relocate"
                  className="ml-2 block text-sm text-ink-muted"
                >
                  Willing to relocate
                </label>
              </div>

              <div className="mt-6 flex space-x-4">
                <button
                  onClick={handleUploadWithLocation}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle disabled:text-ink-subtle disabled:cursor-not-allowed text-white px-6 h-11 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/25"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>🚀</span>
                      <span>
                        Upload {selectedFiles.length} File
                        {selectedFiles.length > 1 ? 's' : ''} with Location
                      </span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setSelectedFiles([])}
                  className="px-6 py-3 border border-line text-ink-muted rounded-lg hover:bg-muted"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* BULK MODE */}
          {uploadMode === 'bulk' && (
            <div className="space-y-4">
              {/* STEP 1: Name your batch - MUST BE FIRST AND PROMINENT */}
              <div className={`p-4 rounded-xl border-2 ${bulkJobName.trim() ? 'border-green-500/25 bg-green-500/10' : 'border-red-500/25 bg-red-500/10'}`}>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm ${bulkJobName.trim() ? 'bg-green-500' : 'bg-red-500'}`}>1</span>
                  <label className="block text-base font-semibold text-ink">
                    Name this upload batch <span className="text-red-500">*</span>
                  </label>
                  {bulkJobName.trim() && <span className="text-green-600">✓</span>}
                </div>
                <input
                  type="text"
                  value={bulkJobName}
                  onChange={(e) => setBulkJobName(e.target.value)}
                  placeholder="Example: Walk-in drive Dec 2025, Campus hiring batch, LinkedIn applicants..."
                  className={`block w-full rounded-lg bg-surface text-ink placeholder:text-ink-subtle border shadow-sm text-base py-3 px-4 focus:outline-none focus:ring-2 ${
                    bulkJobName.trim()
                      ? 'border-green-500/50 focus:border-green-500 focus:ring-green-500'
                      : 'border-red-500/50 focus:border-red-500 focus:ring-red-500'
                  }`}
                />
                {!bulkJobName.trim() && (
                  <p className="mt-2 text-sm text-red-600 font-medium">
                    ⚠️ Required: Enter a name to identify this batch (e.g., "January Resumes" or "Walk-in Drive")
                  </p>
                )}
                {bulkJobName.trim() && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Great! Now select your files below.
                  </p>
                )}
              </div>

              {/* STEP 2: Select files */}
              <div className={`p-4 rounded-xl border-2 ${selectedFiles.length > 0 ? 'border-green-500/25 bg-green-500/10' : 'border-line bg-muted'}`}>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm ${selectedFiles.length > 0 ? 'bg-green-500' : 'bg-subtle'}`}>2</span>
                  <label className="block text-base font-semibold text-ink">
                    Select CV/Resume files
                  </label>
                  {selectedFiles.length > 0 && <span className="text-green-600">✓ {selectedFiles.length} files</span>}
                </div>
                <div className="border-2 border-dashed border-line rounded-xl p-6 text-center hover:border-blue-400 bg-elevated">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <div>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        id="bulk-file-upload"
                        onChange={handleFileSelect}
                      />
                      <label
                        htmlFor="bulk-file-upload"
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                      >
                        📄 Browse Files
                      </label>
                    </div>
                    <div>
                      <input
                        type="file"
                        webkitdirectory=""
                        directory=""
                        multiple
                        className="hidden"
                        id="bulk-folder-upload"
                        onChange={handleFileSelect}
                      />
                      <label
                        htmlFor="bulk-folder-upload"
                        className="inline-flex items-center px-6 py-3 border-2 border-blue-600 text-base font-medium rounded-md text-blue-600 bg-elevated hover:bg-accent-soft cursor-pointer"
                      >
                        📁 Select Folder
                      </label>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-ink-subtle">
                    Supports PDF, DOCX, DOC, TXT, RTF formats
                  </p>
                  {selectedFiles.length > 0 && (
                    <p className="mt-2 text-sm font-medium text-green-600">
                      ✓ {selectedFiles.length} files selected and ready
                    </p>
                  )}
                </div>
              </div>

              {/* STEP 3: Upload - Shows status based on completion of steps 1 & 2 */}
              <div className={`p-4 rounded-xl border-2 ${
                selectedFiles.length > 0 && bulkJobName.trim()
                  ? 'border-green-500/25 bg-green-500/10'
                  : 'border-line bg-muted'
              }`}>
                <div className="flex items-center space-x-2 mb-3">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm ${
                    selectedFiles.length > 0 && bulkJobName.trim() ? 'bg-green-500' : 'bg-subtle'
                  }`}>3</span>
                  <label className="block text-base font-semibold text-ink">
                    Start Upload
                  </label>
                </div>

                {/* Not ready - missing batch name */}
                {!bulkJobName.trim() && (
                  <div className="p-4 bg-red-500/10 border border-red-500/25 rounded-lg text-center">
                    <span className="text-3xl">☝️</span>
                    <p className="text-red-400 font-medium mt-2">
                      Please complete Step 1 first
                    </p>
                    <p className="text-red-600 text-sm mt-1">
                      Enter a batch name above to continue
                    </p>
                  </div>
                )}

                {/* Has batch name but no files */}
                {bulkJobName.trim() && selectedFiles.length === 0 && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/25 rounded-lg text-center">
                    <span className="text-3xl">📁</span>
                    <p className="text-amber-400 font-medium mt-2">
                      Now select your files
                    </p>
                    <p className="text-yellow-600 text-sm mt-1">
                      Use the "Browse Files" button above
                    </p>
                  </div>
                )}

                {/* Ready to upload! */}
                {selectedFiles.length > 0 && bulkJobName.trim() && (
                  <div className="space-y-4">
                    <div className="p-3 bg-green-500/15 border border-green-500/25 rounded-lg">
                      <p className="text-green-300 font-medium text-center">
                        ✅ Ready to upload {selectedFiles.length} files as "{bulkJobName}"
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <button
                        onClick={handleBulkUpload}
                        disabled={uploading}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle text-white px-6 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center space-x-2 shadow-lg"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                            <span>Uploading... Please wait</span>
                          </>
                        ) : (
                          <>
                            <span className="text-2xl">🚀</span>
                            <span>Upload {selectedFiles.length} Files Now</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedFiles([]);
                          setBulkJobName('');
                        }}
                        className="px-6 py-4 border-2 border-line text-ink-muted rounded-lg hover:bg-muted font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="text-sm text-accent-ink bg-accent-soft p-3 rounded-lg">
                      💡 <strong>After clicking upload:</strong> Switch to "Queue Status" tab to monitor progress in real-time
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ZIP MODE */}
          {uploadMode === 'zip' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    Bulk Job Name
                  </label>
                  <input
                    type="text"
                    value={bulkJobName}
                    onChange={(e) => setBulkJobName(e.target.value)}
                    placeholder="e.g. March campus hiring batch"
                    className="block w-full rounded-lg bg-surface text-ink border border-line px-3 py-2 focus:border-blue-500 focus:ring-blue-500 focus:outline-none text-sm disabled:opacity-50"
                  />
                  <p className="mt-1 text-xs text-ink-subtle">
                    This name will be used to identify the bulk ZIP upload job.
                  </p>
                </div>
              </div>

              <div className="border-2 border-dashed border-line rounded-xl p-8 text-center hover:border-green-400">
                <div className="w-20 h-20 mx-auto bg-green-500/15 rounded-full flex items-center justify-center mb-4">
                  <span className="text-4xl">📦</span>
                </div>
                <h3 className="text-lg font-medium text-ink mb-2">
                  Upload ZIP File
                </h3>
                <p className="text-ink-subtle mb-4">
                  Select a ZIP file containing resume documents
                </p>

                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files[0] || null)}
                  className="hidden"
                  id="zip-file-upload"
                />
                <label
                  htmlFor="zip-file-upload"
                  className="arise-btn-primary inline-flex items-center gap-2 px-5 h-11 rounded-lg font-semibold cursor-pointer"
                >
                  Browse ZIP Files
                </label>
              </div>

              {zipFile && bulkJobName.trim().length === 0 && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/25 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-yellow-600">⚠️</span>
                    <span className="text-amber-300 font-medium">ZIP file selected!</span>
                  </div>
                  <p className="text-amber-400 text-sm mt-1">
                    Please enter a job name above to enable upload buttons.
                  </p>
                </div>
              )}

              {zipFile && (
                <div className="p-4 bg-green-500/10 rounded-lg">
                  <h4 className="font-medium text-green-300 mb-2">
                    Selected ZIP File
                  </h4>
                  <div className="text-sm text-green-400 flex items-center space-x-2 mb-4">
                    <span>📦</span>
                    <span>{zipFile.name}</span>
                    <span className="text-green-600">
                      ({(zipFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleZipUpload(zipFile, false)}
                      disabled={uploading || !bulkJobName.trim()}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <span>🚀</span>
                          <span>Upload ZIP File</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleZipUpload(zipFile, true)}
                      disabled={uploading || !bulkJobName.trim()}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle text-white rounded-lg"
                    >
                      {uploading ? 'Analyzing...' : 'Analyze Only'}
                    </button>
                    <button
                      onClick={() => {
                        setZipFile(null);
                        setZipAnalysis(null);
                      }}
                      className="px-6 py-3 border border-line text-ink-muted rounded-lg hover:bg-muted"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* ZIP Analysis Results */}
              {zipAnalysis && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/25 rounded-lg">
                  <h4 className="font-medium text-amber-300 mb-4">
                    ZIP Analysis Results
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {zipAnalysis.valid_files || 0}
                      </div>
                      <div className="text-sm text-ink-muted">Valid Files</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {zipAnalysis.invalid_files || 0}
                      </div>
                      <div className="text-sm text-ink-muted">Invalid Files</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {((zipAnalysis.total_size_bytes || 0) / 1024 / 1024).toFixed(1)}MB
                      </div>
                      <div className="text-sm text-ink-muted">Total Size</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {(zipAnalysis.estimated_processing_time_minutes || 0).toFixed(1)}min
                      </div>
                      <div className="text-sm text-ink-muted">Est. Time</div>
                    </div>
                  </div>

                  {(zipAnalysis.invalid_files || 0) > 0 && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/25 rounded">
                      <h5 className="font-medium text-red-300 mb-2">
                        Issues Found:
                      </h5>
                      <ul className="text-sm text-red-400 space-y-1">
                        {Object.entries(zipAnalysis.validation_summary || {}).map(
                          ([issue, count]) => (
                            <li key={issue}>
                              • {count} files with {issue.replace('_', ' ')}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => handleZipUpload(zipFile, false)}
                    disabled={uploading || (zipAnalysis.valid_files || 0) === 0}
                    className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-subtle text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Process {zipAnalysis.valid_files || 0} Valid Files
                  </button>
                </div>
              )}
            </div>
          )}

          {/* QUEUE MODE */}
          {uploadMode === 'queue' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto bg-accent-soft rounded-full flex items-center justify-center mb-4">
                  <span className="text-4xl">🔄</span>
                </div>
                <h3 className="text-xl font-semibold text-ink mb-2">
                  Profile Processing Queue
                </h3>
                <p className="text-ink-muted">
                  Monitor the status of bulk uploads and processing jobs
                </p>
              </div>
              
              {/* Embed the Profile Intake Queue Component */}
              <ProfileIntakeQueue currentUser={user} />
            </div>
          )}

          {/* Uploading status */}
          {uploading && (
            <div className="mt-6 text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                <span className="text-blue-600 font-medium">
                  Processing files with /tmp labs AI...
                </span>
              </div>
            </div>
          )}

          {/* Tech Attribution */}
          <div className="mt-6 pt-4 border-t border-line">
            <div className="flex items-center justify-center space-x-2 text-sm text-ink-subtle">
              <div className="w-4 h-4 bg-green-400 rounded-full animate-pulse" />
              <span>
                AI-powered document processing active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <div className="bg-elevated rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-ink mb-4">Upload Results</h3>
          <div className="space-y-3">
            {uploadResults.map((result, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  result.status === 'success'
                    ? 'bg-green-500/10 border-green-500/25'
                    : 'bg-red-500/10 border-red-500/25'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`text-2xl ${
                      result.status === 'success'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {result.status === 'success' ? '✅' : '❌'}
                  </div>
                  <div>
                    <div className="font-medium">{result.filename}</div>
                    {result.message && (
                      <div className="text-sm text-ink-muted">
                        {result.message}
                      </div>
                    )}
                    {result.job_id && (
                      <div className="text-xs text-blue-600">
                        Job ID: {result.job_id}
                      </div>
                    )}
                    {result.status === 'success' &&
                      (result.extracted_skills || []).length > 0 && (
                        <div className="text-sm text-ink-muted">
                          Skills:{' '}
                          {(result.extracted_skills || []).slice(0, 3).join(', ')}
                          {(result.extracted_skills || []).length > 3 &&
                            ` +${(result.extracted_skills || []).length - 3} more`}
                        </div>
                      )}
                    {result.status === 'error' && (
                      <div className="text-sm text-red-600">
                        {result.error}
                      </div>
                    )}
                  </div>
                </div>
                {result.status === 'success' && (
                  <div className="text-xs text-blue-600 bg-accent-soft px-2 py-1 rounded">
                    AI Processed
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Post-Upload Forward Pull - NBA for next action */}
          {uploadResults.filter(r => r.status === 'success').length > 0 && (
            <div className="mt-6 pt-6 border-t border-line">
              <NextBestAction
                action={`${uploadResults.filter(r => r.status === 'success').length} profile${uploadResults.filter(r => r.status === 'success').length > 1 ? 's' : ''} ready for matching`}
                description={activeJD ? `Run AutoMatch against "${activeJD.title || 'Active JD'}"` : "Search candidates or select a JD to run AutoMatch"}
                buttonText={activeJD ? "Run AutoMatch" : "Search Candidates"}
                onClick={() => {
                  if (activeJD) {
                    // Run AutoMatch on active JD
                    handleAutoMatch(activeJD.id);
                    setActiveView('jd-builder');
                  } else {
                    setActiveView('search');
                  }
                }}
                secondaryAction={activeJD ? "View Profile Database" : "Select a JD First"}
                onSecondaryClick={() => {
                  if (activeJD) {
                    setActiveView('profiles');
                  } else {
                    setActiveView('jd-builder');
                  }
                }}
                icon="sparkles"
                variant="success"
                why="New profiles are indexed and ready for AI-powered matching"
              />
            </div>
          )}
        </div>
      )}

      {/* Bulk Jobs Status */}
      {(uploadMode === 'bulk' || uploadMode === 'zip') &&
        bulkJobs.length > 0 && (
          <div className="bg-elevated rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-ink mb-4">
              Recent Bulk Upload Jobs
            </h3>
            <div className="space-y-3">
              {bulkJobs.slice(0, 5).map((job, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        job.status === 'completed'
                          ? 'bg-green-400'
                          : job.status === 'processing'
                          ? 'bg-blue-400 animate-pulse'
                          : job.status === 'failed'
                          ? 'bg-red-400'
                          : 'bg-yellow-400'
                      }`}
                    />
                    <div>
                      <div className="font-medium">{job.job_name}</div>
                      <div className="text-sm text-ink-muted">
                        {job.successful_files}/{job.total_files} files processed
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-sm font-medium capitalize ${
                        job.status === 'completed'
                          ? 'text-green-600'
                          : job.status === 'failed'
                          ? 'text-red-600'
                          : 'text-yellow-600'
                      }`}
                    >
                      {job.status}
                    </div>
                    <div className="text-xs text-ink-subtle">
                      {new Date(job.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Queue Status */}
      {(uploadMode === 'bulk' || uploadMode === 'zip') && queueStatus && (
        <div className="bg-elevated rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-ink mb-4">
            Processing Queue Status
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-accent-soft rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {queueStatus.global_stats?.pending_files || 0}
              </div>
              <div className="text-sm text-blue-400">Files Pending</div>
            </div>
            <div className="p-4 bg-green-500/10 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {queueStatus.global_stats?.processing_files || 0}
              </div>
              <div className="text-sm text-green-400">Files Processing</div>
            </div>
            <div className="p-4 bg-accent-soft rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {queueStatus.processing_capacity?.available_slots || 0}
              </div>
              <div className="text-sm text-blue-400">Available Slots</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Profile Intake Queue Component
const ProfileIntakeQueue = ({ currentUser }) => {
  const [activeSubTab, setActiveSubTab] = useState('pending');
  const [jobs, setJobs] = useState([]);
  const [queueStatus, setQueueStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const subTabs = [
    { id: 'pending', name: 'Pending', icon: '⏳', desc: 'Jobs waiting for processing' },
    { id: 'processed', name: 'Processed', icon: '✅', desc: 'Completed processing jobs' },
    { id: 'failed', name: 'Failed', icon: '❌', desc: 'Jobs that failed processing' }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch bulk jobs
      const jobsResponse = await fetch(`${BACKEND_URL}/api/upload/bulk/jobs?status=${activeSubTab === 'pending' ? 'pending' : activeSubTab === 'processed' ? 'completed' : 'failed'}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (jobsResponse.ok) {
        const jobsData = await jobsResponse.json();
        setJobs(jobsData);
      }

      // Fetch queue status
      const queueResponse = await fetch(`${BACKEND_URL}/api/upload/queue/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (queueResponse.ok) {
        const queueData = await queueResponse.json();
        setQueueStatus(queueData);
      }

      setError('');
    } catch (err) {
      setError('Failed to fetch intake queue data');
      console.error('Intake queue error:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  // Auto-refresh every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [activeSubTab]);

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400 bg-green-500/15 border border-green-500/25';
      case 'processing': return 'text-blue-400 bg-blue-500/15 border border-blue-500/25';
      case 'failed': return 'text-red-400 bg-red-500/15 border border-red-500/25';
      case 'cancelled': return 'text-ink-muted bg-subtle border border-line';
      default: return 'text-amber-400 bg-amber-500/15 border border-amber-500/25';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="arise-panel overflow-hidden">
        <div className="bg-muted border-b border-line p-6">
          <div className="flex items-center space-x-3 mb-4">
            <span className="w-10 h-10 rounded-lg bg-accent-soft text-accent-ink flex items-center justify-center shrink-0"><Icon name="clock" className="w-5 h-5" /></span>
            <div>
              <h2 className="text-xl font-bold text-ink tracking-tight">Profile Intake Queue</h2>
              <p className="text-sm text-ink-subtle">Monitor bulk upload processing status and queue performance</p>
            </div>
          </div>

          {/* Sub Navigation */}
          <div className="flex space-x-1 bg-subtle p-1 rounded-lg">
            {subTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  activeSubTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-ink-muted hover:text-ink hover:bg-muted'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>{tab.icon}</span>
                  <span>{tab.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Queue Overview */}
        <div className="p-6 border-b border-line">
          {queueStatus ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-4 bg-muted border border-line-subtle rounded-xl">
                <div className="text-2xl font-bold text-blue-400">{queueStatus.global_stats?.pending_files || 0}</div>
                <div className="text-sm text-ink-subtle">Pending Files</div>
              </div>
              <div className="text-center p-4 bg-muted border border-line-subtle rounded-xl">
                <div className="text-2xl font-bold text-green-400">{queueStatus.global_stats?.processing_files || 0}</div>
                <div className="text-sm text-ink-subtle">Processing Files</div>
              </div>
              <div className="text-center p-4 bg-muted border border-line-subtle rounded-xl">
                <div className="text-2xl font-bold text-blue-400">{queueStatus.processing_capacity?.available_slots || 0}</div>
                <div className="text-sm text-ink-subtle">Available Slots</div>
              </div>
              <div className="text-center p-4 bg-muted border border-line-subtle rounded-xl">
                <div className="text-2xl font-bold text-ink">{queueStatus.global_stats?.total_jobs || 0}</div>
                <div className="text-sm text-ink-subtle">Total Jobs</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-ink-subtle">Loading queue status...</div>
          )}
        </div>
      </div>

      {/* Jobs List */}
      <div className="arise-panel p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-ink">
            {activeSubTab === 'pending' ? 'Pending Jobs' : 
             activeSubTab === 'processed' ? 'Processed Jobs' : 'Failed Jobs'}
          </h3>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 text-sm bg-subtle hover:bg-subtle rounded-lg"
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            <span>Refresh</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-4 mb-6">
            <div className="text-red-400">{error}</div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job, index) => (
              <div key={index} className="bg-surface border border-line rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      job.status === 'completed' ? 'bg-green-400' :
                      job.status === 'processing' ? 'bg-blue-400 animate-pulse' :
                      job.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'
                    }`}></div>
                    <h4 className="font-semibold text-ink">{job.job_name}</h4>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getJobStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                    <span className="text-sm text-ink-subtle">
                      {new Date(job.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-ink-subtle">Total Files</div>
                    <div className="text-lg font-semibold">{job.total_files || 0}</div>
                  </div>
                  <div>
                    <div className="text-sm text-ink-subtle">Processed</div>
                    <div className="text-lg font-semibold text-green-600">{job.successful_files || 0}</div>
                  </div>
                  <div>
                    <div className="text-sm text-ink-subtle">Failed</div>
                    <div className="text-lg font-semibold text-red-600">{job.failed_files || 0}</div>
                  </div>
                  <div>
                    <div className="text-sm text-ink-subtle">Progress</div>
                    <div className="text-lg font-semibold">{job.progress_percentage || 0}%</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-subtle rounded-full h-2 mb-4">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      job.status === 'completed' ? 'bg-green-500' :
                      job.status === 'processing' ? 'bg-blue-500' :
                      job.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${job.progress_percentage || 0}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between text-sm text-ink-muted">
                  <span>Upload Method: <span className="capitalize">{job.upload_method?.replace('_', ' ') || 'N/A'}</span></span>
                  <span>Created by: {job.created_by_name || 'Unknown'}</span>
                  {job.error_message && (
                    <span className="text-red-600">Error: {job.error_message}</span>
                  )}
                </div>

                {job.status === 'processing' && (
                  <div className="mt-4 flex justify-end">
                    <button className="px-4 py-2 text-sm bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg">
                      Cancel Job
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-subtle rounded-full flex items-center justify-center">
              <span className="text-4xl text-ink-subtle">
                {activeSubTab === 'pending' ? '⏳' : activeSubTab === 'processed' ? '✅' : '❌'}
              </span>
            </div>
            <h3 className="text-lg font-medium text-ink mb-2">
              No {activeSubTab} jobs found
            </h3>
            <p className="text-ink-subtle">
              {activeSubTab === 'pending' ? 'No jobs are currently waiting for processing.' :
               activeSubTab === 'processed' ? 'No jobs have been completed yet.' :
               'No jobs have failed processing.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Database Component - With Pagination
const ProfileDatabase = ({
  activeJD = null,
  setActiveView = () => {},
  stats = {},
  handleAutoMatch = () => {},
  addToShortlist = () => {},
  isInShortlist = () => false,
}) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [hasMoreProfiles, setHasMoreProfiles] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [sortBy, setSortBy] = useState('uploaded_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all'); // Liquid Design: Status filter
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // bulk selection
  const [cvProfile, setCvProfile] = useState(null); // View CV modal
  const [openMenuId, setOpenMenuId] = useState(null); // row "More" menu
  const [error, setError] = useState(null); // load error/retry

  const PROFILES_PER_PAGE = 25; // Smaller page size for better UX

  useEffect(() => {
    fetchProfiles(1, true); // Initial load - reset profiles
  }, [sortBy, sortOrder]);

  const fetchProfiles = async (page = 1, resetProfiles = false) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const offset = (page - 1) * PROFILES_PER_PAGE;
      const params = new URLSearchParams({
        limit: PROFILES_PER_PAGE,
        offset: offset,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      const response = await axios.get(`${API}/profiles?${params}`);
      
      // Handle new API response format with pagination metadata
      const profilesData = response.data.profiles || response.data; // Fallback for backward compatibility
      const paginationData = response.data.pagination || {};
      
      if (resetProfiles) {
        setProfiles(profilesData);
      } else {
        setProfiles(prevProfiles => [...prevProfiles, ...profilesData]);
      }
      
      // Use pagination metadata if available, otherwise fall back to old logic
      if (paginationData.total !== undefined) {
        setTotalProfiles(paginationData.total);
        setHasMoreProfiles(paginationData.has_more);
      } else {
        setHasMoreProfiles(profilesData.length === PROFILES_PER_PAGE);
      }
      setCurrentPage(page);
      
      // Fallback: Get total count from stats if pagination data not available and this is first load
      if (page === 1 && paginationData.total === undefined) {
        try {
          const statsResponse = await axios.get(`${API}/stats`);
          setTotalProfiles(statsResponse.data.total_profiles);
        } catch (error) {
          console.log('Could not fetch total profile count');
        }
      }
      
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
      setError("We couldn't load candidate profiles. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadNextPage = () => {
    if (!loadingMore && hasMoreProfiles) {
      fetchProfiles(currentPage + 1, false);
    }
  };

  const refreshProfiles = () => {
    fetchProfiles(1, true);
  };

  if (loading) {
    return (
      <div className="bg-elevated border border-line rounded-xl shadow-sm p-10 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-line border-t-blue-600 mx-auto"></div>
        <p className="mt-4 text-ink-muted">Loading candidate profiles…</p>
      </div>
    );
  }

  if (error && profiles.length === 0) {
    return (
      <div className="bg-elevated border border-line rounded-xl shadow-sm p-10 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/15 flex items-center justify-center">
          <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-ink mb-1">Couldn't load profiles</h3>
        <p className="text-sm text-ink-subtle mb-5 max-w-sm mx-auto">{error}</p>
        <button
          onClick={refreshProfiles}
          className="px-5 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Liquid Design: Filter profiles by status
  const filteredProfiles = profiles.filter(profile => {
    if (statusFilter === 'all') return true;
    const isNew = profile.created_at && (Date.now() - new Date(profile.created_at).getTime()) < 24 * 60 * 60 * 1000;
    const isMatched = profile.match_count > 0 || profile.is_matched;
    const isShortlisted = profile.is_shortlisted || profile.shortlist_count > 0;

    if (statusFilter === 'new') return isNew && !isMatched && !isShortlisted;
    if (statusFilter === 'matched') return isMatched && !isShortlisted;
    if (statusFilter === 'shortlisted') return isShortlisted;
    return true;
  });

  // Bulk selection helpers
  const allVisibleSelected = filteredProfiles.length > 0 && filteredProfiles.every(p => selectedIds.has(p.id));
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds(allVisibleSelected ? new Set() : new Set(filteredProfiles.map(p => p.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const shortlistSelected = () => {
    filteredProfiles.filter(p => selectedIds.has(p.id)).forEach(p => addToShortlist(p));
    clearSelection();
  };
  const exportSelected = () => {
    const rows = filteredProfiles.filter(p => selectedIds.has(p.id));
    if (rows.length === 0) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = ['Resume,Profile ID,Inferred Role,Skills,Uploaded By,Upload Date']
      .concat(rows.map(p => [
        p.filename, p.id, p.inferred_role || p.predicted_role || p.role || '',
        (p.extracted_skills || []).join('; '), p.uploaded_by_name || '',
        p.created_at ? new Date(p.created_at).toLocaleDateString() : ''
      ].map(esc).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'talent-pool-selection.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const roleOf = (p) => p.inferred_role || p.predicted_role || p.role || null;

  return (
    <div className="space-y-6">
      {/* Active JD Context - Liquid Design */}
      {activeJD && (
        <ActiveJDContext
          jd={activeJD}
          stats={{
            profiles: totalProfiles || stats?.total_profiles || 0,
            matched: activeJD.matchCount || 0,
            shortlisted: activeJD.shortlistCount || 0,
          }}
          onViewMatches={() => setActiveView('search')}
          onChangeJD={() => setActiveView('jd-builder')}
          onRunAutoMatch={() => handleAutoMatch(activeJD.id)}
          compact={false}
        />
      )}

      {/* No Active JD Banner */}
      {!activeJD && profiles.length > 0 && (
        <div className="bg-accent-soft border border-blue-500/25 rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1 min-w-[220px]">
              <h4 className="font-semibold text-ink">Ready to Match</h4>
              <p className="text-sm text-ink-muted">Select a JD to run AutoMatch against {totalProfiles || 0} profiles.</p>
            </div>
            <button
              onClick={() => setActiveView('jd-builder')}
              className="px-4 h-9 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Select JD
            </button>
          </div>
        </div>
      )}

      <div className="bg-elevated rounded-xl shadow-lg p-8">
        {/* Header with stats, sorting, and refresh button */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-ink">👥 Profile Database</h2>
            <p className="text-sm text-ink-subtle mt-1">
              Showing {filteredProfiles.length} of {totalProfiles || 'unknown'} profiles
              {statusFilter !== 'all' && ` (filtered: ${statusFilter})`}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Sort Controls */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-ink-muted">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 bg-surface text-ink border border-line rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="uploaded_at">Upload Date</option>
                <option value="filename">Filename</option>
                <option value="skills_count">Skills Count</option>
                <option value="experience_years">Experience</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-3 py-1 bg-surface text-ink border border-line rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <button
              onClick={refreshProfiles}
              className="px-4 py-2 bg-accent-soft hover:bg-blue-500/25 text-blue-400 rounded-lg text-sm font-medium transition-colors"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Status Filter Tabs - Liquid Design */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-line">
          <span className="text-sm text-ink-subtle mr-2">Filter:</span>
          {[
            { id: 'all', label: 'All', count: profiles.length },
            { id: 'new', label: 'New', count: profiles.filter(p => {
              const isNew = p.created_at && (Date.now() - new Date(p.created_at).getTime()) < 24 * 60 * 60 * 1000;
              return isNew && !p.is_matched && !p.is_shortlisted && !p.match_count && !p.shortlist_count;
            }).length, color: 'amber' },
            { id: 'matched', label: 'Matched', count: profiles.filter(p => (p.match_count > 0 || p.is_matched) && !p.is_shortlisted && !p.shortlist_count).length, color: 'blue' },
            { id: 'shortlisted', label: 'Shortlisted', count: profiles.filter(p => p.is_shortlisted || p.shortlist_count > 0).length, color: 'green' },
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setStatusFilter(filter.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                statusFilter === filter.id
                  ? filter.color === 'amber' ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
                  : filter.color === 'blue' ? 'bg-blue-500/15 text-blue-400 border-blue-500/40'
                  : filter.color === 'green' ? 'bg-green-500/15 text-green-400 border-green-500/40'
                  : 'bg-blue-600 text-white border-blue-600'
                  : 'bg-subtle text-ink-muted border-transparent hover:bg-muted hover:text-ink'
              }`}
            >
              {filter.label}
              <span className="ml-1.5 text-xs opacity-75">({filter.count})</span>
            </button>
          ))}
        </div>

        {profiles.length === 0 ? (
          <EmptyState
            type="no-data"
            title="No profiles uploaded yet"
            reason="Upload CV/Resume files to build your talent database"
            action="Upload Profiles"
            onAction={() => setActiveView('upload')}
            icon="profiles"
            preview="Profiles will appear here with skills, experience, and quick actions for evaluation"
            nbaVariant="primary"
          />
        ) : filteredProfiles.length === 0 ? (
          <EmptyState
            type="filtered-out"
            title={`No ${statusFilter} profiles`}
            reason={statusFilter === 'new' ? "No profiles uploaded in the last 24 hours" :
                   statusFilter === 'matched' ? "No profiles have been matched yet. Run AutoMatch on a JD." :
                   "No profiles have been shortlisted yet. Review matched candidates."}
            action={statusFilter === 'matched' ? "Run AutoMatch" : "View All Profiles"}
            onAction={() => statusFilter === 'matched' && activeJD ? handleAutoMatch(activeJD.id) : setStatusFilter('all')}
            icon="profiles"
            compact={true}
          />
        ) : (
          <>
            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-xl bg-accent-soft border border-blue-500/25">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-ink">{selectedIds.size} selected</span>
                  <button onClick={clearSelection} className="text-xs text-ink-subtle hover:text-ink underline">Clear</button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setActiveView('search')} className="px-3 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">Match to JD</button>
                  <button onClick={shortlistSelected} className="px-3 h-9 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors">Shortlist</button>
                  <button onClick={exportSelected} className="px-3 h-9 rounded-lg bg-transparent border border-line text-ink-muted hover:bg-muted hover:text-ink text-sm font-medium transition-colors">Export</button>
                </div>
              </div>
            )}

            {/* Profile Table */}
          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="min-w-full divide-y divide-line">
              <thead className="bg-subtle">
                <tr>
                  <th className="w-10 px-4 py-3 text-left">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all profiles" className="w-4 h-4 accent-blue-600 align-middle cursor-pointer" />
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Candidate / Resume</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Skills</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Experience / Role</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Source / Uploaded</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold text-ink-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filteredProfiles.map((profile) => {
                  const isNew = profile.created_at && (Date.now() - new Date(profile.created_at).getTime()) < 24 * 60 * 60 * 1000;
                  const isMatched = profile.match_count > 0 || profile.is_matched;
                  const isShortlisted = profile.is_shortlisted || profile.shortlist_count > 0;
                  const selected = selectedIds.has(profile.id);
                  const skills = profile.extracted_skills || [];
                  const role = roleOf(profile);
                  const expText = (profile.extracted_experience || []).join(' · ');
                  const alreadyShortlisted = isShortlisted || isInShortlist(profile.id);
                  const accent = isShortlisted ? 'border-l-green-500' : isMatched ? 'border-l-blue-500' : isNew ? 'border-l-amber-400' : 'border-l-transparent';

                  return (
                    <tr key={profile.id} className={`border-l-2 ${accent} ${selected ? 'bg-accent-soft' : 'hover:bg-muted'} transition-colors`}>
                      {/* Select */}
                      <td className="px-4 py-4 align-top">
                        <input type="checkbox" checked={selected} onChange={() => toggleSelect(profile.id)} aria-label={`Select ${profile.filename}`} className="w-4 h-4 accent-blue-600 mt-1 cursor-pointer" />
                      </td>
                      {/* Status */}
                      <td className="px-4 py-4 align-top whitespace-nowrap">
                        {isShortlisted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/25">★ Shortlisted</span>
                        ) : isMatched ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/25">✓ Matched</span>
                        ) : isNew ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/25">✦ New</span>
                        ) : (
                          <span className="text-xs text-ink-subtle">—</span>
                        )}
                      </td>
                      {/* Candidate / Resume */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-subtle text-ink-subtle flex items-center justify-center shrink-0">
                            <Icon name="requirements" className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-ink truncate max-w-[220px]" title={profile.filename}>{profile.filename}</div>
                            <div className="text-xs text-ink-subtle mt-0.5">ID {profile.id.slice(0, 8)}</div>
                          </div>
                        </div>
                      </td>
                      {/* Skills */}
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-wrap gap-1 max-w-[240px]">
                          {skills.slice(0, 4).map((skill, index) => (
                            <span key={index} className="px-2 py-0.5 rounded-md text-xs bg-subtle text-ink-muted border border-line-subtle">{skill}</span>
                          ))}
                          {skills.length > 4 && (
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-soft text-blue-400">+{skills.length - 4} more</span>
                          )}
                          {skills.length === 0 && <span className="text-xs text-ink-subtle">No skills extracted</span>}
                        </div>
                      </td>
                      {/* Experience / Role */}
                      <td className="px-4 py-4 align-top">
                        <div className="max-w-[240px]">
                          {role && <div className="text-sm font-medium text-ink truncate" title={role}>{role}</div>}
                          <div className={`text-xs text-ink-subtle line-clamp-2 ${role ? 'mt-0.5' : ''}`}>{expText || 'Experience not specified'}</div>
                        </div>
                      </td>
                      {/* Source / Uploaded */}
                      <td className="px-4 py-4 align-top">
                        <div className="text-sm text-ink-muted truncate max-w-[160px]" title={profile.uploaded_by_name || 'Direct upload'}>{profile.uploaded_by_name || 'Direct upload'}</div>
                        <div className="text-xs text-ink-subtle mt-0.5">{profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}</div>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-4 align-top text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setCvProfile(profile)} className="px-2.5 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors">View CV</button>
                          <button onClick={() => setActiveView('search')} className="px-2.5 h-8 rounded-lg bg-transparent border border-line text-ink-muted hover:bg-muted hover:text-ink text-xs font-medium transition-colors">Match</button>
                          {!alreadyShortlisted ? (
                            <button onClick={() => addToShortlist(profile)} className="px-2.5 h-8 rounded-lg bg-green-500/15 text-green-400 hover:bg-green-500/25 border border-green-500/25 text-xs font-medium transition-colors">Shortlist</button>
                          ) : (
                            <span className="px-2.5 h-8 inline-flex items-center rounded-lg bg-subtle text-ink-subtle text-xs font-medium">Shortlisted</span>
                          )}
                          <div className="relative">
                            <button onClick={() => setOpenMenuId(openMenuId === profile.id ? null : profile.id)} aria-label="More actions" className="w-8 h-8 rounded-lg bg-transparent border border-line text-ink-muted hover:bg-muted hover:text-ink flex items-center justify-center transition-colors">⋯</button>
                            {openMenuId === profile.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-elevated border border-line rounded-lg shadow-lg z-20 py-1 text-left">
                                <button onClick={() => { setCvProfile(profile); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm text-ink-muted hover:bg-muted hover:text-ink">View CV</button>
                                <button onClick={() => { setSelectedProfile(profile); setShowScheduleModal(true); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm text-ink-muted hover:bg-muted hover:text-ink">Schedule Interview</button>
                                <button onClick={() => { setActiveView('search'); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm text-ink-muted hover:bg-muted hover:text-ink">Match to JD</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-line">
            <div className="text-sm text-ink-subtle">
              Page {currentPage} • {profiles.length} profiles loaded
            </div>
            
            <div className="flex items-center space-x-3">
              {hasMoreProfiles && (
                <button
                  onClick={loadNextPage}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle disabled:text-ink-subtle text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <span>Load More</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              )}
              
              {!hasMoreProfiles && profiles.length > 0 && (
                <span className="text-sm text-ink-subtle italic">
                  ✅ All profiles loaded ({profiles.length} total)
                </span>
              )}
            </div>
          </div>
        </>
        )}
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleModal && selectedProfile && (
        <ScheduleInterviewModal
          profile={selectedProfile}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedProfile(null);
          }}
          onSuccess={() => {
            setShowScheduleModal(false);
            setSelectedProfile(null);
            // Optionally show success message
          }}
        />
      )}

      {/* Close row "More" menu on outside click */}
      {openMenuId && <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />}

      {/* CV Viewer Modal */}
      {cvProfile && (
        <CVViewerModal
          profile={cvProfile}
          isOpen={!!cvProfile}
          onClose={() => setCvProfile(null)}
        />
      )}
    </div>
  );
};

// Mapping from template keys to backend InterviewType enum values
const TEMPLATE_TO_INTERVIEW_TYPE = {
  basic_screening: 'phone_screening',
  technical_round_1: 'technical_interview',
  technical_round_2: 'technical_interview',
  technical_round_3: 'technical_interview',
  hr_round: 'hr_interview',
  final_round: 'final_interview',
  behavioral: 'behavioral_interview',
  panel: 'panel_interview',
  cultural_fit: 'cultural_fit'
};

// Email Templates for Interview Scheduling
const EMAIL_TEMPLATES = {
  basic_screening: {
    name: 'Basic Screening',
    icon: '📞',
    subject: 'Interview Invitation - Initial Screening for {{job_title}} at {{company_name}}',
    body: `Dear {{candidate_name}},

Thank you for your interest in the {{job_title}} position at {{company_name}}.

We were impressed with your profile and would like to invite you for an initial screening call to learn more about your background and discuss the opportunity.

📅 Date & Time: {{interview_datetime}}
⏱️ Duration: {{duration}} minutes
📍 Meeting Link: {{meeting_link}}

Please confirm your availability by replying to this email.

Best regards,
{{recruiter_name}}
{{company_name}}`
  },
  technical_round_1: {
    name: 'Technical Interview - Round 1',
    icon: '💻',
    subject: 'Technical Interview Round 1 - {{job_title}} at {{company_name}}',
    body: `Dear {{candidate_name}},

Congratulations on progressing to the Technical Interview Round 1 for the {{job_title}} position!

This round will focus on:
• Problem-solving and coding skills
• Technical concepts and fundamentals
• Your approach to technical challenges

📅 Date & Time: {{interview_datetime}}
⏱️ Duration: {{duration}} minutes
📍 Meeting Link: {{meeting_link}}
👤 Interviewer(s): {{interviewers}}

Preparation Tips:
- Review data structures and algorithms
- Be ready to code and explain your thought process
- Prepare questions about the role and team

Best of luck!

{{recruiter_name}}
{{company_name}}`
  },
  technical_round_2: {
    name: 'Technical Interview - Round 2',
    icon: '🔧',
    subject: 'Technical Interview Round 2 - {{job_title}} at {{company_name}}',
    body: `Dear {{candidate_name}},

Great news! You've been selected to proceed to Technical Interview Round 2 for the {{job_title}} position.

This round will dive deeper into:
• System design and architecture
• Technical depth in your area of expertise
• Real-world problem scenarios

📅 Date & Time: {{interview_datetime}}
⏱️ Duration: {{duration}} minutes
📍 Meeting Link: {{meeting_link}}
👤 Interviewer(s): {{interviewers}}

Looking forward to speaking with you!

{{recruiter_name}}
{{company_name}}`
  },
  technical_round_3: {
    name: 'Technical Interview - Round 3',
    icon: '🎯',
    subject: 'Final Technical Round - {{job_title}} at {{company_name}}',
    body: `Dear {{candidate_name}},

Excellent progress! You've reached the Final Technical Round for the {{job_title}} position.

This session will cover:
• Advanced technical discussions
• Team collaboration scenarios
• Project and leadership experience

📅 Date & Time: {{interview_datetime}}
⏱️ Duration: {{duration}} minutes
📍 Meeting Link: {{meeting_link}}
👤 Interviewer(s): {{interviewers}}

We're excited to learn more about your expertise!

{{recruiter_name}}
{{company_name}}`
  },
  hr_interview: {
    name: 'HR Interview',
    icon: '🤝',
    subject: 'HR Interview - {{job_title}} at {{company_name}}',
    body: `Dear {{candidate_name}},

You're almost there! We'd like to invite you for the HR Interview for the {{job_title}} position.

This conversation will focus on:
• Your career aspirations and goals
• Cultural fit and values alignment
• Compensation and benefits discussion
• Any questions you may have about the company

📅 Date & Time: {{interview_datetime}}
⏱️ Duration: {{duration}} minutes
📍 Meeting Link: {{meeting_link}}
👤 HR Representative: {{interviewers}}

We look forward to concluding this stage with you!

{{recruiter_name}}
{{company_name}}`
  }
};

// Schedule Interview Modal Component
const ScheduleInterviewModal = ({ profile, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    candidate_id: profile.id,
    candidate_name: profile.extracted_name || profile.filename.replace(/\.[^/.]+$/, ''),
    candidate_email: profile.extracted_email || '',
    job_title: '',
    interview_type: 'phone_screening',
    scheduled_datetime: '',
    duration_minutes: 60,
    provider: 'zoom',
    interviewer_emails: [''],
    template_id: 'basic_screening',
    custom_agenda: '',
    recording_enabled: false,
    // Email customization
    email_subject: EMAIL_TEMPLATES.basic_screening.subject,
    email_body: EMAIL_TEMPLATES.basic_screening.body,
    send_email: true
  });

  const handleInputChange = (field, value) => {
    setScheduleForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    const template = EMAIL_TEMPLATES[templateId];
    if (template) {
      setScheduleForm(prev => ({
        ...prev,
        template_id: templateId,
        interview_type: TEMPLATE_TO_INTERVIEW_TYPE[templateId] || 'phone_screening',
        email_subject: template.subject,
        email_body: template.body
      }));
    }
  };

  // Preview email with placeholders replaced
  const getPreviewEmail = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const replacements = {
      '{{candidate_name}}': scheduleForm.candidate_name || '[Candidate Name]',
      '{{job_title}}': scheduleForm.job_title || '[Job Title]',
      '{{company_name}}': 'Your Company', // Could be from tenant settings
      '{{interview_datetime}}': scheduleForm.scheduled_datetime
        ? new Date(scheduleForm.scheduled_datetime).toLocaleString()
        : '[Date & Time]',
      '{{duration}}': scheduleForm.duration_minutes.toString(),
      '{{meeting_link}}': '[Meeting link will be generated]',
      '{{interviewers}}': scheduleForm.interviewer_emails.filter(e => e).join(', ') || '[Interviewers]',
      '{{recruiter_name}}': user.full_name || '[Recruiter Name]'
    };

    let subject = scheduleForm.email_subject;
    let body = scheduleForm.email_body;

    Object.entries(replacements).forEach(([key, value]) => {
      subject = subject.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
      body = body.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return { subject, body };
  };

  const handleInterviewerEmailChange = (index, value) => {
    const newEmails = [...scheduleForm.interviewer_emails];
    newEmails[index] = value;
    setScheduleForm(prev => ({
      ...prev,
      interviewer_emails: newEmails
    }));
  };

  const addInterviewerEmail = () => {
    setScheduleForm(prev => ({
      ...prev,
      interviewer_emails: [...prev.interviewer_emails, '']
    }));
  };

  const removeInterviewerEmail = (index) => {
    const newEmails = scheduleForm.interviewer_emails.filter((_, i) => i !== index);
    setScheduleForm(prev => ({
      ...prev,
      interviewer_emails: newEmails.length > 0 ? newEmails : ['']
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Filter out empty emails
      const filteredEmails = scheduleForm.interviewer_emails.filter(email => email.trim() !== '');
      
      if (filteredEmails.length === 0) {
        setError('Please add at least one interviewer email');
        setLoading(false);
        return;
      }

      const submitData = {
        ...scheduleForm,
        interviewer_emails: filteredEmails
      };

      const response = await axios.post(`${API}/interviews/schedule`, submitData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });

      onSuccess();
      
      // Show success notification
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Interview scheduled successfully!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
      
    } catch (error) {
      console.error('Failed to schedule interview:', error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-elevated rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🗓️</span>
              <div>
                <h2 className="text-xl font-bold">Schedule Interview</h2>
                <p className="opacity-90">For: {profile.filename}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-elevated hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <span className="text-red-500 text-lg">⚠️</span>
                <span className="text-red-400">{error}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Candidate Name *
              </label>
              <input
                type="text"
                value={scheduleForm.candidate_name}
                onChange={(e) => handleInputChange('candidate_name', e.target.value)}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Candidate Email *
              </label>
              <input
                type="email"
                value={scheduleForm.candidate_email}
                onChange={(e) => handleInputChange('candidate_email', e.target.value)}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Job Title *
              </label>
              <input
                type="text"
                value={scheduleForm.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Software Engineer"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Interview Type & Email Template
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(EMAIL_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleTemplateChange(key)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      scheduleForm.template_id === key
                        ? 'border-blue-500 bg-accent-soft ring-2 ring-blue-200'
                        : 'border-line hover:border-blue-500/25 hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">{template.icon}</span>
                      <span className="text-sm font-medium text-ink">{template.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Date & Time *
              </label>
              <input
                type="datetime-local"
                value={scheduleForm.scheduled_datetime}
                onChange={(e) => handleInputChange('scheduled_datetime', e.target.value)}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min={new Date().toISOString().slice(0, 16)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Duration (minutes)
              </label>
              <select
                value={scheduleForm.duration_minutes}
                onChange={(e) => handleInputChange('duration_minutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Meeting Provider
              </label>
              <select
                value={scheduleForm.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="zoom">📹 Zoom</option>
                <option value="microsoft_teams">💼 Microsoft Teams</option>
                <option value="google_meet">🎥 Google Meet</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="recording"
                checked={scheduleForm.recording_enabled}
                onChange={(e) => handleInputChange('recording_enabled', e.target.checked)}
                className="h-4 w-4 text-blue-400 focus:ring-blue-500 border-line rounded"
              />
              <label htmlFor="recording" className="ml-2 text-sm text-ink-muted">
                Enable recording
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Interviewer Emails *
            </label>
            <div className="space-y-2">
              {scheduleForm.interviewer_emails.map((email, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleInterviewerEmailChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="interviewer@company.com"
                  />
                  {scheduleForm.interviewer_emails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInterviewerEmail(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-500/10 rounded-md"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addInterviewerEmail}
                className="px-3 py-2 text-blue-400 hover:bg-accent-soft rounded-md text-sm"
              >
                + Add Interviewer
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Custom Agenda (Optional)
            </label>
            <textarea
              value={scheduleForm.custom_agenda}
              onChange={(e) => handleInputChange('custom_agenda', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Interview agenda and topics to cover..."
            />
          </div>

          {/* Email Customization Section */}
          <div className="border-t border-line pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={scheduleForm.send_email}
                  onChange={(e) => handleInputChange('send_email', e.target.checked)}
                  className="h-4 w-4 text-blue-400 focus:ring-blue-500 border-line rounded"
                />
                <label htmlFor="send_email" className="text-sm font-medium text-ink">
                  📧 Send invitation email to candidate
                </label>
              </div>
              {scheduleForm.send_email && (
                <button
                  type="button"
                  onClick={() => setShowEmailPreview(!showEmailPreview)}
                  className="text-sm text-blue-400 hover:text-blue-400"
                >
                  {showEmailPreview ? 'Edit Template' : 'Preview Email'}
                </button>
              )}
            </div>

            {scheduleForm.send_email && (
              <div className="space-y-4 bg-muted rounded-lg p-4">
                {!showEmailPreview ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Email Subject
                      </label>
                      <input
                        type="text"
                        value={scheduleForm.email_subject}
                        onChange={(e) => handleInputChange('email_subject', e.target.value)}
                        className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-ink-subtle mt-1">
                        Use placeholders: {'{{candidate_name}}, {{job_title}}, {{company_name}}'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        Email Body
                      </label>
                      <textarea
                        value={scheduleForm.email_body}
                        onChange={(e) => handleInputChange('email_body', e.target.value)}
                        rows={10}
                        className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                      <p className="text-xs text-ink-subtle mt-1">
                        Available placeholders: {'{{candidate_name}}, {{job_title}}, {{company_name}}, {{interview_datetime}}, {{duration}}, {{meeting_link}}, {{interviewers}}, {{recruiter_name}}'}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="bg-elevated rounded-lg border border-line p-4">
                    <div className="border-b border-line pb-3 mb-3">
                      <div className="text-xs text-ink-subtle mb-1">To: {scheduleForm.candidate_email || '[Candidate Email]'}</div>
                      <div className="font-semibold text-ink">{getPreviewEmail().subject}</div>
                    </div>
                    <div className="text-sm text-ink-muted whitespace-pre-wrap font-mono">
                      {getPreviewEmail().body}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-line">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-ink-muted hover:bg-muted border border-line rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Scheduling...</span>
                </>
              ) : (
                <>
                  <span>🗓️</span>
                  <span>Schedule Interview</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Settings Page with Subtabs (Team, Hierarchy, Email Intake)
const SettingsPage = ({ currentUser, hasPermission, isFeatureEnabled }) => {
  const [activeSubTab, setActiveSubTab] = useState('team');

  // Define subtabs with permission and feature requirements
  const subTabs = [
    { id: 'team', name: 'Team', icon: '👥', desc: 'Manage users', permission: 'canManageUsers' },
    { id: 'hierarchy', name: 'Hierarchy', icon: '🏢', desc: 'Team structure & transfers', permission: 'canTransferProfiles' },
    { id: 'billing', name: 'Billing', icon: '💳', desc: 'Subscription & usage', permission: 'canManageTenant' },
    { id: 'email-intake', name: 'Email Intake', icon: '📬', desc: 'BYOD email configuration', permission: 'canManageTenant', feature: 'email_intake' },
    { id: 'admin', name: 'Admin', icon: '🔧', desc: 'Data management & utilities', permission: 'canManageTenant' }
  ];

  // Filter subtabs based on permissions AND feature flags
  const availableSubTabs = subTabs.filter(tab => {
    // Check permission
    if (tab.permission && !hasPermission(tab.permission)) {
      return false;
    }
    // Check feature flag if specified
    if (tab.feature && isFeatureEnabled && !isFeatureEnabled(tab.feature)) {
      return false;
    }
    return true;
  });

  // Set default tab to first available
  React.useEffect(() => {
    if (availableSubTabs.length > 0 && !availableSubTabs.find(tab => tab.id === activeSubTab)) {
      setActiveSubTab(availableSubTabs[0].id);
    }
  }, [availableSubTabs, activeSubTab]);

  return (
    <div className="space-y-6">
      {/* Page header — governed admin console */}
      <div className="bg-elevated rounded-xl border border-line shadow-sm p-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400">
            <Icon name="settings" className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ink">Settings</h2>
            <p className="text-sm text-ink-subtle">Team management, hierarchy, and configuration</p>
          </div>
        </div>

        {/* Segmented sub-tabs */}
        {availableSubTabs.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1 rounded-lg border border-line bg-muted p-1">
            {availableSubTabs.map((tab) => (
              <button
                key={tab.id}
                data-tab={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                title={tab.desc}
                className={`flex-1 min-w-[7rem] px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-surface text-ink border border-line shadow-sm'
                    : 'text-ink-muted hover:text-ink border border-transparent'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div>
        {/* Team Management Tab */}
        {activeSubTab === 'team' && hasPermission('canManageUsers') && (
          <UserManagement currentUser={currentUser} />
        )}

        {/* Hierarchy Tab */}
        {activeSubTab === 'hierarchy' && hasPermission('canTransferProfiles') && (
          <HierarchyManagement currentUser={currentUser} />
        )}

        {/* Billing Tab */}
        {activeSubTab === 'billing' && hasPermission('canManageTenant') && (
          <TenantBillingDashboard currentUser={currentUser} />
        )}

        {/* Email Intake Tab */}
        {activeSubTab === 'email-intake' && hasPermission('canManageTenant') && (
          <TenantEmailIntakeSettings currentUser={currentUser} />
        )}

        {/* Admin Utilities Tab */}
        {activeSubTab === 'admin' && hasPermission('canManageTenant') && (
          <AdminUtilities currentUser={currentUser} />
        )}

        {/* No access message */}
        {availableSubTabs.length === 0 && (
          <div className="bg-surface border border-line rounded-xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-subtle rounded-full flex items-center justify-center">
              <span className="text-3xl">🚫</span>
            </div>
            <h3 className="text-lg font-semibold text-ink mb-1">No Access to Settings</h3>
            <p className="text-ink-subtle">You don't have permission to access any settings features.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// User Management Component
const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'recruiter',
    department: '',
    tenant_id: ''
  });
  const [availableTenants, setAvailableTenants] = useState([]);

  // Support Access Consent State (for tenant admins)
  const [supportAccessStatus, setSupportAccessStatus] = useState(null);
  const [supportAccessLoading, setSupportAccessLoading] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({
    reason: '',
    duration_hours: 24,
    access_level: 'read_only'
  });

  useEffect(() => {
    fetchUsers();
    fetchTenants();
    // Fetch support access status for tenant admins
    if (currentUser?.role === 'admin' && currentUser?.tenant_id) {
      fetchSupportAccessStatus();
    }
  }, [currentUser]);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`);
      setAvailableTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  // Support Access Consent Functions (for tenant admins)
  const fetchSupportAccessStatus = async () => {
    if (!currentUser?.tenant_id) return;
    try {
      setSupportAccessLoading(true);
      const response = await axios.get(`${API}/tenants/${currentUser.tenant_id}/support-access/status`);
      setSupportAccessStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch support access status:', error);
      setSupportAccessStatus({ has_active_grant: false });
    } finally {
      setSupportAccessLoading(false);
    }
  };

  const grantSupportAccess = async () => {
    if (!currentUser?.tenant_id || !grantForm.reason) return;
    try {
      setSupportAccessLoading(true);
      await axios.post(`${API}/tenants/${currentUser.tenant_id}/support-access/grant`, grantForm);
      setShowGrantModal(false);
      setGrantForm({ reason: '', duration_hours: 24, access_level: 'read_only' });
      await fetchSupportAccessStatus();
    } catch (error) {
      console.error('Failed to grant support access:', error);
      alert(error.response?.data?.detail || 'Failed to grant support access');
    } finally {
      setSupportAccessLoading(false);
    }
  };

  const revokeSupportAccess = async () => {
    if (!currentUser?.tenant_id) return;
    if (!window.confirm('Are you sure you want to revoke support access? The platform team will no longer be able to access your data for troubleshooting.')) {
      return;
    }
    try {
      setSupportAccessLoading(true);
      await axios.post(`${API}/tenants/${currentUser.tenant_id}/support-access/revoke`);
      await fetchSupportAccessStatus();
    } catch (error) {
      console.error('Failed to revoke support access:', error);
      alert(error.response?.data?.detail || 'Failed to revoke support access');
    } finally {
      setSupportAccessLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // The API returns {users: [...], total: number}, so we need response.data.users
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]); // Set empty array on error to prevent map errors
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Auto-assign tenant_id for tenant admins
      const userToCreate = { ...newUser };
      if (currentUser.role === 'admin' && currentUser.tenant_id && !userToCreate.tenant_id) {
        userToCreate.tenant_id = currentUser.tenant_id;
      }
      
      // Validate tenant_id is set
      if (!userToCreate.tenant_id) {
        alert('Please select a tenant for the new user.');
        return;
      }
      
      // Use appropriate endpoint based on user role
      const endpoint = currentUser.role === 'superadmin' ? '/admin/users' : '/users';
      await axios.post(`${API}${endpoint}`, userToCreate);
      
      setNewUser({ email: '', password: '', full_name: '', role: 'recruiter', department: '', tenant_id: '' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/users/${userId}`, updates);
      fetchUsers();
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete ${userEmail}?`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    const newPassword = prompt(`Enter new password for ${userEmail}:`);
    if (newPassword) {
      try {
        await axios.post(`${API}/users/${userId}/reset-password`, { password: newPassword });
        alert('Password reset successfully');
      } catch (error) {
        console.error('Failed to reset password:', error);
        alert(error.response?.data?.detail || 'Failed to reset password');
      }
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return '👑';
      case 'manager': return '📋';
      case 'auditor': return '🔍';
      case 'recruiter': return '👤';
      case 'viewer': return '👁️';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'text-green-400 bg-green-500/15 border border-green-500/25';
      case 'inactive': return 'text-ink-muted bg-subtle border border-line';
      case 'suspended': return 'text-red-400 bg-red-500/15 border border-red-500/25';
      default: return 'text-ink-muted bg-subtle border border-line';
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-subtle rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-subtle rounded"></div>
            <div className="h-4 bg-subtle rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const labelCls = "block text-sm font-medium text-ink-muted mb-1";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-bold text-ink mb-1">User Management</h3>
          <p className="text-ink-muted text-sm">Manage users, roles, and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
        >
          {showCreateForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateUser} className="p-6 border border-line rounded-lg bg-muted">
            <h3 className="text-lg font-semibold text-ink mb-4">Create New User</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Full Name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  className={inputCls}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className={inputCls}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className={inputCls}
                  minLength="8"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className={labelCls}>Department</label>
                <input
                  type="text"
                  value={newUser.department}
                  onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                  className={inputCls}
                  placeholder="HR, IT, Sales..."
                />
              </div>
              <div>
                <label className={labelCls}>Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className={`${inputCls} dark:[color-scheme:dark]`}
                >
                  <option value="admin">👑 Business Owner (BO)</option>
                  <option value="manager">📋 TAM (Manager)</option>
                  <option value="recruiter">👤 TAC (Recruiter)</option>
                  <option value="auditor">🔍 Auditor</option>
                  <option value="viewer">👁️ Viewer</option>
                </select>
              </div>
              {currentUser?.role === 'superadmin' && (
                <div>
                  <label className={labelCls}>Tenant</label>
                  <select
                    value={newUser.tenant_id}
                    onChange={(e) => setNewUser({...newUser, tenant_id: e.target.value})}
                    className={`${inputCls} dark:[color-scheme:dark]`}
                    required
                  >
                    <option value="">Select Tenant...</option>
                    {availableTenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {currentUser?.role === 'admin' && (
                <div>
                  <label className={labelCls}>Tenant</label>
                  <input
                    type="text"
                    value={currentUser?.tenant_name || 'Current Tenant'}
                    disabled
                    className="w-full px-3 py-2 border border-line rounded-lg bg-subtle text-ink-subtle"
                  />
                  <p className="text-xs text-ink-subtle mt-1">Users will be created in your tenant</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex space-x-4">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Create User
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-muted hover:bg-subtle text-ink-muted hover:text-ink border border-line px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
          </div>
        </form>
      )}

      {/* Users Table */}
      <div className="mt-6 bg-surface border border-line rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr className="border-b border-line">
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">User</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">Role</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">Department</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">Last Login</th>
                <th className="text-left py-3 px-4 font-semibold text-ink-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted transition-colors">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-ink">{user.full_name}</div>
                      <div className="text-sm text-ink-subtle">{user.email}</div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-ink-muted">
                    <span className="inline-flex items-center space-x-1">
                      <span>{getRoleIcon(user.role)}</span>
                      <span className="capitalize">{user.role}</span>
                    </span>
                  </td>
                  <td className="py-4 px-4 text-ink-muted">
                    {user.department || '-'}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-ink-muted">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex space-x-3">
                      {editingUser === user.id ? (
                        <div className="flex space-x-2">
                          <select
                            onChange={(e) => handleUpdateUser(user.id, { role: e.target.value })}
                            className="text-xs px-2 py-1 bg-surface text-ink border border-line rounded dark:[color-scheme:dark]"
                            defaultValue={user.role}
                          >
                            <option value="admin">BO (Admin)</option>
                            <option value="manager">TAM (Manager)</option>
                            <option value="recruiter">TAC (Recruiter)</option>
                            <option value="auditor">Auditor</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="text-xs bg-muted hover:bg-subtle text-ink-muted hover:text-ink border border-line px-2 py-1 rounded"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingUser(user.id)}
                            className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleResetPassword(user.id, user.email)}
                            className="text-ink-muted hover:text-ink text-sm font-medium"
                          >
                            Reset Password
                          </button>
                          {user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="text-red-400 hover:text-red-300 text-sm font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Support Access Consent Section - Only for tenant admins */}
      {currentUser?.role === 'admin' && (
        <div className="mt-8 pt-6 border-t border-line">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold text-ink flex items-center gap-2">
                <span>🔐</span>
                Support Access Consent
              </h3>
              <p className="text-ink-muted mt-1">
                Grant temporary access to the platform support team for troubleshooting
              </p>
            </div>
            {supportAccessLoading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            )}
          </div>

          {supportAccessStatus?.has_active_grant ? (
            <div className="bg-green-500/10 border border-green-500/25 rounded-lg p-6">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-400 text-xl">✓</span>
                    <h4 className="font-semibold text-green-400">Support Access Granted</h4>
                  </div>
                  <p className="text-sm text-ink-muted mb-2">
                    The platform support team currently has access to your data for troubleshooting.
                  </p>
                  {supportAccessStatus.active_grant && (
                    <div className="text-xs text-ink-muted space-y-1">
                      <p><strong className="text-ink">Reason:</strong> {supportAccessStatus.active_grant.reason}</p>
                      <p><strong className="text-ink">Access Level:</strong> {supportAccessStatus.active_grant.access_level}</p>
                      <p><strong className="text-ink">Granted:</strong> {new Date(supportAccessStatus.active_grant.granted_at).toLocaleString()}</p>
                      <p><strong className="text-ink">Expires:</strong> {new Date(supportAccessStatus.active_grant.expires_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={revokeSupportAccess}
                  disabled={supportAccessLoading}
                  className="px-4 py-2 bg-red-500/15 text-red-400 border border-red-500/25 rounded-lg hover:bg-red-500/25 font-medium text-sm disabled:opacity-50"
                >
                  Revoke Access
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-muted border border-line rounded-lg p-6">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-ink-subtle text-xl">🔒</span>
                    <h4 className="font-semibold text-ink">No Active Support Access</h4>
                  </div>
                  <p className="text-sm text-ink-muted">
                    The platform support team does not have access to your data.
                    Grant access if you need help troubleshooting an issue.
                  </p>
                </div>
                <button
                  onClick={() => setShowGrantModal(true)}
                  disabled={supportAccessLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
                >
                  Grant Support Access
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-ink-subtle">
            <p>
              <strong className="text-ink-muted">Privacy Notice:</strong> Support access allows the platform team to view your profile data
              for troubleshooting purposes only. All access is logged and auditable.
              You can revoke access at any time.
            </p>
          </div>
        </div>
      )}

      {/* Grant Support Access Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-elevated border border-line rounded-xl shadow-2xl p-8 max-w-md w-full">
            <h3 className="text-xl font-bold text-ink mb-4">Grant Support Access</h3>
            <p className="text-ink-muted mb-6 text-sm">
              This will allow the platform support team to access your tenant data for troubleshooting.
              All access will be logged and auditable.
            </p>

            <div className="space-y-4">
              <div>
                <label className={labelCls}>
                  Reason for Access <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={grantForm.reason}
                  onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                  className={inputCls}
                  rows="3"
                  placeholder="e.g., Support ticket #12345 - Investigation of profile upload issue"
                  minLength="10"
                  maxLength="500"
                  required
                />
                <p className="text-xs text-ink-subtle mt-1">Minimum 10 characters</p>
              </div>

              <div>
                <label className={labelCls}>
                  Access Duration
                </label>
                <select
                  value={grantForm.duration_hours}
                  onChange={(e) => setGrantForm({ ...grantForm, duration_hours: parseInt(e.target.value) })}
                  className={`${inputCls} dark:[color-scheme:dark]`}
                >
                  <option value="4">4 hours</option>
                  <option value="8">8 hours</option>
                  <option value="24">24 hours (1 day)</option>
                  <option value="48">48 hours (2 days)</option>
                  <option value="72">72 hours (3 days)</option>
                  <option value="168">168 hours (1 week)</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>
                  Access Level
                </label>
                <select
                  value={grantForm.access_level}
                  onChange={(e) => setGrantForm({ ...grantForm, access_level: e.target.value })}
                  className={`${inputCls} dark:[color-scheme:dark]`}
                >
                  <option value="read_only">Read Only (Recommended)</option>
                  <option value="full">Full Access (Includes modifications)</option>
                </select>
                <p className="text-xs text-ink-subtle mt-1">
                  Read-only is recommended unless data modifications are needed.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGrantModal(false);
                  setGrantForm({ reason: '', duration_hours: 24, access_level: 'read_only' });
                }}
                className="px-4 py-2 border border-line bg-muted text-ink-muted rounded-lg hover:bg-subtle hover:text-ink"
              >
                Cancel
              </button>
              <button
                onClick={grantSupportAccess}
                disabled={supportAccessLoading || grantForm.reason.length < 10}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {supportAccessLoading ? 'Granting...' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// AI INTELLIGENCE DASHBOARD - AutoMatch & Identity Resolution Showcase
// ============================================================================
const AIIntelligenceDashboard = ({ currentUser, hasPermission, isFeatureEnabled }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [automatchStats, setAutomatchStats] = useState(null);
  const [identityStats, setIdentityStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [jobDescriptions, setJobDescriptions] = useState([]);

  const subTabs = [
    { id: 'overview', name: 'AI Overview', icon: '🧠', desc: 'Platform AI capabilities overview' },
    { id: 'automatch', name: 'AutoMatch Engine', icon: '🎯', desc: 'Proprietary candidate-JD matching', feature: 'automatch' },
    { id: 'identity', name: 'Identity Resolution', icon: '🔍', desc: 'Duplicate detection & deduplication', feature: 'identity_resolution' },
    { id: 'algorithms', name: 'Algorithm Insights', icon: '⚡', desc: 'How our AI works' }
  ];

  // Filter subtabs based on feature flags
  const availableSubTabs = subTabs.filter(tab => {
    if (tab.feature && isFeatureEnabled && !isFeatureEnabled(tab.feature)) {
      return false;
    }
    return true;
  });

  // Reset to first available tab if current tab is no longer available
  useEffect(() => {
    if (availableSubTabs.length > 0 && !availableSubTabs.find(tab => tab.id === activeSubTab)) {
      setActiveSubTab(availableSubTabs[0].id);
    }
  }, [availableSubTabs, activeSubTab]);

  useEffect(() => {
    fetchAIStats();
  }, []);

  const fetchAIStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch AutoMatch stats, Identity stats, JDs, and profile analytics in parallel
      const [automatchRes, identityRes, jdRes, profilesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/automatch/stats`, { headers }).catch(() => null),
        fetch(`${BACKEND_URL}/api/identity/stats`, { headers }).catch(() => null),
        fetch(`${BACKEND_URL}/api/jd?status=active`, { headers }),
        fetch(`${BACKEND_URL}/api/analytics/profiles`, { headers })
      ]);

      // Parse JD response - API returns { items: [...] }
      if (jdRes && jdRes.ok) {
        const jdData = await jdRes.json();
        const jds = jdData.items || jdData.jds || jdData || [];
        setJobDescriptions(Array.isArray(jds) ? jds.slice(0, 5) : []);
      }

      // Parse identity resolution stats from dedicated endpoint
      if (identityRes && identityRes.ok) {
        const identityData = await identityRes.json();
        setIdentityStats({
          totalProfiles: identityData.total_profiles || 0,
          uniqueCandidates: identityData.unique_identities || identityData.total_profiles || 0,
          duplicatesDetected: identityData.total_duplicates_detected || 0,
          deduplicationRate: identityData.duplicate_rate || 0
        });
      } else if (profilesRes && profilesRes.ok) {
        // Fallback to profile analytics if identity endpoint not available
        const profileData = await profilesRes.json();
        setIdentityStats({
          totalProfiles: profileData.total_profiles || profileData.total || 0,
          uniqueCandidates: profileData.total_profiles || 0,
          duplicatesDetected: 0,
          deduplicationRate: 0
        });
      }

      // Parse automatch stats if endpoint available
      if (automatchRes && automatchRes.ok) {
        const automatchData = await automatchRes.json();
        setAutomatchStats({
          totalMatches: automatchData.total_matches || 0,
          autoAssigned: automatchData.auto_assign_count || 0,
          suggested: automatchData.suggest_count || 0,
          avgConfidence: automatchData.avg_score || 0,
          lastRunTime: automatchData.period_end
        });
      } else {
        // Set placeholder stats - real data comes after JD matching is run
        setAutomatchStats({
          totalMatches: 0,
          autoAssigned: 0,
          suggested: 0,
          avgConfidence: 0,
          lastRunTime: null
        });
      }

    } catch (err) {
      console.error('Error fetching AI stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Overview Tab - AI Capabilities Showcase (IP-Protected)
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-elevated rounded-2xl border border-line p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-ink mb-2">ARISE AI Intelligence</h2>
            <p className="text-ink-muted text-lg mb-4">Proprietary algorithms powering intelligent recruitment</p>
            <div className="flex flex-wrap gap-3 mt-4">
              {(!isFeatureEnabled || isFeatureEnabled('automatch')) && (
                <span className="px-4 py-2 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-sm font-medium">
                  🎯 AutoMatch Engine
                </span>
              )}
              {(!isFeatureEnabled || isFeatureEnabled('identity_resolution')) && (
                <span className="px-4 py-2 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-sm font-medium">
                  🔍 Identity Resolution
                </span>
              )}
              <span className="px-4 py-2 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-sm font-medium">
                ⚡ Multi-Factor Scoring
              </span>
            </div>
          </div>
          <div className="text-6xl">🧠</div>
        </div>
      </div>

      {/* Core IP Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AutoMatch Engine Card */}
        {(!isFeatureEnabled || isFeatureEnabled('automatch')) && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="bg-blue-500/10 border-b border-blue-500/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-ink">AutoMatch Engine</h3>
                  <p className="text-ink-muted text-sm">Intelligent candidate-JD matching</p>
                </div>
                <span className="text-4xl">🎯</span>
              </div>
            </div>
            <div className="p-6">
              <p className="text-ink-muted mb-4">
                Our proprietary matching engine automatically identifies the best candidates for each job description using advanced multi-dimensional analysis.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Skills &amp; competency matching</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Experience level alignment</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Domain expertise evaluation</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Cultural &amp; location fit scoring</span>
                </div>
              </div>
              <button
                onClick={() => setActiveSubTab('automatch')}
                className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View AutoMatch Dashboard →
              </button>
            </div>
          </div>
        )}

        {/* Identity Resolution Card */}
        {(!isFeatureEnabled || isFeatureEnabled('identity_resolution')) && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="bg-blue-500/10 border-b border-blue-500/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-ink">Identity Resolution</h3>
                  <p className="text-ink-muted text-sm">Duplicate detection &amp; deduplication</p>
                </div>
                <span className="text-4xl">🔍</span>
              </div>
            </div>
            <div className="p-6">
              <p className="text-ink-muted mb-4">
                Intelligent candidate fingerprinting ensures a clean database by automatically detecting and merging duplicate profiles.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Multi-signal identity matching</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Fuzzy name &amp; contact matching</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Resume content similarity</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-line">
                  <span className="text-blue-400">✓</span>
                  <span className="text-ink-muted">Automatic merge suggestions</span>
                </div>
              </div>
              <button
                onClick={() => setActiveSubTab('identity')}
                className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View Identity Dashboard →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confidence Levels */}
      {(!isFeatureEnabled || isFeatureEnabled('automatch')) && (
        <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
          <h3 className="text-lg font-bold text-ink mb-4">AutoMatch Confidence Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/25">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">🟢</span>
                <span className="px-3 py-1 bg-green-500/15 text-green-400 border border-green-500/25 rounded-full text-sm font-bold">HIGH</span>
              </div>
              <p className="text-green-400 font-bold">Auto-Assign</p>
              <p className="text-ink-muted text-sm">Top matches auto-added to shortlist</p>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/25">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">🔵</span>
                <span className="px-3 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-sm font-bold">MEDIUM</span>
              </div>
              <p className="text-blue-400 font-bold">Suggested</p>
              <p className="text-ink-muted text-sm">Recommended for recruiter review</p>
            </div>
            <div className="p-4 bg-muted rounded-xl border border-line">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">⚪</span>
                <span className="px-3 py-1 bg-subtle text-ink-muted border border-line rounded-full text-sm font-bold">LOW</span>
              </div>
              <p className="text-ink font-bold">Logged</p>
              <p className="text-ink-muted text-sm">Tracked for future consideration</p>
            </div>
          </div>
        </div>
      )}

      {/* Value Proposition */}
      <div className="bg-accent-soft rounded-xl p-6 border border-blue-500/20">
        <div className="flex items-start space-x-4">
          <span className="text-3xl">💡</span>
          <div>
            <h4 className="font-bold text-ink">Why ARISE AI?</h4>
            <p className="text-sm text-ink-muted mt-1">
              Our AI engines have been trained on extensive recruitment data and fine-tuned with proprietary algorithms
              to deliver industry-leading accuracy. Save hours of manual screening while ensuring you never miss a great candidate.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // AutoMatch Dashboard Tab
  const renderAutomatchDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-elevated rounded-xl border border-line p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-ink">AutoMatch Engine Dashboard</h2>
            <p className="text-ink-muted mt-1">Real-time candidate-JD matching analytics</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-ink-subtle">Model Version</p>
            <p className="font-mono font-bold text-ink">arise-automatch-v1.0</p>
          </div>
        </div>
      </div>

      {/* Stats Grid with Tooltips */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface rounded-xl border border-line p-5 group relative">
          <div className="flex items-center gap-1">
            <p className="text-ink-subtle text-sm">Total Matches</p>
            <span className="text-ink-subtle cursor-help" title="Total candidate-JD matches found across all AutoMatch runs. View details in JD Builder → Select JD → AutoMatch tab">ⓘ</span>
          </div>
          <p className="text-3xl font-bold text-ink">{automatchStats?.totalMatches ?? '-'}</p>
          <p className="text-xs text-ink-subtle mt-1">View in: JD Builder → AutoMatch tab</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5 group relative">
          <div className="flex items-center gap-1">
            <p className="text-ink-subtle text-sm">Auto-Assigned</p>
            <span className="text-ink-subtle cursor-help" title="High-confidence matches (≥90% score) automatically assigned to shortlist. These candidates are excellent fits requiring minimal review.">ⓘ</span>
          </div>
          <p className="text-3xl font-bold text-green-400">{automatchStats?.autoAssigned ?? 0}</p>
          <p className="text-xs text-ink-subtle mt-1">Score ≥90% • Auto-shortlisted</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5 group relative">
          <div className="flex items-center gap-1">
            <p className="text-ink-subtle text-sm">Suggested</p>
            <span className="text-ink-subtle cursor-help" title="Medium-confidence matches (80-89% score) recommended for recruiter review. Good potential candidates that need human validation.">ⓘ</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">{automatchStats?.suggested ?? 0}</p>
          <p className="text-xs text-ink-subtle mt-1">Score 80-89% • Needs review</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5 group relative">
          <div className="flex items-center gap-1">
            <p className="text-ink-subtle text-sm">Avg Confidence</p>
            <span className="text-ink-subtle cursor-help" title="Average match confidence score across all matches. Higher scores indicate better overall candidate-JD alignment.">ⓘ</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">{automatchStats?.avgConfidence ? `${(automatchStats.avgConfidence * 100).toFixed(0)}%` : '-'}</p>
          <p className="text-xs text-ink-subtle mt-1">Weighted across all dimensions</p>
        </div>
      </div>

      {/* Quick Access to Matches */}
      <div className="bg-accent-soft border border-blue-500/20 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <p className="font-semibold text-ink">Where to view match details?</p>
            <p className="text-sm text-ink-muted">Go to <strong className="text-ink">JD Builder</strong> → Select a JD → Click <strong className="text-ink">"AutoMatch"</strong> tab to see all matched candidates with scores and recommendations.</p>
          </div>
        </div>
      </div>

      {/* Matching Dimensions */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4 flex items-center">
          <span className="mr-2">⚙️</span> Multi-Dimensional Matching
        </h3>
        <p className="text-ink-muted mb-4">
          Our proprietary algorithm evaluates candidates across multiple dimensions to produce accurate match scores:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 bg-muted rounded-lg border border-line text-center">
            <div className="text-2xl mb-1">💻</div>
            <p className="text-sm font-semibold text-ink">Skills</p>
          </div>
          <div className="p-3 bg-muted rounded-lg border border-line text-center">
            <div className="text-2xl mb-1">📊</div>
            <p className="text-sm font-semibold text-ink">Experience</p>
          </div>
          <div className="p-3 bg-muted rounded-lg border border-line text-center">
            <div className="text-2xl mb-1">🏢</div>
            <p className="text-sm font-semibold text-ink">Domain</p>
          </div>
          <div className="p-3 bg-muted rounded-lg border border-line text-center">
            <div className="text-2xl mb-1">🎓</div>
            <p className="text-sm font-semibold text-ink">Education</p>
          </div>
          <div className="p-3 bg-muted rounded-lg border border-line text-center">
            <div className="text-2xl mb-1">📍</div>
            <p className="text-sm font-semibold text-ink">Location</p>
          </div>
        </div>
        <p className="text-xs text-ink-subtle mt-4 italic">
          Proprietary weighting and boosting algorithms ensure optimal candidate ranking.
        </p>
      </div>

      {/* Active JDs for Matching */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4">Active Job Descriptions</h3>
        {jobDescriptions.length > 0 ? (
          <div className="space-y-3">
            {jobDescriptions.map((jd, idx) => (
              <div key={jd.id || idx} className="flex items-center justify-between p-4 bg-muted rounded-lg border border-line hover:border-blue-500/40 transition-colors">
                <div>
                  <p className="font-medium text-ink">{jd.title}</p>
                  <p className="text-sm text-ink-subtle">{jd.department || 'Engineering'} • {jd.location || 'Remote'}</p>
                </div>
                <span className="px-3 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-sm">
                  Active
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-ink-subtle text-center py-8">No active job descriptions. Create one in JD Builder to start matching.</p>
        )}
      </div>
    </div>
  );

  // Identity Resolution Dashboard Tab
  const renderIdentityDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-elevated rounded-xl border border-line p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-ink">Identity Resolution Dashboard</h2>
            <p className="text-ink-muted mt-1">Candidate deduplication &amp; fingerprinting</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-ink-subtle">Engine Version</p>
            <p className="font-mono font-bold text-ink">arise-identity-v1.0</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface rounded-xl border border-line p-5">
          <p className="text-ink-subtle text-sm">Total Profiles</p>
          <p className="text-3xl font-bold text-ink">{identityStats?.totalProfiles?.toLocaleString() || '-'}</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5">
          <p className="text-ink-subtle text-sm">Unique Candidates</p>
          <p className="text-3xl font-bold text-blue-400">{identityStats?.uniqueCandidates?.toLocaleString() || '-'}</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5">
          <p className="text-ink-subtle text-sm">Duplicates Detected</p>
          <p className="text-3xl font-bold text-amber-400">{identityStats?.duplicatesDetected || '-'}</p>
        </div>
        <div className="bg-surface rounded-xl border border-line p-5">
          <p className="text-ink-subtle text-sm">Dedup Rate</p>
          <p className="text-3xl font-bold text-green-400">{identityStats?.deduplicationRate ? `${identityStats.deduplicationRate}%` : '-'}</p>
        </div>
      </div>

      {/* Detection Signals */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4 flex items-center">
          <span className="mr-2">🔬</span> Detection Signals
        </h3>
        <p className="text-ink-muted mb-4">
          Our identity resolution engine uses multiple signal types to accurately detect and merge duplicate candidates:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">📧</div>
            <h4 className="font-bold text-ink">Email Fingerprinting</h4>
            <p className="text-sm text-ink-muted mt-1">Secure hashed email comparison</p>
            <p className="text-xs text-blue-400 mt-2 font-medium">High confidence signal</p>
          </div>
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">📱</div>
            <h4 className="font-bold text-ink">Phone Normalization</h4>
            <p className="text-sm text-ink-muted mt-1">Format-agnostic phone matching</p>
            <p className="text-xs text-blue-400 mt-2 font-medium">High confidence signal</p>
          </div>
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">💼</div>
            <h4 className="font-bold text-ink">Social Profile ID</h4>
            <p className="text-sm text-ink-muted mt-1">LinkedIn and other platform IDs</p>
            <p className="text-xs text-blue-400 mt-2 font-medium">High confidence signal</p>
          </div>
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">👤</div>
            <h4 className="font-bold text-ink">Name Similarity</h4>
            <p className="text-sm text-ink-muted mt-1">Advanced fuzzy matching algorithms</p>
            <p className="text-xs text-ink-subtle mt-2 font-medium">Medium confidence signal</p>
          </div>
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">📄</div>
            <h4 className="font-bold text-ink">Resume Semantics</h4>
            <p className="text-sm text-ink-muted mt-1">AI-powered content analysis</p>
            <p className="text-xs text-ink-subtle mt-2 font-medium">Supporting signal</p>
          </div>
          <div className="p-4 bg-muted rounded-xl border border-line">
            <div className="text-3xl mb-2">🔗</div>
            <h4 className="font-bold text-ink">Composite Scoring</h4>
            <p className="text-sm text-ink-muted mt-1">Multi-signal fusion engine</p>
            <p className="text-xs text-blue-400 mt-2 font-medium">Final decision</p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4">How It Works</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
            <p className="text-ink-muted">Multiple signals are extracted from each candidate profile</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
            <p className="text-ink-muted">Signals are compared using proprietary matching algorithms</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
            <p className="text-ink-muted">A composite confidence score determines duplicate probability</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
            <p className="text-ink-muted">High-confidence duplicates can be auto-merged or flagged for review</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Algorithm Insights Tab
  const renderAlgorithmInsights = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-elevated rounded-xl border border-line p-6">
        <h2 className="text-2xl font-bold text-ink">Algorithm Insights</h2>
        <p className="text-ink-muted mt-1">Understanding ARISE proprietary AI</p>
      </div>

      {/* AutoMatch Overview */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4">AutoMatch Pipeline</h3>
        <p className="text-ink-muted mb-4">
          Our AutoMatch engine uses a sophisticated multi-stage pipeline to identify the best candidates for each role:
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">🎯</span>
              <h4 className="font-bold text-ink mt-2">Requirement Filtering</h4>
              <p className="text-sm text-ink-muted mt-2">Ensures candidates meet essential criteria</p>
            </div>
          </div>
          <div className="flex items-center justify-center text-2xl text-ink-subtle">→</div>
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">🔍</span>
              <h4 className="font-bold text-ink mt-2">AI-Powered Search</h4>
              <p className="text-sm text-ink-muted mt-2">Deep semantic understanding of skills and experience</p>
            </div>
          </div>
          <div className="flex items-center justify-center text-2xl text-ink-subtle">→</div>
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">📊</span>
              <h4 className="font-bold text-ink mt-2">Intelligent Ranking</h4>
              <p className="text-sm text-ink-muted mt-2">Multi-dimensional scoring for optimal matches</p>
            </div>
          </div>
        </div>
      </div>

      {/* Identity Resolution Overview */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4">Identity Resolution Pipeline</h3>
        <p className="text-ink-muted mb-4">
          Our Identity Resolution engine ensures database integrity through intelligent duplicate detection:
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">🔑</span>
              <h4 className="font-bold text-ink mt-2">Identifier Matching</h4>
              <p className="text-sm text-ink-muted mt-2">Unique identifiers like email and phone</p>
            </div>
          </div>
          <div className="flex items-center justify-center text-2xl text-ink-subtle">→</div>
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">🔤</span>
              <h4 className="font-bold text-ink mt-2">Similarity Analysis</h4>
              <p className="text-sm text-ink-muted mt-2">Advanced name and data matching</p>
            </div>
          </div>
          <div className="flex items-center justify-center text-2xl text-ink-subtle">→</div>
          <div className="flex-1 p-4 bg-muted rounded-xl border border-line">
            <div className="text-center">
              <span className="text-3xl">🧠</span>
              <h4 className="font-bold text-ink mt-2">AI Verification</h4>
              <p className="text-sm text-ink-muted mt-2">Deep content analysis for confident merging</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-surface rounded-xl shadow-sm border border-line p-6">
        <h3 className="text-lg font-bold text-ink mb-4">Key Benefits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 p-3 bg-muted rounded-lg border border-line">
            <span className="text-2xl">⚡</span>
            <div>
              <h4 className="font-semibold text-ink">Speed</h4>
              <p className="text-sm text-ink-muted">Process thousands of profiles in seconds</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-muted rounded-lg border border-line">
            <span className="text-2xl">🎯</span>
            <div>
              <h4 className="font-semibold text-ink">Accuracy</h4>
              <p className="text-sm text-ink-muted">Industry-leading match precision</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-muted rounded-lg border border-line">
            <span className="text-2xl">🔄</span>
            <div>
              <h4 className="font-semibold text-ink">Continuous Learning</h4>
              <p className="text-sm text-ink-muted">Algorithms improve with your feedback</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-muted rounded-lg border border-line">
            <span className="text-2xl">🔒</span>
            <div>
              <h4 className="font-semibold text-ink">Data Privacy</h4>
              <p className="text-sm text-ink-muted">Enterprise-grade security standards</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trade Secrets Notice */}
      <div className="bg-amber-500/10 rounded-xl p-6 border border-amber-500/25">
        <div className="flex items-start space-x-4">
          <span className="text-3xl">🔒</span>
          <div>
            <h4 className="font-bold text-amber-400">Proprietary Technology</h4>
            <p className="text-sm text-ink-muted mt-2">
              <span className="font-semibold text-ink">Patent Applications Pending (Singapore 2025)</span>
            </p>
            <ul className="text-sm text-ink-muted mt-1 space-y-1">
              <li>• AutoMatch™ Multi-Dimensional Candidate Scoring System</li>
              <li>• Identity Resolution™ Cross-Platform Profile Deduplication</li>
            </ul>
            <p className="text-sm text-ink-muted mt-3">
              The exact thresholds, weight combinations, and boosting formulas are protected trade secrets
              of /tmp Labs Pte. Ltd. Unauthorized reproduction or reverse engineering is prohibited.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page header + sub-navigation */}
      <div className="bg-elevated rounded-xl border border-line shadow-sm p-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400">
            <Icon name="ai" className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ink">AI Engine</h2>
            <p className="text-sm text-ink-subtle">AutoMatch &amp; Identity Resolution</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-1 rounded-lg border border-line bg-muted p-1">
          {availableSubTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              title={tab.desc}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-surface text-ink border border-line shadow-sm'
                  : 'text-ink-muted hover:text-ink border border-transparent'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Tab Content */}
      {!loading && (
        <>
          {activeSubTab === 'overview' && renderOverview()}
          {activeSubTab === 'automatch' && (!isFeatureEnabled || isFeatureEnabled('automatch')) && renderAutomatchDashboard()}
          {activeSubTab === 'identity' && (!isFeatureEnabled || isFeatureEnabled('identity_resolution')) && renderIdentityDashboard()}
          {activeSubTab === 'algorithms' && renderAlgorithmInsights()}
        </>
      )}
    </div>
  );
};

// Interview Management Component
const InterviewManagement = ({ currentUser, hasPermission }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Define submenu tabs
  const subTabs = [
    { id: 'overview', name: 'Overview', icon: '📋', desc: 'Interview dashboard & quick stats', permission: 'canViewInterviews' },
    { id: 'my-email', name: 'My Email Config', icon: '📧', desc: 'Configure your email for sending invitations', permission: 'canScheduleInterviews' },
    { id: 'integrations', name: 'Tenant Integrations', icon: '🔗', desc: 'Zoom/Teams/GMeet API configuration', permission: 'canManageIntegrations' },
    { id: 'scheduled', name: 'Scheduled Interviews', icon: '🗓️', desc: 'View & manage scheduled interviews', permission: 'canViewInterviews' },
    { id: 'templates', name: 'Meeting Templates', icon: '📝', desc: 'Create & manage interview templates', permission: 'canManageTemplates' }
  ];

  // Filter submenu tabs based on permissions
  const getAvailableSubTabs = () => {
    return subTabs.filter(tab => hasPermission(tab.permission));
  };

  const availableSubTabs = getAvailableSubTabs();

  // Set default tab to first available tab
  React.useEffect(() => {
    if (availableSubTabs.length > 0 && !availableSubTabs.find(tab => tab.id === activeSubTab)) {
      setActiveSubTab(availableSubTabs[0].id);
    }
  }, [availableSubTabs, activeSubTab]);

  return (
    <div className="space-y-6">
      {/* Page header — scheduling operations console */}
      <div className="bg-elevated rounded-xl border border-line shadow-sm p-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400">
            <Icon name="interviews" className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ink">Interviews</h2>
            <p className="text-sm text-ink-subtle">Schedule interviews, manage integrations, and track feedback</p>
          </div>
        </div>

        {/* Segmented sub-tabs */}
        {availableSubTabs.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1 rounded-lg border border-line bg-muted p-1">
            {availableSubTabs.map((tab) => (
              <button
                key={tab.id}
                data-tab={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                title={tab.desc}
                className={`flex-1 min-w-[8rem] px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-surface text-ink border border-line shadow-sm'
                    : 'text-ink-muted hover:text-ink border border-transparent'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-red-400 text-lg">⚠️</span>
            <span className="text-red-400">{error}</span>
          </div>
        </div>
      )}

      {/* Content */}
      {activeSubTab === 'overview' && (
        <InterviewOverview currentUser={currentUser} hasPermission={hasPermission} setError={setError} />
      )}
      {activeSubTab === 'my-email' && hasPermission('canScheduleInterviews') && (
        <UserEmailConfig currentUser={currentUser} setError={setError} />
      )}
      {activeSubTab === 'integrations' && hasPermission('canManageIntegrations') && (
        <TenantIntegrationsConfig currentUser={currentUser} setError={setError} />
      )}
      {activeSubTab === 'scheduled' && (
        <ScheduledInterviews currentUser={currentUser} hasPermission={hasPermission} setError={setError} />
      )}
      {activeSubTab === 'templates' && hasPermission('canManageTemplates') && (
        <MeetingTemplates currentUser={currentUser} setError={setError} />
      )}

      {/* Access Denied */}
      {availableSubTabs.length === 0 && (
        <div className="bg-surface border border-line rounded-xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-subtle rounded-full flex items-center justify-center">
            <span className="text-3xl">🚫</span>
          </div>
          <h3 className="text-lg font-semibold text-ink mb-1">No Access to Interview Management</h3>
          <p className="text-ink-subtle">You don't have permission to access any interview management features.</p>
        </div>
      )}
    </div>
  );
};

// User Email Configuration Component for Interview Scheduling
const UserEmailConfig = ({ currentUser, setError }) => {
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    email_address: '',
    display_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    use_ssl: false,
    provider: 'custom',
    include_calendar_attachment: true,
    cc_self: false,
    default_signature: ''
  });

  // Fetch existing config and presets
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch presets and config in parallel
        const [presetsRes, configRes] = await Promise.all([
          axios.get(`${API}/interviews/email-config/presets`, { headers }),
          axios.get(`${API}/interviews/email-config`, { headers })
        ]);

        setPresets(presetsRes.data.presets || {});

        if (configRes.data) {
          setConfig(configRes.data);
          setFormData({
            email_address: configRes.data.email_address || '',
            display_name: configRes.data.display_name || '',
            smtp_host: configRes.data.smtp_host || '',
            smtp_port: configRes.data.smtp_port || 587,
            smtp_username: configRes.data.smtp_username || '',
            smtp_password: '', // Never populated from server
            use_tls: configRes.data.use_tls ?? true,
            use_ssl: configRes.data.use_ssl ?? false,
            provider: configRes.data.provider || 'custom',
            include_calendar_attachment: configRes.data.include_calendar_attachment ?? true,
            cc_self: configRes.data.cc_self ?? false,
            default_signature: configRes.data.default_signature || ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch email config:', err);
        if (err.response?.status !== 404) {
          setError('Failed to load email configuration');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setError]);

  // Handle provider preset selection
  const handleProviderChange = (provider) => {
    const preset = presets[provider];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        provider: provider,
        smtp_host: preset.smtp_host,
        smtp_port: preset.smtp_port,
        use_tls: preset.use_tls,
        use_ssl: preset.use_ssl
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        provider: provider
      }));
    }
  };

  // Save configuration
  const handleSave = async () => {
    try {
      setSaving(true);
      setTestResult(null);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API}/interviews/email-config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConfig(response.data);
      setTestResult({ success: true, message: 'Configuration saved successfully! Please test your connection.' });
    } catch (err) {
      console.error('Failed to save email config:', err);
      setTestResult({ success: false, message: err.response?.data?.detail || 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  // Test configuration
  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API}/interviews/email-config/test`,
        { send_test_email: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult(response.data);
      if (response.data.success) {
        // Refresh config to get updated verification status
        const configRes = await axios.get(`${API}/interviews/email-config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConfig(configRes.data);
      }
    } catch (err) {
      console.error('Failed to test email config:', err);
      setTestResult({ success: false, message: err.response?.data?.detail || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  // Delete configuration
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your email configuration?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/interviews/email-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConfig(null);
      setFormData({
        email_address: '',
        display_name: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        use_tls: true,
        use_ssl: false,
        provider: 'custom',
        include_calendar_attachment: true,
        cc_self: false,
        default_signature: ''
      });
      setTestResult({ success: true, message: 'Configuration deleted' });
    } catch (err) {
      console.error('Failed to delete email config:', err);
      setTestResult({ success: false, message: 'Failed to delete configuration' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-ink-muted">Loading email configuration...</span>
      </div>
    );
  }

  const inputCls = "w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const labelCls = "block text-sm font-medium text-ink-muted mb-1";
  const checkboxCls = "rounded border-line text-blue-600 focus:ring-blue-500 bg-surface";

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-accent-soft border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📧</span>
          <div>
            <h3 className="font-semibold text-ink">Your Email for Interview Invitations</h3>
            <p className="text-ink-muted text-sm mt-1">
              Configure your email to send interview invitations directly from your mailbox.
              When you schedule interviews, emails will be sent FROM your email address,
              and candidate replies will go to your inbox.
            </p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {config && (
        <div className={`rounded-lg p-4 border ${config.is_verified ? 'bg-green-500/10 border-green-500/25' : 'bg-amber-500/10 border-amber-500/25'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">{config.is_verified ? '✅' : '⚠️'}</span>
              <span className={config.is_verified ? 'text-green-400' : 'text-amber-400'}>
                {config.is_verified
                  ? `Email verified: ${config.email_address}`
                  : 'Email not verified - please test your configuration'}
              </span>
            </div>
            {config.last_used_at && (
              <span className="text-sm text-ink-subtle">
                Last used: {new Date(config.last_used_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Test Result */}
      {testResult && (
        <div className={`rounded-lg p-4 border ${testResult.success ? 'bg-green-500/10 border-green-500/25' : 'bg-red-500/10 border-red-500/25'}`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{testResult.success ? '✅' : '❌'}</span>
            <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
              {testResult.message}
            </span>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-ink mb-4">Email Configuration</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Provider Selection */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-muted mb-2">Email Provider</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { id: 'gmail', name: 'Gmail', icon: '📬' },
                { id: 'outlook', name: 'Outlook', icon: '📧' },
                { id: 'office365', name: 'Office 365', icon: '💼' },
                { id: 'custom', name: 'Custom SMTP', icon: '⚙️' }
              ].map(provider => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => handleProviderChange(provider.id)}
                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                    formData.provider === provider.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-line bg-surface hover:border-blue-500/40'
                  }`}
                >
                  <div className="text-2xl mb-1">{provider.icon}</div>
                  <div className="text-sm font-medium text-ink">{provider.name}</div>
                </button>
              ))}
            </div>
            {presets[formData.provider]?.notes && (
              <p className="mt-2 text-sm text-ink-subtle">
                💡 {presets[formData.provider].notes}
              </p>
            )}
          </div>

          {/* Email Address */}
          <div>
            <label className={labelCls}>Email Address *</label>
            <input
              type="email"
              value={formData.email_address}
              onChange={(e) => setFormData(prev => ({ ...prev, email_address: e.target.value }))}
              placeholder="your.email@company.com"
              className={inputCls}
            />
          </div>

          {/* Display Name */}
          <div>
            <label className={labelCls}>Display Name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder="Your Name"
              className={inputCls}
            />
          </div>

          {/* SMTP Host */}
          <div>
            <label className={labelCls}>SMTP Host *</label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={(e) => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
              placeholder="smtp.gmail.com"
              className={inputCls}
            />
          </div>

          {/* SMTP Port */}
          <div>
            <label className={labelCls}>SMTP Port *</label>
            <input
              type="number"
              value={formData.smtp_port}
              onChange={(e) => setFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
              placeholder="587"
              className={inputCls}
            />
          </div>

          {/* SMTP Username */}
          <div>
            <label className={labelCls}>SMTP Username *</label>
            <input
              type="text"
              value={formData.smtp_username}
              onChange={(e) => setFormData(prev => ({ ...prev, smtp_username: e.target.value }))}
              placeholder="your.email@company.com"
              className={inputCls}
            />
          </div>

          {/* SMTP Password */}
          <div>
            <label className={labelCls}>
              SMTP Password * {config && <span className="text-ink-subtle">(leave blank to keep existing)</span>}
            </label>
            <input
              type="password"
              value={formData.smtp_password}
              onChange={(e) => setFormData(prev => ({ ...prev, smtp_password: e.target.value }))}
              placeholder={config ? '••••••••' : 'App password or SMTP password'}
              className={inputCls}
              autoComplete="off"
            />
          </div>

          {/* TLS/SSL Options */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-muted mb-2">Security</label>
            <div className="flex space-x-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.use_tls}
                  onChange={(e) => setFormData(prev => ({ ...prev, use_tls: e.target.checked, use_ssl: e.target.checked ? false : prev.use_ssl }))}
                  className={checkboxCls}
                />
                <span className="text-sm text-ink-muted">Use TLS (Port 587)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.use_ssl}
                  onChange={(e) => setFormData(prev => ({ ...prev, use_ssl: e.target.checked, use_tls: e.target.checked ? false : prev.use_tls }))}
                  className={checkboxCls}
                />
                <span className="text-sm text-ink-muted">Use SSL (Port 465)</span>
              </label>
            </div>
          </div>

          {/* Email Options */}
          <div className="md:col-span-2 border-t border-line pt-4">
            <label className="block text-sm font-medium text-ink-muted mb-2">Email Options</label>
            <div className="flex space-x-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.include_calendar_attachment}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_calendar_attachment: e.target.checked }))}
                  className={checkboxCls}
                />
                <span className="text-sm text-ink-muted">Attach calendar invite (.ics)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.cc_self}
                  onChange={(e) => setFormData(prev => ({ ...prev, cc_self: e.target.checked }))}
                  className={checkboxCls}
                />
                <span className="text-sm text-ink-muted">CC myself on emails</span>
              </label>
            </div>
          </div>

          {/* Email Signature */}
          <div className="md:col-span-2">
            <label className={labelCls}>Email Signature (optional)</label>
            <textarea
              value={formData.default_signature}
              onChange={(e) => setFormData(prev => ({ ...prev, default_signature: e.target.value }))}
              placeholder="Your signature..."
              rows={3}
              className={inputCls}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-line">
          <div>
            {config && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Delete Configuration
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleTest}
              disabled={testing || !config}
              className="px-4 py-2 bg-muted text-ink-muted border border-line rounded-lg hover:bg-subtle hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ink-muted"></div>
                  <span>Testing...</span>
                </>
              ) : (
                <>
                  <span>🔌</span>
                  <span>Test Connection</span>
                </>
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.email_address || !formData.smtp_host || !formData.smtp_username || (!config && !formData.smtp_password)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>Save Configuration</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-muted border border-line rounded-lg p-4">
        <h4 className="font-medium text-ink mb-2">📚 Setup Guide</h4>
        <div className="text-sm text-ink-muted space-y-2">
          <p><strong className="text-ink">Gmail:</strong> Use an App Password (not your regular password). Go to Google Account → Security → 2-Step Verification → App passwords.</p>
          <p><strong className="text-ink">Outlook/Office 365:</strong> Use your regular password or App Password if 2FA is enabled.</p>
          <p><strong className="text-ink">Custom SMTP:</strong> Enter your organization's SMTP server details.</p>
        </div>
      </div>
    </div>
  );
};

// Interview Overview Component
const InterviewOverview = ({ currentUser, hasPermission, setError }) => {
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/interviews/overview`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        setError('');
      } else {
        setError('Failed to load interview overview');
      }
    } catch (err) {
      setError('Failed to connect to interview service');
      console.error('Interview overview error:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchOverview();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const avgRating = overview.quick_stats?.average_rating;

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface border border-line rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Today's Interviews</p>
              <p className="mt-1 text-2xl font-bold text-blue-400">{overview.today?.total_interviews || 0}</p>
            </div>
            <Icon name="clock" className="w-5 h-5 text-blue-400" />
          </div>
        </div>

        <div className="bg-surface border border-line rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Completion Rate</p>
              <p className="mt-1 text-2xl font-bold text-green-400">{overview.quick_stats?.completion_rate || 0}%</p>
            </div>
            <Icon name="evaluation" className="w-5 h-5 text-green-400" />
          </div>
        </div>

        <div className="bg-surface border border-line rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Upcoming</p>
              <p className="mt-1 text-2xl font-bold text-amber-400">{overview.upcoming?.total_interviews || 0}</p>
            </div>
            <Icon name="clock" className="w-5 h-5 text-amber-400" />
          </div>
        </div>

        <div className="bg-surface border border-line rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Avg Rating</p>
              <p className={`mt-1 text-2xl font-bold ${avgRating ? 'text-blue-400' : 'text-ink-subtle'}`}>
                {avgRating ? avgRating.toFixed(1) : 'N/A'}
              </p>
            </div>
            <Icon name="sparkles" className={`w-5 h-5 ${avgRating ? 'text-blue-400' : 'text-ink-subtle'}`} />
          </div>
        </div>
      </div>

      {/* Liquid Design: Setup Stepper - Show if not fully configured */}
      {(!overview.config_status?.email_configured || !overview.config_status?.has_templates) && (
        <SetupStepper
          title="Get Started with Interviews"
          description="Complete these steps to start scheduling interviews"
          steps={[
            {
              id: 'email',
              title: 'Configure Email',
              description: 'Set up your email to send interview invitations',
              completed: overview.config_status?.email_configured || false,
              actionLabel: 'Configure',
              onClick: () => document.querySelector('[data-tab="my-email"]')?.click(),
            },
            {
              id: 'integration',
              title: 'Connect Meeting Platform',
              description: 'Link Zoom, Teams, or Google Meet for video interviews',
              completed: overview.config_status?.has_integrations || false,
              actionLabel: 'Connect',
              onClick: () => document.querySelector('[data-tab="integrations"]')?.click(),
            },
            {
              id: 'template',
              title: 'Create Interview Template',
              description: 'Save time with reusable interview email templates',
              completed: overview.config_status?.has_templates || false,
              actionLabel: 'Create',
              onClick: () => document.querySelector('[data-tab="templates"]')?.click(),
            },
            {
              id: 'schedule',
              title: 'Schedule First Interview',
              description: 'Pick a candidate and send your first invitation',
              completed: overview.quick_stats?.total_scheduled > 0 || false,
              disabled: !overview.config_status?.email_configured,
              actionLabel: 'Schedule',
              onClick: () => document.querySelector('[data-tab="scheduled"]')?.click(),
            },
          ]}
          collapseCompleted={true}
        />
      )}

      {/* Recent Interviews */}
      <div className="bg-surface border border-line rounded-xl p-6">
        <h3 className="text-lg font-semibold text-ink mb-4">Recent Interviews</h3>
        {overview.today?.interviews && overview.today.interviews.length > 0 ? (
          <div className="space-y-3">
            {overview.today.interviews.slice(0, 5).map((interview, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/15 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-400">
                      {interview.candidate_name?.charAt(0).toUpperCase() || 'C'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-ink">{interview.candidate_name}</p>
                    <p className="text-sm text-ink-subtle">{interview.interview_type?.replace('_', ' ').toUpperCase()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-ink">
                    {new Date(interview.scheduled_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </p>
                  <p className="text-xs text-ink-subtle capitalize">{interview.status}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-ink-subtle text-center py-8">No interviews scheduled for today</p>
        )}
      </div>

      {/* Configuration Status */}
      <div className="bg-surface border border-line rounded-xl p-6">
        <h3 className="text-lg font-semibold text-ink mb-4">Configuration Status</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center space-x-3">
              <span className="text-xl">🔗</span>
              <span className="font-medium text-ink">Meeting Integrations</span>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
              overview.configuration?.has_integration
                ? 'bg-green-500/15 text-green-400 border-green-500/25'
                : 'bg-amber-500/15 text-amber-400 border-amber-500/25'
            }`}>
              {overview.configuration?.has_integration ? 'Configured' : 'Not Set'}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center space-x-3">
              <span className="text-xl">📝</span>
              <span className="font-medium text-ink">Templates</span>
            </div>
            <span className="px-2 py-1 rounded-full text-xs font-medium border bg-blue-500/15 text-blue-400 border-blue-500/25">
              {overview.configuration?.templates_count || 0} Templates
            </span>
          </div>
        </div>

        {overview.configuration?.providers_configured && overview.configuration.providers_configured.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-ink-muted mb-2">Configured Providers:</p>
            <div className="flex space-x-2">
              {overview.configuration.providers_configured.map((provider, index) => (
                <span key={index} className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 rounded-full text-xs font-medium capitalize">
                  {provider}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {hasPermission('canScheduleInterviews') && (
        <div className="bg-surface border border-line rounded-xl p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => document.querySelector('[data-tab="scheduled"]')?.click()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              📅 Schedule Interview
            </button>
            {hasPermission('canManageTemplates') && (
              <button
                onClick={() => document.querySelector('[data-tab="templates"]')?.click()}
                className="px-4 py-2 bg-muted text-ink border border-line rounded-lg hover:bg-subtle transition-colors"
              >
                📝 Create Template
              </button>
            )}
            {hasPermission('canManageIntegrations') && (
              <button
                onClick={() => document.querySelector('[data-tab="integrations"]')?.click()}
                className="px-4 py-2 bg-muted text-ink border border-line rounded-lg hover:bg-subtle transition-colors"
              >
                🔗 Setup Integration
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Placeholder components for other tabs (to be implemented)
const TenantIntegrationsConfig = ({ currentUser, setError }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnections, setTestingConnections] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});
  const [activeTab, setActiveTab] = useState('zoom');

  const [formData, setFormData] = useState({
    default_provider: 'zoom',
    zoom_config: {
      api_key: '',
      api_secret: '',
      account_id: '',
      use_jwt: true,
      default_host_id: 'me'
    },
    teams_config: {
      client_id: '',
      client_secret: '',
      tenant_id: '',
      default_user_id: 'me'
    },
    gmeet_config: {
      service_account_file: '',
      calendar_id: 'primary'
    },
    is_active: true
  });

  useEffect(() => {
    fetchIntegrationConfig();
  }, []);

  const fetchIntegrationConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/interviews/integration-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.data) {
        setConfig(response.data);
        setFormData(response.data);
      }
      
      setError('');
    } catch (error) {
      console.error('Failed to fetch integration config:', error);
      if (error.response?.status !== 404) {
        setError('Failed to load integration configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (provider, field, value) => {
    setFormData(prev => ({
      ...prev,
      [`${provider}_config`]: {
        ...prev[`${provider}_config`],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const method = config ? 'put' : 'post';
      const url = config 
        ? `${API}/interviews/integration-config/${config.id}` 
        : `${API}/interviews/integration-config`;
      
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      setConfig(response.data);
      setError('');
      
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Integration configuration saved successfully!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
      
    } catch (error) {
      console.error('Failed to save integration config:', error);
      setError('Failed to save integration configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (provider) => {
    try {
      setTestingConnections(prev => ({ ...prev, [provider]: true }));
      
      const testConfig = formData[`${provider}_config`];
      const response = await axios.post(`${API}/interviews/test-integration`, {
        provider,
        config: testConfig
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      setConnectionStatus(prev => ({
        ...prev,
        [provider]: {
          success: response.data.status === 'success',
          message: response.data.status === 'success' 
            ? 'Connection successful! Test meeting created and deleted.'
            : response.data.error
        }
      }));
      
    } catch (error) {
      console.error(`Failed to test ${provider} connection:`, error);
      setConnectionStatus(prev => ({
        ...prev,
        [provider]: {
          success: false,
          message: error.response?.data?.detail || 'Connection test failed'
        }
      }));
    } finally {
      setTestingConnections(prev => ({ ...prev, [provider]: false }));
    }
  };

  const inputCls = "w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-sm font-medium text-ink-muted mb-2";
  const checkboxCls = "h-4 w-4 text-blue-600 focus:ring-blue-500 border-line rounded bg-surface";

  const renderZoomConfig = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>API Key *</label>
          <input
            type="text"
            value={formData.zoom_config?.api_key || ''}
            onChange={(e) => handleInputChange('zoom', 'api_key', e.target.value)}
            className={inputCls}
            placeholder="Your Zoom API Key"
          />
        </div>

        <div>
          <label className={labelCls}>API Secret *</label>
          <input
            type="password"
            value={formData.zoom_config?.api_secret || ''}
            onChange={(e) => handleInputChange('zoom', 'api_secret', e.target.value)}
            className={inputCls}
            placeholder="Your Zoom API Secret"
            autoComplete="off"
          />
        </div>

        <div>
          <label className={labelCls}>Account ID</label>
          <input
            type="text"
            value={formData.zoom_config?.account_id || ''}
            onChange={(e) => handleInputChange('zoom', 'account_id', e.target.value)}
            className={inputCls}
            placeholder="Account ID (for OAuth)"
          />
        </div>

        <div>
          <label className={labelCls}>Default Host ID</label>
          <input
            type="text"
            value={formData.zoom_config?.default_host_id || 'me'}
            onChange={(e) => handleInputChange('zoom', 'default_host_id', e.target.value)}
            className={inputCls}
            placeholder="me"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="useJWT"
          checked={formData.zoom_config?.use_jwt || true}
          onChange={(e) => handleInputChange('zoom', 'use_jwt', e.target.checked)}
          className={checkboxCls}
        />
        <label htmlFor="useJWT" className="ml-2 text-sm text-ink-muted">
          Use JWT Authentication (Legacy)
        </label>
      </div>
    </div>
  );

  const renderTeamsConfig = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>Client ID *</label>
          <input
            type="text"
            value={formData.teams_config?.client_id || ''}
            onChange={(e) => handleInputChange('teams', 'client_id', e.target.value)}
            className={inputCls}
            placeholder="Azure AD Application Client ID"
          />
        </div>

        <div>
          <label className={labelCls}>Client Secret *</label>
          <input
            type="password"
            value={formData.teams_config?.client_secret || ''}
            onChange={(e) => handleInputChange('teams', 'client_secret', e.target.value)}
            className={inputCls}
            placeholder="Azure AD Application Client Secret"
            autoComplete="off"
          />
        </div>

        <div>
          <label className={labelCls}>Tenant ID *</label>
          <input
            type="text"
            value={formData.teams_config?.tenant_id || ''}
            onChange={(e) => handleInputChange('teams', 'tenant_id', e.target.value)}
            className={inputCls}
            placeholder="Azure AD Tenant ID"
          />
        </div>

        <div>
          <label className={labelCls}>Default User ID</label>
          <input
            type="text"
            value={formData.teams_config?.default_user_id || 'me'}
            onChange={(e) => handleInputChange('teams', 'default_user_id', e.target.value)}
            className={inputCls}
            placeholder="me"
          />
        </div>
      </div>
    </div>
  );

  const renderGoogleMeetConfig = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className={labelCls}>Service Account JSON *</label>
          <textarea
            value={formData.gmeet_config?.service_account_file || ''}
            onChange={(e) => handleInputChange('gmeet', 'service_account_file', e.target.value)}
            rows={8}
            className={`${inputCls} font-mono text-sm`}
            placeholder='{"type": "service_account", "project_id": "your-project", ...}'
          />
          <p className="text-xs text-ink-subtle mt-1">
            Paste the contents of your Google Service Account JSON file here
          </p>
        </div>

        <div>
          <label className={labelCls}>Calendar ID</label>
          <input
            type="text"
            value={formData.gmeet_config?.calendar_id || 'primary'}
            onChange={(e) => handleInputChange('gmeet', 'calendar_id', e.target.value)}
            className={inputCls}
            placeholder="primary"
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-ink mb-2">Meeting Platform Integrations</h2>
        <p className="text-ink-muted">Configure API credentials for Zoom, Microsoft Teams, and Google Meet</p>
      </div>

      {/* Default Provider Selection */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-ink-muted mb-3">
          Default Meeting Provider
        </label>
        <div className="flex flex-wrap gap-4">
          {[
            { value: 'zoom', label: 'Zoom', icon: '📹' },
            { value: 'teams', label: 'Microsoft Teams', icon: '💼' },
            { value: 'gmeet', label: 'Google Meet', icon: '🎥' }
          ].map((provider) => (
            <label key={provider.value} className="flex items-center">
              <input
                type="radio"
                name="default_provider"
                value={provider.value}
                checked={formData.default_provider === provider.value}
                onChange={(e) => setFormData(prev => ({ ...prev, default_provider: e.target.value }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-line bg-surface"
              />
              <span className="ml-2 text-sm text-ink-muted">
                {provider.icon} {provider.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Provider Configuration Tabs */}
      <div className="border-b border-line mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'zoom', label: 'Zoom', icon: '📹' },
            { key: 'teams', label: 'Microsoft Teams', icon: '💼' },
            { key: 'gmeet', label: 'Google Meet', icon: '🎥' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-ink-subtle hover:text-ink hover:border-line'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Configuration Forms */}
      <div className="mb-8">
        {activeTab === 'zoom' && renderZoomConfig()}
        {activeTab === 'teams' && renderTeamsConfig()}
        {activeTab === 'gmeet' && renderGoogleMeetConfig()}

        {/* Connection Test */}
        <div className="mt-6 pt-6 border-t border-line">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-ink-muted">Connection Test</h4>
              <p className="text-sm text-ink-subtle">Test the configuration before saving</p>
            </div>
            <button
              onClick={() => testConnection(activeTab)}
              disabled={testingConnections[activeTab]}
              className="px-4 py-2 text-sm bg-muted text-ink-muted border border-line rounded-lg hover:bg-subtle hover:text-ink disabled:opacity-50 flex items-center space-x-2"
            >
              {testingConnections[activeTab] ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ink-muted"></div>
                  <span>Testing...</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Test Connection</span>
                </>
              )}
            </button>
          </div>

          {connectionStatus[activeTab] && (
            <div className={`mt-3 p-3 rounded-lg border ${
              connectionStatus[activeTab].success
                ? 'bg-green-500/10 border-green-500/25 text-green-400'
                : 'bg-red-500/10 border-red-500/25 text-red-400'
            }`}>
              <div className="flex items-center space-x-2">
                <span>{connectionStatus[activeTab].success ? '✅' : '❌'}</span>
                <span className="text-sm">{connectionStatus[activeTab].message}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-end space-x-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            className={checkboxCls}
          />
          <label htmlFor="isActive" className="ml-2 text-sm text-ink-muted">
            Enable integrations
          </label>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>{config ? 'Update Configuration' : 'Save Configuration'}</span>
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-accent-soft border border-blue-500/20 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-ink mb-2">Setup Instructions</h4>
        <div className="space-y-2 text-sm text-ink-muted">
          <p><strong className="text-ink">Zoom:</strong> Create a JWT or OAuth app in Zoom Marketplace to get API credentials.</p>
          <p><strong className="text-ink">Teams:</strong> Register an Azure AD application with Microsoft Graph API permissions.</p>
          <p><strong className="text-ink">Google Meet:</strong> Create a service account in Google Cloud Console with Calendar API access.</p>
        </div>
      </div>
    </div>
  );
};

const ScheduledInterviews = ({ currentUser, hasPermission, setError }) => {
  const [interviews, setInterviews] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [integrationConfig, setIntegrationConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    interviewer: ''
  });

  const [scheduleForm, setScheduleForm] = useState({
    candidate_id: '',
    candidate_name: '',
    candidate_email: '',
    job_title: '',
    interview_type: 'technical_interview',
    scheduled_datetime: '',
    duration_minutes: 60,
    provider: 'zoom',
    interviewer_emails: [],
    template_id: '',
    custom_agenda: '',
    recording_enabled: false
  });

  useEffect(() => {
    fetchInterviews();
    fetchTemplates();
    fetchIntegrationConfig();
  }, [filters]);

  const fetchInterviews = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.interviewer) params.append('interviewer_id', filters.interviewer);
      params.append('limit', '100');

      const response = await axios.get(`${API}/interviews/scheduled?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      setInterviews(response.data);
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
      setError('Failed to load interviews');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/interviews/templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchIntegrationConfig = async () => {
    try {
      const response = await axios.get(`${API}/interviews/integration-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setIntegrationConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch integration config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleInterview = async () => {
    try {
      const response = await axios.post(`${API}/interviews/schedule`, scheduleForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      setShowScheduleModal(false);
      setScheduleForm({
        candidate_id: '',
        candidate_name: '',
        candidate_email: '',
        job_title: '',
        interview_type: 'technical_interview',
        scheduled_datetime: '',
        duration_minutes: 60,
        provider: 'zoom',
        interviewer_emails: [],
        template_id: '',
        custom_agenda: '',
        recording_enabled: false
      });
      
      fetchInterviews();
      setError('');
    } catch (error) {
      console.error('Failed to schedule interview:', error);
      setError(getErrorMessage(error));
    }
  };

  const handleUpdateStatus = async (interviewId, newStatus) => {
    try {
      await axios.put(
        `${API}/interviews/scheduled/${interviewId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      
      fetchInterviews();
      setError('');
    } catch (error) {
      console.error('Failed to update interview status:', error);
      setError('Failed to update interview status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'scheduled': 'bg-blue-500/15 text-blue-400 border border-blue-500/25',
      'confirmed': 'bg-green-500/15 text-green-400 border border-green-500/25',
      'in_progress': 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
      'completed': 'bg-blue-500/15 text-blue-400 border border-blue-500/25',
      'cancelled': 'bg-red-500/15 text-red-400 border border-red-500/25',
      'no_show': 'bg-subtle text-ink-muted border border-line'
    };
    return colors[status] || 'bg-subtle text-ink-muted border border-line';
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString();
  };

  const fieldCls = "w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:[color-scheme:dark]";
  const labelCls = "block text-sm font-medium text-ink-muted mb-2";

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Actions */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-ink">Interview Schedule</h2>
          <p className="text-ink-muted">Manage and schedule candidate interviews</p>
        </div>

        {hasPermission('canScheduleInterviews') && (
          <button
            onClick={() => setShowScheduleModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2"
          >
            <span>📅</span>
            <span>Schedule Interview</span>
          </button>
        )}
      </div>

      {/* Integration Status Alert */}
      {!integrationConfig && (
        <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <span className="text-amber-400 text-lg">⚠️</span>
            <div>
              <span className="text-amber-400 font-medium">Integration Setup Required</span>
              <p className="text-amber-400/80 text-sm">Configure Zoom, Teams, or Google Meet integration to schedule meetings.</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-ink mb-4">Filter Interviews</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className={fieldCls}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              className={fieldCls}
            />
          </div>

          <div>
            <label className={labelCls}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              className={fieldCls}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({status: '', startDate: '', endDate: '', interviewer: ''})}
              className="px-4 py-2 border border-line bg-muted text-ink-muted rounded-lg hover:bg-subtle hover:text-ink transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Interviews List */}
      <div className="bg-surface border border-line rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-lg font-semibold text-ink">
            Scheduled Interviews ({interviews.length})
          </h3>
        </div>

        {interviews.length === 0 ? (
          <div className="p-12 text-center">
            <span className="text-5xl">📅</span>
            <h3 className="text-lg font-semibold text-ink mt-4 mb-1">No Interviews Scheduled</h3>
            <p className="text-ink-subtle">Schedule your first interview to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Interview Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Date &amp; Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {interviews.map((interview) => (
                  <tr key={interview.id} className="hover:bg-muted transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-ink">
                          {interview.candidate_name}
                        </div>
                        <div className="text-sm text-ink-subtle">
                          {interview.candidate_email}
                        </div>
                        {interview.job_title && (
                          <div className="text-xs text-blue-400">
                            {interview.job_title}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/25 capitalize">
                        {interview.interview_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                      <div>{formatDateTime(interview.scheduled_datetime)}</div>
                      <div className="text-xs text-ink-subtle">
                        {interview.duration_minutes} minutes
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">
                          {interview.provider === 'zoom' && '💙'}
                          {interview.provider === 'teams' && '🟪'}
                          {interview.provider === 'gmeet' && '🟢'}
                        </span>
                        <span className="text-sm text-ink-muted capitalize">
                          {interview.provider.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(interview.status)}`}>
                        {interview.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {interview.meeting_url && (
                        <a
                          href={interview.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          Join
                        </a>
                      )}

                      {hasPermission('canManageInterviews') && interview.status === 'scheduled' && (
                        <button
                          onClick={() => handleUpdateStatus(interview.id, 'confirmed')}
                          className="text-green-400 hover:text-green-300 ml-2"
                        >
                          Confirm
                        </button>
                      )}

                      {hasPermission('canManageInterviews') && interview.status !== 'cancelled' && interview.status !== 'completed' && (
                        <button
                          onClick={() => handleUpdateStatus(interview.id, 'cancelled')}
                          className="text-red-400 hover:text-red-300 ml-2"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-elevated border border-line rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-ink">Schedule New Interview</h3>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="text-ink-subtle hover:text-ink"
                >
                  <span className="sr-only">Close</span>
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Candidate Information */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-ink">Candidate Information</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Candidate Name *
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.candidate_name}
                      onChange={(e) => setScheduleForm({...scheduleForm, candidate_name: e.target.value})}
                      className={fieldCls}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={scheduleForm.candidate_email}
                      onChange={(e) => setScheduleForm({...scheduleForm, candidate_email: e.target.value})}
                      className={fieldCls}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={scheduleForm.job_title}
                    onChange={(e) => setScheduleForm({...scheduleForm, job_title: e.target.value})}
                    className={fieldCls}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>
              </div>

              {/* Interview Details */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-ink">Interview Details</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Interview Type *
                    </label>
                    <select
                      value={scheduleForm.interview_type}
                      onChange={(e) => setScheduleForm({...scheduleForm, interview_type: e.target.value})}
                      className={fieldCls}
                    >
                      <option value="phone_screening">Phone Screening</option>
                      <option value="technical_interview">Technical Interview</option>
                      <option value="behavioral_interview">Behavioral Interview</option>
                      <option value="panel_interview">Panel Interview</option>
                      <option value="final_interview">Final Interview</option>
                      <option value="cultural_fit">Cultural Fit</option>
                      <option value="hr_interview">HR Interview</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Platform *
                    </label>
                    <select
                      value={scheduleForm.provider}
                      onChange={(e) => setScheduleForm({...scheduleForm, provider: e.target.value})}
                      className={fieldCls}
                    >
                      <option value="zoom">Zoom</option>
                      <option value="teams">Microsoft Teams</option>
                      <option value="gmeet">Google Meet</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Date &amp; Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleForm.scheduled_datetime}
                      onChange={(e) => setScheduleForm({...scheduleForm, scheduled_datetime: e.target.value})}
                      className={fieldCls}
                      min={new Date().toISOString().slice(0, 16)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Duration (minutes)
                    </label>
                    <select
                      value={scheduleForm.duration_minutes}
                      onChange={(e) => setScheduleForm({...scheduleForm, duration_minutes: parseInt(e.target.value)})}
                      className={fieldCls}
                    >
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    Interview Agenda
                  </label>
                  <textarea
                    value={scheduleForm.custom_agenda}
                    onChange={(e) => setScheduleForm({...scheduleForm, custom_agenda: e.target.value})}
                    rows={3}
                    className={fieldCls}
                    placeholder="Brief overview of interview structure and topics..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="recording_enabled"
                    checked={scheduleForm.recording_enabled}
                    onChange={(e) => setScheduleForm({...scheduleForm, recording_enabled: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-line rounded bg-surface"
                  />
                  <label htmlFor="recording_enabled" className="ml-2 block text-sm text-ink-muted">
                    Enable recording for this interview
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 border border-line bg-muted text-ink-muted rounded-lg hover:bg-subtle hover:text-ink transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleInterview}
                disabled={!scheduleForm.candidate_name || !scheduleForm.candidate_email || !scheduleForm.scheduled_datetime}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-subtle disabled:text-ink-subtle disabled:cursor-not-allowed text-white rounded-lg font-semibold"
              >
                Schedule Interview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MeetingTemplates = ({ currentUser, setError }) => {
  return (
    <div className="bg-surface border border-line rounded-xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400">
        <Icon name="requirements" className="w-8 h-8" />
      </div>
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/25 mb-3">
        Coming soon
      </span>
      <h3 className="text-lg font-semibold text-ink mb-1">Meeting templates are coming soon</h3>
      <p className="text-ink-subtle max-w-md mx-auto">Create reusable interview invitation and feedback templates.</p>
    </div>
  );
};

// Tenant Management Component (Superadmin Only)
const TenantManagement = ({ currentUser }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    admin_email: '',
    subscription_tier: 'starter',
    max_profiles: 50000,
    max_users: 25,
    max_searches_per_day: 10000
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenants`);
      setTenants(response.data.tenants || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch tenants');
      console.error('Fetch tenants error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/tenants`, newTenant);
      setCreateModal(false);
      setNewTenant({
        name: '',
        slug: '',
        admin_email: '',
        subscription_tier: 'starter',
        max_profiles: 50000,
        max_users: 25,
        max_searches_per_day: 10000
      });
      await fetchTenants();
    } catch (err) {
      setError('Failed to create tenant');
      console.error('Create tenant error:', err);
    } finally {
      setLoading(false);
    }
  };

  const activateTenant = async (tenantId) => {
    try {
      setLoading(true);
      await axios.put(`${API}/tenants/${tenantId}/activate`);
      await fetchTenants();
      setError('');
    } catch (err) {
      setError('Failed to activate tenant');
      console.error('Activate tenant error:', err);
    } finally {
      setLoading(false);
    }
  };

  const suspendTenant = async (tenantId, reason = 'Administrative action') => {
    try {
      setLoading(true);
      await axios.put(`${API}/tenants/${tenantId}/suspend`, { reason });
      await fetchTenants();
      setError('');
    } catch (err) {
      setError('Failed to suspend tenant');
      console.error('Suspend tenant error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (tenant) => {
    setSelectedTenant({
      id: tenant.id,
      subscription_tier: tenant.subscription_tier,
      max_profiles: tenant.max_profiles,
      max_users: tenant.max_users,
      max_searches_per_day: tenant.max_searches_per_day
    });
    setEditModal(true);
  };

  const updateTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      setLoading(true);
      
      // Check if subscription tier changed
      const originalTenant = tenants.find(t => t.id === selectedTenant.id);
      if (originalTenant.subscription_tier !== selectedTenant.subscription_tier) {
        // Upgrade subscription tier
        await axios.put(`${API}/tenants/${selectedTenant.id}/upgrade`, {
          subscription_tier: selectedTenant.subscription_tier
        });
      } else {
        // Update limits only
        await axios.put(`${API}/tenants/${selectedTenant.id}/limits`, {
          max_profiles: selectedTenant.max_profiles,
          max_users: selectedTenant.max_users,
          max_searches_per_day: selectedTenant.max_searches_per_day
        });
      }
      
      setEditModal(false);
      setSelectedTenant(null);
      await fetchTenants();
      setError('');
    } catch (err) {
      setError('Failed to update tenant');
      console.error('Update tenant error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
            <span className="text-3xl">🏢</span>
            <span>Tenant Management</span>
          </h2>
          <p className="text-gray-600 mt-2">Manage multi-tenant organizations across the platform</p>
        </div>
        <button
          onClick={() => setCreateModal(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Create Tenant</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">❌</span>
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading tenants...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Limits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{tenant.name}</div>
                      <div className="text-sm text-gray-500">{tenant.slug}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      tenant.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                      tenant.status === 'pending_activation' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status === 'pending_activation' ? 'Pending Activation' : tenant.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      tenant.subscription_tier === 'enterprise' ? 'bg-blue-100 text-blue-800' :
                      tenant.subscription_tier === 'professional' || tenant.subscription_tier === 'premium' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {tenant.subscription_tier === 'basic' ? 'Starter' :
                       tenant.subscription_tier === 'premium' ? 'Professional' :
                       tenant.subscription_tier.charAt(0).toUpperCase() + tenant.subscription_tier.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div>{tenant.max_profiles} profiles</div>
                    <div>{tenant.max_users} users</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      {tenant.status === 'pending_activation' && (
                        <button
                          onClick={() => activateTenant(tenant.id)}
                          className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                          title="Activate tenant for platform usage"
                        >
                          ✅ Activate
                        </button>
                      )}
                      {tenant.status === 'active' && (
                        <button
                          onClick={() => suspendTenant(tenant.id)}
                          className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                          title="Suspend tenant"
                        >
                          ⏸️ Suspend
                        </button>
                      )}
                      {tenant.status === 'suspended' && (
                        <button
                          onClick={() => activateTenant(tenant.id)}
                          className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                          title="Reactivate tenant"
                        >
                          ▶️ Reactivate
                        </button>
                      )}
                      {tenant.status === 'active' && (
                        <button
                          onClick={() => {
                            const tenantUrl = `https://${tenant.slug}.yourplatform.com`;
                            window.open(tenantUrl, '_blank');
                          }}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                          title="View tenant portal"
                        >
                          🌐 Portal
                        </button>
                      )}
                      {/* Edit Tenant Button - Always available for active tenants */}
                      <button
                        onClick={() => openEditModal(tenant)}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                        title="Edit subscription tier and limits"
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Tenant Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Create New Tenant</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
                <input
                  type="text"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Acme Corp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL identifier)</label>
                <input
                  type="text"
                  value={newTenant.slug}
                  onChange={(e) => setNewTenant({...newTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., acme-corp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={newTenant.admin_email}
                  onChange={(e) => setNewTenant({...newTenant, admin_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="admin@company.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier</label>
                <select
                  value={newTenant.subscription_tier}
                  onChange={(e) => setNewTenant({...newTenant, subscription_tier: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="starter">Starter ($99/mo)</option>
                  <option value="professional">Professional ($499/mo)</option>
                  <option value="growth">Growth ($899/mo)</option>
                  <option value="enterprise">Enterprise ($1,999+/mo)</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Profiles</label>
                  <input
                    type="number"
                    value={newTenant.max_profiles}
                    onChange={(e) => setNewTenant({...newTenant, max_profiles: parseInt(e.target.value) || 50000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="1000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                  <input
                    type="number"
                    value={newTenant.max_users}
                    onChange={(e) => setNewTenant({...newTenant, max_users: parseInt(e.target.value) || 25})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Daily Searches</label>
                  <input
                    type="number"
                    value={newTenant.max_searches_per_day}
                    onChange={(e) => setNewTenant({...newTenant, max_searches_per_day: parseInt(e.target.value) || 10000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={createTenant}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Tenant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {editModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Edit Tenant</h3>
              <button
                onClick={() => {
                  setEditModal(false);
                  setSelectedTenant(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subscription Tier
                </label>
                <select
                  value={selectedTenant.subscription_tier}
                  onChange={(e) => setSelectedTenant({...selectedTenant, subscription_tier: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="starter">Starter - $99/mo (1K profiles, 2 users)</option>
                  <option value="professional">Professional - $499/mo (10K profiles, 10 users)</option>
                  <option value="growth">Growth - $899/mo (25K profiles, 25 users)</option>
                  <option value="enterprise">Enterprise - $1,999+/mo (Unlimited)</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Profiles
                  </label>
                  <input
                    type="number"
                    value={selectedTenant.max_profiles}
                    onChange={(e) => setSelectedTenant({...selectedTenant, max_profiles: parseInt(e.target.value) || 50000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="1000000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Users
                  </label>
                  <input
                    type="number"
                    value={selectedTenant.max_users}
                    onChange={(e) => setSelectedTenant({...selectedTenant, max_users: parseInt(e.target.value) || 25})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Searches/Day
                  </label>
                  <input
                    type="number"
                    value={selectedTenant.max_searches_per_day}
                    onChange={(e) => setSelectedTenant({...selectedTenant, max_searches_per_day: parseInt(e.target.value) || 10000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="1000000"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <button
                  onClick={() => {
                    setEditModal(false);
                    setSelectedTenant(null);
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={updateTenant}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Tenant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tenant Analytics Dashboard Component (Superadmin Only) - Compliance-Aware
// Shows aggregate stats per tenant (NO PII), with consent-gated support access
const TenantAnalyticsDashboard = ({ currentUser }) => {
  const [analytics, setAnalytics] = useState(null);
  const [supportGrants, setSupportGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({
    reason: '',
    duration_hours: 24,
    access_level: 'read_only'
  });

  useEffect(() => {
    fetchAnalytics();
    fetchActiveSupportGrants();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenants/analytics/dashboard`);
      // Transform API response to expected structure
      const data = response.data;
      const transformedData = {
        platform_totals: {
          total_tenants: data.dashboard?.total_tenants || 0,
          active_tenants: data.dashboard?.active_tenants || 0,
          total_profiles: data.dashboard?.total_profiles_platform || 0,
          total_users: data.dashboard?.total_users_platform || 0,
          total_searches_24h: data.dashboard?.total_searches_24h || 0
        },
        tenant_stats: (data.tenants || []).map(t => ({
          ...t,
          profile_limit: t.max_profiles || 1000,
          user_limit: t.max_users || 2,
          searches_24h: t.searches_24h || 0,
          health_status: t.status === 'active' ? 'healthy' : (t.status === 'suspended' ? 'warning' : 'unknown')
        }))
      };
      setAnalytics(transformedData);
      setError('');
    } catch (err) {
      setError('Failed to fetch tenant analytics');
      console.error('Tenant analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSupportGrants = async () => {
    try {
      const response = await axios.get(`${API}/tenants/support-access/active`);
      setSupportGrants(response.data.active_grants || []);
    } catch (err) {
      console.error('Failed to fetch support grants:', err);
    }
  };

  const revokeSupportAccess = async (tenantId) => {
    try {
      await axios.post(`${API}/tenants/${tenantId}/support-access/revoke`);
      await fetchActiveSupportGrants();
    } catch (err) {
      setError('Failed to revoke support access');
      console.error('Revoke support access error:', err);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const getHealthColor = (health) => {
    if (health === 'healthy') return 'bg-green-100 text-green-800';
    if (health === 'warning') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading tenant analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">📈</span>
          <span>Tenant Analytics Dashboard</span>
        </h2>
        <p className="text-gray-600 mt-2">Per-tenant profile counts and usage metrics (No PII - Aggregate Data Only)</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">❌</span>
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Platform Totals */}
      {analytics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Tenants</h3>
              <p className="text-3xl font-bold text-blue-600">{formatNumber(analytics.platform_totals?.total_tenants)}</p>
              <p className="text-sm text-gray-500">{analytics.platform_totals?.active_tenants || 0} active</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Profiles</h3>
              <p className="text-3xl font-bold text-green-600">{formatNumber(analytics.platform_totals?.total_profiles)}</p>
              <p className="text-sm text-gray-500">Across all tenants</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{formatNumber(analytics.platform_totals?.total_users)}</p>
              <p className="text-sm text-gray-500">Platform-wide</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Searches (24h)</h3>
              <p className="text-3xl font-bold text-orange-600">{formatNumber(analytics.platform_totals?.total_searches_24h)}</p>
              <p className="text-sm text-gray-500">Last 24 hours</p>
            </div>
          </div>

          {/* Active Support Access Grants */}
          {supportGrants.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center gap-2">
                <span>🔓</span>
                Active Support Access Grants ({supportGrants.length})
              </h3>
              <div className="space-y-3">
                {supportGrants.map((grant) => (
                  <div key={grant.id} className="bg-white rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">{grant.tenant_name}</p>
                      <p className="text-sm text-gray-600">Granted by: {grant.granted_by_email}</p>
                      <p className="text-sm text-gray-500">Reason: {grant.reason}</p>
                      <p className="text-xs text-gray-400">
                        Expires: {new Date(grant.expires_at).toLocaleString()} ({grant.access_level})
                      </p>
                    </div>
                    <button
                      onClick={() => revokeSupportAccess(grant.tenant_id)}
                      className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 text-sm font-medium"
                    >
                      Revoke Access
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-Tenant Analytics Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Per-Tenant Profile Counts</h3>
              <p className="text-sm text-gray-500">Aggregate statistics only - no personally identifiable information</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profiles</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Searches (24h)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Support Access</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.tenant_stats?.map((tenant) => {
                    const hasActiveGrant = supportGrants.some(g => g.tenant_id === tenant.tenant_id && g.is_active);
                    return (
                      <tr key={tenant.tenant_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{tenant.tenant_name}</div>
                            <div className="text-sm text-gray-500">{tenant.subscription_tier}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                            tenant.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900 font-medium">{formatNumber(tenant.profile_count)}</div>
                          <div className="text-xs text-gray-500">
                            {tenant.profile_limit === -1 ? 'Unlimited' : `/ ${formatNumber(tenant.profile_limit)}`}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900">{formatNumber(tenant.user_count)}</div>
                          <div className="text-xs text-gray-500">
                            {tenant.user_limit === -1 ? 'Unlimited' : `/ ${formatNumber(tenant.user_limit)}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-900">
                          {formatNumber(tenant.searches_24h)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getHealthColor(tenant.health_status)}`}>
                            {tenant.health_status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {hasActiveGrant ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Access Granted
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              No Access
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compliance Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-blue-500 text-xl">ℹ️</span>
              <div>
                <h4 className="font-medium text-blue-800">Data Privacy Compliance</h4>
                <p className="text-sm text-blue-700 mt-1">
                  This dashboard displays aggregate statistics only. No personally identifiable information (PII) or profile content is shown.
                  To access tenant profile data for support purposes, the tenant administrator must explicitly grant consent via their admin panel.
                  All support access is time-limited, auditable, and can be revoked at any time.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh Analytics
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Billing Usage Dashboard Component (Superadmin Only)
const BillingUsageDashboard = ({ currentUser }) => {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  useEffect(() => {
    fetchUsageData();
  }, [selectedPeriod]);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      
      switch (selectedPeriod) {
        case '7d':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(endDate.getDate() - 90);
          break;
      }

      const response = await fetch(`${BACKEND_URL}/usage-summary?period_start=${startDate.toISOString()}&period_end=${endDate.toISOString()}`);
      const data = await response.json();
      
      setUsage(data.usage_summaries || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch usage data');
      console.error('Billing fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const calculateTotals = () => {
    return usage.reduce((totals, tenant) => ({
      totalCost: totals.totalCost + (tenant.current_usage_cost || 0),
      totalUsers: totals.totalUsers + (tenant.total_active_users || 0),
      totalProfiles: totals.totalProfiles + (tenant.total_profiles_processed || 0),
      totalTokens: totals.totalTokens + (tenant.total_llm_tokens || 0)
    }), { totalCost: 0, totalUsers: 0, totalProfiles: 0, totalTokens: 0 });
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">💰</span>
          <span>Billing & Usage Dashboard</span>
        </h2>
        <p className="text-gray-600 mt-2">Tenant usage metrics and billing overview</p>
      </div>

      {error && <ErrorAlert message={error} />}

      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
        <button
          onClick={fetchUsageData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            Total Revenue
            <span className="ml-1 text-xs text-gray-400" title="Estimated monthly revenue from all active tenants based on usage metrics">ℹ️</span>
          </h3>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(totals.totalCost)}</p>
          <p className="text-sm text-gray-500">Calculated from usage patterns across all tenants</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            Active Users
            <span className="ml-1 text-xs text-gray-400" title="Users who have logged in during the selected time period">ℹ️</span>
          </h3>
          <p className="text-3xl font-bold text-blue-600">{totals.totalUsers.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Users with login activity in selected period</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            Profiles Processed
            <span className="ml-1 text-xs text-gray-400" title="Total CV/resume profiles uploaded and processed in the selected period">ℹ️</span>
          </h3>
          <p className="text-3xl font-bold text-blue-600">{totals.totalProfiles.toLocaleString()}</p>
          <p className="text-sm text-gray-500">CV uploads during selected period</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            LLM Tokens
            <span className="ml-1 text-xs text-gray-400" title="Total AI language model tokens consumed for searches, analysis, and processing">ℹ️</span>
          </h3>
          <p className="text-3xl font-bold text-orange-600">{(totals.totalTokens / 1000000).toFixed(1)}M</p>
          <p className="text-sm text-gray-500">Tokens used for AI operations in selected period</p>
        </div>
      </div>

      {/* Usage Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Tenant Usage Breakdown</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading usage data...</p>
          </div>
        ) : usage.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No usage data available for the selected period
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profiles</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LLM Tokens</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usage.map((tenant) => (
                  <tr key={tenant.tenant_id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{tenant.tenant_name || 'Unknown'}</div>
                        <div className="text-sm text-gray-500">{tenant.tenant_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {(tenant.total_active_users || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {(tenant.total_profiles_processed || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {((tenant.total_llm_tokens || 0) / 1000).toFixed(0)}K
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(tenant.current_usage_cost || 0)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        (tenant.usage_trend_7d || 0) > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(tenant.usage_trend_7d || 0) > 0 ? '↗️' : '↘️'} 
                        {Math.abs(tenant.usage_trend_7d || 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Cross-Tenant User Directory Component (Superadmin Only)
const CrossTenantUserDirectory = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    tenant_id: '',
    role: '',
    status: '',
    search: ''
  });
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchAnalytics();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('limit', '100');

      // Use the correct cross-tenant endpoint for superadmin
      const response = await axios.get(`${API}/admin/users/cross-tenant?${params}`);
      setUsers(response.data || []); // Cross-tenant endpoint returns array directly
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Users fetch error:', err);
      setUsers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Get analytics data from system stats and user data
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/users?limit=1000`)
      ]);
      
      const users = usersResponse.data.users || [];
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const activeUsers30d = users.filter(user => {
        return user.last_login && new Date(user.last_login) > thirtyDaysAgo;
      }).length;
      
      const suspendedUsers = users.filter(user => user.status === 'suspended').length;
      const highRiskUsers = users.filter(user => user.status === 'inactive').length;
      
      setAnalytics({
        total_users: users.length,
        active_users_30d: activeUsers30d,
        suspended_users: suspendedUsers,
        high_risk_users: highRiskUsers
      });
    } catch (err) {
      console.error('Analytics fetch error:', err);
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      // For now, this functionality is not implemented in the backend
      // We would need proper user management endpoints
      setError(`User status update not yet implemented for cross-tenant management`);
      console.log(`Would update user ${userId} to ${newStatus}`);
      
      // TODO: Implement proper user management endpoints
      // await axios.put(`${API}/users/${userId}`, { status: newStatus });
      // await fetchUsers();
    } catch (err) {
      setError(`Failed to ${newStatus} user`);
      console.error('Update user status error:', err);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      superadmin: 'bg-blue-100 text-blue-800',
      admin: 'bg-blue-100 text-blue-800',
      auditor: 'bg-orange-100 text-orange-800',
      recruiter: 'bg-green-100 text-green-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getActivityLevel = (user) => {
    const total = (user.login_count_30d || 0) + (user.api_calls_30d || 0) + (user.searches_30d || 0);
    if (total === 0) return { level: 'inactive', color: 'text-gray-500' };
    if (total < 10) return { level: 'low', color: 'text-yellow-600' };
    if (total < 100) return { level: 'medium', color: 'text-blue-600' };
    return { level: 'high', color: 'text-green-600' };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">👥</span>
          <span>Cross-Tenant User Directory</span>
        </h2>
        <p className="text-gray-600 mt-2">Manage users across all tenants</p>
      </div>

      {error && <ErrorAlert message={error} />}

      {/* Analytics Summary */}
      {analytics.total_users && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Users</h3>
            <p className="text-3xl font-bold text-blue-600">{analytics.total_users}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold text-gray-900">Active (30d)</h3>
            <p className="text-3xl font-bold text-green-600">{analytics.active_users_30d || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold text-gray-900">High Risk</h3>
            <p className="text-3xl font-bold text-red-600">{analytics.high_risk_users || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold text-gray-900">Suspended</h3>
            <p className="text-3xl font-bold text-orange-600">{analytics.suspended_users || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="Name or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({...filters, role: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              <option value="superadmin">Superadmin</option>
              <option value="admin">Admin</option>
              <option value="auditor">Auditor</option>
              <option value="recruiter">Recruiter</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
            <input
              type="text"
              value={filters.tenant_id}
              onChange={(e) => setFilters({...filters, tenant_id: e.target.value})}
              placeholder="Tenant ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Users ({users.length})</h3>
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No users found matching the current filters
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  const activity = getActivityLevel(user);
                  return (
                    <tr key={user.id}>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm text-gray-900">{user.tenant_name || 'Platform'}</div>
                          <div className="text-xs text-gray-500">{user.tenant_id || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-medium ${activity.color}`}>
                          {activity.level}
                        </span>
                        <div className="text-xs text-gray-500">
                          {user.login_count_30d || 0} logins
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          {user.status === 'active' && user.role !== 'superadmin' && (
                            <button
                              onClick={() => updateUserStatus(user.user_id, 'suspended')}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Suspend user"
                            >
                              ⏸️
                            </button>
                          )}
                          {user.status === 'suspended' && (
                            <button
                              onClick={() => updateUserStatus(user.user_id, 'active')}
                              className="text-green-600 hover:text-green-900"
                              title="Activate user"
                            >
                              ▶️
                            </button>
                          )}
                          {user.mfa_enabled && (
                            <span className="text-green-600" title="MFA Enabled">🔐</span>
                          )}
                          {user.account_locked && (
                            <span className="text-red-600" title="Account Locked">🔒</span>
                          )}
                          {(user.risk_score || 0) > 0.7 && (
                            <span className="text-red-600" title="High Risk">⚠️</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Smart JD Builder Components ====================

// Smart JD Builder Component (Tenant Users)
const SmartJDBuilder = ({ currentUser }) => {
  const [jds, setJds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuickGenerate, setShowQuickGenerate] = useState(false);
  const [selectedJD, setSelectedJD] = useState(null);
  const [editingJD, setEditingJD] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [generating, setGenerating] = useState(false);
  const [quickBrief, setQuickBrief] = useState('');
  const [generatedJD, setGeneratedJD] = useState(null);
  const [saving, setSaving] = useState(false);

  // AutoMatch state
  const [autoMatchResults, setAutoMatchResults] = useState([]);
  const [loadingAutoMatch, setLoadingAutoMatch] = useState(false);
  const [autoMatchError, setAutoMatchError] = useState(null);
  const [runningAutoMatch, setRunningAutoMatch] = useState(false);
  const [selectedMatchDetail, setSelectedMatchDetail] = useState(null);

  // New JD form state
  const [newJD, setNewJD] = useState({
    title: '',
    department: '',
    experience_level: 'mid',
    experience_years_min: 0,
    experience_years_max: 5,
    employment_type: 'full_time',
    locations: [],
    is_remote: false,
    remote_type: 'onsite',
    salary_currency: 'INR',
    salary_min: null,
    salary_max: null,
    positions_count: 1,
    tone: 'formal'
  });

  useEffect(() => {
    fetchJDs();
  }, [statusFilter]);

  const fetchJDs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${BACKEND_URL}/api/jd?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch job descriptions');

      const data = await response.json();
      // API returns 'items' not 'jds'
      setJds(data.items || data.jds || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // AutoMatch Functions
  const fetchAutoMatchResults = async (jdId) => {
    if (!jdId) return;
    setLoadingAutoMatch(true);
    setAutoMatchError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/automatch/jd/${jdId}/matches?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        if (response.status === 404) {
          setAutoMatchResults([]);
          return;
        }
        throw new Error('Failed to fetch AutoMatch results');
      }
      const data = await response.json();
      setAutoMatchResults(data || []);
    } catch (err) {
      setAutoMatchError(err.message);
      setAutoMatchResults([]);
    } finally {
      setLoadingAutoMatch(false);
    }
  };

  const runAutoMatchForJD = async (jdId) => {
    if (!jdId) return;
    setRunningAutoMatch(true);
    setAutoMatchError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/automatch/run/jd/${jdId}?max_matches=20&min_score=0.70`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to run AutoMatch');
      const result = await response.json();
      // Refresh the match results
      await fetchAutoMatchResults(jdId);
      return result;
    } catch (err) {
      setAutoMatchError(err.message);
    } finally {
      setRunningAutoMatch(false);
    }
  };

  const submitMatchFeedback = async (matchId, accepted, reason = null) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/automatch/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ match_id: matchId, accepted, reason })
      });
      if (!response.ok) throw new Error('Failed to submit feedback');
      // Update local state to reflect feedback
      setAutoMatchResults(prev => prev.map(m =>
        m.match_id === matchId ? { ...m, user_feedback: { accepted, reason } } : m
      ));
    } catch (err) {
      console.error('Failed to submit match feedback:', err);
    }
  };

  // Load AutoMatch results when viewing a JD
  useEffect(() => {
    if (selectedJD && selectedJD.id && selectedJD.status === 'active') {
      fetchAutoMatchResults(selectedJD.id);
    } else {
      setAutoMatchResults([]);
    }
  }, [selectedJD?.id]);

  const handleQuickGenerate = async () => {
    if (!quickBrief.trim()) return;

    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/jd/quick-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ brief: quickBrief })
      });

      if (!response.ok) throw new Error('Failed to generate JD');

      const data = await response.json();
      setGeneratedJD(data);
      setShowQuickGenerate(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateJD = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/jd`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newJD)
      });

      if (!response.ok) throw new Error('Failed to create JD');

      setShowCreateModal(false);
      setNewJD({
        title: '',
        department: '',
        experience_level: 'mid',
        experience_years_min: 0,
        experience_years_max: 5,
        employment_type: 'full_time',
        locations: [],
        is_remote: false,
        remote_type: 'onsite',
        salary_currency: 'INR',
        salary_min: null,
        salary_max: null,
        positions_count: 1,
        tone: 'formal'
      });
      fetchJDs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusChange = async (jdId, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/jd/${jdId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      fetchJDs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveGeneratedJD = async () => {
    if (!generatedJD) return;

    try {
      const token = localStorage.getItem('auth_token');

      // Helper to convert requirement objects to strings
      const normalizeArrayItems = (items) => {
        if (!items || !Array.isArray(items)) return [];
        return items.map(item => {
          if (typeof item === 'string') return item;
          if (typeof item === 'object' && item !== null) {
            // Convert {name, description} objects to readable strings
            if (item.name && item.description) {
              return `${item.name}: ${item.description}`;
            }
            if (item.name) return item.name;
            if (item.description) return item.description;
            return JSON.stringify(item);
          }
          return String(item);
        });
      };

      // Construct JD data from generated content
      const jdData = {
        title: generatedJD.parsed_input?.title || 'Untitled Position',
        department: generatedJD.parsed_input?.department || '',
        experience_level: generatedJD.parsed_input?.experience_level || 'mid',
        experience_years_min: generatedJD.parsed_input?.experience_years_min || 0,
        experience_years_max: generatedJD.parsed_input?.experience_years_max || null,
        employment_type: generatedJD.parsed_input?.employment_type || 'full_time',
        locations: generatedJD.parsed_input?.locations || [],
        is_remote: generatedJD.parsed_input?.is_remote || false,
        remote_type: generatedJD.parsed_input?.remote_type || 'onsite',
        tone: generatedJD.parsed_input?.tone || 'formal',
        // Rich generated content - normalize arrays to ensure string items
        intro_blurb: generatedJD.generated_jd?.intro_blurb || '',
        summary: generatedJD.generated_jd?.summary || '',
        description: generatedJD.generated_jd?.description || '',
        responsibilities: normalizeArrayItems(generatedJD.generated_jd?.responsibilities),
        requirements: normalizeArrayItems(generatedJD.generated_jd?.requirements),
        nice_to_have: normalizeArrayItems(generatedJD.generated_jd?.nice_to_have),
        benefits: normalizeArrayItems(generatedJD.generated_jd?.benefits),
        diversity_statement: generatedJD.generated_jd?.diversity_statement || '',
        tags: generatedJD.generated_jd?.extracted_skills || []
      };

      const response = await fetch(`${BACKEND_URL}/api/jd`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jdData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save JD');
      }

      // Success - close modal and refresh list
      setGeneratedJD(null);
      setQuickBrief('');
      fetchJDs();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-subtle text-ink-muted border border-line',
      active: 'bg-green-500/150/15 text-green-400 border border-green-500/25',
      paused: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
      closed: 'bg-red-500/100/15 text-red-400 border border-red-500/25',
      archived: 'bg-slate-500/15 text-slate-300 border border-slate-500/25'
    };
    return colors[status] || 'bg-subtle text-ink-muted border border-line';
  };

  const filteredJDs = jds.filter(jd =>
    !searchTerm ||
    jd.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jd.jd_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ink tracking-tight">Requirements</h2>
          <p className="text-ink-muted mt-1">Create, manage and activate role requirements for AI matching.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowQuickGenerate(true)}
            className="arise-btn-primary h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-2"
          >
            <Icon name="sparkles" className="w-4 h-4" /> Quick Generate
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="arise-btn-secondary h-10 px-4 rounded-lg text-sm font-semibold flex items-center gap-2"
          >
            <span className="text-base leading-none">+</span> New
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/100/10 border border-red-500/25 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          placeholder="Search requirements..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 h-10 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 h-10 bg-surface text-ink border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* JD List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-2 text-ink-muted">Loading job descriptions...</p>
        </div>
      ) : filteredJDs.length === 0 ? (
        <EmptyState
          type="no-data"
          title="No Job Descriptions Yet"
          reason="Create your first AI-powered job description to start matching candidates"
          action="Quick Generate with AI"
          onAction={() => setShowQuickGenerate(true)}
          secondaryAction="Create Manually"
          onSecondaryAction={() => setShowCreateModal(true)}
          icon="jd"
          preview="JDs will appear here with status badges, candidate counts, and AutoMatch controls"
          nbaVariant="primary"
        />
      ) : (
        <div className="grid gap-4">
          {filteredJDs.map((jd) => (
            <div key={jd.id} className="arise-panel rounded-2xl p-5 transition-colors hover:border-blue-500/30">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-ink">{jd.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(jd.status)}`}>
                      {jd.status?.toUpperCase()}
                    </span>
                    {/* Liquid Design: Next Step Indicator */}
                    {jd.status === 'active' && (
                      <JDNextStep
                        jd={{
                          ...jd,
                          profileCount: jd.profile_count || 0,
                          matchCount: jd.match_count || 0,
                          suggestedCount: jd.suggested_count || 0,
                          shortlistCount: jd.shortlist_count || 0,
                        }}
                        onAction={(action, jdData) => {
                          if (action === 'automatch') {
                            setSelectedJD(jdData);
                            runAutoMatchForJD(jdData.id);
                          } else if (action === 'review') {
                            setSelectedJD(jdData);
                          } else if (action === 'upload') {
                            // Navigate to upload
                            window.location.hash = 'upload';
                          }
                        }}
                        compact
                      />
                    )}
                  </div>
                  <p className="text-sm text-ink-subtle mb-2">
                    <span className="font-mono bg-subtle px-2 py-0.5 rounded">{jd.jd_code}</span>
                    {jd.department && <span className="ml-3">{jd.department}</span>}
                    {jd.client_name && (
                      <span className="ml-3 inline-flex items-center gap-1">
                        <span className="text-ink-subtle">|</span>
                        {jd.client_confidential ? (
                          <span className="text-amber-600" title={`Confidential: ${jd.client_name}`}>
                            🔒 {jd.client_display_name || 'Confidential Client'}
                          </span>
                        ) : (
                          <span className="text-blue-400">🏢 {jd.client_name}</span>
                        )}
                        {jd.client_type && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${
                            jd.client_type === 'direct' ? 'bg-green-500/150/15 text-green-400 border-green-500/25' :
                            jd.client_type === 'agency' ? 'bg-blue-500/100/15 text-blue-400 border-blue-500/25' :
                            'bg-amber-500/15 text-amber-400 border-amber-500/25'
                          }`}>
                            {jd.client_type === 'direct' ? 'Direct' : jd.client_type === 'agency' ? 'Agency' : 'Sole'}
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm text-ink-muted">
                    <span className="flex items-center gap-1">
                      <span>📍</span> {jd.locations?.join(', ') || 'Not specified'}
                    </span>
                    <span className="flex items-center gap-1">
                      <span>💼</span> {jd.experience_years_min}-{jd.experience_years_max} yrs
                    </span>
                    <span className="flex items-center gap-1">
                      <span>👥</span> {jd.positions_count} position(s)
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={jd.status}
                    onChange={(e) => handleStatusChange(jd.id, e.target.value)}
                    className="text-sm h-8 px-2 bg-surface text-ink border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    onClick={() => setSelectedJD(jd)}
                    className="h-8 px-3 text-sm font-medium text-blue-400 hover:bg-accent-soft rounded-lg transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => setEditingJD({...jd})}
                    className="h-8 px-3 text-sm font-medium text-ink-muted hover:text-ink hover:bg-muted rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
              {jd.extracted_skills?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {jd.extracted_skills.slice(0, 6).map((skill, i) => (
                    <span key={i} className="px-2 py-0.5 bg-accent-soft text-accent-ink text-xs rounded-full">
                      {skill}
                    </span>
                  ))}
                  {jd.extracted_skills.length > 6 && (
                    <span className="px-2 py-0.5 text-ink-subtle text-xs">
                      +{jd.extracted_skills.length - 6} more
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick Generate Modal */}
      {showQuickGenerate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-elevated rounded-2xl w-full max-w-2xl p-6 m-4">
            <h3 className="text-xl font-bold text-ink mb-4">Quick Generate JD with AI</h3>
            <p className="text-ink-muted mb-4">
              Describe the role in natural language and our AI will create a complete job description.
            </p>
            <textarea
              value={quickBrief}
              onChange={(e) => setQuickBrief(e.target.value)}
              placeholder="Example: Senior Backend Developer, 5+ years experience, Python and Django expertise, hybrid role in Bangalore, startup culture, competitive salary..."
              className="w-full h-32 px-4 py-3 border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowQuickGenerate(false)}
                className="px-4 py-2 text-ink-muted hover:bg-subtle rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleQuickGenerate}
                disabled={generating || !quickBrief.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <span className="animate-spin">⚙️</span> Generating...
                  </>
                ) : (
                  <>
                    <span>✨</span> Generate JD
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated JD Preview Modal */}
      {generatedJD && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-elevated rounded-2xl w-full max-w-4xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-ink mb-4">Generated Job Description</h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-ink-muted">Title</h4>
                <p className="text-ink">{generatedJD.parsed_input?.title || generatedJD.title}</p>
              </div>

              {generatedJD.generated_jd?.intro_blurb && (
                <div>
                  <h4 className="font-semibold text-ink-muted">Introduction</h4>
                  <p className="text-ink-muted">{generatedJD.generated_jd.intro_blurb}</p>
                </div>
              )}

              {generatedJD.generated_jd?.summary && (
                <div>
                  <h4 className="font-semibold text-ink-muted">Summary</h4>
                  <p className="text-ink-muted whitespace-pre-line">{generatedJD.generated_jd.summary}</p>
                </div>
              )}

              {generatedJD.generated_jd?.responsibilities && (
                <div>
                  <h4 className="font-semibold text-ink-muted">Responsibilities</h4>
                  <ul className="list-disc list-inside text-ink-muted">
                    {generatedJD.generated_jd.responsibilities.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {generatedJD.generated_jd?.requirements && (
                <div>
                  <h4 className="font-semibold text-ink-muted">Requirements</h4>
                  <ul className="list-disc list-inside text-ink-muted">
                    {generatedJD.generated_jd.requirements.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {generatedJD.generated_jd?.extracted_skills && (
                <div>
                  <h4 className="font-semibold text-ink-muted">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {generatedJD.generated_jd.extracted_skills.map((skill, i) => (
                      <span key={i} className="px-2 py-1 bg-accent-soft text-blue-400 text-sm rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setGeneratedJD(null)}
                className="px-4 py-2 text-ink-muted hover:bg-subtle rounded-lg"
              >
                Discard
              </button>
              <button
                onClick={handleSaveGeneratedJD}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View JD Modal */}
      {selectedJD && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-elevated rounded-2xl w-full max-w-4xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-ink">{selectedJD.title}</h3>
                <p className="text-sm text-ink-subtle font-mono">{selectedJD.jd_code}</p>
              </div>
              <button
                onClick={() => setSelectedJD(null)}
                className="text-ink-subtle hover:text-ink-muted text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Show options if no content */}
              {!selectedJD.intro_blurb && !selectedJD.summary && selectedJD.responsibilities?.length === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/25 p-6 rounded-xl">
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">📝</div>
                    <h4 className="font-semibold text-amber-400">No Content Yet</h4>
                    <p className="text-amber-400 text-sm">Choose how you want to add content to this JD</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Option 1: Write Manually */}
                    <div className="bg-elevated p-4 rounded-lg border border-line hover:border-blue-500/40 transition-colors">
                      <div className="text-2xl mb-2">✍️</div>
                      <h5 className="font-semibold text-ink mb-1">Write Manually</h5>
                      <p className="text-ink-muted text-sm mb-3">Write or paste your own job description content</p>
                      <button
                        onClick={() => {
                          // Switch to edit mode
                          setEditingJD({
                            ...selectedJD,
                            description: selectedJD.description || '',
                            responsibilities: selectedJD.responsibilities || [],
                            requirements: selectedJD.requirements || [],
                            nice_to_have: selectedJD.nice_to_have || [],
                            benefits: selectedJD.benefits || []
                          });
                          setSelectedJD(null);
                        }}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Open Editor
                      </button>
                    </div>

                    {/* Option 2: Generate with AI */}
                    <div className="bg-elevated p-4 rounded-lg border border-line hover:border-blue-500/40 transition-colors">
                      <div className="text-2xl mb-2">✨</div>
                      <h5 className="font-semibold text-ink mb-1">Generate with AI</h5>
                      <p className="text-ink-muted text-sm mb-3">AI will create content based on title & experience</p>
                      <button
                        onClick={async () => {
                          setGenerating(true);
                          try {
                            const token = localStorage.getItem('auth_token');
                            const brief = `${selectedJD.title}, ${selectedJD.experience_years_min}-${selectedJD.experience_years_max} years experience, ${selectedJD.department || ''}, ${selectedJD.employment_type}, ${selectedJD.remote_type}`;
                            const response = await fetch(`${BACKEND_URL}/api/jd/quick-generate`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ brief })
                            });
                            if (!response.ok) throw new Error('Failed to generate content');
                            const data = await response.json();

                            // Update the JD with generated content
                            const updateResponse = await fetch(`${BACKEND_URL}/api/jd/${selectedJD.id}`, {
                              method: 'PATCH',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify(data.generated_jd)
                            });
                            if (!updateResponse.ok) throw new Error('Failed to update JD');

                            // Refresh
                            fetchJDs();
                            setSelectedJD(null);
                          } catch (err) {
                            setError(err.message);
                          } finally {
                            setGenerating(false);
                          }
                        }}
                        disabled={generating}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                      >
                        {generating ? '⚙️ Generating...' : '✨ Generate Now'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedJD.intro_blurb && (
                <div className="bg-accent-soft border border-blue-500/20 p-4 rounded-lg">
                  <p className="text-ink-muted italic">{selectedJD.intro_blurb}</p>
                </div>
              )}

              {selectedJD.summary && (
                <div>
                  <h4 className="font-semibold text-ink-muted mb-2">About the Role</h4>
                  <p className="text-ink-muted whitespace-pre-line">{selectedJD.summary}</p>
                </div>
              )}

              {selectedJD.responsibilities?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-ink-muted mb-2">Responsibilities</h4>
                  <ul className="list-disc list-inside text-ink-muted space-y-1">
                    {selectedJD.responsibilities.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJD.requirements?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-ink-muted mb-2">Requirements</h4>
                  <ul className="list-disc list-inside text-ink-muted space-y-1">
                    {selectedJD.requirements.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJD.nice_to_have?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-ink-muted mb-2">Nice to Have</h4>
                  <ul className="list-disc list-inside text-ink-muted space-y-1">
                    {selectedJD.nice_to_have.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJD.benefits?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-ink-muted mb-2">Benefits</h4>
                  <ul className="list-disc list-inside text-ink-muted space-y-1">
                    {selectedJD.benefits.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJD.diversity_statement && (
                <div className="bg-green-500/15 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-400 mb-2">Diversity & Inclusion</h4>
                  <p className="text-green-400">{selectedJD.diversity_statement}</p>
                </div>
              )}

              {/* Ingestion Email */}
              {selectedJD.ingestion_endpoint && (
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold text-ink-muted mb-2">Application Email</h4>
                  <p className="font-mono text-sm text-blue-400">{selectedJD.ingestion_endpoint}</p>
                  <p className="text-xs text-ink-subtle mt-1">Candidates can apply by sending their CV to this email</p>
                </div>
              )}

              {/* AutoMatch Results Panel */}
              {selectedJD.status === 'active' && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <span className="text-white text-sm">AI</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-ink">AutoMatch Candidates</h4>
                        <p className="text-xs text-ink-subtle">AI-powered candidate matching</p>
                      </div>
                    </div>
                    <button
                      onClick={() => runAutoMatchForJD(selectedJD.id)}
                      disabled={runningAutoMatch}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {runningAutoMatch ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Running...
                        </>
                      ) : (
                        <>Refresh Matches</>
                      )}
                    </button>
                  </div>

                  {loadingAutoMatch && (
                    <div className="flex items-center justify-center py-8">
                      <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  )}

                  {autoMatchError && (
                    <div className="bg-red-500/100/10 border border-red-500/25 text-red-400 px-4 py-3 rounded-lg">
                      {autoMatchError}
                    </div>
                  )}

                  {!loadingAutoMatch && !autoMatchError && autoMatchResults.length === 0 && (
                    <div className="text-center py-8 bg-muted rounded-lg">
                      <div className="text-4xl mb-2">🔍</div>
                      <p className="text-ink-muted">No matches found yet</p>
                      <p className="text-ink-subtle text-sm mt-1">Click "Refresh Matches" to find matching candidates</p>
                    </div>
                  )}

                  {!loadingAutoMatch && autoMatchResults.length > 0 && (
                    <div className="space-y-3">
                      {autoMatchResults.map((match) => (
                        <div
                          key={match.match_id}
                          className="border rounded-lg p-4 hover:border-blue-500/40 transition-colors bg-elevated"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-ink">{match.candidate_name}</h5>
                                <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                  match.grade === 'A+' || match.grade === 'A' ? 'bg-green-500/15 border border-green-500/25 text-green-400' :
                                  match.grade === 'B+' || match.grade === 'B' ? 'bg-blue-500/100/15 border border-blue-500/25 text-blue-400' :
                                  match.grade === 'C+' || match.grade === 'C' ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400' :
                                  'bg-subtle text-ink'
                                }`}>
                                  {match.grade}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  match.confidence_level === 'AUTO_ASSIGN' ? 'bg-subtle border border-line text-ink-muted' :
                                  match.confidence_level === 'SUGGEST' ? 'bg-blue-500/100/15 border border-blue-500/25 text-blue-400' :
                                  'bg-subtle text-ink-muted'
                                }`}>
                                  {match.confidence_level === 'AUTO_ASSIGN' ? 'Auto-Assign' :
                                   match.confidence_level === 'SUGGEST' ? 'Suggested' : 'Logged'}
                                </span>
                              </div>
                              {match.candidate_title && (
                                <p className="text-sm text-ink-muted">{match.candidate_title}</p>
                              )}
                              {match.candidate_experience_years && (
                                <p className="text-xs text-ink-subtle">{match.candidate_experience_years} years experience</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-blue-400">
                                {Math.round(match.overall_score * 100)}%
                              </div>
                              <div className="text-xs text-ink-subtle">Match Score</div>
                            </div>
                          </div>

                          {/* Score breakdown */}
                          <div className="mt-3 grid grid-cols-5 gap-2 text-xs">
                            <div className="text-center">
                              <div className="font-semibold text-ink-muted">{Math.round(match.skills_score * 100)}%</div>
                              <div className="text-ink-subtle">Skills</div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-ink-muted">{Math.round(match.experience_score * 100)}%</div>
                              <div className="text-ink-subtle">Exp</div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-ink-muted">{Math.round(match.domain_score * 100)}%</div>
                              <div className="text-ink-subtle">Domain</div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-ink-muted">{Math.round(match.education_score * 100)}%</div>
                              <div className="text-ink-subtle">Edu</div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-ink-muted">{Math.round(match.location_score * 100)}%</div>
                              <div className="text-ink-subtle">Location</div>
                            </div>
                          </div>

                          {/* Strengths preview */}
                          {match.strengths && match.strengths.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1">
                              {match.strengths.slice(0, 3).map((s, i) => (
                                <span key={i} className="px-2 py-0.5 bg-green-500/15 text-green-400 text-xs rounded">
                                  {s.category}
                                </span>
                              ))}
                              {match.strengths.length > 3 && (
                                <span className="px-2 py-0.5 text-ink-subtle text-xs">
                                  +{match.strengths.length - 3} more
                                </span>
                              )}
                            </div>
                          )}

                          {/* Gaps preview */}
                          {match.gaps && match.gaps.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {match.gaps.slice(0, 2).map((g, i) => (
                                <span key={i} className={`px-2 py-0.5 text-xs rounded ${
                                  g.severity === 'High' ? 'bg-red-500/10 text-red-400' :
                                  g.severity === 'Medium' ? 'bg-amber-500/10 text-amber-400' :
                                  'bg-muted text-ink-muted'
                                }`}>
                                  {g.category}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-3 flex items-center justify-between pt-3 border-t">
                            <button
                              onClick={() => setSelectedMatchDetail(match)}
                              className="text-sm text-blue-400 hover:text-blue-400"
                            >
                              View Details
                            </button>
                            <div className="flex gap-2">
                              {!match.user_feedback && (
                                <>
                                  <button
                                    onClick={() => submitMatchFeedback(match.match_id, true)}
                                    className="px-3 py-1 text-xs bg-green-500/15 border border-green-500/25 text-green-400 rounded hover:bg-green-500/25"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => submitMatchFeedback(match.match_id, false)}
                                    className="px-3 py-1 text-xs bg-red-500/100/15 border border-red-500/25 text-red-400 rounded hover:bg-red-500/100/25"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {match.user_feedback && (
                                <span className={`px-3 py-1 text-xs rounded ${
                                  match.user_feedback.accepted ? 'bg-green-500/15 border border-green-500/25 text-green-400' : 'bg-red-500/100/15 border border-red-500/25 text-red-400'
                                }`}>
                                  {match.user_feedback.accepted ? 'Accepted' : 'Rejected'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit JD Modal - Full Editor */}
      {editingJD && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-elevated rounded-2xl w-full max-w-4xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-ink">Edit Job Description</h3>
                <p className="text-sm text-ink-subtle">{editingJD.jd_code}</p>
              </div>
              <button
                onClick={() => setEditingJD(null)}
                className="text-ink-subtle hover:text-ink-muted text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-ink-muted mb-3">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Job Title *</label>
                    <input
                      type="text"
                      value={editingJD.title || ''}
                      onChange={(e) => setEditingJD({...editingJD, title: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Department</label>
                    <input
                      type="text"
                      value={editingJD.department || ''}
                      onChange={(e) => setEditingJD({...editingJD, department: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Experience Level</label>
                    <select
                      value={editingJD.experience_level || 'mid'}
                      onChange={(e) => setEditingJD({...editingJD, experience_level: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="entry">Entry</option>
                      <option value="mid">Mid</option>
                      <option value="senior">Senior</option>
                      <option value="lead">Lead</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Employment Type</label>
                    <select
                      value={editingJD.employment_type || 'full_time'}
                      onChange={(e) => setEditingJD({...editingJD, employment_type: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                      <option value="freelance">Freelance</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Min Experience (Years)</label>
                    <input
                      type="number"
                      min="0"
                      value={editingJD.experience_years_min || 0}
                      onChange={(e) => setEditingJD({...editingJD, experience_years_min: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Max Experience (Years)</label>
                    <input
                      type="number"
                      min="0"
                      value={editingJD.experience_years_max || 0}
                      onChange={(e) => setEditingJD({...editingJD, experience_years_max: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Number of Positions</label>
                    <input
                      type="number"
                      min="1"
                      value={editingJD.positions_count || 1}
                      onChange={(e) => setEditingJD({...editingJD, positions_count: parseInt(e.target.value) || 1})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Tone</label>
                    <select
                      value={editingJD.tone || 'formal'}
                      onChange={(e) => setEditingJD({...editingJD, tone: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="formal">Formal</option>
                      <option value="casual">Casual</option>
                      <option value="startup">Startup</option>
                      <option value="technical">Technical</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Client Information Section */}
              <div className="bg-accent-soft p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-ink-muted mb-3">Client Information (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Client Type</label>
                    <select
                      value={editingJD.client_type || ''}
                      onChange={(e) => setEditingJD({...editingJD, client_type: e.target.value || null})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not Specified</option>
                      <option value="direct">Direct Hire</option>
                      <option value="agency">Agency</option>
                      <option value="sole_sourcing">Sole Sourcing</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Client Name (Internal)</label>
                    <input
                      type="text"
                      value={editingJD.client_name || ''}
                      onChange={(e) => setEditingJD({...editingJD, client_name: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., DBS Bank"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingJD.client_confidential || false}
                        onChange={(e) => setEditingJD({...editingJD, client_confidential: e.target.checked})}
                        className="w-4 h-4 text-amber-600 border-line rounded"
                      />
                      <span className="text-sm text-ink-muted">Keep client name confidential</span>
                    </label>
                  </div>
                  {editingJD.client_confidential && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-ink-muted mb-1">Public Display Name</label>
                      <input
                        type="text"
                        value={editingJD.client_display_name || ''}
                        onChange={(e) => setEditingJD({...editingJD, client_display_name: e.target.value})}
                        className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., A Global Bank"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Client Contact Name</label>
                    <input
                      type="text"
                      value={editingJD.client_contact_name || ''}
                      onChange={(e) => setEditingJD({...editingJD, client_contact_name: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">Client Contact Email</label>
                    <input
                      type="email"
                      value={editingJD.client_contact_email || ''}
                      onChange={(e) => setEditingJD({...editingJD, client_contact_email: e.target.value})}
                      className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-ink-muted">Job Content</h4>

                {/* Description/Summary */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Job Summary / Description</label>
                  <textarea
                    value={editingJD.description || ''}
                    onChange={(e) => setEditingJD({...editingJD, description: e.target.value, summary: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the role and what the candidate will be doing..."
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Responsibilities (one per line)</label>
                  <textarea
                    value={(editingJD.responsibilities || []).join('\n')}
                    onChange={(e) => setEditingJD({...editingJD, responsibilities: e.target.value.split('\n').filter(r => r.trim())})}
                    rows={4}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Design and develop software applications&#10;Collaborate with team members"
                  />
                </div>

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Requirements (one per line)</label>
                  <textarea
                    value={(editingJD.requirements || []).join('\n')}
                    onChange={(e) => setEditingJD({...editingJD, requirements: e.target.value.split('\n').filter(r => r.trim())})}
                    rows={4}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="5+ years experience in software development&#10;Strong knowledge of Python/JavaScript"
                  />
                </div>

                {/* Nice to Have */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Nice to Have (one per line)</label>
                  <textarea
                    value={(editingJD.nice_to_have || []).join('\n')}
                    onChange={(e) => setEditingJD({...editingJD, nice_to_have: e.target.value.split('\n').filter(r => r.trim())})}
                    rows={3}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Experience with cloud platforms&#10;Knowledge of DevOps practices"
                  />
                </div>

                {/* Benefits */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Benefits (one per line)</label>
                  <textarea
                    value={(editingJD.benefits || []).join('\n')}
                    onChange={(e) => setEditingJD({...editingJD, benefits: e.target.value.split('\n').filter(r => r.trim())})}
                    rows={3}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Competitive salary&#10;Health insurance&#10;Flexible working hours"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setEditingJD(null)}
                className="px-4 py-2 text-ink-muted hover:bg-subtle rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setSaving(true);
                  try {
                    const token = localStorage.getItem('auth_token');
                    const response = await fetch(`${BACKEND_URL}/api/jd/${editingJD.id}`, {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        title: editingJD.title,
                        department: editingJD.department,
                        experience_level: editingJD.experience_level,
                        employment_type: editingJD.employment_type,
                        experience_years_min: editingJD.experience_years_min,
                        experience_years_max: editingJD.experience_years_max,
                        positions_count: editingJD.positions_count,
                        tone: editingJD.tone,
                        client_type: editingJD.client_type,
                        client_name: editingJD.client_name,
                        client_display_name: editingJD.client_display_name,
                        client_confidential: editingJD.client_confidential,
                        client_contact_name: editingJD.client_contact_name,
                        client_contact_email: editingJD.client_contact_email,
                        description: editingJD.description,
                        summary: editingJD.description,
                        responsibilities: editingJD.responsibilities,
                        requirements: editingJD.requirements,
                        nice_to_have: editingJD.nice_to_have,
                        benefits: editingJD.benefits
                      })
                    });
                    if (!response.ok) throw new Error('Failed to save JD');
                    fetchJDs();
                    setEditingJD(null);
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving || !editingJD.title}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Match Detail Modal */}
      {selectedMatchDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-elevated rounded-2xl w-full max-w-3xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-ink">{selectedMatchDetail.candidate_name}</h3>
                  <span className={`px-2 py-0.5 text-sm font-bold rounded ${
                    selectedMatchDetail.grade === 'A+' || selectedMatchDetail.grade === 'A' ? 'bg-green-500/15 border border-green-500/25 text-green-400' :
                    selectedMatchDetail.grade === 'B+' || selectedMatchDetail.grade === 'B' ? 'bg-blue-500/100/15 border border-blue-500/25 text-blue-400' :
                    selectedMatchDetail.grade === 'C+' || selectedMatchDetail.grade === 'C' ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400' :
                    'bg-subtle text-ink'
                  }`}>
                    Grade {selectedMatchDetail.grade}
                  </span>
                </div>
                {selectedMatchDetail.candidate_title && (
                  <p className="text-ink-muted">{selectedMatchDetail.candidate_title}</p>
                )}
                <p className="text-sm text-ink-subtle mt-1">
                  Matched for: <span className="font-medium">{selectedMatchDetail.jd_title}</span> ({selectedMatchDetail.jd_code})
                </p>
              </div>
              <button
                onClick={() => setSelectedMatchDetail(null)}
                className="text-ink-subtle hover:text-ink-muted text-2xl"
              >
                ×
              </button>
            </div>

            {/* Overall Score */}
            <div className="bg-accent-soft border border-blue-500/20 p-4 rounded-xl mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ink-muted">Overall Match Score</p>
                  <p className="text-4xl font-bold text-blue-400">{Math.round(selectedMatchDetail.overall_score * 100)}%</p>
                </div>
                <div className={`px-4 py-2 rounded-lg ${
                  selectedMatchDetail.confidence_level === 'AUTO_ASSIGN' ? 'bg-subtle border border-line text-ink-muted' :
                  selectedMatchDetail.confidence_level === 'SUGGEST' ? 'bg-blue-500/100/15 border border-blue-500/25 text-blue-400' :
                  'bg-subtle text-ink-muted'
                }`}>
                  <p className="text-xs font-medium">Confidence</p>
                  <p className="text-lg font-bold">
                    {selectedMatchDetail.confidence_level === 'AUTO_ASSIGN' ? 'Auto-Assign' :
                     selectedMatchDetail.confidence_level === 'SUGGEST' ? 'Suggested' : 'Logged'}
                  </p>
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="mb-6">
              <h4 className="font-semibold text-ink-muted mb-3">Score Breakdown</h4>
              <div className="grid grid-cols-5 gap-4">
                <div className="bg-muted p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-400">{Math.round(selectedMatchDetail.skills_score * 100)}%</div>
                  <div className="text-xs text-ink-subtle mt-1">Skills</div>
                  <div className="text-xs text-ink-subtle">40% weight</div>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{Math.round(selectedMatchDetail.experience_score * 100)}%</div>
                  <div className="text-xs text-ink-subtle mt-1">Experience</div>
                  <div className="text-xs text-ink-subtle">25% weight</div>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-slate-600">{Math.round(selectedMatchDetail.domain_score * 100)}%</div>
                  <div className="text-xs text-ink-subtle mt-1">Domain</div>
                  <div className="text-xs text-ink-subtle">20% weight</div>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{Math.round(selectedMatchDetail.education_score * 100)}%</div>
                  <div className="text-xs text-ink-subtle mt-1">Education</div>
                  <div className="text-xs text-ink-subtle">8% weight</div>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-teal-600">{Math.round(selectedMatchDetail.location_score * 100)}%</div>
                  <div className="text-xs text-ink-subtle mt-1">Location</div>
                  <div className="text-xs text-ink-subtle">7% weight</div>
                </div>
              </div>
            </div>

            {/* Strengths */}
            {selectedMatchDetail.strengths && selectedMatchDetail.strengths.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-ink-muted mb-3 flex items-center gap-2">
                  <span className="text-green-500">+</span> Strengths
                </h4>
                <div className="space-y-2">
                  {selectedMatchDetail.strengths.map((strength, i) => (
                    <div key={i} className="bg-green-500/150/10 border border-green-500/25 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-green-400">{strength.category}</span>
                        <span className="text-sm text-green-600">{Math.round(strength.score * 100)}%</span>
                      </div>
                      <p className="text-sm text-green-400">{strength.details}</p>
                      {strength.matched && strength.matched.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {strength.matched.map((item, j) => (
                            <span key={j} className="px-2 py-0.5 bg-green-500/15 border border-green-500/25 text-green-400 text-xs rounded">
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gaps */}
            {selectedMatchDetail.gaps && selectedMatchDetail.gaps.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-ink-muted mb-3 flex items-center gap-2">
                  <span className="text-yellow-500">!</span> Gaps
                </h4>
                <div className="space-y-2">
                  {selectedMatchDetail.gaps.map((gap, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${
                      gap.severity === 'High' ? 'bg-red-500/10 border-red-500/25' :
                      gap.severity === 'Medium' ? 'bg-amber-500/10 border-amber-500/25' :
                      'bg-muted border-line-subtle'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium ${
                          gap.severity === 'High' ? 'text-red-400' :
                          gap.severity === 'Medium' ? 'text-amber-400' :
                          'text-ink'
                        }`}>{gap.category}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          gap.severity === 'High' ? 'bg-red-500/100/15 border border-red-500/25 text-red-400' :
                          gap.severity === 'Medium' ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400' :
                          'bg-subtle text-ink-muted'
                        }`}>{gap.severity}</span>
                      </div>
                      <p className={`text-sm ${
                        gap.severity === 'High' ? 'text-red-400' :
                        gap.severity === 'Medium' ? 'text-amber-400' :
                        'text-ink-muted'
                      }`}>{gap.details}</p>
                      {gap.missing && gap.missing.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {gap.missing.map((item, j) => (
                            <span key={j} className={`px-2 py-0.5 text-xs rounded ${
                              gap.severity === 'High' ? 'bg-red-500/100/15 border border-red-500/25 text-red-400' :
                              gap.severity === 'Medium' ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400' :
                              'bg-subtle text-ink-muted'
                            }`}>
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Red Flags */}
            {selectedMatchDetail.red_flags && selectedMatchDetail.red_flags.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-ink-muted mb-3 flex items-center gap-2">
                  <span className="text-red-500">⚠</span> Red Flags
                </h4>
                <div className="space-y-2">
                  {selectedMatchDetail.red_flags.map((flag, i) => (
                    <div key={i} className="bg-red-500/100/10 border border-red-500/25 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-red-400">{flag.type}</span>
                        <span className="text-xs px-2 py-0.5 bg-red-500/100/15 border border-red-500/25 text-red-400 rounded">{flag.severity}</span>
                      </div>
                      <p className="text-sm text-red-400">{flag.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            {selectedMatchDetail.recommendation && (
              <div className="mb-6 bg-accent-soft p-4 rounded-lg">
                <h4 className="font-semibold text-blue-400 mb-2">AI Recommendation</h4>
                <p className="text-blue-400">{selectedMatchDetail.recommendation}</p>
              </div>
            )}

            {/* Metadata */}
            <div className="text-xs text-ink-subtle border-t pt-4 flex items-center justify-between">
              <span>Matched on: {new Date(selectedMatchDetail.matched_at).toLocaleString()}</span>
              <span>Model: {selectedMatchDetail.model_version} • {selectedMatchDetail.computation_time_ms}ms</span>
            </div>

            {/* Actions */}
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setSelectedMatchDetail(null)}
                className="px-4 py-2 text-ink-muted hover:text-ink"
              >
                Close
              </button>
              {!selectedMatchDetail.user_feedback && (
                <>
                  <button
                    onClick={() => {
                      submitMatchFeedback(selectedMatchDetail.match_id, false);
                      setSelectedMatchDetail(prev => ({ ...prev, user_feedback: { accepted: false } }));
                    }}
                    className="px-4 py-2 bg-red-500/100/15 border border-red-500/25 text-red-400 rounded-lg hover:bg-red-500/100/25"
                  >
                    Reject Match
                  </button>
                  <button
                    onClick={() => {
                      submitMatchFeedback(selectedMatchDetail.match_id, true);
                      setSelectedMatchDetail(prev => ({ ...prev, user_feedback: { accepted: true } }));
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Accept Match
                  </button>
                </>
              )}
              {selectedMatchDetail.user_feedback && (
                <span className={`px-4 py-2 rounded-lg ${
                  selectedMatchDetail.user_feedback.accepted ? 'bg-green-500/15 border border-green-500/25 text-green-400' : 'bg-red-500/100/15 border border-red-500/25 text-red-400'
                }`}>
                  {selectedMatchDetail.user_feedback.accepted ? 'Match Accepted' : 'Match Rejected'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create JD Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-elevated rounded-2xl w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-ink mb-4">Create Job Description</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">Job Title *</label>
                <input
                  type="text"
                  value={newJD.title}
                  onChange={(e) => setNewJD({...newJD, title: e.target.value})}
                  className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Department</label>
                  <input
                    type="text"
                    value={newJD.department}
                    onChange={(e) => setNewJD({...newJD, department: e.target.value})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Engineering"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Experience Level</label>
                  <select
                    value={newJD.experience_level}
                    onChange={(e) => setNewJD({...newJD, experience_level: e.target.value})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry">Entry</option>
                    <option value="mid">Mid</option>
                    <option value="senior">Senior</option>
                    <option value="lead">Lead</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Min Experience (Years)</label>
                  <input
                    type="number"
                    min="0"
                    value={newJD.experience_years_min}
                    onChange={(e) => setNewJD({...newJD, experience_years_min: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Max Experience (Years)</label>
                  <input
                    type="number"
                    min="0"
                    value={newJD.experience_years_max}
                    onChange={(e) => setNewJD({...newJD, experience_years_max: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Employment Type</label>
                  <select
                    value={newJD.employment_type}
                    onChange={(e) => setNewJD({...newJD, employment_type: e.target.value})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="freelance">Freelance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">Tone</label>
                  <select
                    value={newJD.tone}
                    onChange={(e) => setNewJD({...newJD, tone: e.target.value})}
                    className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="formal">Formal</option>
                    <option value="casual">Casual</option>
                    <option value="startup">Startup</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">Number of Positions</label>
                <input
                  type="number"
                  min="1"
                  value={newJD.positions_count}
                  onChange={(e) => setNewJD({...newJD, positions_count: parseInt(e.target.value) || 1})}
                  className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Client Information Section (Optional) */}
              <div className="pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setNewJD({...newJD, showClientInfo: !newJD.showClientInfo})}
                  className="flex items-center gap-2 text-sm font-medium text-ink-muted hover:text-blue-400"
                >
                  <span className={`transform transition-transform ${newJD.showClientInfo ? 'rotate-90' : ''}`}>▶</span>
                  Client Information (Optional)
                  {newJD.client_name && <span className="text-xs bg-blue-500/100/15 border border-blue-500/25 text-blue-400 px-2 py-0.5 rounded-full ml-2">Added</span>}
                </button>

                {newJD.showClientInfo && (
                  <div className="mt-4 p-4 bg-muted rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-1">Client Type</label>
                        <select
                          value={newJD.client_type || ''}
                          onChange={(e) => setNewJD({...newJD, client_type: e.target.value || null})}
                          className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Not Specified</option>
                          <option value="direct">Direct Hire (Company posting directly)</option>
                          <option value="agency">Agency (Posting on behalf of client)</option>
                          <option value="sole_sourcing">Sole Sourcing (Exclusive mandate)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-1">Client Name (Internal)</label>
                        <input
                          type="text"
                          value={newJD.client_name || ''}
                          onChange={(e) => setNewJD({...newJD, client_name: e.target.value})}
                          className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., DBS Bank"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-500/25 rounded-lg">
                      <input
                        type="checkbox"
                        id="clientConfidential"
                        checked={newJD.client_confidential || false}
                        onChange={(e) => setNewJD({...newJD, client_confidential: e.target.checked})}
                        className="w-4 h-4 text-amber-600 border-line rounded focus:ring-amber-500"
                      />
                      <label htmlFor="clientConfidential" className="text-sm text-amber-400">
                        <span className="font-medium">Keep client name confidential</span>
                        <span className="block text-xs text-amber-600">Client name will be hidden from public JD views and candidates</span>
                      </label>
                    </div>

                    {newJD.client_confidential && (
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-1">Public Display Name</label>
                        <input
                          type="text"
                          value={newJD.client_display_name || ''}
                          onChange={(e) => setNewJD({...newJD, client_display_name: e.target.value})}
                          className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., A Global Bank, A Leading Tech Company"
                        />
                        <p className="mt-1 text-xs text-ink-subtle">This name will be shown publicly instead of the actual client name</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-1">Client Contact Name</label>
                        <input
                          type="text"
                          value={newJD.client_contact_name || ''}
                          onChange={(e) => setNewJD({...newJD, client_contact_name: e.target.value})}
                          className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., John Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-1">Client Contact Email</label>
                        <input
                          type="email"
                          value={newJD.client_contact_email || ''}
                          onChange={(e) => setNewJD({...newJD, client_contact_email: e.target.value})}
                          className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., john@client.com"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Content Section - Write or Generate */}
              <div className="pt-4 border-t">
                <label className="block text-sm font-medium text-ink-muted mb-3">Job Description Content</label>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setNewJD({...newJD, contentMode: 'manual'})}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      newJD.contentMode === 'manual'
                        ? 'border-blue-500 bg-accent-soft'
                        : 'border-line hover:border-blue-500/40'
                    }`}
                  >
                    <div className="text-2xl mb-1">✍️</div>
                    <div className="font-semibold text-ink">Write Manually</div>
                    <div className="text-sm text-ink-muted">Enter your own content</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewJD({...newJD, contentMode: 'ai'})}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      newJD.contentMode === 'ai'
                        ? 'border-blue-500 bg-accent-soft'
                        : 'border-line hover:border-blue-500/40'
                    }`}
                  >
                    <div className="text-2xl mb-1">✨</div>
                    <div className="font-semibold text-ink">Generate with AI</div>
                    <div className="text-sm text-ink-muted">AI creates content from title</div>
                  </button>
                </div>

                {/* Manual Content Fields */}
                {newJD.contentMode === 'manual' && (
                  <div className="space-y-3 bg-muted p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">Description</label>
                      <textarea
                        value={newJD.description || ''}
                        onChange={(e) => setNewJD({...newJD, description: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe the role..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">Responsibilities (one per line)</label>
                      <textarea
                        value={newJD.responsibilities || ''}
                        onChange={(e) => setNewJD({...newJD, responsibilities: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Design and develop...&#10;Collaborate with..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">Requirements (one per line)</label>
                      <textarea
                        value={newJD.requirements || ''}
                        onChange={(e) => setNewJD({...newJD, requirements: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-line rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="5+ years experience...&#10;Strong knowledge of..."
                      />
                    </div>
                  </div>
                )}

                {/* AI Generation Mode */}
                {newJD.contentMode === 'ai' && (
                  <div className="bg-accent-soft p-4 rounded-lg">
                    <p className="text-sm text-blue-400">
                      <span className="font-medium">AI will generate:</span> Description, responsibilities, requirements, nice-to-have, benefits, and diversity statement based on the job title and settings above.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewJD({...newJD, contentMode: undefined, description: '', responsibilities: '', requirements: ''});
                }}
                className="px-4 py-2 text-ink-muted hover:bg-subtle rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (newJD.contentMode === 'ai') {
                    // Generate with AI first, then save
                    setGenerating(true);
                    try {
                      const token = localStorage.getItem('auth_token');
                      const brief = `${newJD.title}, ${newJD.experience_years_min}-${newJD.experience_years_max} years experience, ${newJD.department || ''}, ${newJD.employment_type}, ${newJD.tone}`;
                      const genResponse = await fetch(`${BACKEND_URL}/api/jd/quick-generate`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ brief })
                      });
                      if (!genResponse.ok) throw new Error('Failed to generate content');
                      const genData = await genResponse.json();

                      // Now create the JD with generated content
                      const jdData = {
                        ...newJD,
                        ...genData.generated_jd,
                        ai_generated: true
                      };
                      delete jdData.contentMode;

                      const response = await fetch(`${BACKEND_URL}/api/jd`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(jdData)
                      });
                      if (!response.ok) throw new Error('Failed to create JD');

                      fetchJDs();
                      setShowCreateModal(false);
                      setNewJD({title: '', department: '', experience_level: 'mid', experience_years_min: 0, experience_years_max: 5, employment_type: 'full_time', tone: 'formal', positions_count: 1});
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setGenerating(false);
                    }
                  } else if (newJD.contentMode === 'manual') {
                    // Create with manual content
                    const jdData = {
                      ...newJD,
                      description: newJD.description,
                      summary: newJD.description,
                      responsibilities: (newJD.responsibilities || '').split('\n').filter(r => r.trim()),
                      requirements: (newJD.requirements || '').split('\n').filter(r => r.trim())
                    };
                    delete jdData.contentMode;

                    try {
                      const token = localStorage.getItem('auth_token');
                      const response = await fetch(`${BACKEND_URL}/api/jd`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(jdData)
                      });
                      if (!response.ok) throw new Error('Failed to create JD');

                      fetchJDs();
                      setShowCreateModal(false);
                      setNewJD({title: '', department: '', experience_level: 'mid', experience_years_min: 0, experience_years_max: 5, employment_type: 'full_time', tone: 'formal', positions_count: 1});
                    } catch (err) {
                      setError(err.message);
                    }
                  } else {
                    // Just create basic JD (old behavior)
                    handleCreateJD();
                  }
                }}
                disabled={!newJD.title || generating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <span className="animate-spin">⚙️</span> Generating...
                  </>
                ) : newJD.contentMode === 'ai' ? (
                  <>✨ Create with AI</>
                ) : (
                  'Create JD'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Smart JD Builder Admin Component (Superadmin - Cross-Tenant View)
const SmartJDBuilderAdmin = ({ currentUser }) => {
  const [jds, setJds] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchTenants();
    fetchAllJDs();
  }, [statusFilter]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      }
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    }
  };

  const fetchAllJDs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${BACKEND_URL}/api/jd/all-tenants?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch job descriptions');

      const data = await response.json();
      // API returns 'items' not 'jds'
      setJds(data.items || data.jds || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      closed: 'bg-red-100 text-red-700',
      archived: 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredJDs = jds.filter(jd =>
    !selectedTenant || jd.tenant_slug === selectedTenant
  );

  // Group by tenant
  const jdsByTenant = filteredJDs.reduce((acc, jd) => {
    const key = jd.tenant_name || 'Unknown Tenant';
    if (!acc[key]) acc[key] = [];
    acc[key].push(jd);
    return acc;
  }, {});

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">JD Builder - Platform View</h2>
          <p className="text-gray-600">Manage job descriptions across all tenants</p>
        </div>
        <div className="text-sm text-gray-500">
          Total: {jds.length} JDs across {Object.keys(jdsByTenant).length} tenants
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Tenants</option>
          {tenants.map(t => (
            <option key={t.id} value={t.slug}>{t.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="closed">Closed</option>
        </select>
        <button
          onClick={fetchAllJDs}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
        >
          <span>🔄</span> Refresh
        </button>
      </div>

      {/* JD List by Tenant */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading job descriptions from all tenants...</p>
        </div>
      ) : Object.keys(jdsByTenant).length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-lg font-medium text-gray-900">No Job Descriptions Found</h3>
          <p className="text-gray-600">No tenants have created job descriptions yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(jdsByTenant).map(([tenantName, tenantJDs]) => (
            <div key={tenantName} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span>🏢</span> {tenantName}
                </h3>
                <span className="text-sm text-gray-500">{tenantJDs.length} JD(s)</span>
              </div>
              <div className="divide-y divide-gray-100">
                {tenantJDs.map((jd) => (
                  <div key={jd.id || jd._id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{jd.title}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(jd.status)}`}>
                            {jd.status?.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{jd.jd_code}</span>
                          {jd.department && <span className="ml-2">• {jd.department}</span>}
                          {jd.locations?.length > 0 && <span className="ml-2">• {jd.locations.join(', ')}</span>}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {jd.created_at ? new Date(jd.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// System Health Dashboard Component (Superadmin Only)
const SystemHealthDashboard = ({ currentUser }) => {
  const [health, setHealth] = useState({});
  const [services, setServices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    fetchSystemHealth();
    
    // Auto-refresh every 15 seconds for real-time monitoring
    const interval = setInterval(fetchSystemHealth, 15000);
    setRefreshInterval(interval);
    
    return () => clearInterval(interval);
  }, []);

  const fetchSystemHealth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/system-health`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setHealth(data.system_health || {});
        setServices(data.services || []);
        setAlerts(data.alerts || []);
        setError('');
      }
    } catch (err) {
      setError('Failed to fetch system health data');
      console.error('System health fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'unhealthy':
      case 'critical': return 'text-red-600 bg-red-100';
      case 'degraded': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'warning': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'info': return 'bg-blue-100 border-blue-300 text-blue-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading system health data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <span className="text-3xl">❤️</span>
            <span>System Health Dashboard</span>
          </h2>
          <p className="text-gray-600 mt-2">NOC dashboard and system monitoring • Auto-refreshes every 15s</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchSystemHealth}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <div className={`px-4 py-2 rounded font-medium ${getStatusColor(health.overall_status)}`}>
            {health.overall_status?.toUpperCase() || 'UNKNOWN'}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Overall System Health Score */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">System Health Score</h3>
            <p className="text-sm text-gray-600">Overall system performance and availability</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-blue-600">{health.health_score || 0}%</div>
            <div className="text-sm text-gray-500">Uptime: {health.uptime_hours || 0}h</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${
                (health.health_score || 0) > 80 ? 'bg-green-500' : 
                (health.health_score || 0) > 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${health.health_score || 0}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Resource Usage Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                CPU Usage
                <span className="ml-1 text-xs text-gray-400" title="Current CPU utilization percentage">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-blue-600">{(health.cpu_usage || 0).toFixed(1)}%</p>
            </div>
            <div className="text-blue-500 text-2xl">🖥️</div>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  (health.cpu_usage || 0) > 80 ? 'bg-red-500' : 
                  (health.cpu_usage || 0) > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${health.cpu_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Load: [{(health.load_average || [0,0,0]).map(l => l.toFixed(1)).join(', ')}]
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                Memory Usage
                <span className="ml-1 text-xs text-gray-400" title="RAM utilization percentage">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-green-600">{(health.memory_usage || 0).toFixed(1)}%</p>
            </div>
            <div className="text-green-500 text-2xl">💾</div>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  (health.memory_usage || 0) > 85 ? 'bg-red-500' : 
                  (health.memory_usage || 0) > 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${health.memory_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Processes: {health.active_processes || 0}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                Disk Usage
                <span className="ml-1 text-xs text-gray-400" title="Storage utilization percentage">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-blue-600">{(health.disk_usage || 0).toFixed(1)}%</p>
            </div>
            <div className="text-blue-500 text-2xl">💽</div>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  (health.disk_usage || 0) > 85 ? 'bg-red-500' : 
                  (health.disk_usage || 0) > 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${health.disk_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            I/O operations monitoring
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                Network I/O
                <span className="ml-1 text-xs text-gray-400" title="Network traffic bytes sent/received">ℹ️</span>
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {formatBytes((health.network?.bytes_sent || 0) + (health.network?.bytes_recv || 0))}
              </p>
            </div>
            <div className="text-orange-500 text-2xl">🌐</div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            ↑ {formatBytes(health.network?.bytes_sent || 0)} sent
          </div>
          <div className="text-xs text-gray-500">
            ↓ {formatBytes(health.network?.bytes_recv || 0)} received
          </div>
        </div>
      </div>

      {/* Services Status */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Service Health</h3>
          <p className="text-sm text-gray-600">Critical system services monitoring</p>
        </div>
        <div className="p-6">
          <div className="grid gap-4">
            {services.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${
                    service.status === 'healthy' ? 'bg-green-500' :
                    service.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}></div>
                  <div>
                    <h4 className="font-medium text-gray-900">{service.name}</h4>
                    <p className="text-sm text-gray-500">{service.details}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                    {service.status.toUpperCase()}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {service.response_time}ms • {service.uptime}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts & Incidents */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Active Alerts</h3>
            <p className="text-sm text-gray-600">System alerts and incidents requiring attention</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {alerts.map((alert, index) => (
                <div key={alert.id} className={`p-4 border rounded-lg ${getAlertColor(alert.severity)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {alert.severity === 'critical' && <span className="text-red-600 text-lg">🚨</span>}
                        {alert.severity === 'warning' && <span className="text-yellow-600 text-lg">⚠️</span>}
                        {alert.severity === 'info' && <span className="text-blue-600 text-lg">ℹ️</span>}
                      </div>
                      <div>
                        <h4 className="font-medium">{alert.title}</h4>
                        <p className="text-sm mt-1">{alert.message}</p>
                        <div className="text-xs mt-2 opacity-75">
                          Component: {alert.component} • {new Date(alert.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {alert.auto_resolve && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">Auto-resolve</span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert.severity === 'critical' ? 'bg-red-200' :
                        alert.severity === 'warning' ? 'bg-yellow-200' : 'bg-blue-200'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance Trends */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Trends (24h)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {Math.round(health.performance_trends?.requests_per_hour || 0)}
            </div>
            <div className="text-sm text-gray-600">Requests/hour</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {(health.performance_trends?.avg_cpu_24h || 0).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Avg CPU</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {(health.performance_trends?.avg_response_time_24h || 0).toFixed(1)}ms
            </div>
            <div className="text-sm text-gray-600">Avg Response</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {(health.performance_trends?.error_rate_24h || 0).toFixed(2)}%
            </div>
            <div className="text-sm text-gray-600">Error Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// LLM Monitoring Dashboard Component (Superadmin Only)
const LLMMonitoringDashboard = ({ currentUser }) => {
  const [llmMetrics, setLlmMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // TODO: Fetch LLM usage metrics from backend
    setLoading(false);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-3xl">🤖</span>
          <span>LLM Monitoring Dashboard</span>
        </h2>
        <p className="text-gray-600 mt-2">AI usage tracking and optimization</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-center text-gray-500 py-8">
          🤖 LLM usage monitoring and optimization coming soon
        </p>
      </div>
    </div>
  );
};

// Feature Flag Management Component (Priority 1 - Superadmin Only)
const FeatureFlagManagement = ({ currentUser }) => {
  // State for tenant selection
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);

  // State for feature registry and tenant features
  const [featureRegistry, setFeatureRegistry] = useState([]);
  const [tenantFeatures, setTenantFeatures] = useState({ enabled_features: [], tier: 'professional' });

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [togglingFeature, setTogglingFeature] = useState(null);

  // Category filter
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch tenants and feature registry on mount
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchTenants(), fetchFeatureRegistry()]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Fetch tenant features when tenant is selected
  useEffect(() => {
    if (selectedTenantId) {
      fetchTenantFeatures(selectedTenantId);
      // Find selected tenant details
      const tenant = tenants.find(t => t.id === selectedTenantId);
      setSelectedTenant(tenant);
    } else {
      setTenantFeatures({ enabled_features: [], tier: 'professional' });
      setSelectedTenant(null);
    }
  }, [selectedTenantId, tenants]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { tenants: [...], total: N, limit: X, offset: Y }
      setTenants(response.data?.tenants || []);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setError('Failed to fetch tenants');
    }
  };

  const fetchFeatureRegistry = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/tenants/features/registry`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns { features: [...], by_category: {...}, total: N }
      setFeatureRegistry(response.data?.features || []);
    } catch (err) {
      console.error('Failed to fetch feature registry:', err);
      setError('Failed to fetch feature registry');
    }
  };

  const fetchTenantFeatures = async (tenantId) => {
    try {
      setLoadingFeatures(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/tenants/${tenantId}/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantFeatures(response.data || { enabled_features: [], tier: 'professional' });
      setError('');
    } catch (err) {
      console.error('Failed to fetch tenant features:', err);
      setError('Failed to fetch tenant features');
    } finally {
      setLoadingFeatures(false);
    }
  };

  const toggleFeature = async (featureKey, isCurrentlyEnabled) => {
    if (!selectedTenantId) return;

    setTogglingFeature(featureKey);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = isCurrentlyEnabled ? 'disable' : 'enable';

      await axios.post(
        `${API}/tenants/${selectedTenantId}/features/${endpoint}`,
        { feature_key: featureKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh tenant features
      await fetchTenantFeatures(selectedTenantId);
      setSuccessMessage(`Feature "${featureKey}" ${isCurrentlyEnabled ? 'disabled' : 'enabled'} successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to toggle feature:', err);
      // Handle different error response formats
      let errorDetail = 'Failed to toggle feature';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorDetail = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation error format
        errorDetail = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorDetail = detail.msg || detail.message || JSON.stringify(detail);
      }
      setError(errorDetail);
    } finally {
      setTogglingFeature(null);
    }
  };

  const resetToDefaults = async () => {
    if (!selectedTenantId) return;
    if (!window.confirm('Reset all features to tier defaults? This will override any custom settings.')) return;

    setLoadingFeatures(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/tenants/${selectedTenantId}/features/reset-to-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchTenantFeatures(selectedTenantId);
      setSuccessMessage('Features reset to tier defaults successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to reset features:', err);
      setError('Failed to reset features to defaults');
    } finally {
      setLoadingFeatures(false);
    }
  };

  const isFeatureEnabled = (featureKey) => {
    return tenantFeatures.enabled_features?.includes(featureKey) || false;
  };

  const getTierBadgeColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'starter': return 'bg-gray-100 text-gray-700';
      case 'professional': return 'bg-blue-100 text-blue-700';
      case 'growth': return 'bg-purple-100 text-purple-700';
      case 'enterprise': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryBadgeColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'ai features': return 'bg-purple-100 text-purple-700';
      case 'communication': return 'bg-blue-100 text-blue-700';
      case 'advanced': return 'bg-orange-100 text-orange-700';
      case 'enterprise': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Get unique categories from feature registry
  const categories = ['all', ...new Set(featureRegistry.map(f => f.category))];

  // Filter features by category
  const filteredFeatures = selectedCategory === 'all'
    ? featureRegistry
    : featureRegistry.filter(f => f.category === selectedCategory);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading feature management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
            <span className="text-3xl">🚀</span>
            <span>Tenant Feature Management</span>
          </h2>
          <p className="text-gray-600 mt-2">Enable or disable features per tenant with dependency validation</p>
          <p className="text-sm text-green-600 mt-1 flex items-center space-x-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span>Changes are saved automatically when you toggle a feature</span>
          </p>
        </div>

        {selectedTenantId && (
          <button
            onClick={resetToDefaults}
            disabled={loadingFeatures}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <span>🔄</span>
            <span>Reset to Tier Defaults</span>
          </button>
        )}
      </div>

      {/* Tenant Selector */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Select Tenant to Manage Features
        </label>
        <div className="flex items-center space-x-4">
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
          >
            <option value="">-- Choose a tenant --</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.slug}) - {tenant.subscription_tier || 'professional'}
              </option>
            ))}
          </select>

          {selectedTenant && (
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTierBadgeColor(selectedTenant.subscription_tier)}`}>
                {selectedTenant.subscription_tier || 'Professional'} Tier
              </span>
              <span className="text-sm text-gray-500">
                {tenantFeatures.enabled_features?.length || 0} features enabled
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">❌</span>
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-green-500">✅</span>
            <span className="text-green-700">{successMessage}</span>
          </div>
        </div>
      )}

      {/* No tenant selected state */}
      {!selectedTenantId && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-6xl mb-4">🏢</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Tenant</h3>
          <p className="text-gray-500">Choose a tenant from the dropdown above to manage their features</p>
        </div>
      )}

      {/* Feature Management Grid */}
      {selectedTenantId && (
        <>
          {/* Category Filter */}
          <div className="flex items-center space-x-2 mb-6 overflow-x-auto pb-2">
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Filter by:</span>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category === 'all' ? 'All Features' : category}
              </button>
            ))}
          </div>

          {loadingFeatures ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading tenant features...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredFeatures.map((feature) => {
                const enabled = isFeatureEnabled(feature.key);
                const isToggling = togglingFeature === feature.key;

                return (
                  <div
                    key={feature.key}
                    className={`border rounded-lg p-5 transition-all duration-200 ${
                      enabled
                        ? 'border-green-200 bg-green-50/50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {/* Feature Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">{feature.name}</h3>
                          {feature.is_beta && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                              BETA
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 font-mono mt-1">{feature.key}</p>
                      </div>

                      {/* Toggle Switch */}
                      <button
                        onClick={() => toggleFeature(feature.key, enabled)}
                        disabled={isToggling}
                        className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          enabled ? 'bg-green-500' : 'bg-gray-300'
                        } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 flex items-center justify-center ${
                            enabled ? 'translate-x-7' : 'translate-x-0'
                          }`}
                        >
                          {isToggling && (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          )}
                        </span>
                      </button>
                    </div>

                    {/* Feature Description */}
                    <p className="text-sm text-gray-600 mb-3">{feature.description}</p>

                    {/* Feature Metadata */}
                    <div className="space-y-2">
                      {/* Category & Tier */}
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryBadgeColor(feature.category)}`}>
                          {feature.category}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getTierBadgeColor(feature.min_tier)}`}>
                          {feature.min_tier}+ Tier
                        </span>
                      </div>

                      {/* Dependencies */}
                      {feature.dependencies && feature.dependencies.length > 0 && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Requires:</span>{' '}
                          {feature.dependencies.map((dep, idx) => (
                            <span key={dep}>
                              <span className={isFeatureEnabled(dep) ? 'text-green-600' : 'text-red-600'}>
                                {dep}
                              </span>
                              {idx < feature.dependencies.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Dependent Features Warning */}
                      {enabled && feature.key && (
                        <div className="text-xs text-amber-600">
                          {featureRegistry.filter(f => f.dependencies?.includes(feature.key) && isFeatureEnabled(f.key)).length > 0 && (
                            <span>
                              ⚠️ Required by: {featureRegistry
                                .filter(f => f.dependencies?.includes(feature.key) && isFeatureEnabled(f.key))
                                .map(f => f.key)
                                .join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Status Indicator */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${enabled ? 'text-green-600' : 'text-gray-500'}`}>
                          {enabled ? '✓ Enabled' : '○ Disabled'}
                        </span>
                        {feature.is_beta && (
                          <span className="text-xs text-orange-500">Experimental feature</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary Stats */}
          {!loadingFeatures && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {tenantFeatures.enabled_features?.length || 0}
                  </div>
                  <div className="text-sm text-green-700">Features Enabled</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {featureRegistry.length - (tenantFeatures.enabled_features?.length || 0)}
                  </div>
                  <div className="text-sm text-gray-700">Features Disabled</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedTenant?.subscription_tier || 'professional'}
                  </div>
                  <div className="text-sm text-blue-700">Current Tier</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {featureRegistry.filter(f => f.is_beta).length}
                  </div>
                  <div className="text-sm text-blue-700">Beta Features</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// RBAC Matrix Management Component (Superadmin Only)
const RBACMatrixManagement = ({ currentUser }) => {
  const [rbacMatrix, setRbacMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Define available roles
  const roles = [
    { id: 'superadmin', name: 'Superadmin', icon: '👑', desc: 'Platform owner with god-mode access across all tenants' },
    { id: 'admin', name: 'Tenant Admin', icon: '👨‍💼', desc: 'Organization owner with full control within tenant boundary' },
    { id: 'manager', name: 'Manager', icon: '👔', desc: 'Team lead overseeing recruiters with analytics access' },
    { id: 'recruiter', name: 'Recruiter', icon: '🎯', desc: 'Core operational user for candidate management workflow' },
    { id: 'hr', name: 'HR', icon: '👥', desc: 'Broader visibility with reporting and analytics access' },
    { id: 'auditor', name: 'Auditor', icon: '🔍', desc: 'Compliance role with read-only audit and security access' },
    { id: 'viewer', name: 'Viewer', icon: '👁️', desc: 'Stakeholder/observer with minimal read-only access' }
  ];

  // Define all features/pages
  const features = [
    { id: 'smart_search', name: 'Smart Search', desc: 'AI-powered candidate search', category: 'core' },
    { id: 'upload_profiles', name: 'Upload Profiles', desc: 'Process CV documents', category: 'core' },
    { id: 'profile_database', name: 'Profile Database', desc: 'Browse candidates', category: 'core' },
    { id: 'shortlist', name: 'Shortlist', desc: 'Candidate Shortlist', category: 'core' },
    { id: 'interviews', name: 'Interviews', desc: 'Schedule & manage interviews', category: 'core' },
    { id: 'analytics', name: 'Analytics', desc: 'System insights', category: 'reporting' },
    { id: 'user_management', name: 'User Management', desc: 'Manage users, roles, permissions', category: 'admin' },
    { id: 'tenant_management', name: 'Tenant Management', desc: 'Manage multi-tenant organizations', category: 'platform' },
    { id: 'email_intake', name: 'Intelligent Intake Inbox™', desc: 'STP++ Auto-Ingestion with AI Routing', category: 'automation' },
    { id: 'billing_usage', name: 'Billing & Usage Dashboard', desc: 'Tenant usage & billing', category: 'platform' },
    { id: 'user_directory', name: 'Cross-Tenant User Directory', desc: 'Manage users across tenants', category: 'platform' },
    { id: 'feature_flags', name: 'Feature Flag Management', desc: 'Control feature rollouts', category: 'platform' },
    { id: 'audit_trail', name: 'System-wide Audit Trail', desc: 'Comprehensive audit logging', category: 'security' },
    { id: 'system_health', name: 'System Health Dashboard', desc: 'NOC dashboard & monitoring', category: 'platform' },
    { id: 'security_compliance', name: 'Security & Compliance', desc: 'Security monitoring', category: 'security' },
    { id: 'observability', name: 'Platform Observability', desc: 'Real-time metrics', category: 'platform' }
  ];

  // Permission levels
  const permissionLevels = [
    { key: 'FULL', label: 'FULL', color: 'bg-green-100 text-green-800 border-green-300', desc: 'Complete CRUD access' },
    { key: 'READ', label: 'READ', color: 'bg-blue-100 text-blue-800 border-blue-300', desc: 'View-only access' },
    { key: 'LIMITED', label: 'LIMITED', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', desc: 'Restricted access' },
    { key: 'NO', label: 'NO', color: 'bg-red-100 text-red-800 border-red-300', desc: 'No access' }
  ];

  // Initialize default matrix
  const initializeMatrix = () => {
    const defaultMatrix = {};
    features.forEach(feature => {
      defaultMatrix[feature.id] = {};
      roles.forEach(role => {
        // Set default permissions based on your table
        if (role.id === 'superadmin') {
          defaultMatrix[feature.id][role.id] = 'FULL';
        } else if (role.id === 'admin') {
          defaultMatrix[feature.id][role.id] = ['tenant_management', 'user_directory', 'system_health', 'observability'].includes(feature.id) ? 'NO' : 'FULL';
        } else if (role.id === 'recruiter') {
          defaultMatrix[feature.id][role.id] = ['smart_search', 'upload_profiles', 'profile_database', 'shortlist', 'interviews'].includes(feature.id) ? 'FULL' : 
                                              ['analytics', 'email_intake', 'audit_trail'].includes(feature.id) ? 'LIMITED' : 'NO';
        } else if (role.id === 'hr') {
          defaultMatrix[feature.id][role.id] = ['smart_search', 'upload_profiles', 'profile_database', 'shortlist', 'interviews', 'analytics'].includes(feature.id) ? 'FULL' :
                                              ['user_management', 'email_intake', 'audit_trail', 'security_compliance'].includes(feature.id) ? 'READ' : 'NO';
        } else if (role.id === 'auditor') {
          defaultMatrix[feature.id][role.id] = ['analytics', 'audit_trail', 'security_compliance'].includes(feature.id) ? 'FULL' :
                                              ['profile_database', 'shortlist', 'interviews', 'user_management', 'billing_usage', 'email_intake'].includes(feature.id) ? 'READ' : 'NO';
        } else if (role.id === 'viewer') {
          defaultMatrix[feature.id][role.id] = ['smart_search', 'profile_database', 'shortlist', 'interviews', 'analytics'].includes(feature.id) ? 'READ' : 'NO';
        }
      });
    });
    return defaultMatrix;
  };

  useEffect(() => {
    loadRBACMatrix();
  }, []);

  const loadRBACMatrix = async () => {
    try {
      setLoading(true);
      
      // Load RBAC matrix from backend
      const response = await axios.get(`${API}/admin/rbac/matrix`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.data && response.data.matrix) {
        setRbacMatrix(response.data.matrix);
      } else {
        // If no matrix exists in backend, use default
        const matrix = initializeMatrix();
        setRbacMatrix(matrix);
      }
      setError('');
    } catch (err) {
      console.error('Load RBAC matrix error:', err);
      // Fallback to default matrix
      const matrix = initializeMatrix();
      setRbacMatrix(matrix);
      setError('Failed to load RBAC matrix from server. Using default settings.');
    } finally {
      setLoading(false);
    }
  };

  const updatePermission = async (featureId, roleId, newPermission) => {
    try {
      // Update local state immediately for responsiveness
      const updatedMatrix = {
        ...rbacMatrix,
        [featureId]: {
          ...rbacMatrix[featureId],
          [roleId]: newPermission
        }
      };
      setRbacMatrix(updatedMatrix);

      // TODO: Replace with actual API call
      // await axios.put(`${API}/admin/rbac-matrix/${featureId}/${roleId}`, 
      //   { permission: newPermission },
      //   { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      // );

      setSuccessMessage(`Updated ${features.find(f => f.id === featureId)?.name} permission for ${roles.find(r => r.id === roleId)?.name}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update permission');
      console.error('Update permission error:', err);
      // Revert local state on error
      loadRBACMatrix();
    }
  };

  const getNextPermission = (currentPermission) => {
    const currentIndex = permissionLevels.findIndex(p => p.key === currentPermission);
    const nextIndex = (currentIndex + 1) % permissionLevels.length;
    return permissionLevels[nextIndex].key;
  };

  const saveMatrix = async () => {
    try {
      setSaving(true);
      
      // Save RBAC matrix to backend
      await axios.post(`${API}/admin/rbac/matrix`, 
        { matrix: rbacMatrix }, 
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      
      setSuccessMessage('RBAC Matrix saved successfully! All user permissions have been updated.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to save RBAC matrix: ${err.response?.data?.detail || err.message}`);
      console.error('Save RBAC matrix error:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset to default permissions? This will overwrite all current settings.')) {
      setRbacMatrix(initializeMatrix());
      setSuccessMessage('Matrix reset to defaults');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      core: 'bg-blue-50 border-blue-200',
      reporting: 'bg-blue-50 border-blue-200',
      admin: 'bg-orange-50 border-orange-200',
      platform: 'bg-blue-50 border-blue-200',
      automation: 'bg-green-50 border-green-200',
      security: 'bg-red-50 border-red-200'
    };
    return colors[category] || 'bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading RBAC Matrix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
              <span className="text-3xl">🛡️</span>
              <span>RBAC Matrix Management</span>
            </h2>
            <p className="text-gray-600 mt-2">
              Configure role-based access control permissions across the platform. 
              Click any permission cell to cycle through: FULL → READ → LIMITED → NO
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={resetToDefaults}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Reset to Default Matrix"
            >
              🔄 Reset
            </button>
            <button
              onClick={saveMatrix}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? '💾 Saving...' : '💾 Save Matrix'}
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            ✅ {successMessage}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            ❌ {error}
          </div>
        )}

        {/* Permission Legend */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {permissionLevels.map(level => (
            <div key={level.key} className={`p-2 rounded border ${level.color} text-center`}>
              <div className="font-bold">{level.label}</div>
              <div className="text-xs">{level.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* RBAC Matrix Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {/* Header Row */}
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
                <th className="px-4 py-3 text-left font-bold text-gray-900 min-w-[300px]">
                  Feature / Page
                </th>
                {roles.map(role => (
                  <th key={role.id} className="px-3 py-3 text-center font-bold text-gray-900 min-w-[120px]">
                    <div className="flex flex-col items-center space-y-1">
                      <span className="text-lg">{role.icon}</span>
                      <span>{role.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Matrix Body */}
            <tbody>
              {features.map((feature, featureIndex) => (
                <tr key={feature.id} className={`border-b hover:bg-gray-50 ${getCategoryColor(feature.category)}`}>
                  {/* Feature Name Column */}
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-semibold text-gray-900">{feature.name}</div>
                      <div className="text-xs text-gray-500">{feature.desc}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        <span className="px-2 py-1 bg-gray-200 rounded-full text-xs uppercase">
                          {feature.category}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Permission Cells */}
                  {roles.map(role => {
                    const currentPermission = rbacMatrix[feature.id]?.[role.id] || 'NO';
                    const permissionLevel = permissionLevels.find(p => p.key === currentPermission);
                    
                    return (
                      <td key={role.id} className="px-3 py-3 text-center">
                        <button
                          onClick={() => updatePermission(feature.id, role.id, getNextPermission(currentPermission))}
                          className={`w-full px-3 py-2 rounded border font-medium transition-all hover:shadow-md active:scale-95 ${permissionLevel?.color || 'bg-gray-100 text-gray-800'}`}
                          title={`Click to change from ${currentPermission}. ${permissionLevel?.desc}`}
                        >
                          {currentPermission}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Descriptions */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">🎭 Role Definitions</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <div key={role.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-2xl">{role.icon}</span>
                <span className="font-semibold text-gray-900">{role.name}</span>
              </div>
              <p className="text-sm text-gray-600">{role.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-3">📋 How to Use RBAC Matrix</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <p>• <strong>Click any permission cell</strong> to cycle through access levels: FULL → READ → LIMITED → NO</p>
          <p>• <strong>FULL</strong>: Complete Create, Read, Update, Delete access to the feature</p>
          <p>• <strong>READ</strong>: View-only access, no modifications allowed</p>
          <p>• <strong>LIMITED</strong>: Restricted access with specific constraints (own data only, etc.)</p>
          <p>• <strong>NO</strong>: Feature not available, access completely denied</p>
          <p>• <strong>Save changes</strong> to apply across all tenants. Tenant admins will use these roles when creating users.</p>
          <p>• <strong>Collaborate with tenant admins</strong> to determine which roles they need for their organization.</p>
        </div>
      </div>
    </div>
  );
};

// Platform Observability Dashboard Component (Superadmin Only)
const PlatformObservabilityDashboard = ({ currentUser }) => {
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Initial fetch
    fetchObservabilityMetrics();

    // Start polling interval
    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchObservabilityMetrics, 60000);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Handle tab visibility changes (Edge sleeping tabs, tab switching)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Tab became visible - refresh data and restart polling
        fetchObservabilityMetrics();
        startPolling();
      }
    };

    // Start polling and listen for visibility changes
    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchObservabilityMetrics = async () => {
    try {
      setLoading(true);

      // Get auth token for authenticated requests
      const token = localStorage.getItem('auth_token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch multiple metrics in parallel (public endpoints for platform observability)
      const [statsResponse, healthResponse, auditResponse, systemHealthResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/stats`, { headers: authHeaders }).then(res => res.json()).catch(err => {
          console.error('Stats API error:', err);
          return { total_tenants: 0, total_users: 0, system_status: 'unhealthy' };
        }),
        fetch(`${API}/health`).then(res => res.json()).catch(() => ({ status: 'unhealthy' })),
        fetch(`${BACKEND_URL}/api/audit/metrics`, { headers: authHeaders }).then(res => res.json()).catch(() => ({
          total_requests: 0,
          avg_response_time: 0,
          error_rate: 0,
          llm_requests_24h: 0,
          active_users_24h: 0,
          unique_ips_24h: 0
        })),
        fetch(`${BACKEND_URL}/system-health`, { headers: authHeaders }).then(res => res.json()).catch(() => ({
          system_health: { cpu_usage: 0, memory_usage: 0, disk_usage: 0, overall_status: 'unknown', health_score: 0 }
        }))
      ]);

      // Calculate estimated tokens based on LLM requests (approximate 500 tokens per request)
      const estimated_tokens = (auditResponse.llm_requests_24h || statsResponse.llm_requests_24h || 0) * 500;
      
      setMetrics({
        tenants: {
          total: statsResponse.total_tenants || 0,
          active: statsResponse.active_tenants || 0,
          growth_24h: statsResponse.tenants_created_today || 0,
        },
        users: {
          total: statsResponse.total_users || 0,
          active_today: auditResponse.active_users_24h || statsResponse.active_users_24h || 0,
          online_now: auditResponse.unique_ips_24h || Math.floor((statsResponse.active_users_24h || 0) / 3) || Math.floor((statsResponse.total_users || 0) / 8), // Estimate concurrent users
        },
        api: {
          requests_24h: auditResponse.total_requests || statsResponse.api_requests_24h || 0,
          avg_response_time: Math.round(auditResponse.avg_response_time || statsResponse.performance?.avg_response_time || 0),
          error_rate: auditResponse.error_rate || statsResponse.performance?.error_rate || 0,
        },
        ai: {
          llm_calls_24h: auditResponse.llm_requests_24h || statsResponse.llm_requests_24h || 0,
          tokens_used: estimated_tokens,
          avg_generation_time: Math.round(auditResponse.avg_response_time || 0),
        },
        system: {
          status: systemHealthResponse.system_health?.overall_status || healthResponse.status || 'healthy',
          cpu_usage: (systemHealthResponse.system_health?.cpu_usage ?? 0).toFixed(1),
          memory_usage: (systemHealthResponse.system_health?.memory_usage ?? 0).toFixed(1),
          disk_usage: (systemHealthResponse.system_health?.disk_usage ?? 0).toFixed(1),
          uptime: statsResponse.performance?.uptime || "99.9%",
          health_score: systemHealthResponse.system_health?.health_score ?? 0
        }
      });
    } catch (error) {
      console.error('Failed to fetch observability metrics:', error);
      // Set fallback data if API calls fail
      setMetrics({
        tenants: { total: 0, active: 0, growth_24h: 0 },
        users: { total: 0, active_today: 0, online_now: 0 },
        api: { requests_24h: 0, avg_response_time: 0, error_rate: 0 },
        ai: { llm_calls_24h: 0, tokens_used: 0, avg_generation_time: 0 },
        system: { status: 'unhealthy', cpu_usage: 0, memory_usage: 0, disk_usage: 0, uptime: "0%" }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !metrics.tenants) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading platform metrics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <span className="text-3xl">📊</span>
            <span>Platform Observability</span>
          </h2>
          <p className="text-gray-600 mt-2">Real-time platform metrics and performance monitoring</p>
        </div>
        <button
          onClick={fetchObservabilityMetrics}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Tenant Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                Total Tenants
                <span className="ml-1 text-xs text-gray-400" title="Total number of tenant organizations using the platform">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-blue-600">{metrics.tenants?.total || 0}</p>
            </div>
            <div className="text-blue-500 text-2xl">🏢</div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <span className="text-green-600">↗ {metrics.tenants?.growth_24h || 0}</span>
            <span className="text-gray-500 ml-1">new today</span>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Includes active, pending, and suspended tenant organizations
          </div>
        </div>

        {/* User Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                Active Users
                <span className="ml-1 text-xs text-gray-400" title="Users who have logged in within the last 24 hours">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-green-600">{metrics.users?.online_now || 0}</p>
            </div>
            <div className="text-green-500 text-2xl">👥</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {metrics.users?.active_today || 0} active today
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Users with recent login activity across all tenants
          </div>
        </div>

        {/* API Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                API Requests (24h)
                <span className="ml-1 text-xs text-gray-400" title="Total API calls made to the platform in the last 24 hours">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-blue-600">{metrics.api?.requests_24h?.toLocaleString() || '0'}</p>
            </div>
            <div className="text-blue-500 text-2xl">⚡</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {metrics.api?.avg_response_time || 0}ms avg response
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Includes all REST API endpoints and authenticated requests
          </div>
        </div>

        {/* LLM Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 flex items-center">
                AI Tokens Used
                <span className="ml-1 text-xs text-gray-400" title="Total LLM tokens consumed for AI searches, analysis, and profile processing">ℹ️</span>
              </p>
              <p className="text-3xl font-bold text-orange-600">{metrics.ai?.tokens_used?.toLocaleString() || '0'}</p>
            </div>
            <div className="text-orange-500 text-2xl">🤖</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {metrics.ai?.llm_calls_24h || 0} AI calls today
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Includes search queries, profile analysis, and JD processing
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>CPU Usage</span>
              <span>{metrics.system?.cpu_usage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${metrics.system?.cpu_usage}%` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Memory Usage</span>
              <span>{metrics.system?.memory_usage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${metrics.system?.memory_usage}%` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Disk Usage</span>
              <span>{metrics.system?.disk_usage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${metrics.system?.disk_usage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            API Performance
            <span className="ml-2 text-xs text-gray-400" title="Real-time API performance metrics calculated from audit logs">ℹ️</span>
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 flex items-center">
                Average Response Time
                <span className="ml-1 text-xs text-gray-400" title="Mean response time for successful API calls in the last 24 hours">ℹ️</span>
              </span>
              <span className="text-lg font-semibold text-blue-600">{metrics.api?.avg_response_time}ms</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 flex items-center">
                Error Rate
                <span className="ml-1 text-xs text-gray-400" title="Percentage of failed requests vs total requests in the last 24 hours">ℹ️</span>
              </span>
              <span className="text-lg font-semibold text-red-600">{metrics.api?.error_rate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 flex items-center">
                Requests/min
                <span className="ml-1 text-xs text-gray-400" title="Average API requests per minute (calculated from 24h total ÷ 1440 minutes)">ℹ️</span>
              </span>
              <span className="text-lg font-semibold text-green-600">{Math.floor((metrics.api?.requests_24h || 0) / 1440)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Performance</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Generation Time</span>
              <span className="text-lg font-semibold text-blue-600">{metrics.ai?.avg_generation_time}ms</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tokens/Request</span>
              <span className="text-lg font-semibold text-orange-600">{Math.floor((metrics.ai?.tokens_used || 0) / Math.max(metrics.ai?.llm_calls_24h || 1, 1))}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Model Status</span>
              <span className="text-lg font-semibold text-green-600">Healthy</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Security & Compliance Dashboard Component (Superadmin Only)
const SecurityComplianceDashboard = ({ currentUser }) => {
  const [securityMetrics, setSecurityMetrics] = useState({});
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSecurityMetrics();
  }, []);

  const fetchSecurityMetrics = async () => {
    try {
      setLoading(true);
      
      // Fetch real security metrics from database
      const response = await fetch(`${BACKEND_URL}/security-metrics`);
      const data = await response.json();
      
      if (data.error) {
        console.error('Security metrics error:', data.error);
        setSecurityMetrics({});
        setThreats([]);
      } else {
        setSecurityMetrics(data.security_metrics || {});
        setThreats(data.security_events || []);
      }
    } catch (error) {
      console.error('Failed to fetch security metrics:', error);
      setSecurityMetrics({});
      setThreats([]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'blocked': return 'bg-red-100 text-red-800';
      case 'monitoring': return 'bg-yellow-100 text-yellow-800';
      case 'investigated': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        <p className="mt-2 text-gray-600">Loading security metrics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <span className="text-3xl">🔒</span>
            <span>Security & Compliance</span>
          </h2>
          <p className="text-gray-600 mt-2">Security monitoring and compliance dashboard</p>
        </div>
        <button
          onClick={fetchSecurityMetrics}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          🛡️ Refresh Security Scan
        </button>
      </div>

      {/* Security Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Failed Logins (24h)</p>
              <p className="text-3xl font-bold text-red-600">{securityMetrics.failed_logins_24h}</p>
            </div>
            <div className="text-red-500 text-2xl">🚫</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Automatic blocking active
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Suspicious IPs</p>
              <p className="text-3xl font-bold text-yellow-600">{securityMetrics.suspicious_ips}</p>
            </div>
            <div className="text-yellow-500 text-2xl">⚠️</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Under investigation
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">JWT Anomalies</p>
              <p className="text-3xl font-bold text-blue-600">{securityMetrics.jwt_anomalies}</p>
            </div>
            <div className="text-blue-500 text-2xl">🔑</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Token validation issues
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Compliance Score</p>
              <p className="text-3xl font-bold text-green-600">{securityMetrics.compliance_score}%</p>
            </div>
            <div className="text-green-500 text-2xl">✅</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Security Controls Active
          </div>
        </div>
      </div>

      {/* Threat Detection Log */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Security Events</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {threats.map((threat) => (
                <tr key={threat.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{threat.type}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityColor(threat.severity)}`}>
                      {threat.severity.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-mono">{threat.ip}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{threat.user_email}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(threat.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(threat.status)}`}>
                      {threat.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security Recommendations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Recommendations</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="text-yellow-500 text-lg">⚠️</div>
            <div>
              <p className="font-medium text-gray-900">Enable Two-Factor Authentication</p>
              <p className="text-sm text-gray-600">Only 60% of admin users have 2FA enabled. Consider enforcing 2FA for all admin accounts.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="text-green-500 text-lg">✅</div>
            <div>
              <p className="font-medium text-gray-900">Password Policy Compliant</p>
              <p className="text-sm text-gray-600">All user passwords meet the required security standards.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="text-blue-500 text-lg">🔍</div>
            <div>
              <p className="font-medium text-gray-900">Regular Security Audit</p>
              <p className="text-sm text-gray-600">Last security audit completed {new Date(securityMetrics.last_security_scan).toLocaleDateString()}. Next audit due in 30 days.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System-wide Audit Logs Component (Enhanced)
const SystemWideAuditLogs = ({ currentUser }) => {
  const [logs, setLogs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({
    action: 'all',
    tenant: 'all',
    dateRange: '7d',
    searchTerm: '',
    severity: 'all'
  });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEvents: 0,
    criticalEvents: 0,
    failedLogins: 0,
    suspiciousActivities: 0
  });

  // Fetch tenants list for filter dropdown
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${BACKEND_URL}/api/tenants?limit=100`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      }
    };
    fetchTenants();
  }, []);

  const auditActions = [
    { value: 'all', label: 'All Actions', severity: 'info' },
    { value: 'login', label: 'Login Attempts', severity: 'info' },
    { value: 'logout', label: 'Logout Events', severity: 'info' },
    { value: 'login_failed', label: 'Failed Logins ⚠️', severity: 'warning' },
    { value: 'user_created', label: 'User Creation', severity: 'info' },
    { value: 'user_updated', label: 'User Updates', severity: 'info' },
    { value: 'user_deleted', label: 'User Deletion', severity: 'warning' },
    { value: 'tenant_settings_updated', label: 'Admin Actions', severity: 'warning' },
    { value: 'profile_created', label: 'Profile Uploads', severity: 'info' },
    { value: 'profile_viewed', label: 'Profile Access', severity: 'info' },
    { value: 'profile_downloaded', label: 'Profile Downloads', severity: 'info' },
    { value: 'bulk_upload', label: 'Bulk Resume Uploads', severity: 'info' },
    { value: 'search_performed', label: 'Job Searches', severity: 'info' },
    { value: 'data_export', label: 'Data Exports', severity: 'warning' },
    { value: 'unauthorized_access_attempt', label: 'Security Violations 🚨', severity: 'critical' },
    { value: 'suspicious_activity', label: 'Suspicious Activity 🚨', severity: 'critical' },
    { value: 'permission_denied', label: 'Permission Denied', severity: 'warning' },
    { value: 'password_changed', label: 'Password Changes', severity: 'info' }
  ];

  const severityFilters = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: '🚨 Critical' },
    { value: 'warning', label: '⚠️ Warning' },
    { value: 'info', label: 'ℹ️ Info' }
  ];

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.append('action', filters.action);
      if (filters.tenant !== 'all') params.append('tenant_id', filters.tenant);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.dateRange !== 'all') {
        const days = parseInt(filters.dateRange.replace('d', ''));
        const since = new Date();
        since.setDate(since.getDate() - days);
        params.append('since', since.toISOString());
      }
      if (filters.searchTerm) params.append('search', filters.searchTerm);
      params.append('limit', '500'); // Increased limit for comprehensive view

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/audit/logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setLogs(data.logs || []);
      
      // Calculate statistics based on actual severity levels assigned by backend
      const criticalEvents = (data.logs || []).filter(log => 
        log.severity === 'critical'
      ).length;
      const failedLogins = (data.logs || []).filter(log => log.action === 'login_failed').length;
      const suspiciousActivities = (data.logs || []).filter(log => 
        log.action === 'suspicious_activity' || log.action === 'unauthorized_access_attempt'
      ).length;
      
      setStats({
        totalEvents: (data.logs || []).length,
        criticalEvents,
        failedLogins,
        suspiciousActivities
      });
      
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setLogs([]);
      setStats({ totalEvents: 0, criticalEvents: 0, failedLogins: 0, suspiciousActivities: 0 });
    }
    setLoading(false);
  };

  const getActionSeverity = (action) => {
    const actionConfig = auditActions.find(a => a.value === action);
    return actionConfig?.severity || 'info';
  };

  const getSeverityColor = (severity, isBackground = false) => {
    switch (severity) {
      case 'critical':
        return isBackground ? 'bg-red-100 border-red-300' : 'bg-red-100 text-red-800';
      case 'warning':
        return isBackground ? 'bg-yellow-100 border-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'info':
      default:
        return isBackground ? 'bg-blue-100 border-blue-300' : 'bg-blue-100 text-blue-800';
    }
  };

  const exportLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.append('action', filters.action);
      if (filters.tenant !== 'all') params.append('tenant_id', filters.tenant);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.dateRange !== 'all') {
        const days = parseInt(filters.dateRange.replace('d', ''));
        const since = new Date();
        since.setDate(since.getDate() - days);
        params.append('since', since.toISOString());
      }
      if (filters.searchTerm) params.append('search', filters.searchTerm);
      params.append('format', 'csv');
      
      window.open(`${API}/api/audit/export?${params.toString()}`, '_blank');
    } catch (error) {
      console.error('Failed to export logs:', error);
      alert('Export failed. Please try again.');
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [filters]);

  // Auto-refresh every 30 seconds for real-time monitoring
  useEffect(() => {
    const interval = setInterval(fetchAuditLogs, 30000);
    return () => clearInterval(interval);
  }, [filters]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System-wide Audit Trail</h2>
          <p className="text-sm text-gray-600 mt-1">
            Comprehensive audit logging across all tenants and operations • Auto-refreshes every 30s
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchAuditLogs}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 flex items-center"
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <button
            onClick={exportLogs}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
          >
            📄 Export CSV
          </button>
        </div>
      </div>

      {/* Security Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-medium">📊</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Total Events</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalEvents.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-sm font-medium">🚨</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Critical Events</p>
              <p className="text-2xl font-semibold text-red-600">{stats.criticalEvents}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 text-sm font-medium">🔐</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Failed Logins</p>
              <p className="text-2xl font-semibold text-yellow-600">{stats.failedLogins}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-medium">⚡</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900">Suspicious Activity</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.suspiciousActivities}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Advanced Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-blue-500">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({...filters, action: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {auditActions.map(action => (
                <option key={action.value} value={action.value}>{action.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity Level</label>
            <select
              value={filters.severity}
              onChange={(e) => setFilters({...filters, severity: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {severityFilters.map(severity => (
                <option key={severity.value} value={severity.value}>{severity.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
            <select
              value={filters.tenant}
              onChange={(e) => setFilters({...filters, tenant: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="all">All Tenants ({tenants.length})</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name || tenant.slug || tenant.id}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="1d">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="User, IP, resource..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
      
      {/* Results Summary */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Showing {logs.length.toLocaleString()} audit events</span>
            {filters.action !== 'all' && ` • ${auditActions.find(a => a.value === filters.action)?.label}`}
            {filters.tenant !== 'all' && ` • Tenant: ${tenants.find(t => t.id === filters.tenant)?.name || filters.tenant}`}
            {filters.severity !== 'all' && ` • ${severityFilters.find(s => s.value === filters.severity)?.label}`}
            <span className="ml-4 text-xs text-gray-400">Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
          <div className="text-xs text-gray-500">
            {loading && <span className="animate-pulse">🔄 Loading...</span>}
          </div>
        </div>
      </div>
      
      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resource</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP / Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      Loading comprehensive audit trail...
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    <div className="text-gray-400">
                      <div className="text-4xl mb-2">📝</div>
                      <div>No audit logs found matching your criteria</div>
                      <div className="text-xs mt-1">Try adjusting your filters or date range</div>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => {
                  const severity = getActionSeverity(log.action);
                  const isHighRisk = !log.success || severity === 'critical';
                  
                  return (
                    <tr 
                      key={index} 
                      className={`${isHighRisk ? getSeverityColor(severity, true) : ''} hover:bg-gray-50 transition-colors`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                        <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                        <div className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getSeverityColor(severity)}`}>
                          {severity === 'critical' ? '🚨' : severity === 'warning' ? '⚠️' : 'ℹ️'}
                        </span>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium text-gray-900">
                          {log.action.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <div className="text-gray-900 font-medium">
                          {log.user_email || 'System'}
                        </div>
                        <div className="text-gray-500 truncate max-w-24">
                          {log.user_id?.substring(0, 8) || 'N/A'}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <div className="text-gray-900">
                          {log.tenant_name || 'Unknown'}
                        </div>
                        <div className="text-gray-500 truncate max-w-24">
                          {log.tenant_id?.substring(0, 8) || 'N/A'}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 text-xs text-gray-900">
                        <div>
                          {log.resource_type ? (
                            <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {log.resource_type}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </div>
                        {log.resource_id && (
                          <div className="text-gray-500 mt-1 truncate max-w-20">
                            {log.resource_id.substring(0, 12)}
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-32">
                        <div className="truncate" title={JSON.stringify(log.details || {})}>
                          {log.details ? (
                            Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                              <div key={key} className="text-xs">
                                <span className="font-medium">{key}:</span> {String(value).substring(0, 20)}
                              </div>
                            ))
                          ) : (
                            'No details'
                          )}
                        </div>
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <div className="text-gray-900 font-mono">
                          {log.ip_address || 'Unknown'}
                        </div>
                        {log.user_agent && (
                          <div className="text-gray-500 truncate max-w-24" title={log.user_agent}>
                            {log.user_agent.substring(0, 20)}...
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          log.success === false
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {log.success === false ? '❌ FAIL' : '✅ OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination could be added here if needed */}
      {logs.length >= 500 && (
        <div className="mt-4 text-center">
          <div className="bg-yellow-100 border border-yellow-300 rounded px-4 py-2 text-sm text-yellow-800">
            ⚠️ Showing first 500 results. Use filters to narrow down the search for better performance.
          </div>
        </div>
      )}
    </div>
  );
};

// Analytics Component
const Analytics = ({ stats, onDrillThrough }) => {
  const [profileAnalytics, setProfileAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSupply, setOpenSupply] = useState(true);
  const [openDemand, setOpenDemand] = useState(false);
  const [openCategories, setOpenCategories] = useState(false);

  useEffect(() => {
    fetchProfileAnalytics();
  }, []);

  const fetchProfileAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/profiles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setProfileAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch profile analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrillThrough = (type, value) => {
    // Navigate to search tab with pre-filled query
    if (onDrillThrough) {
      onDrillThrough(type, value);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface border border-line rounded-xl shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-ink-muted">Loading analytics...</p>
      </div>
    );
  }

  // Presentational helpers — calm, semantic, dual-theme (no logic).
  const SectionHead = ({ icon, title, subtitle, right, size = 'lg' }) => (
    <div className="flex items-start justify-between gap-3 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400 text-lg">{icon}</div>
        <div>
          <h3 className={`${size === 'xl' ? 'text-2xl' : 'text-xl'} font-bold text-ink`}>{title}</h3>
          {subtitle && <p className="text-sm text-ink-subtle">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );

  const Metric = ({ value, label, sub, tone = 'text-ink' }) => (
    <div className="rounded-xl border border-line bg-muted p-5">
      <div className={`text-2xl md:text-3xl font-bold ${tone}`}>{value}</div>
      <div className="text-sm text-ink-muted mt-1">{label}</div>
      {sub && <div className="text-xs text-ink-subtle mt-0.5">{sub}</div>}
    </div>
  );

  const ListCard = ({ children }) => (
    <div className="rounded-xl border border-line overflow-hidden divide-y divide-line">{children}</div>
  );

  const ListRow = ({ rank, badgeTone = 'bg-subtle text-ink-muted', name, sub, value, valueSub, valueTone = 'text-ink', onClick, title }) => (
    <div
      onClick={onClick}
      title={title}
      className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors ${onClick ? 'cursor-pointer hover:bg-muted' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line text-xs font-semibold ${badgeTone}`}>{rank}</div>
        <div className="min-w-0">
          <div className="font-medium text-ink truncate">{name}</div>
          {sub && <div className="text-xs text-ink-subtle">{sub}</div>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-sm font-bold ${valueTone}`}>{value}</div>
        {valueSub && <div className="text-xs text-ink-subtle">{valueSub}</div>}
      </div>
    </div>
  );

  const metaCls = "text-xs text-ink-subtle";

  const Chevron = ({ open }) => (
    <svg className={`w-5 h-5 shrink-0 text-ink-subtle transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );

  const SectionToggle = ({ icon, title, subtitle, right, open, onToggle }) => (
    <button onClick={onToggle} className="w-full flex items-start justify-between gap-3 text-left">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/25 flex items-center justify-center text-blue-400 text-lg">{icon}</div>
        <div>
          <h3 className="text-xl font-bold text-ink">{title}</h3>
          {subtitle && <p className="text-sm text-ink-subtle">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {right}
        <Chevron open={open} />
      </div>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* ROI Dashboard + time savings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <ROIWidget />
        </div>
        <div className="lg:col-span-2 bg-surface border border-line rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <span>⚡</span>
            How ARISE Saves You Time
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-line bg-muted p-4">
              <div className="text-ink-muted font-medium mb-1 text-sm">Manual Search</div>
              <div className="text-2xl font-bold text-amber-400">2.5 hours</div>
              <div className="text-xs text-ink-subtle">per search query</div>
            </div>
            <div className="rounded-lg border border-line bg-muted p-4">
              <div className="text-ink-muted font-medium mb-1 text-sm">ARISE Search</div>
              <div className="text-2xl font-bold text-green-400">~90 seconds</div>
              <div className="text-xs text-ink-subtle">same results</div>
            </div>
            <div className="rounded-lg border border-line bg-muted p-4">
              <div className="text-ink-muted font-medium mb-1 text-sm">Manual CV Screening</div>
              <div className="text-2xl font-bold text-amber-400">5 hours</div>
              <div className="text-xs text-ink-subtle">per 100 CVs</div>
            </div>
            <div className="rounded-lg border border-line bg-muted p-4">
              <div className="text-ink-muted font-medium mb-1 text-sm">ARISE Screening</div>
              <div className="text-2xl font-bold text-blue-400">2 minutes</div>
              <div className="text-xs text-ink-subtle">per 100 CVs</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-ink-subtle text-center">
            Based on $49/hr recruiter rate • 13-person team average
          </div>
        </div>
      </div>

      {/* Profile Metrics Dashboard */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
        <SectionHead icon="📊" size="xl" title="Profile Metrics Dashboard" subtitle="Real-time insights powered by /tmp labs" />

        {/* Data Quality Indicators */}
        {profileAnalytics?.data_quality && (
          <div className="mb-6 flex flex-wrap gap-2">
            <div className="px-3 py-1 bg-muted border border-line text-ink-muted text-xs rounded-full">
              Skills coverage: {profileAnalytics.data_quality.skills_coverage}%
            </div>
            <div className="px-3 py-1 bg-muted border border-line text-ink-muted text-xs rounded-full">
              Location coverage: {profileAnalytics.data_quality.location_coverage}%
            </div>
            <div className="px-3 py-1 bg-muted border border-line text-ink-muted text-xs rounded-full">
              Parse success: {profileAnalytics.data_quality.parse_success_rate}%
            </div>
            <div className="px-3 py-1 bg-muted border border-line text-ink-muted text-xs rounded-full">
              Categorized: {profileAnalytics.data_quality.profiles_with_categories}%
            </div>
            <div className="px-3 py-1 bg-muted border border-line text-ink-muted text-xs rounded-full">
              Last 30d updates: {profileAnalytics.data_quality.last_30d_updates}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Metric value={profileAnalytics?.total_profiles || 0} label="Total Profiles" sub="Parsed & Indexed" tone="text-blue-400" />
          <Metric value={profileAnalytics?.profiles_added_today || 0} label="Added Today" sub="New profiles" />
          <Metric value={profileAnalytics?.profiles_added_this_week || 0} label="This Week" sub="Weekly growth" />
          <Metric value={profileAnalytics?.average_skills_per_profile || 0} label="Avg Skills" sub="Per profile" />
        </div>

        {/* Top Skills (Supply) */}
        {profileAnalytics?.top_skills && profileAnalytics.top_skills.data && profileAnalytics.top_skills.data.length > 0 && (
          <div>
            <button onClick={() => setOpenSupply(!openSupply)} className="w-full flex items-center justify-between gap-3 text-left mb-3">
              <h3 className="text-base font-semibold text-ink flex items-center gap-2">
                <span>🏆</span>
                <span>Top Profile Skills (Supply)</span>
                <span className="text-xs font-normal text-ink-subtle">({Math.min(10, profileAnalytics.top_skills.data.length)})</span>
              </h3>
              <div className="flex items-center gap-3">
                <div className={`${metaCls} hidden sm:block text-right`}>
                  <div>Source: {profileAnalytics.top_profile_skills?.source || "profiles"}</div>
                  <div>Unit: {profileAnalytics.top_profile_skills?.unit || "profiles (distinct)"}</div>
                </div>
                <Chevron open={openSupply} />
              </div>
            </button>
            {openSupply && (
            <ListCard>
              {profileAnalytics.top_skills.data.slice(0, 10).map((skill, index) => (
                <ListRow
                  key={index}
                  rank={index + 1}
                  name={skill.skill_name}
                  sub={`${skill.profiles} profiles`}
                  value={skill.profiles}
                  valueSub="profiles"
                  valueTone="text-blue-400"
                  onClick={() => handleDrillThrough('skill', skill.skill_name)}
                  title={`Click to view ${skill.profiles} profiles with ${skill.skill_name}`}
                />
              ))}
            </ListCard>
            )}
          </div>
        )}

        {/* Top Differentiating Skills */}
        {profileAnalytics?.differentiating_skills && profileAnalytics.differentiating_skills.data && profileAnalytics.differentiating_skills.data.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-ink flex items-center gap-2">
                <span>💎</span>
                <span>Top Differentiating Skills</span>
              </h3>
              <div className={metaCls}>
                <div>Source: {profileAnalytics.differentiating_skills?.source || "TF-IDF analysis"}</div>
                <div>Unit: {profileAnalytics.differentiating_skills?.unit || "specialized profiles"}</div>
              </div>
            </div>
            <div className="text-xs text-ink-subtle mb-3">
              Specialized expertise that sets candidates apart (1-40% frequency, excludes generic skills)
            </div>
            <ListCard>
              {profileAnalytics.differentiating_skills.data.slice(0, 8).map((skill, index) => (
                <ListRow
                  key={index}
                  rank={index + 1}
                  name={skill.skill_name}
                  sub={`${skill.profiles} profiles (${(skill.frequency * 100).toFixed(1)}%)`}
                  value={skill.profiles}
                  valueSub={`${(skill.frequency * 100).toFixed(1)}%`}
                  onClick={() => handleDrillThrough('skill', skill.skill_name)}
                  title={`Click to view ${skill.profiles} profiles with ${skill.skill_name} (${(skill.frequency * 100).toFixed(1)}% of database)`}
                />
              ))}
            </ListCard>
          </div>
        )}
      </div>

      {/* System Analytics */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
        <SectionHead icon="⚡" size="xl" title="System Analytics" subtitle="Performance metrics and insights" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric value={stats?.total_profiles || 0} label="Total Profiles" sub="In database" tone="text-blue-400" />
          <Metric value={stats?.vector_store_count || 0} label="Searchable Profiles" sub="AI-Indexed" />
          <Metric value={stats?.recent_uploads || 0} label="Recent Uploads" sub="Today" />
        </div>
      </div>

      {/* Market Insights */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
        <SectionHead icon="📈" title="Market Insights" subtitle="Talent market trends and analysis" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric
            value={`${profileAnalytics?.experience_distribution?.senior || '0'}%`}
            label="Senior Level Talent"
            sub={profileAnalytics?.experience_distribution?.senior_count
              ? `(n=${profileAnalytics.experience_distribution.senior_count} / ${profileAnalytics?.total_profiles || 1190} parsed)`
              : '5+ years experience'}
          />
          <Metric value={profileAnalytics?.geographic_distribution?.unique_locations || '0'} label="Geographic Reach" sub="Different locations" tone="text-blue-400" />
          <Metric value={profileAnalytics?.skill_categories?.variety_score || '0'} label="Skill Categories" sub="Unique skill areas" tone="text-blue-400" />
        </div>
      </div>

      {/* Hiring Demand Analysis */}
      <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
        <SectionToggle icon="📊" title="Hiring Demand Analysis" subtitle="Most sought-after roles and skills" open={openDemand} onToggle={() => setOpenDemand(!openDemand)} />
        {openDemand && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="mb-3">
              <h4 className="font-semibold text-ink flex items-center gap-2">
                <span>🎯</span>
                <span>Profile Role Distribution</span>
              </h4>
              <div className={`${metaCls} mt-1`}>Source: profile titles; Unit: profiles_with_role (distinct)</div>
            </div>
            <ListCard>
              {(profileAnalytics?.profile_role_distribution?.roles || []).map((roleData, index) => (
                <ListRow
                  key={index}
                  rank={index + 1}
                  name={roleData.role}
                  value={roleData.profiles_with_role}
                  valueTone="text-blue-400"
                  onClick={() => handleDrillThrough('role', roleData.role)}
                  title={`Click to view ${roleData.profiles_with_role} profiles with ${roleData.role} role`}
                />
              ))}
            </ListCard>
          </div>

          <div>
            <div className="mb-3">
              <h4 className="font-semibold text-ink flex items-center gap-2">
                <span>⚡</span>
                <span>Top Profile Skills</span>
              </h4>
              <div className={`${metaCls} mt-1`}>Source: profiles; Unit: profiles (distinct)</div>
            </div>
            <ListCard>
              {(profileAnalytics?.top_profile_skills?.skills?.slice(0, 4) || []).map((skillData, index) => (
                <ListRow
                  key={index}
                  rank="↗"
                  name={skillData.skill_name}
                  value={skillData.profiles}
                  valueTone="text-green-400"
                  onClick={() => handleDrillThrough('skill', skillData.skill_name)}
                  title={`Click to view ${skillData.profiles} profiles with ${skillData.skill_name}`}
                />
              ))}
            </ListCard>
          </div>
        </div>
        )}
      </div>

      {/* Geographic Distribution */}
      {profileAnalytics?.geographic_distribution && profileAnalytics.geographic_distribution.top_locations && profileAnalytics.geographic_distribution.top_locations.length > 0 && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
          <SectionHead
            icon="🌍"
            title={profileAnalytics.geographic_distribution.metadata?.title || "Geographic Distribution"}
            subtitle={profileAnalytics.geographic_distribution.metadata?.description || "Top locations in candidate pool"}
            right={profileAnalytics.geographic_distribution.metadata && (
              <div className="text-xs text-ink-subtle text-right">
                <div>Source: {profileAnalytics.geographic_distribution.metadata.source}</div>
                <div>Unit: {profileAnalytics.geographic_distribution.metadata.unit}</div>
                <div>Quality: {profileAnalytics.geographic_distribution.metadata.data_quality}</div>
              </div>
            )}
          />
          <ListCard>
            {profileAnalytics.geographic_distribution.top_locations.slice(0, 6).map((location, index) => (
              <ListRow
                key={index}
                rank={index + 1}
                name={location._id || 'Unknown'}
                value={location.count}
                valueTone="text-blue-400"
                onClick={() => handleDrillThrough('location', location._id)}
                title={`Click to view ${location.count} profiles from ${location._id}`}
              />
            ))}
            {profileAnalytics.geographic_distribution.unknown_count && profileAnalytics.geographic_distribution.unknown_count > 0 && (
              <ListRow
                rank="?"
                name="Unknown"
                value={profileAnalytics.geographic_distribution.unknown_count}
                onClick={() => handleDrillThrough('location', 'Unknown')}
                title={`Click to view ${profileAnalytics.geographic_distribution.unknown_count} profiles with unknown locations`}
              />
            )}
          </ListCard>
        </div>
      )}

      {/* Skill Categories Breakdown */}
      {profileAnalytics?.skill_categories && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
          <SectionToggle
            open={openCategories}
            onToggle={() => setOpenCategories(!openCategories)}
            icon="📊"
            title="Skill Categories Distribution"
            subtitle="Skills breakdown by technology area (hover title for insights)"
            right={(
              <span
                className="text-sm text-ink-subtle cursor-help"
                title={(() => {
                  const categories = profileAnalytics?.skill_categories?.categories || {};
                  const sortedCategories = Object.entries(categories)
                    .filter(([_, data]) => data.profiles > 0)
                    .sort((a, b) => b[1].profiles - a[1].profiles);
                  const topAreas = sortedCategories.slice(0, 3)
                    .map(([cat, data]) => `${cat} (${data.profiles})`)
                    .join(', ');
                  const varietyScore = profileAnalytics?.skill_categories?.variety_score || 0;
                  return `📊 Technology Areas Coverage: Shows how your talent pool is distributed across different technology domains. Variety Score: ${varietyScore}. Top areas: ${topAreas || 'No data yet'}. Use this to understand project capabilities and recruitment priorities.`;
                })()}
              >
                ℹ️
              </span>
            )}
          />
          {openCategories && (
          <ListCard>
            {Object.entries(profileAnalytics.skill_categories.categories || {})
              .filter(([category, data]) => data.profiles > 0)
              .sort((a, b) => b[1].profiles - a[1].profiles)
              .slice(0, 6)
              .map(([category, data], index) => {
                const strengthLevel = data.profiles > 500 ? '🔥 Strong' : data.profiles > 200 ? '✅ Good' : '⚠️ Limited';
                const tooltip = `${category}: ${data.profiles} profiles with skills in this area. ${strengthLevel} capability for ${category.toLowerCase()} projects.`;
                return (
                  <ListRow
                    key={category}
                    rank={index + 1}
                    name={category}
                    sub={`${data.profiles} profiles`}
                    value={data.profiles}
                    valueTone="text-blue-400"
                    onClick={() => handleDrillThrough('category', category)}
                    title={tooltip}
                  />
                );
              })}
          </ListCard>
          )}
        </div>
      )}

      {/* Profiles by User */}
      {profileAnalytics?.profiles_by_user && profileAnalytics.profiles_by_user.users && profileAnalytics.profiles_by_user.users.length > 0 && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6 md:p-8">
          <SectionHead icon="👥" title="Profiles by Team Member" subtitle="Who uploaded how many profiles" />
          <ListCard>
            {profileAnalytics.profiles_by_user.users.slice(0, 9).map((user, index) => (
              <ListRow
                key={user.user_id || index}
                rank={user.name ? user.name.charAt(0).toUpperCase() : '?'}
                badgeTone="bg-blue-500/15 text-blue-400"
                name={user.name || 'Unknown'}
                sub={user.email || ''}
                value={user.profile_count}
                valueSub={`${user.percentage}%`}
                valueTone="text-blue-400"
              />
            ))}
          </ListCard>
          {profileAnalytics.profiles_by_user.total_uploaders > 0 && (
            <div className="mt-4 pt-4 border-t border-line text-center">
              <span className="text-sm text-ink-subtle">
                {profileAnalytics.profiles_by_user.total_uploaders} team member{profileAnalytics.profiles_by_user.total_uploaders !== 1 ? 's' : ''} contributed profiles
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Login Component
// ============================================================================
// STATIC PAGES - Features, Pricing, About, Privacy, Terms, etc.
// ============================================================================

// Features Page Component
const FeaturesPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Powerful Features for Modern Recruiting
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Everything you need to hire smarter, faster, and more efficiently.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Feature 1 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔍</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Search</h3>
          <p className="text-gray-600">
            Natural language search that understands context. Find candidates by skills, experience, or even soft criteria like "team player."
          </p>
        </div>

        {/* Feature 2 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📄</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Smart CV Parsing</h3>
          <p className="text-gray-600">
            Automatically extract skills, experience, and qualifications from any CV format—PDF, DOCX, or even images.
          </p>
        </div>

        {/* Feature 3 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">⚡</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Bulk Upload</h3>
          <p className="text-gray-600">
            Upload hundreds of CVs at once. Our background workers process them automatically while you focus on hiring.
          </p>
        </div>

        {/* Feature 4 - AutoMatch Engine (NEW v2.0) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-300 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">v2.0</div>
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🎯</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">AutoMatch Engine</h3>
          <p className="text-gray-600">
            Automatically match candidates to job descriptions. Get AI-scored rankings based on skills, experience, and qualifications — no manual screening required.
          </p>
        </div>

        {/* Feature 5 - Identity Resolution (NEW v2.0) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-300 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">v2.0</div>
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔗</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Identity Resolution</h3>
          <p className="text-gray-600">
            AI-powered detection of duplicate profiles across your database. Smart merge suggestions keep your candidate data clean and accurate.
          </p>
        </div>

        {/* Feature 6 - Smart JD Builder (NEW) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-200 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">NEW</div>
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">✍️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Smart JD Builder</h3>
          <p className="text-gray-600">
            AI-assisted job descriptions in seconds — bias-checked, compliant, and fully editable. Generate formal, casual, or startup tones.
          </p>
        </div>

        {/* Feature 5 - Match Reasoning (NEW) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-green-200 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">NEW</div>
          <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔍</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Match Reasoning</h3>
          <p className="text-gray-600">
            See exactly why each candidate was ranked. Full transparency into AI decision-making — no black boxes, just verifiable reasoning.
          </p>
        </div>

        {/* Feature 6 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🎯</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">AI Ranking</h3>
          <p className="text-gray-600">
            Automatically rank candidates by fit score. See why each candidate matches—or doesn't—with detailed AI explanations.
          </p>
        </div>

        {/* Feature 5 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📊</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">ROI Dashboard</h3>
          <p className="text-gray-600">
            Track time saved, value generated, and efficiency gains. Prove the impact of AI recruitment to stakeholders.
          </p>
        </div>

        {/* Feature 6 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🏢</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Tenant</h3>
          <p className="text-gray-600">
            Complete data isolation per organization. Perfect for agencies managing multiple client databases.
          </p>
        </div>

        {/* Feature 7 - Intelligent Intake Inbox (NEW) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-200 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-slate-600 text-white text-xs px-2 py-1 rounded-full font-medium">STP++</div>
          <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📧</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Intake Inbox™</h3>
          <p className="text-gray-600">
            From inbox to pipeline in seconds. AI-powered parsing, smart routing, automatic deduplication — no manual uploads, no sorting, no busywork.
          </p>
        </div>

        {/* Feature 8 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔐</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise-Grade Security</h3>
          <p className="text-gray-600">
            256-bit AES encryption, role-based access control, complete audit trails, and multi-tenant data isolation. Your data is protected.
          </p>
        </div>

        {/* Feature 9 */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
          <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">👥</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Role-Based Access</h3>
          <p className="text-gray-600">
            Admin, recruiter, auditor, and viewer roles. Control who sees what with granular permissions.
          </p>
        </div>

        {/* Feature 10 - Complete Audit Trail (v2.0) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-300 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">v2.0</div>
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📋</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Audit Trail</h3>
          <p className="text-gray-600">
            Full activity logging for compliance and reporting. Track all user actions, filter by date range, user, or action type, and export for audits.
          </p>
        </div>

        {/* Feature 11 - Interview Scheduling (v2.0) */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-300 hover:shadow-xl transition-shadow relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">v2.0</div>
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🗓️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Interview Scheduling</h3>
          <p className="text-gray-600">
            Schedule and manage candidate interviews directly within ARISE. Sync with calendars, send automated invitations, and track your interview pipeline.
          </p>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// ROI Calculator Component
const ROICalculator = () => {
  const [cvsPerWeek, setCvsPerWeek] = useState(200);
  const [hoursPerCv, setHoursPerCv] = useState(1.5);
  const [recruiterRate, setRecruiterRate] = useState(150);

  // Calculate ROI metrics
  const manualHoursPerWeek = cvsPerWeek * hoursPerCv;
  const ariseHoursPerWeek = cvsPerWeek * 0.02; // 2 minutes per 100 CVs = ~1.2 seconds per CV
  const timeSavedPerWeek = manualHoursPerWeek - ariseHoursPerWeek;
  const monthlyCostSaved = timeSavedPerWeek * 4.33 * recruiterRate; // 4.33 weeks/month
  const investment = 499; // Professional plan
  const roi = ((monthlyCostSaved - investment) / investment) * 100;
  const paybackHours = investment / (timeSavedPerWeek * recruiterRate / 40); // hours to break even

  return (
    <div className="max-w-4xl mx-auto mb-16">
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg border border-blue-100">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Calculate Your ROI</h2>
        <p className="text-gray-600 text-center mb-8 text-sm">See how much time and money ARISE can save your team</p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CVs you screen per week
              </label>
              <input
                type="range"
                min="50"
                max="1000"
                step="50"
                value={cvsPerWeek}
                onChange={(e) => setCvsPerWeek(parseInt(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">50</span>
                <span className="text-lg font-bold text-blue-600">{cvsPerWeek}</span>
                <span className="text-xs text-gray-500">1000</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minutes spent per CV (manual screening)
              </label>
              <input
                type="range"
                min="0.5"
                max="5"
                step="0.5"
                value={hoursPerCv * 60}
                onChange={(e) => setHoursPerCv(parseFloat(e.target.value) / 60)}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">30 sec</span>
                <span className="text-lg font-bold text-blue-600">{Math.round(hoursPerCv * 60)} min</span>
                <span className="text-xs text-gray-500">5 min</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recruiter hourly cost ($)
              </label>
              <input
                type="range"
                min="50"
                max="300"
                step="10"
                value={recruiterRate}
                onChange={(e) => setRecruiterRate(parseInt(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">$50</span>
                <span className="text-lg font-bold text-blue-600">${recruiterRate}/hr</span>
                <span className="text-xs text-gray-500">$300</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">With ARISE Professional</h3>

            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-gray-600">Time saved per week</span>
                <span className="text-xl font-bold text-green-600">{Math.round(timeSavedPerWeek)} hours</span>
              </div>

              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-gray-600">Monthly cost saved</span>
                <span className="text-xl font-bold text-green-600">${monthlyCostSaved.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </div>

              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-gray-600">Your investment</span>
                <span className="text-xl font-bold text-gray-900">${investment}/mo</span>
              </div>

              <div className="pt-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-800 font-medium">Return on Investment</span>
                  <span className="text-2xl font-bold text-blue-600">{roi > 10000 ? '10,000+' : Math.round(roi).toLocaleString()}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Payback period</span>
                  <span className="text-sm font-medium text-gray-900">
                    {paybackHours < 1 ? 'Less than 1 hour' : paybackHours < 8 ? `${Math.round(paybackHours)} hours` : `${Math.round(paybackHours / 8)} days`}
                  </span>
                </div>
              </div>
            </div>

            <a href="mailto:customer_care@tmplabs.ai?subject=Free%20Trial%20Request%20-%20ARISE%20Platform" className="block w-full mt-6 py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors text-center">
              Start Free Trial
            </a>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          * Calculations based on ARISE screening 100 CVs in 2 minutes vs. manual review
        </p>
      </div>
    </div>
  );
};

// Pricing Page Component
const PricingPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-7xl mx-auto px-8 py-16">
      {/* Hero Section - Outcome Focused */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          AI-Powered Recruitment for Every Team Size
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
          From solo recruiters to enterprise teams. Find better candidates faster with AI-powered automation.
        </p>
        <p className="text-sm text-gray-500">
          No hidden fees. Transparent pricing. 14-day free trial. Cancel anytime. All prices in USD.
        </p>
      </div>

      {/* 4-Tier Pricing Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-16">
        {/* Starter Plan */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 flex flex-col">
          <div className="text-center mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Starter</h3>
            <p className="text-gray-500 text-sm mb-3">Solo recruiters</p>
            <div className="text-3xl font-bold text-gray-900">$99</div>
            <div className="text-gray-500 text-sm">/month</div>
            <div className="text-xs text-green-600 mt-1">$89/mo billed annually (10%)</div>
          </div>
          {/* Value Props */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs">
            <div className="font-semibold text-gray-700 mb-1">Perfect for getting started</div>
            <div className="text-gray-600">AI-powered shortlisting included</div>
          </div>
          <ul className="space-y-3 mb-6 flex-grow text-sm">
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Up to <strong>1,000 profiles</strong></span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>2</strong> team members</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="Basic: Keyword + semantic search for quick candidate matching">Basic AI search</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="AI-enhanced candidate analysis showing match scores, skill matches, and fit explanations in search results">50 AI summaries/month</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>100 CV uploads/month</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="Email support with 24-hour response time">Email support (24hr)</span>
            </li>
          </ul>
          <a href="mailto:customer_care@tmplabs.ai?subject=Free%20Trial%20Request%20-%20Starter%20Plan" className="block w-full py-3 px-4 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors text-sm text-center">
            Start Free Trial
          </a>
        </div>

        {/* Professional Plan - Most Popular */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 shadow-xl text-white flex flex-col relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold">MOST POPULAR</span>
          </div>
          <div className="text-center mb-6 mt-2">
            <h3 className="text-lg font-bold mb-1">Professional</h3>
            <p className="text-blue-200 text-sm mb-3">5-14 person teams</p>
            <div className="text-3xl font-bold">$499</div>
            <div className="text-blue-200 text-sm">/month</div>
            <div className="text-xs text-green-300 mt-1">$449/mo billed annually (10%)</div>
          </div>

          {/* Value Props */}
          <div className="bg-white/10 rounded-lg p-3 mb-4 text-xs">
            <div className="font-semibold mb-1">Screen 100 CVs in 2 minutes</div>
            <div className="text-blue-200">vs 3 hours manual screening</div>
          </div>

          <ul className="space-y-3 mb-6 flex-grow text-sm">
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span>Up to <strong>10,000 profiles</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span><strong>14</strong> team members</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span title="Standard: Multi-agent AI matching with skill extraction, experience analysis, and ranked results"><strong>AI Agentic Search</strong> (Standard)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span title="AI-enhanced candidate analysis showing match scores, skill matches, and fit explanations in search results">Unlimited AI summaries*</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span>500 CV uploads/month</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-200 mt-0.5">★</span>
              <span><strong>Intelligent Intake Inbox™</strong> <span className="text-xs bg-blue-200 text-blue-900 px-1 rounded">STP++</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-300 mt-0.5">★</span>
              <span><strong>Smart JD Builder</strong> <span className="text-xs bg-yellow-400 text-yellow-900 px-1 rounded">NEW</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-300 mt-0.5">★</span>
              <span><strong>Match Reasoning</strong> <span className="text-xs bg-yellow-400 text-yellow-900 px-1 rounded">NEW</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-300 mt-0.5">✓</span>
              <span>Priority support (4hr SLA)</span>
            </li>
          </ul>
          <a href="mailto:customer_care@tmplabs.ai?subject=Free%20Trial%20Request%20-%20Professional%20Plan" className="block w-full py-3 px-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors text-sm text-center">
            Start Free Trial
          </a>
        </div>

        {/* Growth Plan - NEW */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200 flex flex-col relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold">BEST VALUE</span>
          </div>
          <div className="text-center mb-6 mt-2">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Growth</h3>
            <p className="text-gray-500 text-sm mb-3">15-50 person agencies</p>
            <div className="text-3xl font-bold text-gray-900">$899</div>
            <div className="text-gray-500 text-sm">/month</div>
            <div className="text-xs text-green-600 mt-1">$809/mo billed annually (10%)</div>
          </div>
          {/* Value Props - Inherits Professional */}
          <div className="bg-blue-50 rounded-lg p-3 mb-4 text-xs">
            <div className="font-semibold text-blue-700 mb-1">Screen 500 CVs in 10 minutes</div>
            <div className="text-blue-600">Everything in Professional, plus:</div>
          </div>
          <ul className="space-y-3 mb-6 flex-grow text-sm">
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Up to <strong>25,000 profiles</strong></span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>50</strong> team members</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="Expert: Enhanced multi-agent AI with priority processing, advanced skill inference, and batch operations"><strong>AI Agentic Search</strong> (Expert)</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="AI-enhanced candidate analysis showing match scores, skill matches, and fit explanations in search results">Unlimited AI summaries*</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>2,500</strong> CV uploads/month</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>API access</strong> (100K calls/mo)</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>SSO</strong> + custom scoring</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-slate-500 mt-0.5">★</span>
              <span><strong>Intake Inbox™</strong> + AI Routing + Dedupe</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-blue-500 mt-0.5">★</span>
              <span><strong>Smart JD Builder</strong> + <strong>Match Reasoning</strong></span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Dedicated support (2hr SLA)</span>
            </li>
          </ul>
          <a href="mailto:customer_care@tmplabs.ai?subject=Free%20Trial%20Request%20-%20Growth%20Plan" className="block w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors text-sm text-center">
            Start Free Trial
          </a>
        </div>

        {/* Enterprise Plan */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 flex flex-col">
          <div className="text-center mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Enterprise</h3>
            <p className="text-gray-500 text-sm mb-3">50+ recruiters, staffing firms, RPOs</p>
            <div className="text-2xl font-bold text-gray-900">From $1,999</div>
            <div className="text-gray-500 text-sm">/month</div>
            <div className="text-xs text-gray-500 mt-1">Typical: $2K-$10K/mo based on scale</div>
          </div>
          {/* Value Props - Inherits Growth */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs">
            <div className="font-semibold text-gray-700 mb-1">Unlimited CV screening capacity</div>
            <div className="text-gray-600">Everything in Growth, plus:</div>
          </div>
          <ul className="space-y-3 mb-6 flex-grow text-sm">
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>Unlimited</strong> profiles, uploads & AI summaries</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>Unlimited</strong> team members</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span title="Custom: Dedicated AI models trained on your data, custom matching rules, and white-glove optimization"><strong>AI Agentic Search</strong> (Custom)</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>SSO</strong> + custom scoring + API</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>Dedicated</strong> infrastructure</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>Multi-region (IN/SG/EU) + data residency</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span><strong>99.95% SLA</strong> + SOC2/GDPR</span>
            </li>
            <li className="flex items-start gap-2 text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span>
              <span>24/7 phone (1hr SLA) + onboarding</span>
            </li>
          </ul>
          <a href="mailto:customer_care@tmplabs.ai?subject=Enterprise%20Inquiry%20-%20ARISE%20Platform" className="block w-full py-3 px-4 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors text-sm text-center">
            Contact Sales
          </a>
        </div>
      </div>

      {/* ROI Calculator Section */}
      <ROICalculator />

      {/* Feature Comparison Table */}
      <div className="max-w-6xl mx-auto mb-16">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Compare Plans</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Feature</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Starter</th>
                <th className="text-center py-3 px-4 font-semibold text-blue-600 bg-blue-50">Professional</th>
                <th className="text-center py-3 px-4 font-semibold text-blue-600">Growth</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Enterprise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-3 px-4 text-gray-700">Candidate Profiles</td>
                <td className="py-3 px-4 text-center">1,000</td>
                <td className="py-3 px-4 text-center bg-blue-50">10,000</td>
                <td className="py-3 px-4 text-center">25,000</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Team Members</td>
                <td className="py-3 px-4 text-center">2</td>
                <td className="py-3 px-4 text-center bg-blue-50">14</td>
                <td className="py-3 px-4 text-center">50</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700" title="Multi-agent AI that analyzes job requirements and matches candidates">AI Agentic Search</td>
                <td className="py-3 px-4 text-center" title="Basic keyword + semantic search">Basic</td>
                <td className="py-3 px-4 text-center bg-blue-50 font-medium text-blue-600" title="Multi-agent matching with skill extraction and ranking">Standard</td>
                <td className="py-3 px-4 text-center font-medium text-blue-600" title="Enhanced AI with priority processing and batch operations">Expert</td>
                <td className="py-3 px-4 text-center" title="Dedicated AI models trained on your data">Custom</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">CV Uploads</td>
                <td className="py-3 px-4 text-center">100/mo</td>
                <td className="py-3 px-4 text-center bg-blue-50">500/mo</td>
                <td className="py-3 px-4 text-center">2,500/mo</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">AI Summaries</td>
                <td className="py-3 px-4 text-center">50/mo</td>
                <td className="py-3 px-4 text-center bg-blue-50">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr className="bg-slate-50/30">
                <td className="py-3 px-4 text-gray-700 font-medium">
                  <span className="inline-flex items-center gap-1">
                    Intelligent Intake Inbox™ <span className="text-xs bg-slate-600 text-white px-1.5 py-0.5 rounded-full">STP++</span>
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-green-600">✓ Basic</td>
                <td className="py-3 px-4 text-center text-green-600">✓ + AI Routing</td>
                <td className="py-3 px-4 text-center text-green-600">✓ + Identity Resolution</td>
              </tr>
              <tr className="bg-blue-50/30">
                <td className="py-3 px-4 text-gray-700 font-medium">
                  <span className="inline-flex items-center gap-1">
                    Smart JD Builder <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-green-600">✓ Basic</td>
                <td className="py-3 px-4 text-center text-green-600">✓ Advanced</td>
                <td className="py-3 px-4 text-center text-green-600">✓ Custom Tones</td>
              </tr>
              <tr className="bg-green-50/30">
                <td className="py-3 px-4 text-gray-700 font-medium">
                  <span className="inline-flex items-center gap-1">
                    Match Reasoning <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-green-600">✓</td>
                <td className="py-3 px-4 text-center text-green-600">✓ + Export</td>
                <td className="py-3 px-4 text-center text-green-600">✓ + Audit API</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">API Access</td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-gray-500">-</td>
                <td className="py-3 px-4 text-center text-green-600">100K/mo</td>
                <td className="py-3 px-4 text-center text-green-600">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">SSO</td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-gray-500">-</td>
                <td className="py-3 px-4 text-center text-green-600">✓</td>
                <td className="py-3 px-4 text-center text-green-600">✓</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Custom Scoring</td>
                <td className="py-3 px-4 text-center text-gray-400">-</td>
                <td className="py-3 px-4 text-center bg-blue-50 text-gray-500">-</td>
                <td className="py-3 px-4 text-center text-green-600">✓</td>
                <td className="py-3 px-4 text-center text-green-600">✓</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Support</td>
                <td className="py-3 px-4 text-center">Email (24hr)</td>
                <td className="py-3 px-4 text-center bg-blue-50">Priority (4hr SLA)</td>
                <td className="py-3 px-4 text-center">Dedicated (2hr SLA)</td>
                <td className="py-3 px-4 text-center">24/7 Phone (1hr SLA)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-4 text-center">*Unlimited AI summaries subject to fair use policy. Typical usage: 500-2,000/month.</p>
      </div>

      {/* FAQ Section - Trust, Security & AI Focused */}
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Why Trust ARISE?</h2>
        <p className="text-gray-600 text-center mb-8">Addressing your concerns about AI, security, and data privacy</p>
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-green-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-green-500">&#10003;</span> Does ARISE send my data to third-party AI providers?
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-green-700">No.</strong> ARISE uses 100% open-source AI models that run within your environment. Your CV data, candidate information, and search queries are <strong>never sent to any external AI service</strong>. This is a key differentiator.</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-green-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-green-500">&#10003;</span> Can AI hallucinate or invent candidates?
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-green-700">Impossible.</strong> Unlike generic AI chatbots, ARISE only searches and analyzes CVs <strong>you upload</strong>. It cannot invent candidates or fabricate information. Every result comes from your actual data.</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-green-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-green-500">&#10003;</span> How do I know why AI recommended a candidate?
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-green-700">Full explainability.</strong> Every AI-Native search includes a complete audit trail showing exactly which agents processed your query, what data they analyzed, and why each candidate was matched. No black boxes.</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-blue-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-blue-500">&#9679;</span> We can't use cloud - our data must stay on-premise
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-blue-700">ARISE supports fully airgapped deployment.</strong> Deploy on your own servers with local AI models. Zero external connections required. Your CVs and candidate data never leave your network. Ideal for government, defense, and regulated industries.</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-blue-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-blue-500">&#9679;</span> What if there's a data breach or hack?
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-blue-700">Defense in depth.</strong> AES-256 encryption at rest, TLS 1.3 in transit, complete tenant isolation, role-based access controls, and full audit logging. Cloud deployment runs on GCP's SOC 1/2/3, ISO 27001 certified infrastructure. On-premise puts you in full control.</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-blue-500">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-blue-500">&#9679;</span> Can I try it before committing?
            </h3>
            <p className="text-gray-600 text-sm"><strong className="text-blue-700">14-day free trial.</strong> Full access to all features. No credit card required. Upload your real CVs and test with your actual data.</p>
          </div>
        </div>
        <div className="text-center mt-8">
          <button
            onClick={() => onNavigate('register')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
          >
            Start Your Free Trial
            <span>→</span>
          </button>
          <p className="text-gray-500 text-xs mt-2">14 days free • No credit card required • Full FAQ available after login</p>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Integrations Page Component
const IntegrationsPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Integrations
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Connect ARISE with your existing tools and workflows.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* ATS Integrations */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📋</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">ATS Systems</h3>
          <p className="text-gray-600 mb-4">
            Sync candidates with your applicant tracking system.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• Greenhouse</li>
            <li>• Lever</li>
            <li>• Workday</li>
            <li>• BambooHR</li>
          </ul>
          <div className="mt-4 inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">Coming Soon</div>
        </div>

        {/* Email Integrations */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📧</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Email Providers</h3>
          <p className="text-gray-600 mb-4">
            Auto-ingest CVs from email applications.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• Google Workspace</li>
            <li>• Microsoft 365</li>
            <li>• IMAP/SMTP</li>
          </ul>
          <div className="mt-4 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Available</div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">📅</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Calendar</h3>
          <p className="text-gray-600 mb-4">
            Schedule interviews directly from candidate profiles.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• Google Calendar</li>
            <li>• Outlook</li>
            <li>• Calendly</li>
          </ul>
          <div className="mt-4 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Available</div>
        </div>

        {/* Cloud Storage */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">☁️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Cloud Storage</h3>
          <p className="text-gray-600 mb-4">
            Import CVs directly from cloud storage.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• Google Cloud Storage</li>
            <li>• AWS S3</li>
            <li>• Azure Blob</li>
          </ul>
          <div className="mt-4 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Available</div>
        </div>

        {/* SSO */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔐</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Single Sign-On</h3>
          <p className="text-gray-600 mb-4">
            Enterprise SSO for secure team access.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• Okta</li>
            <li>• Azure AD</li>
            <li>• Google Workspace</li>
          </ul>
          <div className="mt-4 inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">Enterprise</div>
        </div>

        {/* Webhooks */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
            <span className="text-3xl">🔗</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Webhooks & API</h3>
          <p className="text-gray-600 mb-4">
            Build custom integrations with our REST API.
          </p>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>• REST API</li>
            <li>• Webhooks</li>
            <li>• Custom workflows</li>
          </ul>
          <div className="mt-4 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Available</div>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// API Documentation Page
const APIPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          API Documentation
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Build powerful integrations with the ARISE REST API.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Quick Start */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Start</h2>
            <div className="bg-gray-900 rounded-xl p-6 text-green-400 font-mono text-sm overflow-x-auto">
              <pre>{`# Authenticate and get access token
curl -X POST https://api.arise.tmplabs.ai/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "you@company.com", "password": "****"}'

# Search for candidates
curl -X POST https://api.arise.tmplabs.ai/api/search \\
  -H "Authorization: Bearer <token>" \\
  -d '{"query": "Senior Python developer with AWS"}'`}</pre>
            </div>
          </div>

          {/* Endpoints */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Endpoints</h2>
            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-mono">POST</span>
                  <span className="font-mono text-gray-700">/api/search</span>
                </div>
                <p className="text-gray-600 text-sm">Search for candidates using natural language or structured queries</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-mono">GET</span>
                  <span className="font-mono text-gray-700">/api/profiles</span>
                </div>
                <p className="text-gray-600 text-sm">List all profiles with pagination and filtering</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-mono">POST</span>
                  <span className="font-mono text-gray-700">/api/profiles/upload</span>
                </div>
                <p className="text-gray-600 text-sm">Upload CV files (PDF, DOCX, TXT) for processing</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-mono">POST</span>
                  <span className="font-mono text-gray-700">/api/bulk-upload</span>
                </div>
                <p className="text-gray-600 text-sm">Bulk upload multiple CVs in a single request</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-mono">GET</span>
                  <span className="font-mono text-gray-700">/api/roi/summary</span>
                </div>
                <p className="text-gray-600 text-sm">Get ROI metrics and time savings data</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
            <h3 className="font-bold text-gray-900 mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-2">
                  <span>📄</span> Full API Reference
                </a>
              </li>
              <li>
                <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-2">
                  <span>🔧</span> SDKs & Libraries
                </a>
              </li>
              <li>
                <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-2">
                  <span>📝</span> Changelog
                </a>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
            <h3 className="font-bold mb-3">Need Help?</h3>
            <p className="text-blue-200 text-sm mb-4">
              Our developer team is here to help you integrate ARISE.
            </p>
            <button className="w-full bg-white text-blue-600 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// About Page Component
const AboutPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          About /tmp labs
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Building AI-powered solutions that help organizations work smarter.
        </p>
      </div>

      {/* Mission */}
      <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100 mb-12">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              We believe recruitment should be about finding great people, not drowning in paperwork.
              ARISE uses AI to handle the tedious screening work, so recruiters can focus on what
              matters most—building relationships and making great hires.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Founded in Singapore, /tmp labs is committed to creating technology that
              augments human capabilities rather than replacing them.
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">Teams</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">1M+</div>
                <div className="text-gray-600">CVs Processed</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">50K+</div>
                <div className="text-gray-600">Hours Saved</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">2025</div>
                <div className="text-gray-600">Founded</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Our Values</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🎯</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">User-First</h3>
            <p className="text-gray-600">
              Every feature we build starts with a real user problem. We ship solutions, not technology for its own sake.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🔒</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Privacy-Centric</h3>
            <p className="text-gray-600">
              We handle sensitive candidate data. Security and privacy aren't features—they're foundations.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🚀</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Continuous Improvement</h3>
            <p className="text-gray-600">
              AI gets better with use. We're constantly refining our models based on real-world feedback.
            </p>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">Based in Singapore</h2>
        <p className="text-blue-200 text-lg mb-6">
          Ubi Techpark, Singapore 408564
        </p>
        <p className="text-blue-200">
          Email: customer_care@tmplabs.ai
        </p>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Blog Page Component (Placeholder)
const BlogPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Blog
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Insights on AI, recruitment, and the future of hiring.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Blog Post 1 */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
          <div className="h-48 bg-gradient-to-br from-blue-400 to-blue-600"></div>
          <div className="p-6">
            <div className="text-sm text-blue-600 mb-2">AI & Recruitment</div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">How AI is Transforming CV Screening</h3>
            <p className="text-gray-600 text-sm mb-4">
              Learn how modern AI can reduce screening time by 90% while improving candidate quality.
            </p>
            <div className="text-sm text-gray-500">Coming Soon</div>
          </div>
        </div>

        {/* Blog Post 2 */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
          <div className="h-48 bg-gradient-to-br from-blue-400 to-cyan-500"></div>
          <div className="p-6">
            <div className="text-sm text-blue-600 mb-2">Best Practices</div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Building a Scalable Candidate Database</h3>
            <p className="text-gray-600 text-sm mb-4">
              Tips for organizing, tagging, and maintaining a searchable pool of candidates.
            </p>
            <div className="text-sm text-gray-500">Coming Soon</div>
          </div>
        </div>

        {/* Blog Post 3 */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
          <div className="h-48 bg-gradient-to-br from-green-400 to-emerald-500"></div>
          <div className="p-6">
            <div className="text-sm text-green-600 mb-2">ROI</div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Measuring Recruitment Efficiency</h3>
            <p className="text-gray-600 text-sm mb-4">
              How to quantify time savings and prove the value of AI recruitment tools.
            </p>
            <div className="text-sm text-gray-500">Coming Soon</div>
          </div>
        </div>
      </div>

      <div className="text-center mt-12">
        <p className="text-gray-600">
          Blog content coming soon. Subscribe to our newsletter to get notified.
        </p>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Careers Page Component
const CareersPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-6xl mx-auto px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Join Our Team
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Help us build the future of AI-powered recruitment.
        </p>
      </div>

      {/* Why Join */}
      <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Why /tmp labs?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center p-4">
            <div className="text-3xl mb-3">🌏</div>
            <h3 className="font-semibold text-gray-900 mb-2">Remote-First</h3>
            <p className="text-gray-600 text-sm">Work from anywhere. We're a distributed team across Asia and beyond.</p>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl mb-3">🚀</div>
            <h3 className="font-semibold text-gray-900 mb-2">Cutting-Edge AI</h3>
            <p className="text-gray-600 text-sm">Work with the latest in LLMs, vector search, and multi-agent systems.</p>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl mb-3">💰</div>
            <h3 className="font-semibold text-gray-900 mb-2">Competitive Pay</h3>
            <p className="text-gray-600 text-sm">Market-rate salaries plus equity. We share success with our team.</p>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl mb-3">📈</div>
            <h3 className="font-semibold text-gray-900 mb-2">Growth</h3>
            <p className="text-gray-600 text-sm">Small team, big impact. Your work shapes the product directly.</p>
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Open Positions</h2>
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Senior Full-Stack Engineer</h3>
              <p className="text-gray-500 text-sm">Remote • Full-time</p>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
              Apply
            </button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">ML/AI Engineer</h3>
              <p className="text-gray-500 text-sm">Remote • Full-time</p>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
              Apply
            </button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Product Designer</h3>
              <p className="text-gray-500 text-sm">Remote • Full-time</p>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
              Apply
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center">
        <p className="text-gray-600">
          Don't see a perfect fit? Email us at <a href="mailto:careers@tmplabs.ai" className="text-blue-600 hover:text-blue-700">careers@tmplabs.ai</a>
        </p>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Contact Page Component
const ContactPage = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open mailto with form data
    const subject = encodeURIComponent(`Contact Form - ${formData.company || 'Website Inquiry'}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'N/A'}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:customer_care@tmplabs.ai?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <PageHeader onNavigate={onNavigate} />
      <div className="max-w-6xl mx-auto px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            {submitted ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">📧</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Email</h3>
                  <a href="mailto:customer_care@tmplabs.ai" className="text-blue-600">customer_care@tmplabs.ai</a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
              <h3 className="font-bold mb-3">Schedule a Demo</h3>
              <p className="text-blue-200 text-sm mb-4">
                See ARISE in action with a personalized walkthrough.
              </p>
              <a href="mailto:customer_care@tmplabs.ai?subject=Demo%20Request%20-%20ARISE%20Platform" className="block w-full bg-white text-blue-600 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center">
                Book Demo Call
              </a>
            </div>
          </div>
        </div>
      </div>
      <PageFooter onNavigate={onNavigate} />
    </div>
  );
};

// Privacy Policy Page Component (Adapted from /tmp labs docs)
const PrivacyPolicyPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-4xl mx-auto px-8 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-gray-500 mb-12">Last Updated: September 2025</p>

      <div className="prose prose-lg max-w-none">
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              /TMP LABS PTE. LTD. and our related entities ("/tmp labs", "we", "us", or "our") are committed to protecting your privacy.
              This Privacy Policy outlines how we collect, use, share and process identifiable information about you ("Personal Data")
              across ARISE and all websites, applications and platforms that we own or operate.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. What Personal Data do we collect?</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We may collect Personal Data both directly from you, and automatically when you interact with our Services:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Account information: Name, email address, phone number, password</li>
              <li>Profile data: Company name, department, role</li>
              <li>Candidate data: CVs, resumes, and profile information you upload</li>
              <li>Usage data: IP address, device information, browser type</li>
              <li>Search history and preferences within the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How do we use Personal Data?</h2>
            <p className="text-gray-600 leading-relaxed mb-4">We process Personal Data to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Deliver and administer our recruitment search services</li>
              <li>Provide AI-powered candidate matching and recommendations</li>
              <li>Analyze and improve the effectiveness of our Services</li>
              <li>Send you communications about your account and services</li>
              <li>Comply with legal obligations and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organizational measures to safeguard Personal Data, including:
              encryption of data in transit and at rest, access controls and role-based permissions,
              regular security assessments and audits, and contractual confidentiality obligations for all personnel.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4">You may have the following rights:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of your Personal Data</li>
              <li><strong>Correction:</strong> Correct or delete your Personal Data</li>
              <li><strong>Portability:</strong> Request your data in a structured format</li>
              <li><strong>Withdrawal:</strong> Withdraw consent at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions about this Privacy Policy, please contact our Data Protection Officer:<br /><br />
              <strong>/TMP LABS PTE. LTD.</strong> (Singapore)<br />
              Email: privacy@tmplabs.ai
            </p>
          </section>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Terms of Service Page Component (Adapted from /tmp labs docs)
const TermsOfServicePage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-4xl mx-auto px-8 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
      <p className="text-gray-500 mb-12">Last Updated: September 2025</p>

      <div className="prose prose-lg max-w-none">
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. About ARISE</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms of Service ("Terms") apply to ARISE and all websites, applications, and platforms owned or operated
              by /tmp labs pte. ltd and its affiliates ("/tmp labs"). By accessing or using ARISE, you agree to be legally
              bound by these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. User Accounts and Security</h2>
            <p className="text-gray-600 leading-relaxed mb-4">If you create an account, you must:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Keep your credentials secure and confidential</li>
              <li>Not share your login information with unauthorized parties</li>
              <li>Promptly notify us of any unauthorized access</li>
              <li>Use the service only for legitimate recruitment purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Acceptable Use</h2>
            <p className="text-gray-600 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Use the service for unlawful purposes</li>
              <li>Upload malicious content or malware</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Scrape or harvest data without permission</li>
              <li>Violate the privacy rights of candidates</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content on ARISE—including text, graphics, software, and AI models—is the intellectual property of
              /tmp labs or its licensors. You retain ownership of any candidate data you upload, and grant us a
              limited license to process it for providing our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the fullest extent permitted by law, /tmp labs disclaims liability for any indirect, incidental,
              or consequential damages arising from your use of ARISE. The AI matching and ranking features are
              provided as aids to decision-making, not as substitutes for human judgment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms are governed by the laws of Singapore. You agree to submit to the exclusive jurisdiction
              of the Singapore courts in relation to any dispute arising under these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact</h2>
            <p className="text-gray-600 leading-relaxed">
              For legal notices, please contact:<br /><br />
              <strong>/TMP LABS PTE. LTD.</strong><br />
              Ubi Techpark, Singapore 408564<br />
              Email: legal@tmplabs.ai
            </p>
          </section>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Cookie Policy Page Component
const CookiePolicyPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-4xl mx-auto px-8 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
      <p className="text-gray-500 mb-12">Last Updated: September 2025</p>

      <div className="prose prose-lg max-w-none">
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-600 leading-relaxed">
              Cookies are small text files stored by your web browser when you visit websites. We also use similar
              technologies such as local storage and web beacons. These help us provide and improve our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-600 leading-relaxed mb-4">We use cookies to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Essential cookies:</strong> Keep you logged in and maintain your session</li>
              <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics cookies:</strong> Understand how you use our platform to improve it</li>
              <li><strong>Performance cookies:</strong> Optimize page load times and functionality</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cookie Types</h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 text-gray-900 font-semibold">Cookie</th>
                    <th className="py-3 text-gray-900 font-semibold">Purpose</th>
                    <th className="py-3 text-gray-900 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr className="border-b border-gray-100">
                    <td className="py-3">auth_token</td>
                    <td className="py-3">Authentication session</td>
                    <td className="py-3">Session</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3">preferences</td>
                    <td className="py-3">User preferences</td>
                    <td className="py-3">1 year</td>
                  </tr>
                  <tr>
                    <td className="py-3">_ga</td>
                    <td className="py-3">Analytics (Google)</td>
                    <td className="py-3">2 years</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Managing Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              You can control cookies through your browser settings. Note that disabling essential cookies may
              prevent you from using some features of ARISE. Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
              <li>See what cookies are stored and delete them individually</li>
              <li>Block third-party cookies</li>
              <li>Block cookies from specific sites</li>
              <li>Block all cookies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Contact</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions about our use of cookies, please contact:<br /><br />
              <strong>/TMP LABS PTE. LTD.</strong> (Singapore)<br />
              Email: privacy@tmplabs.ai
            </p>
          </section>
        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Security Overview Page Component
const SecurityOverviewPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <PageHeader onNavigate={onNavigate} />
    <div className="max-w-4xl mx-auto px-8 py-16">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
          <span className="text-3xl">🛡️</span>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Security Overview</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Enterprise-grade security built into every layer of ARISE. Your data protection is our priority.
        </p>
      </div>

      <div className="prose prose-lg max-w-none">
        <div className="space-y-10">

          {/* Data Encryption */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔐</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Encryption</h2>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">256-bit AES Encryption at Rest</strong>
                      <p className="mt-1">All stored data is encrypted using AES-256, the same standard used by financial institutions and government agencies.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">TLS 1.3 in Transit</strong>
                      <p className="mt-1">All data transmitted between your browser and our servers is protected with the latest TLS 1.3 encryption protocol.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Encrypted Database Connections</strong>
                      <p className="mt-1">All database connections use SSL/TLS encryption with certificate verification.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Access Control */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">👥</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Control</h2>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Role-Based Access Control (RBAC)</strong>
                      <p className="mt-1">Granular permissions system with Admin, Manager, Recruiter, and Viewer roles. Each role has precisely defined access levels.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Multi-Tenant Data Isolation</strong>
                      <p className="mt-1">Complete data segregation between organizations. Your data is never accessible to other tenants.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Secure Authentication</strong>
                      <p className="mt-1">JWT-based authentication with secure HttpOnly cookies, password hashing using bcrypt, and rate limiting on login attempts.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Audit & Monitoring */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">📋</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Audit Trail & Monitoring</h2>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Complete Audit Logging</strong>
                      <p className="mt-1">Every action is logged with timestamp, user, IP address, and action details. Audit logs are retained for 7 years.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Real-Time Security Monitoring</strong>
                      <p className="mt-1">Continuous monitoring for suspicious activities, failed login attempts, and potential security threats.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Exportable Audit Reports</strong>
                      <p className="mt-1">Generate compliance reports for internal audits and regulatory requirements.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Infrastructure Security */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">☁️</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Infrastructure Security</h2>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Flexible Deployment Options</strong>
                      <p className="mt-1"><strong>Cloud (SaaS):</strong> Hosted on GCP with SOC 1/2/3, ISO 27001, and PCI DSS certified infrastructure.</p>
                      <p className="mt-1"><strong>On-Premise / Airgapped:</strong> Deploy on your own servers. 100% open-source AI models (Ollama) - no data sent to OpenAI, Google, or any third-party AI provider.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Kubernetes with Network Policies</strong>
                      <p className="mt-1">Container orchestration with strict network segmentation and pod security policies.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Regular Security Updates</strong>
                      <p className="mt-1">Automated patching and updates for all infrastructure components and dependencies.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Protection */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">📁</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection & Privacy</h2>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Data Retention Policies</strong>
                      <p className="mt-1">Configurable retention periods with automated data cleanup. Default 7-year retention for compliance.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Data Residency & Sovereignty</strong>
                      <p className="mt-1"><strong>Cloud:</strong> Primary data center in Asia-South1 (Mumbai). Enterprise customers can request regional deployment.</p>
                      <p className="mt-1"><strong>On-Premise:</strong> Complete data sovereignty - your CVs and candidate data never leave your network.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Backup & Recovery</strong>
                      <p className="mt-1">Automated daily backups with point-in-time recovery. Backups are encrypted and stored in separate availability zones.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Compliance Roadmap */}
          <section className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Compliance Roadmap</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🎯</span>
                  <h3 className="font-bold text-gray-900">SOC 2 Type II</h3>
                </div>
                <p className="text-gray-600 text-sm mb-3">Enterprise security controls audit covering security, availability, and confidentiality.</p>
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">Planned Q2 2026</span>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🌍</span>
                  <h3 className="font-bold text-gray-900">GDPR Compliance</h3>
                </div>
                <p className="text-gray-600 text-sm mb-3">Full GDPR workflow including data access requests, right-to-deletion, and consent management.</p>
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">In Progress</span>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">📜</span>
                  <h3 className="font-bold text-gray-900">ISO 27001</h3>
                </div>
                <p className="text-gray-600 text-sm mb-3">Information security management system certification.</p>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">Planned 2026</span>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🇮🇳</span>
                  <h3 className="font-bold text-gray-900">DPDP Act (India)</h3>
                </div>
                <p className="text-gray-600 text-sm mb-3">Digital Personal Data Protection Act compliance for Indian operations.</p>
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Compliant</span>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Questions?</h2>
            <p className="text-gray-600 mb-6">
              Our security team is available to discuss your specific requirements and answer any questions about our security practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:security@tmplabs.ai" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                <span>✉️</span> Contact Security Team
              </a>
              <button onClick={() => onNavigate('contact')} className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors">
                <span>📅</span> Schedule Security Review
              </button>
            </div>
          </section>

        </div>
      </div>
    </div>
    <PageFooter onNavigate={onNavigate} />
  </div>
);

// Shared Page Header Component
const PageHeader = ({ onNavigate }) => (
  <div className="py-4 px-8 border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <div className="flex items-center gap-8">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => onNavigate('home')}>
          <TmpLabsLogo size="mobile" />
          <span className="text-lg font-bold text-gray-800">ARISE</span>
        </div>
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-gray-600">
          <div className="relative group">
            <span className="hover:text-blue-600 cursor-pointer flex items-center gap-1">
              Product
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <div className="py-2">
                <span onClick={() => onNavigate('features')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">Features</span>
                <span onClick={() => onNavigate('integrations')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">Integrations</span>
                <span onClick={() => onNavigate('api')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">API</span>
              </div>
            </div>
          </div>
          <span onClick={() => onNavigate('pricing')} className="hover:text-blue-600 cursor-pointer">Pricing</span>
          <div className="relative group">
            <span className="hover:text-blue-600 cursor-pointer flex items-center gap-1">
              Company
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <div className="py-2">
                <span onClick={() => onNavigate('about')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">About</span>
                <span onClick={() => onNavigate('blog')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">Blog</span>
                <span onClick={() => onNavigate('careers')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">Careers</span>
                <span onClick={() => onNavigate('contact')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">Contact</span>
              </div>
            </div>
          </div>
        </nav>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={() => onNavigate('home')}
          className="hidden sm:block px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          Sign In
        </button>
        <button
          onClick={() => onNavigate('home')}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Get Demo
        </button>
      </div>
    </div>
  </div>
);

// Shared Page Footer Component
const PageFooter = ({ onNavigate }) => (
  <div className="py-12 px-8 bg-gray-900 text-gray-400">
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-4 cursor-pointer" onClick={() => onNavigate('home')}>
            <TmpLabsLogo size="mobile" showCursor={false} />
            <span className="text-white font-bold">ARISE</span>
          </div>
          <p className="text-sm">AI-powered recruitment intelligence that helps you hire smarter, not harder.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Product</h4>
          <ul className="space-y-2 text-sm">
            <li onClick={() => onNavigate('features')} className="hover:text-white cursor-pointer">Features</li>
            <li onClick={() => onNavigate('pricing')} className="hover:text-white cursor-pointer">Pricing</li>
            <li onClick={() => onNavigate('integrations')} className="hover:text-white cursor-pointer">Integrations</li>
            <li onClick={() => onNavigate('api')} className="hover:text-white cursor-pointer">API</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li onClick={() => onNavigate('about')} className="hover:text-white cursor-pointer">About</li>
            <li onClick={() => onNavigate('blog')} className="hover:text-white cursor-pointer">Blog</li>
            <li onClick={() => onNavigate('careers')} className="hover:text-white cursor-pointer">Careers</li>
            <li onClick={() => onNavigate('contact')} className="hover:text-white cursor-pointer">Contact</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Enterprise-Grade Security</h4>
          <ul className="space-y-2 text-sm">
            <li>✓ 256-bit AES Encryption</li>
            <li>✓ Role-Based Access Control</li>
            <li>✓ Complete Audit Trail</li>
            <li onClick={() => onNavigate('security')} className="hover:text-white cursor-pointer">Security Overview →</li>
          </ul>
        </div>
      </div>
      <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm">© 2025 /tmp labs. All rights reserved.</p>
        <div className="flex items-center gap-6 text-sm">
          <span onClick={() => onNavigate('privacy')} className="hover:text-white cursor-pointer">Privacy Policy</span>
          <span onClick={() => onNavigate('terms')} className="hover:text-white cursor-pointer">Terms of Service</span>
          <span onClick={() => onNavigate('cookies')} className="hover:text-white cursor-pointer">Cookie Policy</span>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// LOGIN PAGE - Main landing page with state-based routing
// ============================================================================

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  // Product-led hero: the auth card is hidden until the user chooses to sign in
  // or start a trial (keeps the hero about the product, not the login form).
  const [showAuth, setShowAuth] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    department: '',
    role: 'recruiter'
  });

  // A/B test color scheme
  const { colors, trackConversion } = useColorScheme();

  // Navigation handler
  const handleNavigate = (view) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render different pages based on currentView
  if (currentView === 'features') return <FeaturesPage onNavigate={handleNavigate} />;
  if (currentView === 'pricing') return <PricingPage onNavigate={handleNavigate} />;
  if (currentView === 'integrations') return <IntegrationsPage onNavigate={handleNavigate} />;
  if (currentView === 'api') return <APIPage onNavigate={handleNavigate} />;
  if (currentView === 'about') return <AboutPage onNavigate={handleNavigate} />;
  if (currentView === 'blog') return <BlogPage onNavigate={handleNavigate} />;
  if (currentView === 'careers') return <CareersPage onNavigate={handleNavigate} />;
  if (currentView === 'contact') return <ContactPage onNavigate={handleNavigate} />;
  if (currentView === 'privacy') return <PrivacyPolicyPage onNavigate={handleNavigate} />;
  if (currentView === 'terms') return <TermsOfServicePage onNavigate={handleNavigate} />;
  if (currentView === 'cookies') return <CookiePolicyPage onNavigate={handleNavigate} />;
  if (currentView === 'security') return <SecurityOverviewPage onNavigate={handleNavigate} />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Route superadmin to dedicated endpoint
      const loginEndpoint = email === 'superadmin@tmplabs.com' 
        ? `${API}/auth/superadmin/login` 
        : `${API}/auth/login`;
        
      const response = await axios.post(loginEndpoint, {
        email,
        password
      });
      
      console.log('Login response:', response.data);
      const { access_token } = response.data;
      
      if (!access_token) {
        throw new Error('No access token received');
      }
      
      localStorage.setItem('auth_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get user profile
      const userResponse = await axios.get(`${API}/auth/me`);
      console.log('User profile:', userResponse.data);
      onLogin(userResponse.data);
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
      // Clear password on error for security
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: registerData.email,
        password: registerData.password,
        full_name: registerData.full_name,
        department: registerData.department,
        role: registerData.role
      });
      
      // Registration successful - auto-login
      console.log('Registration successful, auto-logging in...');
      setEmail(registerData.email);
      setPassword(registerData.password);
      setShowRegister(false);
      
      // Small delay to ensure backend is ready
      setTimeout(() => {
        handleLogin(e);
      }, 100);
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(getErrorMessage(err));
      // Don't try to login if registration failed
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted to-surface">
      {/* Brand backdrop — geometric /tmp Labs mark + soft depth */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src="/brand/tmplabs-mark-teal.png"
          alt=""
          aria-hidden="true"
          className="absolute -top-20 -right-16 w-[30rem] max-w-none opacity-[0.05] rotate-12 select-none"
        />
        <div className="absolute top-1/3 -left-24 w-80 h-80 bg-accent-soft rounded-full blur-3xl opacity-40"></div>
      </div>

      <div className="relative min-h-screen">
        {/* TOP: Header with Full Navigation */}
        <div className="border-b border-line bg-surface/75 backdrop-blur-lg sticky top-0 z-50">
          <div className="arise-container h-[72px] flex items-center justify-between">
            <div className="flex items-center gap-8">
              <AriseBrand variant="light" size="md" onClick={() => handleNavigate('home')} />
              <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-ink-muted">
                <div className="relative group">
                  <span className="hover:text-blue-700 cursor-pointer flex items-center gap-1">
                    Product
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-elevated rounded-lg shadow-lg border border-line-subtle opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="py-2">
                      <span onClick={() => handleNavigate('features')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">Features</span>
                      <span onClick={() => handleNavigate('integrations')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">Integrations</span>
                      <span onClick={() => handleNavigate('api')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">API</span>
                    </div>
                  </div>
                </div>
                <span onClick={() => handleNavigate('pricing')} className="hover:text-blue-700 cursor-pointer">Pricing</span>
                <div className="relative group">
                  <span className="hover:text-blue-700 cursor-pointer flex items-center gap-1">
                    Company
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-elevated rounded-lg shadow-lg border border-line-subtle opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="py-2">
                      <span onClick={() => handleNavigate('about')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">About</span>
                      <span onClick={() => handleNavigate('blog')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">Blog</span>
                      <span onClick={() => handleNavigate('careers')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">Careers</span>
                      <span onClick={() => handleNavigate('contact')} className="block px-4 py-2 text-sm text-ink-muted hover:bg-muted cursor-pointer">Contact</span>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <button
                onClick={() => { setShowAuth(true); setShowRegister(false); }}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink transition-colors"
              >
                Sign In
              </button>
              <button onClick={() => handleNavigate('contact')} className="px-4 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 rounded-lg transition-colors">
                Get Demo
              </button>
            </div>
          </div>
        </div>

        {/* v2.0 Announcement Banner */}
        <div className="bg-muted border-b border-line py-2.5 px-4 text-center">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-ink-muted">
            <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-bold">NEW</span>
            <span>v2.0 is here! AutoMatch Engine, Identity Resolution, and more.</span>
            <span onClick={() => handleNavigate('features')} className="text-blue-500 hover:text-blue-400 font-semibold cursor-pointer ml-1">See what's new →</span>
          </span>
        </div>

        {/* HERO: Main Content Area */}
        <div className="arise-hero">
          <div className="arise-container py-20 lg:py-24 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
            {/* Left: Value Props */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-accent-soft text-accent-ink px-4 py-2 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                <span>500+ recruiting teams use ARISE</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-ink mb-6 leading-[1.1] tracking-tight">
                Shortlist 100 CVs in <span className="text-blue-500">Minutes</span>
                <span className="block text-2xl md:text-3xl mt-3 font-semibold text-ink-muted">With AI You Can Trust</span>
              </h1>

              <p className="text-lg md:text-xl text-ink-muted mb-8 leading-relaxed max-w-xl">
                ARISE helps recruiting teams screen resumes, explain candidate fit, and
                build auditable shortlists — without losing human control.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={() => { setShowAuth(true); setShowRegister(true); trackConversion('cta_click_trial'); }}
                  className="arise-btn-primary h-12 px-6 text-base font-bold rounded-xl w-full sm:w-auto"
                >
                  Start Free Trial
                </button>
                <button className="arise-btn-secondary h-12 px-6 text-base font-semibold rounded-xl flex items-center justify-center gap-2 w-full sm:w-auto">
                  <span className="text-blue-500">▶</span> Watch 2-Min Demo
                </button>
              </div>

              {/* Trust Line */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-ink-subtle">
                <span className="text-green-500">✓</span> No credit card required
                <span className="mx-2">•</span>
                <span className="text-green-500">✓</span> Set up in 5 minutes
                <span className="mx-2">•</span>
                <span className="text-green-500">✓</span> Cancel anytime
              </div>
            </div>

            {/* Right: product preview by default, auth card on demand */}
            <div className="lg:pl-8">
              {showAuth ? (
              <div className="bg-elevated rounded-2xl shadow-xl overflow-hidden border border-line">
                {/* Form Header */}
                <div className="p-6 border-b border-line">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-ink">
                        {showRegister ? 'Start Your Free Trial' : 'Welcome Back'}
                      </h2>
                      <p className="text-ink-subtle text-sm mt-1">
                        {showRegister ? '14 days free • No credit card needed' : 'Sign in to your dashboard'}
                      </p>
                    </div>
                    <button type="button" onClick={() => setShowAuth(false)} aria-label="Close"
                      className="text-ink-subtle hover:text-ink text-2xl leading-none -mt-1">×</button>
                  </div>
                </div>

                <div className="p-8">
              {!showRegister ? (
                <form onSubmit={handleLogin}>
                  <ErrorAlert error={error} onClose={() => setError('')} />

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="you@company.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="••••••••"
                        required
                        autoComplete="current-password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full mt-6 ${colors.primary} text-white py-3 rounded-lg font-semibold ${colors.primaryHover} transition-all disabled:opacity-50`}
                  >
                    {loading ? 'Logging in...' : 'Log In'}
                  </button>

                  <p className="mt-4 text-center text-sm text-ink-muted">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setShowRegister(true)}
                      className={`${colors.primaryText} ${colors.primaryTextHover} font-semibold`}
                    >
                      Register
                    </button>
                  </p>
                </form>
              ) : (
              <form onSubmit={handleRegister}>
                <ErrorAlert error={error} onClose={() => setError('')} />
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                      className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        value={registerData.department}
                        onChange={(e) => setRegisterData({...registerData, department: e.target.value})}
                        className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="HR, Tech, Sales..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-1">
                        Role
                      </label>
                      <select
                        value={registerData.role}
                        onChange={(e) => setRegisterData({...registerData, role: e.target.value})}
                        className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="recruiter">Recruiter</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      minLength="8"
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-ink-subtle mt-1">
                      Minimum 8 characters, must include uppercase, lowercase and number
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-ink-muted mb-1">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                      className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  onClick={() => trackConversion('register_submit')}
                  className={`w-full mt-6 ${colors.primary} text-white py-3 rounded-lg font-semibold ${colors.primaryHover} transition-all disabled:opacity-50`}
                >
                  {loading ? 'Creating Account...' : 'Register'}
                </button>

                <p className="mt-4 text-center text-sm text-ink-muted">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setShowRegister(false)}
                    className={`${colors.primaryText} ${colors.primaryTextHover} font-semibold`}
                  >
                    Log In
                  </button>
                </p>
              </form>
            )}
            </div>
          </div>
              ) : (
                <div className="arise-preview">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-muted">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400"></span>
                    </div>
                    <span className="text-xs font-medium text-ink-subtle hidden sm:block">Shortlist · Senior Backend Engineer</span>
                    <span className="text-xs text-blue-500 font-semibold">AI ranked</span>
                  </div>
                  <div className="p-5 space-y-3">
                    {[
                      { n: 'Priya S.', r: '8y · Python, AWS, Kafka', s: 94, t: 'green' },
                      { n: 'Daniel R.', r: '6y · Go, Postgres, gRPC', s: 89, t: 'green' },
                      { n: 'Mei L.', r: '5y · Java, Spring, K8s', s: 82, t: 'blue' },
                      { n: 'Omar K.', r: '4y · Node, Redis, GraphQL', s: 76, t: 'blue' },
                    ].map((c, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-surface border border-line-subtle">
                        <div className="w-9 h-9 rounded-full bg-accent-soft text-accent-ink flex items-center justify-center font-bold text-sm shrink-0">{c.n[0]}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-ink truncate">{c.n}</div>
                          <div className="text-xs text-ink-subtle truncate">{c.r}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-lg font-bold ${c.t === 'green' ? 'text-green-500' : 'text-blue-500'}`}>{c.s}%</div>
                          <div className="text-[10px] uppercase tracking-wide text-ink-subtle">match</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-5 py-3 border-t border-line bg-muted flex items-center gap-2 text-xs text-ink-muted">
                    <span>🎯</span> Every score is explainable — skills matched, gaps, and evidence.
                  </div>
                  <div className="px-5 pb-5 pt-1">
                    <button onClick={() => { setShowAuth(true); setShowRegister(false); }}
                      className="text-sm text-blue-500 hover:text-blue-400 font-semibold">Sign in to your dashboard →</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CUSTOMER LOGOS SECTION */}
        <div className="bg-muted py-12 px-8 border-y border-line-subtle">
          <div className="max-w-6xl mx-auto">
            <p className="text-center text-sm text-ink-subtle mb-8">Trusted by 500+ recruiting teams at leading companies</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60">
              {['TechCorp', 'GlobalHire', 'TalentFirst', 'RecruitPro', 'HireWise'].map((company) => (
                <div key={company} className="text-xl font-bold text-ink-subtle">{company}</div>
              ))}
            </div>
          </div>
        </div>

        {/* STATS WITH CONTEXT */}
        <div className="py-16 px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-ink mb-4 tracking-tight">
              How ARISE Saves You 15+ Hours Per Week
            </h2>
            <p className="text-center text-ink-muted mb-14 max-w-2xl mx-auto text-lg">
              Real results from recruiting teams who switched from manual screening
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="arise-card rounded-[20px] p-7 min-h-[156px] flex flex-col justify-center">
                <div className="text-6xl font-extrabold text-blue-500 mb-3 tracking-tight">90%</div>
                <div className="text-lg font-semibold text-ink">Less screening time</div>
                <div className="text-sm text-ink-subtle mt-1">vs manual review — save ~3 hours per role</div>
              </div>

              <div className="arise-card rounded-[20px] p-7 min-h-[156px] flex flex-col justify-center">
                <div className="text-6xl font-extrabold text-blue-500 mb-3 tracking-tight">2 min</div>
                <div className="text-lg font-semibold text-ink">Shortlist generation</div>
                <div className="text-sm text-ink-subtle mt-1">100 CVs ranked — 90× faster than manual</div>
              </div>

              <div className="arise-card rounded-[20px] p-7 min-h-[156px] flex flex-col justify-center">
                <div className="text-6xl font-extrabold text-green-500 mb-3 tracking-tight">75%</div>
                <div className="text-lg font-semibold text-ink">Better hiring signal</div>
                <div className="text-sm text-ink-subtle mt-1">AI matches get hired more vs 25% manual</div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW CAPABILITIES HIGHLIGHT SECTION */}
        <div className="py-16 px-8 bg-gradient-to-b from-surface to-muted">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-subtle text-ink-muted px-4 py-1 rounded-full text-sm font-semibold mb-4">NEW CAPABILITIES</span>
              <h2 className="text-3xl font-bold text-ink mb-4">
                AI That Works the Way Recruiters Think
              </h2>
              <p className="text-ink-muted max-w-2xl mx-auto">
                Three powerful features that eliminate busywork and give you transparency you can trust.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Intelligent Intake Inbox */}
              <div className="arise-card p-7 min-h-[220px] relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-slate-700 text-white text-xs px-3 py-1 rounded-bl-lg font-bold">STP++</div>
                <div className="w-14 h-14 bg-accent-soft rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">📧</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Intelligent Intake Inbox™</h3>
                <p className="text-ink-muted text-sm mb-4">
                  From inbox to pipeline in seconds. AI parses, routes, and dedupes — no manual uploads.
                </p>
                <ul className="space-y-2.5 text-sm text-ink-subtle leading-relaxed">
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Auto-extracts candidate data</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Supports PDF, DOCX and bulk uploads</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Deduplicates candidate profiles</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Keeps data private and auditable</li>
                </ul>
              </div>

              {/* Smart JD Builder */}
              <div className="arise-card p-7 min-h-[220px] relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg font-bold">NEW</div>
                <div className="w-14 h-14 bg-accent-soft rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">✍️</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Smart JD Builder</h3>
                <p className="text-ink-muted text-sm mb-4">
                  AI-generated job descriptions in seconds — bias-checked, compliant, and fully editable.
                </p>
                <ul className="space-y-2.5 text-sm text-ink-subtle leading-relaxed">
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Creates structured role-specific JDs</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Separates must-have and nice-to-have skills</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Improves fit-scoring consistency</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Built from recruiter workflows</li>
                </ul>
              </div>

              {/* Match Reasoning */}
              <div className="arise-card p-7 min-h-[220px] relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg font-bold">NEW</div>
                <div className="w-14 h-14 bg-accent-soft rounded-xl flex items-center justify-center mb-4">
                  <span className="text-3xl">🔍</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Match Reasoning</h3>
                <p className="text-ink-muted text-sm mb-4">
                  See exactly why each candidate was ranked. Full transparency — no black boxes.
                </p>
                <ul className="space-y-2.5 text-sm text-ink-subtle leading-relaxed">
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Explains why a candidate matches</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Highlights skills, gaps and risks</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Supports transparent recruiter review</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Produces exportable reasoning</li>
                </ul>
              </div>
            </div>

            <div className="mt-10 text-center">
              <button
                onClick={() => handleNavigate('pricing')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
              >
                See Pricing & Plans
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </button>
            </div>
          </div>
        </div>

        {/* HOW IT WORKS - BENEFITS */}
        <div className="bg-muted py-20 px-8 border-y border-line-subtle">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-ink mb-14 tracking-tight">
              From CV Chaos to Perfect Shortlist in 3 Steps
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="arise-card p-7">
                <div className="w-11 h-11 rounded-full bg-accent-soft text-accent-ink flex items-center justify-center font-extrabold mb-5">01</div>
                <h3 className="text-xl font-bold text-ink mb-2">Upload 100+ CVs</h3>
                <p className="text-ink-muted mb-4">AI reads them all in 90 seconds — PDF, DOCX, images.</p>
                <div className="text-sm text-green-500 font-medium">→ Save 3 hours of manual screening</div>
              </div>

              <div className="arise-card p-7">
                <div className="w-11 h-11 rounded-full bg-accent-soft text-accent-ink flex items-center justify-center font-extrabold mb-5">02</div>
                <h3 className="text-xl font-bold text-ink mb-2">AI Ranks by Fit</h3>
                <p className="text-ink-muted mb-4">Top matches surfaced instantly, with a transparent score.</p>
                <div className="text-sm text-green-500 font-medium">→ Save 5 hours comparing candidates</div>
              </div>

              <div className="arise-card p-7">
                <div className="w-11 h-11 rounded-full bg-accent-soft text-accent-ink flex items-center justify-center font-extrabold mb-5">03</div>
                <h3 className="text-xl font-bold text-ink mb-2">AI Explains "Why"</h3>
                <p className="text-ink-muted mb-4">Know who to call — and why — before interviews.</p>
                <div className="text-sm text-green-500 font-medium">→ Save 7 hours of wasted interviews</div>
              </div>
            </div>
          </div>
        </div>

        {/* COMPARISON TABLE */}
        <div className="py-12 sm:py-16 px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-ink mb-8 sm:mb-12 tracking-tight">
              ARISE vs Manual Screening
            </h2>

            {/* Mobile: Card Layout */}
            <div className="block sm:hidden space-y-4">
              {[
                { task: 'Screen 100 CVs', manual: '3+ hours', arise: '2 minutes' },
                { task: 'End-to-end candidate evaluation', manual: '3–5 hours', arise: '10–15 minutes' },
                { task: 'Interview prep', manual: '2+ hours', arise: 'Auto-generated' },
                { task: 'Bias risk', manual: 'High', arise: 'Minimized', manualRed: true, ariseGreen: true },
                { task: 'Cost per hire', manual: '$1,200+', arise: '$250' }
              ].map((row, i) => (
                <div key={i} className="bg-elevated rounded-xl p-4 shadow-sm border border-line">
                  <div className="font-semibold text-ink mb-3">{row.task}</div>
                  <div className="flex justify-between">
                    <div className="text-center">
                      <div className="text-xs text-ink-subtle mb-1">Manual</div>
                      <div className={`font-medium ${row.manualRed ? 'text-red-500' : 'text-ink-subtle'}`}>{row.manual}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-blue-400 mb-1">ARISE</div>
                      <div className={`font-semibold ${row.ariseGreen ? 'text-green-600' : 'text-blue-600'}`}>{row.arise}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden sm:block bg-elevated rounded-2xl shadow-lg border border-line overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-ink-subtle">Task</th>
                    <th className="px-6 py-4 text-center text-[11px] font-bold uppercase tracking-wider text-ink-subtle">Manual</th>
                    <th className="px-6 py-4 text-center text-[11px] font-bold uppercase tracking-wider text-blue-500">ARISE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-subtle text-sm">
                  <tr>
                    <td className="px-6 py-4 text-ink-muted font-medium">Screen 100 CVs</td>
                    <td className="px-6 py-4 text-center text-ink-subtle">3+ hours</td>
                    <td className="px-6 py-4 text-center text-blue-500 font-bold">2 minutes</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-ink-muted font-medium">End-to-end candidate evaluation</td>
                    <td className="px-6 py-4 text-center text-ink-subtle">3–5 hours</td>
                    <td className="px-6 py-4 text-center text-blue-500 font-bold">10–15 minutes</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-ink-muted font-medium">Interview prep</td>
                    <td className="px-6 py-4 text-center text-ink-subtle">2+ hours</td>
                    <td className="px-6 py-4 text-center text-blue-500 font-bold">Auto-generated</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-ink-muted font-medium">Bias risk</td>
                    <td className="px-6 py-4 text-center text-red-500 font-semibold">High</td>
                    <td className="px-6 py-4 text-center text-green-500 font-bold">Minimized</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-ink-muted font-medium">Cost per hire</td>
                    <td className="px-6 py-4 text-center text-ink-subtle">$1,200+</td>
                    <td className="px-6 py-4 text-center text-blue-500 font-bold">$250</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* TESTIMONIALS */}
        <div className="bg-muted py-16 px-8">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-center text-2xl font-bold text-ink mb-4">
              What Recruiting Teams Say
            </h3>
            <p className="text-center text-ink-muted mb-12">Real results from real customers</p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Testimonial 1 */}
              <div className="arise-card p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <blockquote className="text-lg text-ink-muted mb-4 leading-relaxed">
                  "Cut our screening time by 90%. ARISE is a total game changer. We now focus on high-value interviews instead of drowning in CVs."
                </blockquote>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    SK
                  </div>
                  <div>
                    <p className="font-semibold text-ink">Sarah K.</p>
                    <p className="text-sm text-ink-subtle">Head of TA, TechCorp (500+ employees)</p>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg px-4 py-2 text-sm text-green-700">
                  <span className="font-semibold">Result:</span> Time-to-hire: 45 days → 18 days
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="arise-card p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <blockquote className="text-lg text-ink-muted mb-4 leading-relaxed">
                  "The AI matching is incredibly accurate. We've reduced our time-to-hire by 60% and the quality of candidates has actually improved."
                </blockquote>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    RM
                  </div>
                  <div>
                    <p className="font-semibold text-ink">Rahul M.</p>
                    <p className="text-sm text-ink-subtle">Recruitment Manager, GlobalHire Agency</p>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg px-4 py-2 text-sm text-green-700">
                  <span className="font-semibold">Result:</span> 3x more placements, same team size
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FINAL CTA — contained premium banner (not a full-width strip) */}
        <div className="py-20">
          <div className="arise-container">
            <div className="arise-final-cta p-8 sm:p-10 md:p-14 text-center">
              <img
                src="/brand/tmplabs-mark-teal.png"
                alt=""
                aria-hidden="true"
                className="h-12 w-12 mx-auto mb-6 opacity-90 select-none"
              />
              <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4 tracking-tight">
                Take Control of Your Hiring Pipeline
              </h2>
              <p className="text-lg md:text-xl text-ink-muted mb-8 max-w-2xl mx-auto">
                Screen faster, explain every shortlist, and keep recruiters in control.
              </p>
              <button
                onClick={() => { setShowAuth(true); setShowRegister(true); trackConversion('cta_click_final'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="arise-btn-primary h-12 px-8 text-base font-bold rounded-xl w-full sm:w-auto"
              >
                Start Your Free 14-Day Trial
              </button>
              <p className="mt-4 text-sm text-ink-subtle">
                ✓ No credit card required · ✓ Set up in 5 minutes · ✓ Cancel anytime
              </p>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="py-12 px-8 bg-gray-900 text-ink-subtle">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <AriseBrand variant="dark" size="md" className="mb-4" onClick={() => handleNavigate('home')} />
                <p className="text-sm">AI-powered recruitment intelligence that helps you hire smarter, not harder.</p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Product</h4>
                <ul className="space-y-2 text-sm">
                  <li onClick={() => handleNavigate('features')} className="hover:text-white cursor-pointer">Features</li>
                  <li onClick={() => handleNavigate('pricing')} className="hover:text-white cursor-pointer">Pricing</li>
                  <li onClick={() => handleNavigate('integrations')} className="hover:text-white cursor-pointer">Integrations</li>
                  <li onClick={() => handleNavigate('api')} className="hover:text-white cursor-pointer">API</li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-sm">
                  <li onClick={() => handleNavigate('about')} className="hover:text-white cursor-pointer">About</li>
                  <li onClick={() => handleNavigate('blog')} className="hover:text-white cursor-pointer">Blog</li>
                  <li onClick={() => handleNavigate('careers')} className="hover:text-white cursor-pointer">Careers</li>
                  <li onClick={() => handleNavigate('contact')} className="hover:text-white cursor-pointer">Contact</li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Enterprise-Grade Security</h4>
                <ul className="space-y-2 text-sm">
                  <li>✓ 256-bit AES Encryption</li>
                  <li>✓ Role-Based Access Control</li>
                  <li>✓ Complete Audit Trail</li>
                  <li onClick={() => handleNavigate('security')} className="hover:text-white cursor-pointer">Security Overview →</li>
                </ul>
              </div>
            </div>
            <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm">© 2026 /tmp Labs. All rights reserved.</p>
              <div className="flex items-center gap-6 text-sm">
                <span onClick={() => handleNavigate('privacy')} className="hover:text-white cursor-pointer">Privacy Policy</span>
                <span onClick={() => handleNavigate('terms')} className="hover:text-white cursor-pointer">Terms of Service</span>
                <span onClick={() => handleNavigate('cookies')} className="hover:text-white cursor-pointer">Cookie Policy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TENANT BILLING DASHBOARD - Subscription, Usage & Payments
// ============================================================================
const TenantBillingDashboard = ({ currentUser }) => {
  const [dashboard, setDashboard] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchBillingData();

    // Check for Stripe payment return parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment_status');

    if (sessionId && paymentStatus === 'success') {
      // Payment returned successfully - verify with backend
      handlePaymentReturn(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch dashboard, pricing, and invoices in parallel
      const [dashboardRes, pricingRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/billing/dashboard`),
        axios.get(`${API}/billing/pricing`),
        axios.get(`${API}/billing/invoices?limit=10`)
      ]);

      setDashboard(dashboardRes.data);
      setPricingTiers(pricingRes.data);
      setInvoices(invoicesRes.data);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError(err.response?.data?.detail || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (newTier) => {
    if (!window.confirm(`Upgrade to ${newTier.toUpperCase()}? Your new plan will be effective immediately.`)) {
      return;
    }

    try {
      setUpgradeLoading(true);
      await axios.post(`${API}/billing/upgrade`, {
        new_tier: newTier,
        billing_cycle: 'monthly'
      });
      await fetchBillingData();
      alert('Subscription upgraded successfully!');
    } catch (err) {
      console.error('Failed to upgrade:', err);
      alert(err.response?.data?.detail || 'Failed to upgrade subscription');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handlePaymentReturn = async (sessionId) => {
    try {
      setPaymentLoading(true);
      // Verify payment with backend
      await axios.post(`${API}/billing/payment-success`, {
        session_id: sessionId
      });
      alert('Payment successful! Thank you.');
      await fetchBillingData();
    } catch (err) {
      console.error('Payment verification failed:', err);
      alert('Payment completed but verification failed. Please contact support.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setPaymentLoading(true);

      // Create Stripe checkout session on backend
      const checkoutRes = await axios.post(`${API}/billing/create-checkout`, {
        currency: 'usd',
        mode: 'payment'  // 'payment' for one-time, 'subscription' for recurring
      });

      const { checkout_url, session_id } = checkoutRes.data;

      if (!checkout_url) {
        alert('Failed to create checkout session. Please try again.');
        setPaymentLoading(false);
        return;
      }

      // Redirect to Stripe's hosted checkout page
      window.location.href = checkout_url;

    } catch (err) {
      console.error('Failed to initiate payment:', err);
      alert(err.response?.data?.detail || 'Failed to initiate payment');
      setPaymentLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-ink-muted">Loading billing information...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-6">
        <div className="flex items-center">
          <span className="text-2xl mr-3">⚠️</span>
          <div>
            <h3 className="font-semibold text-red-400">Error Loading Billing</h3>
            <p className="text-ink-muted">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchBillingData}
          className="mt-4 px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/25 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Billing Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: '📊' },
            { id: 'usage', name: 'Usage', icon: '📈' },
            { id: 'plans', name: 'Plans & Pricing', icon: '💰' },
            { id: 'invoices', name: 'Invoices', icon: '📄' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-400'
                  : 'border-transparent text-ink-subtle hover:text-ink-muted hover:border-line'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Trial Banner */}
          {dashboard.is_trial && (
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center">
                    <span className="text-3xl">⏳</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-amber-400">Trial Period Active</h3>
                    <p className="text-ink-muted">
                      {dashboard.trial_days_remaining} days remaining • Ends {formatDate(dashboard.trial_end_date)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('plans')}
                  className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          )}

          {/* Payment Due/Overdue Banner */}
          {(dashboard.payment_status === 'overdue' || dashboard.payment_status === 'pending') && (
            <div className={`rounded-xl p-6 ${
              dashboard.payment_status === 'overdue'
                ? 'bg-red-500/10 border border-red-500/25'
                : 'bg-blue-500/10 border border-blue-500/25'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    dashboard.payment_status === 'overdue' ? 'bg-red-500/15' : 'bg-blue-500/15'
                  }`}>
                    <span className="text-3xl">{dashboard.payment_status === 'overdue' ? '⚠️' : '💳'}</span>
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${
                      dashboard.payment_status === 'overdue' ? 'text-red-400' : 'text-blue-400'
                    }`}>
                      {dashboard.payment_status === 'overdue' ? 'Payment Overdue' : 'Payment Due'}
                    </h3>
                    <p className={dashboard.payment_status === 'overdue' ? 'text-ink-muted' : 'text-ink-muted'}>
                      {formatCurrency(dashboard.subscription_price)} due {formatDate(dashboard.next_billing_date)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  disabled={paymentLoading}
                  className={`px-6 py-2 text-white rounded-lg font-medium ${
                    dashboard.payment_status === 'overdue'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } ${paymentLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {paymentLoading ? 'Processing...' : 'Pay Now'}
                </button>
              </div>
            </div>
          )}

          {/* Subscription Card */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-ink">Current Subscription</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                dashboard.payment_status === 'active' ? 'bg-green-500/15 text-green-400 border border-green-500/25' :
                dashboard.payment_status === 'trial' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' :
                dashboard.payment_status === 'overdue' ? 'bg-red-500/15 text-red-400 border border-red-500/25' :
                'bg-subtle text-ink-muted border border-line'
              }`}>
                {dashboard.payment_status?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-sm text-ink-subtle font-medium">Plan</div>
                <div className="text-2xl font-bold text-ink mt-1 capitalize">
                  {dashboard.subscription_tier}
                </div>
                <div className="text-ink-muted mt-1">
                  {formatCurrency(dashboard.subscription_price)}/month
                </div>
              </div>

              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-sm text-ink-subtle font-medium">Billing Cycle</div>
                <div className="text-2xl font-bold text-ink mt-1 capitalize">
                  {dashboard.billing_cycle || 'Monthly'}
                </div>
                <div className="text-ink-muted mt-1">
                  Next: {formatDate(dashboard.next_billing_date)}
                </div>
              </div>

              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-sm text-ink-subtle font-medium">Payment Method</div>
                <div className="text-lg font-bold text-ink mt-1">
                  {dashboard.has_payment_method ? (
                    <>
                      {dashboard.payment_method_type} •••• {dashboard.payment_method_last4}
                    </>
                  ) : (
                    'No payment method'
                  )}
                </div>
                {!dashboard.has_payment_method && !dashboard.is_trial && (
                  <button className="text-blue-400 hover:text-blue-300 text-sm mt-1">
                    + Add payment method
                  </button>
                )}
              </div>
            </div>

            {/* Features */}
            {dashboard.features && dashboard.features.length > 0 && (
              <div className="mt-6 pt-6 border-t border-line">
                <h4 className="text-sm font-medium text-ink-muted mb-3">Included Features</h4>
                <div className="flex flex-wrap gap-2">
                  {dashboard.features.map((feature, idx) => (
                    <span key={idx} className="px-3 py-1 bg-subtle text-ink-muted border border-line rounded-full text-sm">
                      ✓ {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Make Payment Button - Always Visible */}
            <div className="mt-6 pt-6 border-t border-line">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-ink-muted">Make a Payment</h4>
                  <p className="text-xs text-ink-subtle mt-1">Pay via credit card or PayNow QR</p>
                </div>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all"
                >
                  💳 Pay Now
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-surface border border-line rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-ink-subtle">Profiles</div>
                  <div className="text-2xl font-bold text-ink">
                    {dashboard.profiles_used?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-ink-subtle">
                    of {dashboard.profiles_limit?.toLocaleString() || 'N/A'}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <span className="text-xl">👥</span>
                </div>
              </div>
              <div className="mt-3 bg-subtle rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(dashboard.profiles_usage_percent || 0, 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-surface border border-line rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-ink-subtle">Users</div>
                  <div className="text-2xl font-bold text-ink">
                    {dashboard.users_used || 0}
                  </div>
                  <div className="text-sm text-ink-subtle">
                    of {dashboard.users_limit || 'N/A'}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center">
                  <span className="text-xl">🧑‍💼</span>
                </div>
              </div>
              <div className="mt-3 bg-subtle rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(dashboard.users_usage_percent || 0, 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-surface border border-line rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-ink-subtle">Searches Today</div>
                  <div className="text-2xl font-bold text-ink">
                    {dashboard.searches_today || 0}
                  </div>
                  <div className="text-sm text-ink-subtle">
                    of {dashboard.searches_limit?.toLocaleString() || 'Unlimited'}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <span className="text-xl">🔍</span>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-line rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-ink-subtle">Last Payment</div>
                  <div className="text-2xl font-bold text-ink">
                    {dashboard.last_payment_amount ? formatCurrency(dashboard.last_payment_amount) : 'N/A'}
                  </div>
                  <div className="text-sm text-ink-subtle">
                    {formatDate(dashboard.last_payment_date)}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
                  <span className="text-xl">💳</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Tab */}
      {activeTab === 'usage' && dashboard && (
        <div className="space-y-6">
          <div className="bg-surface border border-line rounded-xl p-6">
            <h3 className="text-lg font-semibold text-ink mb-6">Resource Usage</h3>

            {/* Profiles Usage */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-ink-muted">Profiles</span>
                <span className="text-sm text-ink-subtle">
                  {dashboard.profiles_used?.toLocaleString()} / {dashboard.profiles_limit?.toLocaleString()}
                </span>
              </div>
              <div className="bg-subtle rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${
                    dashboard.profiles_usage_percent > 90 ? 'bg-red-500' :
                    dashboard.profiles_usage_percent > 75 ? 'bg-amber-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min(dashboard.profiles_usage_percent || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-sm text-ink-subtle mt-1">
                {dashboard.profiles_usage_percent?.toFixed(1)}% of limit used
              </p>
              {dashboard.profiles_usage_percent > 90 && (
                <p className="text-sm text-red-400 mt-1">
                  ⚠️ Approaching profile limit. Consider upgrading your plan.
                </p>
              )}
            </div>

            {/* Users Usage */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-ink-muted">Team Members</span>
                <span className="text-sm text-ink-subtle">
                  {dashboard.users_used} / {dashboard.users_limit}
                </span>
              </div>
              <div className="bg-subtle rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${
                    dashboard.users_usage_percent > 90 ? 'bg-red-500' :
                    dashboard.users_usage_percent > 75 ? 'bg-amber-500' : 'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(dashboard.users_usage_percent || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-sm text-ink-subtle mt-1">
                {dashboard.users_usage_percent?.toFixed(1)}% of limit used
              </p>
            </div>

            {/* Daily Searches */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-ink-muted">Searches Today</span>
                <span className="text-sm text-ink-subtle">
                  {dashboard.searches_today} / {dashboard.searches_limit?.toLocaleString() || 'Unlimited'}
                </span>
              </div>
              <div className="bg-subtle rounded-full h-4">
                <div
                  className="h-4 rounded-full bg-blue-600 transition-all"
                  style={{ width: `${Math.min((dashboard.searches_today / (dashboard.searches_limit || 1000)) * 100, 100)}%` }}
                ></div>
              </div>
              <p className="text-sm text-ink-subtle mt-1">
                Resets at midnight UTC
              </p>
            </div>
          </div>

          {/* Billing Period */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <h3 className="text-lg font-semibold text-ink mb-4">Current Billing Period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-ink-subtle">Period Start</div>
                <div className="font-medium text-ink">{formatDate(dashboard.current_period_start)}</div>
              </div>
              <div>
                <div className="text-sm text-ink-subtle">Period End</div>
                <div className="font-medium text-ink">{formatDate(dashboard.current_period_end)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans & Pricing Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-ink">Choose Your Plan</h3>
            <p className="text-ink-muted mt-2">Scale your recruitment operations with the right plan</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingTiers.map((tier) => (
              <div
                key={tier.tier}
                className={`relative bg-surface border rounded-xl p-6 ${
                  tier.is_current
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : tier.is_popular
                    ? 'border-blue-400'
                    : 'border-line'
                }`}
              >
                {tier.is_current && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                {tier.is_popular && !tier.is_current && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                {tier.is_best_value && !tier.is_current && !tier.is_popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                      BEST VALUE
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h4 className="text-xl font-bold text-ink capitalize">{tier.name}</h4>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-ink">
                      {formatCurrency(tier.monthly_price)}
                    </span>
                    <span className="text-ink-subtle">/month</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>{tier.limits?.max_profiles?.toLocaleString() || 'N/A'} profiles</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>{tier.limits?.max_users || 'N/A'} team members</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>{tier.limits?.max_searches_per_day?.toLocaleString() || 'Unlimited'} searches/day</span>
                  </div>
                  {tier.features?.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm">
                      <span className="text-green-400 mr-2">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {tier.is_current ? (
                  <button
                    disabled
                    className="w-full py-3 bg-subtle text-ink-subtle rounded-lg font-medium"
                  >
                    Current Plan
                  </button>
                ) : dashboard?.can_upgrade && dashboard?.available_upgrades?.includes(tier.tier) ? (
                  <button
                    onClick={() => handleUpgrade(tier.tier)}
                    disabled={upgradeLoading}
                    className={`w-full py-3 rounded-lg font-medium transition-colors ${
                      tier.is_popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {upgradeLoading ? 'Upgrading...' : 'Upgrade'}
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full py-3 bg-subtle text-ink-subtle rounded-lg font-medium"
                  >
                    Not Available
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Payment Options Section */}
          <div className="mt-8 bg-accent-soft border border-blue-500/20 rounded-xl p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-ink mb-2">Ready to Pay?</h3>
              <p className="text-ink-muted mb-4">Choose your preferred payment method</p>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all text-lg"
              >
                💳 Make Payment
              </button>
              <p className="text-sm text-ink-subtle mt-3">
                Pay via Credit Card (Stripe) or PayNow QR Code
              </p>
            </div>
          </div>

          <div className="text-center text-sm text-ink-subtle mt-6">
            <p>All plans include 14-day free trial. No credit card required to start.</p>
            <p className="mt-1">Need a custom plan? <a href="mailto:sales@tmplabs.ai" className="text-blue-400 hover:underline">Contact Sales</a></p>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-6">
          <div className="bg-surface border border-line rounded-xl overflow-hidden">
            <div className="p-6 border-b border-line">
              <h3 className="text-lg font-semibold text-ink">Invoice History</h3>
            </div>

            {invoices.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-subtle rounded-full flex items-center justify-center">
                  <span className="text-3xl">📄</span>
                </div>
                <h4 className="text-lg font-medium text-ink mb-2">No invoices yet</h4>
                <p className="text-ink-subtle">
                  {dashboard?.is_trial
                    ? 'Invoices will appear after your trial ends.'
                    : 'No invoices have been generated for your account.'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-line">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-line">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-muted">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-ink">
                          {invoice.invoice_number || invoice.id?.slice(0, 8)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-ink">{formatCurrency(invoice.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-500/15 text-green-400 border border-green-500/25' :
                          invoice.status === 'pending' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' :
                          invoice.status === 'overdue' ? 'bg-red-500/15 text-red-400 border border-red-500/25' :
                          'bg-subtle text-ink-muted border border-line'
                        }`}>
                          {invoice.status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-subtle">
                        {formatDate(invoice.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button className="text-blue-400 hover:text-blue-300">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent Payments Summary */}
          {dashboard?.recent_invoices?.length > 0 && (
            <div className="bg-accent-soft border border-blue-500/20 rounded-xl p-6">
              <h4 className="font-medium text-ink mb-2">Payment Summary</h4>
              <p className="text-ink-muted">
                Last payment: {formatCurrency(dashboard.last_payment_amount)} on {formatDate(dashboard.last_payment_date)}
              </p>
              {dashboard.next_billing_date && (
                <p className="text-ink-muted mt-1">
                  Next payment due: {formatDate(dashboard.next_billing_date)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payment Options Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-elevated border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-muted border-b border-line p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-ink">Choose Payment Method</h2>
                  <p className="text-ink-muted mt-1">
                    Amount due: {formatCurrency(dashboard?.subscription_price || 99)}
                  </p>
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-ink-subtle hover:text-ink text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Payment Options */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Stripe Card Payment */}
                <div className="border border-line hover:border-blue-500/50 bg-surface rounded-xl p-6 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/15 rounded-full flex items-center justify-center">
                      <span className="text-3xl">💳</span>
                    </div>
                    <h3 className="text-xl font-bold text-ink mb-2">Credit/Debit Card</h3>
                    <p className="text-ink-muted text-sm mb-4">
                      Pay securely with Visa, Mastercard, or American Express via Stripe
                    </p>
                    <div className="flex justify-center gap-2 mb-4">
                      <span className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 text-xs rounded">Visa</span>
                      <span className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 text-xs rounded">Mastercard</span>
                      <span className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/25 text-xs rounded">Amex</span>
                    </div>
                    <button
                      onClick={() => {
                        setShowPaymentModal(false);
                        handlePayment();
                      }}
                      disabled={paymentLoading}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
                    >
                      {paymentLoading ? 'Redirecting...' : 'Pay with Card'}
                    </button>
                  </div>
                </div>

                {/* PayNow QR Code */}
                <div className="border border-line hover:border-blue-500/50 bg-surface rounded-xl p-6 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/15 rounded-full flex items-center justify-center">
                      <span className="text-3xl">📱</span>
                    </div>
                    <h3 className="text-xl font-bold text-ink mb-2">PayNow / SGQR</h3>
                    <p className="text-ink-muted text-sm mb-4">
                      Scan QR code with your banking app (Singapore banks supported)
                    </p>
                    <div className="bg-white p-4 rounded-lg border border-line mb-4">
                      <img
                        src="/MayBankPayNowQR.png"
                        alt="PayNow QR Code"
                        className="w-48 h-48 mx-auto object-contain"
                      />
                    </div>
                    <div className="text-sm text-ink-muted">
                      <p className="font-medium text-ink">Maybank Singapore</p>
                      <p className="text-xs text-ink-subtle mt-1">
                        After payment, please email your receipt to<br />
                        <a href="mailto:billing@tmplabs.ai" className="text-blue-400">billing@tmplabs.ai</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Text */}
              <div className="mt-6 p-4 bg-muted border border-line rounded-lg">
                <h4 className="font-medium text-ink mb-2">💡 Payment Instructions</h4>
                <ul className="text-sm text-ink-muted space-y-1">
                  <li>• <strong>Card Payment:</strong> Instant activation after successful payment</li>
                  <li>• <strong>PayNow:</strong> Send payment receipt to billing@tmplabs.ai for manual verification (within 24 hours)</li>
                  <li>• Need help? Contact <a href="mailto:support@tmplabs.ai" className="text-blue-400">support@tmplabs.ai</a></li>
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-muted rounded-b-2xl border-t border-line">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full py-3 bg-subtle hover:bg-muted text-ink-muted border border-line rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ADMIN UTILITIES - Data Management & System Tools (Tenant Admin)
// ============================================================================
const AdminUtilities = ({ currentUser }) => {
  const [activeSection, setActiveSection] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data stats
  const [dataStats, setDataStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Purge form
  const [purgeScope, setPurgeScope] = useState('all_candidate_data');
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purging, setPurging] = useState(false);

  // Batch scan
  const [scanConfig, setScanConfig] = useState({
    dry_run: true,
    max_profiles: null,
    min_confidence: 0.85
  });
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  // System health
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Fetch data stats on mount
  useEffect(() => {
    fetchDataStats();
  }, []);

  const fetchDataStats = async () => {
    try {
      setStatsLoading(true);
      setError('');
      const response = await axios.get(`${API}/admin/data/stats`);
      setDataStats(response.data);
    } catch (err) {
      console.error('Failed to fetch data stats:', err);
      setError(err.response?.data?.detail || 'Failed to fetch data statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchHealthStatus = async () => {
    try {
      setHealthLoading(true);
      const response = await axios.get(`${API}/admin/health/detailed`);
      setHealthStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch health status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch health status');
    } finally {
      setHealthLoading(false);
    }
  };

  const handlePurge = async () => {
    if (!purgeConfirm) {
      setError('You must confirm the purge operation');
      return;
    }

    try {
      setPurging(true);
      setError('');
      setSuccess('');
      setPurgeResult(null);

      const response = await axios.post(`${API}/admin/data/purge`, {
        scope: purgeScope,
        confirm: true,
        reason: purgeReason || 'Admin requested purge'
      });

      setPurgeResult(response.data);
      setSuccess(`Successfully purged ${response.data.total_documents_deleted} documents`);
      setPurgeConfirm(false);
      setPurgeReason('');

      // Refresh stats after purge
      fetchDataStats();
    } catch (err) {
      console.error('Purge failed:', err);
      setError(err.response?.data?.detail || 'Purge operation failed');
    } finally {
      setPurging(false);
    }
  };

  const handleBatchScan = async () => {
    try {
      setScanning(true);
      setError('');
      setScanResult(null);

      const response = await axios.post(`${API}/admin/data/batch-scan`, scanConfig);
      setScanResult(response.data);

      if (response.data.status === 'completed') {
        setSuccess(`Scan complete: found ${response.data.duplicates_found} potential duplicates in ${response.data.profiles_scanned} profiles`);
      }
    } catch (err) {
      console.error('Batch scan failed:', err);
      setError(err.response?.data?.detail || 'Batch scan failed');
    } finally {
      setScanning(false);
    }
  };

  const purgeScopes = [
    { value: 'profiles_only', label: 'Profiles Only', desc: 'Delete all candidate profiles' },
    { value: 'profiles_and_vectors', label: 'Profiles + Embeddings', desc: 'Delete profiles and vector embeddings' },
    { value: 'identity_data', label: 'Identity Data', desc: 'Delete identity resolution data only' },
    { value: 'automatch_data', label: 'AutoMatch Data', desc: 'Delete AutoMatch results only' },
    { value: 'all_candidate_data', label: 'All Candidate Data', desc: 'Delete all candidate-related data' },
    { value: 'full_tenant_reset', label: 'Full Reset', desc: 'Delete everything except config and users' }
  ];

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'stats', label: 'Data Statistics', icon: '📊' },
          { id: 'purge', label: 'Purge Data', icon: '🗑️' },
          { id: 'scan', label: 'Duplicate Scan', icon: '🔍' },
          { id: 'health', label: 'System Health', icon: '❤️' }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => {
              setActiveSection(section.id);
              if (section.id === 'health') fetchHealthStatus();
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-blue-600 text-white'
                : 'bg-muted text-ink-muted border border-line hover:bg-subtle hover:text-ink'
            }`}
          >
            <span className="mr-2">{section.icon}</span>
            {section.label}
          </button>
        ))}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/25 text-red-400 px-4 py-3 rounded-lg flex items-center">
          <span className="mr-2">❌</span>
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/25 text-green-400 px-4 py-3 rounded-lg flex items-center">
          <span className="mr-2">✅</span>
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-300">×</button>
        </div>
      )}

      {/* Data Statistics Section */}
      {activeSection === 'stats' && (
        <div className="bg-surface rounded-xl border border-line shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-ink flex items-center">
              <span className="mr-2">📊</span>
              Tenant Data Statistics
            </h3>
            <button
              onClick={fetchDataStats}
              disabled={statsLoading}
              className="px-4 py-2 bg-muted text-ink-muted border border-line rounded-lg hover:bg-subtle hover:text-ink disabled:opacity-50"
            >
              {statsLoading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {statsLoading && !dataStats ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-ink-subtle">Loading statistics...</p>
            </div>
          ) : dataStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-blue-400">{dataStats.total_profiles?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">Total Profiles</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-green-400">{dataStats.profiles_with_embeddings?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">With Embeddings</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.identity_matches?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">Identity Matches</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.identity_clusters?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">Identity Clusters</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.automatch_results?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">AutoMatch Results</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.automatch_runs?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">AutoMatch Runs</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.jds?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">Job Descriptions</div>
              </div>
              <div className="bg-muted border border-line rounded-lg p-4">
                <div className="text-3xl font-bold text-ink">{dataStats.interviews?.toLocaleString() || 0}</div>
                <div className="text-sm text-ink-muted">Interviews</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-ink-subtle">
              No statistics available
            </div>
          )}

          {dataStats && (
            <div className="mt-6 pt-6 border-t border-line text-sm text-ink-subtle">
              <div className="flex flex-wrap justify-between gap-2">
                <span>Oldest Profile: {dataStats.oldest_profile ? new Date(dataStats.oldest_profile).toLocaleDateString() : 'N/A'}</span>
                <span>Newest Profile: {dataStats.newest_profile ? new Date(dataStats.newest_profile).toLocaleDateString() : 'N/A'}</span>
                <span>Generated: {dataStats.generated_at ? new Date(dataStats.generated_at).toLocaleString() : 'N/A'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Purge Data Section */}
      {activeSection === 'purge' && (
        <div className="bg-surface rounded-xl border border-red-500/30 shadow-sm p-6">
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">⚠️</span>
            <div>
              <h3 className="text-lg font-bold text-red-400">Purge Tenant Data</h3>
              <p className="text-sm text-ink-muted">Warning: This action permanently deletes data and cannot be undone.</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Purge Scope Selection */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">Select Purge Scope</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {purgeScopes.map(scope => (
                  <label
                    key={scope.value}
                    className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                      purgeScope === scope.value
                        ? 'border-red-500 bg-red-500/10'
                        : 'border-line hover:border-red-500/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="purgeScope"
                      value={scope.value}
                      checked={purgeScope === scope.value}
                      onChange={(e) => setPurgeScope(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium text-ink">{scope.label}</div>
                      <div className="text-sm text-ink-subtle">{scope.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">Reason for Purge (Optional)</label>
              <textarea
                value={purgeReason}
                onChange={(e) => setPurgeReason(e.target.value)}
                placeholder="Enter reason for purge (for audit log)"
                className="w-full px-4 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={2}
              />
            </div>

            {/* Confirmation */}
            <div className="bg-red-500/15 border border-red-500/30 rounded-lg p-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={purgeConfirm}
                  onChange={(e) => setPurgeConfirm(e.target.checked)}
                  className="mr-3 w-5 h-5 text-red-600 focus:ring-red-500 border-line bg-surface rounded"
                />
                <span className="text-red-400 font-medium">
                  I understand this action is irreversible and will permanently delete the selected data
                </span>
              </label>
            </div>

            {/* Purge Button */}
            <button
              onClick={handlePurge}
              disabled={!purgeConfirm || purging}
              className={`w-full py-3 px-6 rounded-lg font-bold text-white transition-colors ${
                purgeConfirm && !purging
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-subtle text-ink-subtle cursor-not-allowed'
              }`}
            >
              {purging ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Purging Data...
                </span>
              ) : (
                <span>🗑️ Execute Purge</span>
              )}
            </button>

            {/* Purge Result */}
            {purgeResult && (
              <div className={`p-4 rounded-lg border ${purgeResult.success ? 'bg-green-500/10 border-green-500/25' : 'bg-red-500/10 border-red-500/25'}`}>
                <h4 className={`font-bold mb-2 ${purgeResult.success ? 'text-green-400' : 'text-red-400'}`}>{purgeResult.success ? '✅ Purge Complete' : '❌ Purge Failed'}</h4>
                <div className="text-sm space-y-1 text-ink-muted">
                  <p>Profiles Deleted: {purgeResult.profiles_deleted}</p>
                  <p>Embeddings Deleted: {purgeResult.embeddings_deleted}</p>
                  <p>Identity Matches Deleted: {purgeResult.identity_matches_deleted}</p>
                  <p>Identity Clusters Deleted: {purgeResult.identity_clusters_deleted}</p>
                  <p>AutoMatch Results Deleted: {purgeResult.automatch_results_deleted}</p>
                  <p className="font-bold pt-2 border-t border-line mt-2 text-ink">Total Documents Deleted: {purgeResult.total_documents_deleted}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Duplicate Scan Section */}
      {activeSection === 'scan' && (
        <div className="bg-surface rounded-xl border border-line shadow-sm p-6">
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">🔍</span>
            <div>
              <h3 className="text-lg font-bold text-ink">Batch Duplicate Scan</h3>
              <p className="text-sm text-ink-muted">Scan existing profiles for potential duplicates</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Scan Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">Mode</label>
                <select
                  value={scanConfig.dry_run ? 'dry_run' : 'record'}
                  onChange={(e) => setScanConfig({...scanConfig, dry_run: e.target.value === 'dry_run'})}
                  className="w-full px-4 py-2 bg-surface text-ink border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:[color-scheme:dark]"
                >
                  <option value="dry_run">Dry Run (Preview Only)</option>
                  <option value="record">Record Matches</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">Max Profiles</label>
                <input
                  type="number"
                  value={scanConfig.max_profiles || ''}
                  onChange={(e) => setScanConfig({...scanConfig, max_profiles: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="All profiles"
                  min="1"
                  max="10000"
                  className="w-full px-4 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">Min Confidence</label>
                <input
                  type="number"
                  value={scanConfig.min_confidence}
                  onChange={(e) => setScanConfig({...scanConfig, min_confidence: parseFloat(e.target.value)})}
                  min="0.5"
                  max="1.0"
                  step="0.05"
                  className="w-full px-4 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Scan Button */}
            <button
              onClick={handleBatchScan}
              disabled={scanning}
              className={`w-full py-3 px-6 rounded-lg font-bold text-white transition-colors ${
                !scanning
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-subtle text-ink-subtle cursor-not-allowed'
              }`}
            >
              {scanning ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Scanning Profiles...
                </span>
              ) : (
                <span>🔍 Start Duplicate Scan</span>
              )}
            </button>

            {/* Scan Result */}
            {scanResult && (
              <div className="bg-accent-soft border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-ink">Scan Results</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    scanResult.status === 'completed' ? 'bg-green-500/15 text-green-400 border-green-500/25' : 'bg-amber-500/15 text-amber-400 border-amber-500/25'
                  }`}>
                    {scanResult.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">{scanResult.profiles_scanned}</div>
                    <div className="text-xs text-ink-subtle">Profiles Scanned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">{scanResult.duplicates_found}</div>
                    <div className="text-xs text-ink-subtle">Duplicates Found</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-ink">{scanResult.flagged_for_review}</div>
                    <div className="text-xs text-ink-subtle">Flagged for Review</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-ink">{scanResult.processing_time_ms}ms</div>
                    <div className="text-xs text-ink-subtle">Processing Time</div>
                  </div>
                </div>

                {scanResult.results && scanResult.results.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-medium text-ink-muted mb-2">Sample Matches:</h5>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {scanResult.results.slice(0, 10).map((match, idx) => (
                        <div key={idx} className="bg-surface rounded p-3 border border-line flex items-center justify-between">
                          <div>
                            <span className="font-medium text-ink">{match.profile_name}</span>
                            <span className="text-ink-subtle mx-2">↔</span>
                            <span className="font-medium text-ink">{match.matched_profile_name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded text-xs border ${
                              match.confidence_score >= 0.9 ? 'bg-green-500/15 text-green-400 border-green-500/25' :
                              match.confidence_score >= 0.7 ? 'bg-amber-500/15 text-amber-400 border-amber-500/25' :
                              'bg-subtle text-ink-muted border-line'
                            }`}>
                              {(match.confidence_score * 100).toFixed(0)}%
                            </span>
                            <span className="text-xs text-ink-subtle">{match.match_reasons.join(', ')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Health Section */}
      {activeSection === 'health' && (
        <div className="bg-surface rounded-xl border border-line shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-3">❤️</span>
              <div>
                <h3 className="text-lg font-bold text-ink">System Health</h3>
                <p className="text-sm text-ink-muted">Real-time status of system components</p>
              </div>
            </div>
            <button
              onClick={fetchHealthStatus}
              disabled={healthLoading}
              className="px-4 py-2 bg-muted text-ink-muted border border-line rounded-lg hover:bg-subtle hover:text-ink disabled:opacity-50"
            >
              {healthLoading ? 'Checking...' : '🔄 Refresh'}
            </button>
          </div>

          {healthLoading && !healthStatus ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-ink-subtle">Checking system health...</p>
            </div>
          ) : healthStatus ? (
            <div className="space-y-4">
              {/* Overall Status */}
              <div className={`p-4 rounded-lg border flex items-center justify-between ${
                healthStatus.status === 'healthy' ? 'bg-green-500/10 border-green-500/25' :
                healthStatus.status === 'degraded' ? 'bg-amber-500/10 border-amber-500/25' :
                'bg-red-500/10 border-red-500/25'
              }`}>
                <div className="flex items-center">
                  <span className="text-2xl mr-3">
                    {healthStatus.status === 'healthy' ? '✅' : healthStatus.status === 'degraded' ? '⚠️' : '❌'}
                  </span>
                  <div>
                    <div className="font-bold text-ink">Overall Status: {healthStatus.status?.toUpperCase()}</div>
                    <div className="text-sm text-ink-muted">Last checked: {new Date(healthStatus.timestamp).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Component Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthStatus.components && Object.entries(healthStatus.components).map(([name, component]) => (
                  <div key={name} className={`p-4 rounded-lg border ${
                    component.status === 'healthy' ? 'bg-green-500/10 border-green-500/25' :
                    component.status === 'unhealthy' ? 'bg-red-500/10 border-red-500/25' :
                    'bg-muted border-line'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-ink capitalize">{name.replace('_', ' ')}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${
                        component.status === 'healthy' ? 'bg-green-500/15 text-green-400 border-green-500/25' :
                        component.status === 'unhealthy' ? 'bg-red-500/15 text-red-400 border-red-500/25' :
                        'bg-subtle text-ink-muted border-line'
                      }`}>
                        {component.status}
                      </span>
                    </div>
                    <p className="text-sm text-ink-muted">{component.message}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <button
                onClick={fetchHealthStatus}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Check System Health
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TENANT EMAIL INTAKE SETTINGS - BYOD Email Configuration (Tenant Admin)
// ============================================================================
const TenantEmailIntakeSettings = ({ currentUser }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('connection');

  // Form state
  const [formData, setFormData] = useState({
    email_address: '',
    provider_type: 'imap',
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    auto_reply_enabled: false,
    auto_reply_subject: 'Thank you for your application',
    auto_reply_body: 'We have received your application and will review it shortly.',
    spam_filtering_enabled: true,
    duplicate_detection_enabled: true,
    attachment_extraction_enabled: true,
    daily_email_limit: 1000,
    forwarding_address: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/email-intake/tenant/byod-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data) {
        setConfig(response.data);
        // Populate form with existing config
        setFormData({
          email_address: response.data.email_address || '',
          provider_type: response.data.provider_type || 'imap',
          imap_host: response.data.imap_config?.host || '',
          imap_port: response.data.imap_config?.port || 993,
          imap_username: response.data.imap_config?.username || '',
          imap_password: '', // Never pre-fill password
          imap_use_ssl: response.data.imap_config?.use_ssl ?? true,
          smtp_host: response.data.smtp_config?.host || '',
          smtp_port: response.data.smtp_config?.port || 587,
          smtp_username: response.data.smtp_config?.username || '',
          smtp_password: '', // Never pre-fill password
          smtp_use_tls: response.data.smtp_config?.use_tls ?? true,
          auto_reply_enabled: response.data.processing_options?.auto_reply_enabled ?? false,
          auto_reply_subject: response.data.processing_options?.auto_reply_subject || 'Thank you for your application',
          auto_reply_body: response.data.processing_options?.auto_reply_body || 'We have received your application and will review it shortly.',
          spam_filtering_enabled: response.data.processing_options?.spam_filtering_enabled ?? true,
          duplicate_detection_enabled: response.data.processing_options?.duplicate_detection_enabled ?? true,
          attachment_extraction_enabled: response.data.processing_options?.attachment_extraction_enabled ?? true,
          daily_email_limit: response.data.rate_limits?.daily_email_limit || 1000,
          forwarding_address: response.data.forwarding_address || ''
        });
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to load email configuration');
      }
      // 404 means no config exists yet - that's OK
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/email-intake/tenant/byod-config/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setConnectionStatus(response.data);
    } catch (err) {
      // Status might not exist if config doesn't exist
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        email_address: formData.email_address,
        provider_type: formData.provider_type,
        imap_config: {
          host: formData.imap_host,
          port: parseInt(formData.imap_port),
          username: formData.imap_username,
          password: formData.imap_password || undefined,
          use_ssl: formData.imap_use_ssl
        },
        smtp_config: {
          host: formData.smtp_host,
          port: parseInt(formData.smtp_port),
          username: formData.smtp_username,
          password: formData.smtp_password || undefined,
          use_tls: formData.smtp_use_tls
        },
        processing_options: {
          auto_reply_enabled: formData.auto_reply_enabled,
          auto_reply_subject: formData.auto_reply_subject,
          auto_reply_body: formData.auto_reply_body,
          spam_filtering_enabled: formData.spam_filtering_enabled,
          duplicate_detection_enabled: formData.duplicate_detection_enabled,
          attachment_extraction_enabled: formData.attachment_extraction_enabled
        },
        forwarding_address: formData.forwarding_address || undefined
      };

      // Remove empty password fields if not provided
      if (!payload.imap_config.password) delete payload.imap_config.password;
      if (!payload.smtp_config.password) delete payload.smtp_config.password;

      let response;
      if (config) {
        // Update existing config
        response = await axios.put(`${API}/email-intake/tenant/byod-config`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
      } else {
        // Create new config
        response = await axios.post(`${API}/email-intake/tenant/byod-config`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
      }

      setConfig(response.data);
      setSuccess('Email configuration saved. Please test the connection.');
      setShowPasswordFields(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError('');
      setSuccess('');

      const response = await axios.post(`${API}/email-intake/tenant/byod-config/test-connection`, {
        test_imap: true,
        test_smtp: true
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.overall_success) {
        setSuccess('Connection test successful! Your email configuration is working.');
      } else {
        const errors = [];
        if (!response.data.imap_success) errors.push(`IMAP: ${response.data.imap_error || 'Failed'}`);
        if (!response.data.smtp_success) errors.push(`SMTP: ${response.data.smtp_error || 'Failed'}`);
        setError(`Connection test failed: ${errors.join(', ')}`);
      }
      fetchConnectionStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this email configuration? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await axios.delete(`${API}/email-intake/tenant/byod-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setConfig(null);
      setFormData({
        email_address: '',
        provider_type: 'imap',
        imap_host: '',
        imap_port: 993,
        imap_username: '',
        imap_password: '',
        imap_use_ssl: true,
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_use_tls: true,
        auto_reply_enabled: false,
        auto_reply_subject: 'Thank you for your application',
        auto_reply_body: 'We have received your application and will review it shortly.',
        spam_filtering_enabled: true,
        duplicate_detection_enabled: true,
        attachment_extraction_enabled: true,
        daily_email_limit: 1000,
        forwarding_address: ''
      });
      setSuccess('Email configuration deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'connection', name: 'Email Connection', icon: '🔌' },
    { id: 'processing', name: 'Processing Options', icon: '⚙️' },
    { id: 'auto-reply', name: 'Auto-Reply', icon: '↩️' },
    { id: 'status', name: 'Status & Logs', icon: '📊' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-4xl">📬</span>
          <div>
            <h2 className="text-2xl font-bold">Email Intake Settings <span className="text-sm bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full ml-2">BYOD</span></h2>
            <p className="opacity-90">Configure your organization's email ingestion for automatic resume processing</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-white bg-opacity-20 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-white hover:bg-white hover:bg-opacity-20'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Status Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-red-500 text-lg">⚠️</span>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-green-500 text-lg">✅</span>
              <span className="text-green-700">{success}</span>
            </div>
          </div>
        )}

        {/* Superadmin Override Warning */}
        {config?.superadmin_override?.processing_enabled_override === false && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-orange-500 text-lg">🚫</span>
              <span className="text-orange-700">
                <strong>Processing Suspended by Platform Admin:</strong> Your email intake has been temporarily disabled.
                {config.superadmin_notes && <span className="block mt-1 text-sm">Reason: {config.superadmin_notes}</span>}
              </span>
            </div>
          </div>
        )}

        {/* Connection Tab */}
        {activeTab === 'connection' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address for Resume Intake *
                </label>
                <input
                  type="email"
                  name="email_address"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  placeholder="careers@yourcompany.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">This is the email address candidates will send resumes to</p>
              </div>

              {/* Provider Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider Type</label>
                <select
                  name="provider_type"
                  value={formData.provider_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="imap">IMAP (Recommended)</option>
                  <option value="gmail">Gmail API</option>
                  <option value="outlook">Microsoft 365 / Outlook</option>
                  <option value="forward_only">Forward Only (No Direct Access)</option>
                </select>
              </div>

              {/* Forwarding Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Forwarding Address
                </label>
                <input
                  type="email"
                  name="forwarding_address"
                  value={formData.forwarding_address}
                  onChange={handleInputChange}
                  placeholder="intake-yourcompany@arise.tmplabs.ai"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">Optional: Forward emails here for processing</p>
              </div>
            </div>

            {/* IMAP Settings */}
            {formData.provider_type === 'imap' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">IMAP Configuration (Incoming Mail)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Host *</label>
                    <input
                      type="text"
                      name="imap_host"
                      value={formData.imap_host}
                      onChange={handleInputChange}
                      placeholder="imap.gmail.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Port *</label>
                    <input
                      type="number"
                      name="imap_port"
                      value={formData.imap_port}
                      onChange={handleInputChange}
                      placeholder="993"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username *</label>
                    <input
                      type="text"
                      name="imap_username"
                      value={formData.imap_username}
                      onChange={handleInputChange}
                      placeholder="careers@yourcompany.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password / App Password {config ? '(leave blank to keep existing)' : '*'}
                    </label>
                    <input
                      type="password"
                      name="imap_password"
                      value={formData.imap_password}
                      onChange={handleInputChange}
                      placeholder={config ? '••••••••' : 'Enter app password'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="imap_use_ssl"
                      checked={formData.imap_use_ssl}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-700">Use SSL/TLS</label>
                  </div>
                </div>
              </div>
            )}

            {/* SMTP Settings */}
            {formData.provider_type === 'imap' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Configuration (Outgoing Mail - for Auto-Reply)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                    <input
                      type="text"
                      name="smtp_host"
                      value={formData.smtp_host}
                      onChange={handleInputChange}
                      placeholder="smtp.gmail.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                    <input
                      type="number"
                      name="smtp_port"
                      value={formData.smtp_port}
                      onChange={handleInputChange}
                      placeholder="587"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                    <input
                      type="text"
                      name="smtp_username"
                      value={formData.smtp_username}
                      onChange={handleInputChange}
                      placeholder="careers@yourcompany.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password {config ? '(leave blank to keep existing)' : ''}
                    </label>
                    <input
                      type="password"
                      name="smtp_password"
                      value={formData.smtp_password}
                      onChange={handleInputChange}
                      placeholder={config ? '••••••••' : 'Enter password'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="smtp_use_tls"
                      checked={formData.smtp_use_tls}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-700">Use STARTTLS</label>
                  </div>
                </div>
              </div>
            )}

            {/* Gmail/Outlook Quick Setup */}
            {(formData.provider_type === 'gmail' || formData.provider_type === 'outlook') && (
              <div className="border-t pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-blue-900 mb-2">
                    {formData.provider_type === 'gmail' ? 'Gmail API Setup' : 'Microsoft 365 Setup'}
                  </h3>
                  <p className="text-blue-700 text-sm mb-4">
                    {formData.provider_type === 'gmail'
                      ? 'Connect via Google OAuth for secure, passwordless access to your Gmail inbox.'
                      : 'Connect via Microsoft Graph API for secure access to your Outlook/365 inbox.'}
                  </p>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {formData.provider_type === 'gmail' ? 'Connect Gmail' : 'Connect Microsoft 365'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Processing Options Tab */}
        {activeTab === 'processing' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Spam Filtering</h4>
                  <p className="text-sm text-gray-500">Automatically filter out spam and irrelevant emails</p>
                </div>
                <input
                  type="checkbox"
                  name="spam_filtering_enabled"
                  checked={formData.spam_filtering_enabled}
                  onChange={handleInputChange}
                  className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Duplicate Detection</h4>
                  <p className="text-sm text-gray-500">Detect and flag duplicate resume submissions</p>
                </div>
                <input
                  type="checkbox"
                  name="duplicate_detection_enabled"
                  checked={formData.duplicate_detection_enabled}
                  onChange={handleInputChange}
                  className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Attachment Extraction</h4>
                  <p className="text-sm text-gray-500">Automatically extract and process resume attachments (PDF, DOCX)</p>
                </div>
                <input
                  type="checkbox"
                  name="attachment_extraction_enabled"
                  checked={formData.attachment_extraction_enabled}
                  onChange={handleInputChange}
                  className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
              </div>
            </div>

            {/* Rate Limits Display */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rate Limits</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Daily Email Limit</h4>
                    <p className="text-sm text-gray-500">Maximum emails processed per day</p>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-green-600">{config?.rate_limits?.daily_email_limit || 1000}</span>
                    <p className="text-sm text-gray-500">emails/day</p>
                  </div>
                </div>
                {config?.rate_limits?.emails_processed_today > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Today's usage</span>
                      <span>{config.rate_limits.emails_processed_today} / {config.rate_limits.daily_email_limit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${Math.min(100, (config.rate_limits.emails_processed_today / config.rate_limits.daily_email_limit) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">Contact support to increase your limit</p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-Reply Tab */}
        {activeTab === 'auto-reply' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Enable Auto-Reply</h4>
                <p className="text-sm text-gray-500">Automatically send acknowledgment emails to applicants</p>
              </div>
              <input
                type="checkbox"
                name="auto_reply_enabled"
                checked={formData.auto_reply_enabled}
                onChange={handleInputChange}
                className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
            </div>

            {formData.auto_reply_enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-Reply Subject
                  </label>
                  <input
                    type="text"
                    name="auto_reply_subject"
                    value={formData.auto_reply_subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-Reply Message
                  </label>
                  <textarea
                    name="auto_reply_body"
                    value={formData.auto_reply_body}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Available variables: {'{candidate_name}'}, {'{position}'}, {'{company_name}'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status Tab */}
        {activeTab === 'status' && (
          <div className="space-y-6">
            {/* Connection Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border-2 ${
                connectionStatus?.imap_connected ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                <div className="flex items-center space-x-2">
                  <span className={`text-2xl ${connectionStatus?.imap_connected ? 'text-green-500' : 'text-gray-400'}`}>
                    {connectionStatus?.imap_connected ? '✅' : '⚪'}
                  </span>
                  <div>
                    <h4 className="font-medium text-gray-900">IMAP Connection</h4>
                    <p className="text-sm text-gray-500">
                      {connectionStatus?.imap_connected ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                {connectionStatus?.last_imap_check && (
                  <p className="text-xs text-gray-400 mt-2">
                    Last checked: {new Date(connectionStatus.last_imap_check).toLocaleString()}
                  </p>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 ${
                connectionStatus?.smtp_connected ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                <div className="flex items-center space-x-2">
                  <span className={`text-2xl ${connectionStatus?.smtp_connected ? 'text-green-500' : 'text-gray-400'}`}>
                    {connectionStatus?.smtp_connected ? '✅' : '⚪'}
                  </span>
                  <div>
                    <h4 className="font-medium text-gray-900">SMTP Connection</h4>
                    <p className="text-sm text-gray-500">
                      {connectionStatus?.smtp_connected ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
                {connectionStatus?.last_smtp_check && (
                  <p className="text-xs text-gray-400 mt-2">
                    Last checked: {new Date(connectionStatus.last_smtp_check).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Config Status */}
            {config && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Configuration Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className={`text-2xl font-bold ${
                      config.status === 'active' ? 'text-green-600' :
                      config.status === 'suspended' ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {config.status?.toUpperCase() || 'DRAFT'}
                    </div>
                    <div className="text-sm text-gray-500">Status</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {config.rate_limits?.emails_processed_today || 0}
                    </div>
                    <div className="text-sm text-gray-500">Processed Today</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {config.stats?.total_processed || 0}
                    </div>
                    <div className="text-sm text-gray-500">Total Processed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">
                      {config.stats?.spam_blocked || 0}
                    </div>
                    <div className="text-sm text-gray-500">Spam Blocked</div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity Placeholder */}
            <div className="border-t pt-6">
              <h4 className="font-medium text-gray-900 mb-3">Recent Activity</h4>
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <span className="text-4xl">📭</span>
                <p className="text-gray-500 mt-2">No recent activity</p>
                <p className="text-sm text-gray-400">Email processing logs will appear here</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 pt-6 border-t flex justify-between">
          <div>
            {config && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Delete Configuration
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            {config && (
              <button
                onClick={handleTestConnection}
                disabled={testing || saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {testing ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Testing...</span>
                  </>
                ) : (
                  <>
                    <span>🔌</span>
                    <span>Test Connection</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.email_address}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>{config ? 'Update Configuration' : 'Save Configuration'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Intelligent Intake Inbox™ Management Component (Superadmin Only)
const EmailIntakeManagement = ({ currentUser }) => {
  const [activeSubTab, setActiveSubTab] = useState('tenant-configs');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Define submenu tabs
  const subTabs = [
    { id: 'tenant-configs', name: 'Tenant Configurations', icon: '🏢', desc: 'Manage all tenant email configurations' },
    { id: 'mailbox-routing', name: 'Mailbox Routing Rules', icon: '📮', desc: 'Configure email routing and filtering rules' },
    { id: 'jd-mappings', name: 'JD → Email Mapping', icon: '🔗', desc: 'Job description to email address mappings' },
    { id: 'inbox-logs', name: 'Inbox Processing Logs', icon: '📋', desc: 'Email processing activity and status' },
    { id: 'attachment-logs', name: 'Attachment Extraction Logs', icon: '📎', desc: 'Resume extraction and parsing logs' },
    { id: 'spam-security', name: 'Spam / Security Filter', icon: '🛡️', desc: 'Spam detection and security filtering' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-4xl">📧</span>
          <div>
            <h2 className="text-2xl font-bold">Intelligent Intake Inbox™ <span className="text-sm bg-slate-100 text-slate-700 px-2 py-1 rounded-full ml-2">STP++</span></h2>
            <p className="opacity-90">JD-Specific Auto-Ingestion System</p>
          </div>
        </div>

        {/* Submenu Tabs */}
        <div className="flex space-x-1 bg-white bg-opacity-20 p-1 rounded-lg">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeSubTab === tab.id 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-white hover:bg-white hover:bg-opacity-20'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-red-500 text-lg">⚠️</span>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Tenant Configurations Tab (Superadmin) */}
        {activeSubTab === 'tenant-configs' && (
          <SuperadminTenantEmailConfigs currentUser={currentUser} setError={setError} />
        )}

        {/* Mailbox Routing Rules Tab */}
        {activeSubTab === 'mailbox-routing' && (
          <MailboxRoutingRules currentUser={currentUser} setError={setError} />
        )}

        {/* JD Email Mappings Tab */}
        {activeSubTab === 'jd-mappings' && (
          <JDEmailMappings currentUser={currentUser} setError={setError} />
        )}

        {/* Inbox Processing Logs Tab */}
        {activeSubTab === 'inbox-logs' && (
          <InboxProcessingLogs currentUser={currentUser} setError={setError} />
        )}

        {/* Attachment Extraction Logs Tab */}
        {activeSubTab === 'attachment-logs' && (
          <AttachmentExtractionLogs currentUser={currentUser} setError={setError} />
        )}

        {/* Spam Security Filter Tab */}
        {activeSubTab === 'spam-security' && (
          <SpamSecurityFilter currentUser={currentUser} setError={setError} />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SUPERADMIN TENANT EMAIL CONFIGURATIONS MANAGEMENT
// ============================================================================
const SuperadminTenantEmailConfigs = ({ currentUser, setError }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    suspendedOnly: false,
    overLimitOnly: false
  });
  const [alerts, setAlerts] = useState([]);
  const [suspendReason, setSuspendReason] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchConfigs();
    fetchAlerts();
  }, [filters]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status_filter', filters.status);
      if (filters.suspendedOnly) params.append('suspended_only', 'true');
      if (filters.overLimitOnly) params.append('over_limit_only', 'true');

      const response = await axios.get(`${API}/email-intake/admin/configs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setConfigs(response.data || []);
    } catch (err) {
      setError('Failed to load tenant configurations');
      console.error('Error fetching configs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/email-intake/admin/alerts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setAlerts(response.data || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const fetchConfigDetail = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/email-intake/admin/configs/${tenantId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSelectedConfig(response.data);
      setShowDetailModal(true);
    } catch (err) {
      setError('Failed to load configuration details');
    }
  };

  const handleSuspend = async (tenantId) => {
    if (!suspendReason || suspendReason.length < 10) {
      alert('Please provide a reason for suspension (min 10 characters)');
      return;
    }
    try {
      setActionLoading(true);
      await axios.post(`${API}/email-intake/admin/configs/${tenantId}/suspend`, {
        reason: suspendReason,
        notify_tenant: true
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSuspendReason('');
      fetchConfigs();
      setShowDetailModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to suspend tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async (tenantId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/email-intake/admin/configs/${tenantId}/resume`, {
        notes: 'Resumed by superadmin'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      fetchConfigs();
      setShowDetailModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resume tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetLimits = async (tenantId) => {
    if (!newLimit || parseInt(newLimit) < 0) {
      alert('Please enter a valid daily limit');
      return;
    }
    try {
      setActionLoading(true);
      await axios.put(`${API}/email-intake/admin/configs/${tenantId}/limits`, {
        daily_email_limit: parseInt(newLimit),
        reset_current_count: false
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setNewLimit('');
      fetchConfigs();
      if (selectedConfig) {
        fetchConfigDetail(tenantId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update limits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNotes = async (tenantId) => {
    if (!adminNotes) return;
    try {
      setActionLoading(true);
      await axios.put(`${API}/email-intake/admin/configs/${tenantId}/notes`, {
        notes: adminNotes,
        mark_reviewed: true
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setAdminNotes('');
      if (selectedConfig) {
        fetchConfigDetail(tenantId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add notes');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      disabled: 'bg-gray-100 text-gray-800',
      draft: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.draft}`}>
        {status?.toUpperCase() || 'DRAFT'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Active Alerts</h3>
          <div className="space-y-2">
            {alerts.map((alert, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                alert.severity === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span>{alert.severity === 'critical' ? '🚨' : alert.severity === 'warning' ? '⚠️' : 'ℹ️'}</span>
                    <span className={`font-medium ${
                      alert.severity === 'critical' ? 'text-red-700' :
                      alert.severity === 'warning' ? 'text-yellow-700' : 'text-blue-700'
                    }`}>{alert.message}</span>
                  </div>
                  <button
                    onClick={() => fetchConfigDetail(alert.tenant_id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Tenant Email Configurations</h3>
          <p className="text-gray-500">Manage email intake settings across all tenants</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="draft">Draft</option>
            <option value="disabled">Disabled</option>
          </select>
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={filters.suspendedOnly}
              onChange={(e) => setFilters({ ...filters, suspendedOnly: e.target.checked })}
              className="rounded text-blue-600"
            />
            <span>Suspended Only</span>
          </label>
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={filters.overLimitOnly}
              onChange={(e) => setFilters({ ...filters, overLimitOnly: e.target.checked })}
              className="rounded text-blue-600"
            />
            <span>Over Limit</span>
          </label>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{configs.length}</div>
          <div className="text-sm text-gray-600">Total Configs</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-600">
            {configs.filter(c => c.status === 'active').length}
          </div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-red-600">
            {configs.filter(c => c.status === 'suspended').length}
          </div>
          <div className="text-sm text-gray-600">Suspended</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-orange-600">
            {alerts.length}
          </div>
          <div className="text-sm text-gray-600">Active Alerts</div>
        </div>
      </div>

      {/* Configs Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage Today</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connection</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <div className="text-6xl mb-4">📭</div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Configurations Found</h4>
                  <p className="text-gray-500">No tenant email configurations match your filters</p>
                </td>
              </tr>
            ) : (
              configs.map((config) => (
                <tr key={config.tenant_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{config.tenant_name || config.tenant_id}</div>
                    <div className="text-xs text-gray-500">{config.tenant_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{config.email_address}</div>
                    <div className="text-xs text-gray-500">{config.provider_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(config.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {config.emails_processed_today || 0} / {config.daily_email_limit || 1000}
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className={`h-1.5 rounded-full ${
                          ((config.emails_processed_today || 0) / (config.daily_email_limit || 1000)) > 0.8
                            ? 'bg-red-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, ((config.emails_processed_today || 0) / (config.daily_email_limit || 1000)) * 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <span className={config.imap_connected ? 'text-green-500' : 'text-gray-400'}>
                        {config.imap_connected ? '✅' : '⚪'}
                      </span>
                      <span className="text-xs text-gray-500">IMAP</span>
                      <span className={config.smtp_connected ? 'text-green-500' : 'text-gray-400'}>
                        {config.smtp_connected ? '✅' : '⚪'}
                      </span>
                      <span className="text-xs text-gray-500">SMTP</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => fetchConfigDetail(config.tenant_id)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </button>
                      {config.status === 'active' ? (
                        <button
                          onClick={() => {
                            setSelectedConfig(config);
                            setShowDetailModal(true);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Suspend
                        </button>
                      ) : config.status === 'suspended' ? (
                        <button
                          onClick={() => handleResume(config.tenant_id)}
                          disabled={actionLoading}
                          className="text-green-600 hover:text-green-800 text-sm disabled:opacity-50"
                        >
                          Resume
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedConfig.tenant_name || selectedConfig.tenant_id}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedConfig.email_address}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="font-medium">Status:</span>
                {getStatusBadge(selectedConfig.status)}
              </div>

              {/* Connection Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Connection Status</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <span className={selectedConfig.connection_status?.imap_connected ? 'text-green-500' : 'text-red-500'}>
                      {selectedConfig.connection_status?.imap_connected ? '✅' : '❌'}
                    </span>
                    <span>IMAP: {selectedConfig.connection_status?.imap_connected ? 'Connected' : 'Disconnected'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={selectedConfig.connection_status?.smtp_connected ? 'text-green-500' : 'text-red-500'}>
                      {selectedConfig.connection_status?.smtp_connected ? '✅' : '❌'}
                    </span>
                    <span>SMTP: {selectedConfig.connection_status?.smtp_connected ? 'Connected' : 'Disconnected'}</span>
                  </div>
                </div>
              </div>

              {/* Rate Limits */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Rate Limits</h4>
                <div className="flex items-center justify-between mb-2">
                  <span>Daily Usage:</span>
                  <span className="font-medium">
                    {selectedConfig.rate_limits?.emails_processed_today || 0} / {selectedConfig.rate_limits?.daily_email_limit || 1000}
                  </span>
                </div>
                <div className="flex items-center space-x-2 mt-4">
                  <input
                    type="number"
                    value={newLimit}
                    onChange={(e) => setNewLimit(e.target.value)}
                    placeholder="New daily limit"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => handleSetLimits(selectedConfig.tenant_id)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Update Limit
                  </button>
                </div>
              </div>

              {/* Superadmin Actions */}
              {selectedConfig.status === 'active' && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-3">Suspend Tenant</h4>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder="Reason for suspension (required, min 10 characters)"
                    rows={3}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm mb-2"
                  />
                  <button
                    onClick={() => handleSuspend(selectedConfig.tenant_id)}
                    disabled={actionLoading || suspendReason.length < 10}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Suspending...' : 'Suspend Email Intake'}
                  </button>
                </div>
              )}

              {selectedConfig.status === 'suspended' && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-3">Resume Tenant</h4>
                  <p className="text-sm text-green-700 mb-3">
                    Suspended reason: {selectedConfig.suspension_reason || 'No reason provided'}
                  </p>
                  <button
                    onClick={() => handleResume(selectedConfig.tenant_id)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Resuming...' : 'Resume Email Intake'}
                  </button>
                </div>
              )}

              {/* Admin Notes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Admin Notes</h4>
                {selectedConfig.superadmin_notes && (
                  <div className="bg-white p-3 rounded border border-gray-200 mb-3 text-sm">
                    {selectedConfig.superadmin_notes}
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add investigation notes..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => handleAddNotes(selectedConfig.tenant_id)}
                    disabled={actionLoading || !adminNotes}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 disabled:opacity-50"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Mailbox Routing Rules Component
const MailboxRoutingRules = ({ currentUser, setError }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchRoutingRules();
  }, []);

  const fetchRoutingRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email-intake/routing-rules`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setRules(response.data);
      setError('');
    } catch (error) {
      console.error('Failed to fetch routing rules:', error);
      setError('Failed to load routing rules');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Mailbox Routing Rules</h3>
          <p className="text-gray-500">Configure how inbound emails are routed and processed</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Create Rule</span>
        </button>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{rules.length}</div>
            <div className="text-sm text-gray-600">Active Rules</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">24,891</div>
            <div className="text-sm text-gray-600">Emails Processed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">97.3%</div>
            <div className="text-sm text-gray-600">Success Rate</div>
          </div>
        </div>

        <div className="space-y-4">
          {rules.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">📮</div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Routing Rules</h4>
              <p className="text-gray-500 mb-4">Create routing rules to manage email processing</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create First Rule
              </button>
            </div>
          ) : (
            rules.map((rule) => (
              <div key={rule.id} className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${rule.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <div>
                      <h4 className="font-medium text-gray-900">{rule.rule_name}</h4>
                      <p className="text-sm text-gray-500">{rule.rule_description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                      Priority {rule.priority}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600">
                      <span className="text-xl">⚙️</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// JD Email Mappings Component
const JDEmailMappings = ({ currentUser, setError }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJDMappings();
  }, []);

  const fetchJDMappings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email-intake/jd-mappings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setMappings(response.data);
      setError('');
    } catch (error) {
      console.error('Failed to fetch JD mappings:', error);
      setError('Failed to load JD mappings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900">JD → Email Mapping</h3>
        <p className="text-gray-500">Job description to email address mappings across all tenants</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{mappings.length}</div>
            <div className="text-sm text-gray-600">Active Mappings</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">1,247</div>
            <div className="text-sm text-gray-600">Resumes Received</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">923</div>
            <div className="text-sm text-gray-600">Successful Conversions</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">74.1%</div>
            <div className="text-sm text-gray-600">Conversion Rate</div>
          </div>
        </div>

        <div className="bg-white rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    JD Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resumes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mappings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="text-6xl mb-4">🔗</div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No JD Mappings</h4>
                      <p className="text-gray-500">No job description email mappings have been created yet</p>
                    </td>
                  </tr>
                ) : (
                  mappings.map((mapping) => (
                    <tr key={mapping.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{mapping.jd_code}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{mapping.jd_title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-blue-600">{mapping.dedicated_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{mapping.tenant_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{mapping.total_resumes_processed || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 text-xs leading-5 font-semibold rounded-full ${
                          mapping.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {mapping.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const InboxProcessingLogs = ({ currentUser, setError }) => {
  return (
    <div className="text-center py-12">
      <span className="text-6xl">📋</span>
      <h3 className="text-xl font-bold text-gray-900 mt-4 mb-2">Inbox Processing Logs</h3>
      <p className="text-gray-500">Real-time email processing activity and status monitoring</p>
      <p className="text-sm text-blue-600 mt-4">Advanced logging interface coming soon</p>
    </div>
  );
};

const AttachmentExtractionLogs = ({ currentUser, setError }) => {
  return (
    <div className="text-center py-12">
      <span className="text-6xl">📎</span>
      <h3 className="text-xl font-bold text-gray-900 mt-4 mb-2">Attachment Extraction Logs</h3>
      <p className="text-gray-500">Resume extraction and parsing activity logs</p>
      <p className="text-sm text-blue-600 mt-4">Advanced extraction analytics coming soon</p>
    </div>
  );
};

const SpamSecurityFilter = ({ currentUser, setError }) => {
  return (
    <div className="text-center py-12">
      <span className="text-6xl">🛡️</span>
      <h3 className="text-xl font-bold text-gray-900 mt-4 mb-2">Spam / Security Filter</h3>
      <p className="text-gray-500">Spam detection and security filtering configuration</p>
      <p className="text-sm text-blue-600 mt-4">Advanced security controls coming soon</p>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // A/B test color scheme hook - used for landing page
  const { colors: landingColors, scheme: colorScheme, trackConversion } = useColorScheme();

  // Removed feature flag loading - using pure RBAC control now

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API}/auth/me`);
          setCurrentUser(response.data);
          setIsAuthenticated(true);
          
          // Authentication successful
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('auth_token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const handleLogin = async (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Continue with logout even if API call fails
    }
    
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setIsAuthenticated(false);
    
    // Clear any cached data
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <DensityProvider>
      <div className="App">
        {isAuthenticated ? (
          <Dashboard
            user={currentUser}
            onLogout={handleLogout}
          />
        ) : (
          <LoginPage onLogin={handleLogin} />
        )}
      </div>
    </DensityProvider>
  );
}

export default App;